import styled, { ThemeProvider as ThemeProvider$1, css, keyframes, ThemeContext } from 'styled-components';
import * as React from 'react';
import React__default, { useState, useRef, useEffect, useMemo, useContext, createContext, useCallback, useReducer } from 'react';
import { lighten, getContrast, rgba } from 'polished';
import ReactDOM, { flushSync } from 'react-dom';

const BaseButton = styled.button.attrs(({ type }) => ({
    type: type || 'button',
})) `
  display: inline-block;
  border: 1px transparent solid;
  background: transparent;
  user-select: none;
  box-shadow: none;
  text-decoration: none;
  vertical-align: baseline;
  text-align: center;
  padding: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  outline-color: silver;
`;

const Fonts = [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'body1',
    'body2',
    'subtitle1',
    'subtitle2',
    'fontButton',
    'smallFontButton',
    'caption1',
    'caption2',
    'link',
    'input',
];
const ThemeProvider = ({ theme, children }) => {
    return React.createElement(ThemeProvider$1, { theme: theme }, children);
};

const box = css `
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.box.borderRadius};
`;
const shadow1 = css `
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;
const shadow2 = css `
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`;
const card = css `
  ${box};
  ${shadow1};
`;
const bubble = css `
  ${box};
`;
const input = css `
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.input.borderRadius};
  padding: 7px 8px 8px 7px;
`;
const smallInput = css `
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.input.borderRadius};
  padding: 2px 5px 2px 5px;
`;
const button = css `
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.button.default.borderRadius};
  padding: ${({ theme }) => theme.button.default.padding};
`;
const smallButton = css `
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.button.small.borderRadius};
  padding: ${({ theme }) => theme.button.small.padding};
`;
const circle = css `
  ${box};
  border-radius: 50%;
`;
const line = css `
  ${box};
  height: 1px;
`;
const Shapes = {
    card,
    bubble,
    input,
    button,
    smallButton,
    circle,
    line,
    shadow1,
    shadow2,
    smallInput,
};

const antialias = css `
  -webkit-text-size-adjust: 100%;
  -ms-text-size-adjust: 100%;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-feature-settings: 'pnum' on, 'lnum' on;
  font-variant-ligatures: none;
`;
const getTypo = (font) => {
    return css `
    ${antialias};
    font-family: ${({ theme }) => theme.fonts[font].family};
    font-weight: ${({ theme }) => theme.fonts[font].weight};
    font-size: ${({ theme }) => theme.fonts[font].size};
    line-height: ${({ theme }) => theme.fonts[font].lineHeight};
    color: ${({ theme }) => theme.fonts[font].color};
    letter-spacing: ${({ theme }) => theme.fonts[font].letterSpacing};
    font-style: ${({ theme }) => theme.fonts[font].style};
    text-transform: ${({ theme }) => theme.fonts[font].textTransform};
    text-decoration: ${({ theme }) => theme.fonts[font].textDecoration};
    margin: ${({ theme }) => theme.fonts[font].margin};
  `;
};
const SpanH1 = styled.span `
  ${getTypo('h1')};
`;
const SpanH2 = styled.span `
  ${getTypo('h2')};
`;
const SpanH3 = styled.span `
  ${getTypo('h3')};
`;
const SpanH4 = styled.span `
  ${getTypo('h4')};
`;
const SpanH5 = styled.span `
  ${getTypo('h5')};
`;
const SpanH6 = styled.span `
  ${getTypo('h6')};
`;
const SpanBody1 = styled.span `
  ${getTypo('body1')};
`;
const SpanBody2 = styled.span `
  ${getTypo('body2')};
`;
const SpanCaption1 = styled.span `
  ${getTypo('caption1')};
`;
const SpanCaption2 = styled.span `
  ${getTypo('caption2')};
`;
const SpanButton = styled.span `
  ${getTypo('fontButton')};
`;
const SpanSmallButton = styled.span `
  ${getTypo('smallFontButton')};
`;
const SpanSubtitle1 = styled.span `
  ${getTypo('subtitle1')};
`;
const SpanSubtitle2 = styled.span `
  ${getTypo('subtitle2')};
`;
const SpanLink = styled.span `
  ${getTypo('link')};
  cursor: pointer;
  transition: color ease-in-out 200ms;
  &:hover {
    color: ${({ theme }) => lighten(0.2, theme.fonts.link.color)};
  }
`;
const H1 = styled.h1 `
  ${getTypo('h1')};
`;
const H2 = styled.h2 `
  ${getTypo('h2')};
`;
const H3 = styled.h3 `
  ${getTypo('h3')};
`;
const H4 = styled.h4 `
  ${getTypo('h4')};
`;
const H5 = styled.h5 `
  ${getTypo('h5')};
`;
const H6 = styled.h6 `
  ${getTypo('h6')};
`;
const P = styled.p `
  ${getTypo('body1')};
  margin: 0 0 8px 0;
`;
const PageTitle = styled.h3 `
  ${getTypo('h3')};
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 15px 0;
`;
const PageSubtitle = styled.h6 `
  ${getTypo('h6')};
  color: ${({ theme }) => theme.colors.grey40};
  margin: 0 0 15px 0;
`;
const CardTitle = styled.h4 `
  ${getTypo('body2')};
  transition: color ease-in-out 200ms;
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 15px 0;
`;
const CardSubtitle = styled.p `
  ${getTypo('subtitle1')};
  transition: color ease-in-out 200ms;
  color: ${({ theme }) => theme.colors.grey40};
  margin: 0 0 25px 0;
`;
const DashboardCardSubtitle = styled.p `
  ${getTypo('subtitle1')};
  color: ${({ theme }) => theme.colors.grey60};
  margin: 0 0 16px 0;
`;
const DashboardCardTitle = styled.h3 `
  ${getTypo('h3')};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.dark};
  margin: 0 0 16px 0;
`;
const DashboardCardBody = styled.p `
  ${getTypo('body1')};
  color: ${({ theme }) => theme.colors.grey40};
  margin: 0;
`;

const fadeIn = [{ opacity: 0 }, { opacity: 1 }];
const fadeOut = [{ opacity: 1 }, { opacity: 0 }];
const slideInFrom = (x = '0', y = '0') => {
    return [
        {
            opacity: 0,
            transform: `translate3d(${x},${y},0)`,
            transformOrigin: 'top',
        },
        {
            opacity: 1,
            transform: 'translate3d(0,0,0)',
            transformOrigin: 'top',
        },
    ];
};
const slideOutFrom = (x = '0', y = '0') => {
    return [
        {
            opacity: 1,
            pointerEvents: 'none',
            transform: 'translate3d(0,0,0)',
            transformOrigin: 'top',
        },
        {
            opacity: 0,
            pointerEvents: 'none',
            transform: `translate3d(${x},${y},0)`,
            transformOrigin: 'top',
        },
    ];
};
const slideInFromRight = [
    {
        opacity: 0,
        transform: 'translate3d(50%,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 1,
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
];
const slideInFromLeft = [
    {
        opacity: 0,
        transform: 'translate3d(-50%,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 1,
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
];
const slideInFromTop = [
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(0,-50%,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
];
const slideInFromBottom = [
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(0,50%,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
];
const slideOutToRight = [
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(50%,0,0)',
        transformOrigin: 'top',
    },
];
const slideOutToLeft = [
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(-50%,0,0)',
        transformOrigin: 'top',
    },
];
const slideOutToTop = [
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(0,-50%,0)',
        transformOrigin: 'top',
    },
];
const slideOutToBottom = [
    {
        opacity: 1,
        pointerEvents: 'none',
        transform: 'translate3d(0,0,0)',
        transformOrigin: 'top',
    },
    {
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translate3d(0,50%,0)',
        transformOrigin: 'top',
    },
];
const growInToRight = [
    {
        transform: 'scale(0,1)',
        transformOrigin: 'left',
    },
    {
        transform: 'scale(1,1)',
        transformOrigin: 'left',
    },
];
const increaseHeight = (maxHeight) => {
    return [
        {
            maxHeight: '0px',
            overflow: 'hidden',
        },
        {
            maxHeight,
            overflow: 'hidden',
        },
    ];
};
const increaseWidth = (maxWidth) => {
    return [
        {
            maxWidth: '0px',
            overflow: 'hidden',
        },
        {
            maxWidth,
            overflow: 'hidden',
        },
    ];
};
const decreaseWidth = (maxWidth) => {
    return [
        {
            maxWidth,
            overflow: 'hidden',
        },
        {
            maxWidth: '0px',
            overflow: 'hidden',
        },
    ];
};
const decreaseHeight = (maxHeight) => {
    return [
        {
            maxHeight,
            overflow: 'hidden',
        },
        {
            maxHeight: '0px',
            overflow: 'hidden',
        },
    ];
};
const Keyframes = {
    fadeIn,
    fadeOut,
    slideInFromRight,
    slideInFromLeft,
    slideInFromTop,
    slideInFromBottom,
    slideOutToRight,
    slideOutToLeft,
    slideOutToTop,
    slideOutToBottom,
    growInToRight,
    increaseWidth,
    increaseHeight,
    decreaseHeight,
    decreaseWidth,
    slideInFrom,
    slideOutFrom,
};

const created = 'created';
const completed = 'completed';
const finalized = 'finalized';
const error = 'error';

const DEVICES = ['xs', 'small', 'tablet', 'desktop', 'xl'];
const PickChoiceByDevice = (device, choices) => {
    switch (device) {
        case 'xl':
            return choices[4] || choices[3] || choices[2] || choices[1] || choices[0];
        case 'desktop':
            return choices[3] || choices[2] || choices[1] || choices[0];
        case 'tablet':
            return choices[2] || choices[1] || choices[0];
        case 'small':
            return choices[1] || choices[0];
        case 'xs':
        default:
            return choices[0];
    }
};
const MakeCssMediaQuery = (css, devices) => {
    let media = 0;
    switch (devices) {
        case 'xs':
            return css;
        case 'small':
            media = 576;
            break;
        case 'tablet':
            media = 768;
            break;
        case 'desktop':
            media = 992;
            break;
        case 'xl':
            media = 1200;
            break;
    }
    return `
     @media (min-width: ${media}px) {
        ${css}
     }
    `;
};
const MakeCssMediaQueryStrict = (css, devices) => {
    let min = 0;
    let max = 0;
    switch (devices) {
        case 'xs':
            max = 575;
            break;
        case 'small':
            min = 576;
            max = 767;
            break;
        case 'tablet':
            min = 768;
            max = 991;
            break;
        case 'desktop':
            min = 992;
            max = 1200;
            break;
        case 'xl':
            min = 1200;
            break;
    }
    return `
     @media (min-width: ${min}px) ${max ? `and (max-width: ${max}px)` : ''}  {
        ${css}
     }
    `;
};

const HslToCss = (hue, saturate, light) => {
    return `hsl(${Math.round(hue)},${Math.round(saturate)}%,${Math.round(light)}%)`;
};
const ColorSelectGradientToCss = (hue) => {
    return `linear-gradient(90deg,#ffffff 0%, hsl(${hue}, 100%, 50%) 100%)`;
};
const HueToRgb = (p, q, t) => {
    if (t < 0)
        t += 1;
    if (t > 1)
        t -= 1;
    if (t < 1 / 6)
        return p + (q - p) * 6 * t;
    if (t < 1 / 2)
        return q;
    if (t < 2 / 3)
        return p + (q - p) * (2 / 3 - t) * 6;
    return p;
};
const HslToRgb = (hue, saturate, light) => {
    const h = hue;
    const s = saturate;
    const l = light;
    let r, g, b;
    if (s === 0) {
        r = g = b = l;
    }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = HueToRgb(p, q, h + 1 / 3);
        g = HueToRgb(p, q, h);
        b = HueToRgb(p, q, h - 1 / 3);
    }
    return new Uint8Array([Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]);
};
const calcHue = (max, rRatio, gRatio, bRatio, d) => {
    switch (max) {
        case rRatio:
            return (gRatio - bRatio) / d + (gRatio < bRatio ? 6 : 0);
        case gRatio:
            return (bRatio - rRatio) / d + 2;
        case bRatio:
            return (rRatio - gRatio) / d + 4;
        default:
            return 0;
    }
};
const RgbToHsl = (red, green, blue) => {
    const rRatio = red / 255;
    const gRatio = green / 255;
    const bRatio = blue / 255;
    const max = Math.max(rRatio, gRatio, bRatio);
    const min = Math.min(rRatio, gRatio, bRatio);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        h = calcHue(max, rRatio, gRatio, bRatio, d) / 6;
    }
    return new Float32Array([h, s, l]);
};
const HsvToHsl = (hue, sat, val) => {
    const newHue = (2 - sat) * val;
    return [
        hue,
        newHue > 0 ? (newHue < 2 ? (sat * val) / (newHue < 1 ? newHue : 2 - newHue) : 1) : 0,
        newHue / 2,
    ];
};
const HslToHsv = (hue, sat, light) => {
    sat *= light < 0.5 ? light : 1 - light;
    const sumLS = light + sat;
    return [hue, sumLS > 0 ? (2 * sat) / sumLS : 0, sumLS];
};
/**
 * return color of the list which better for contrast
 * @param whichColor color to test contrast
 * @param contrastColors list of colors to choose for contrast
 * @constructor
 */
const SelectContrast = (whichColor, contrastColors) => {
    const contrastValues = contrastColors.map((color) => getContrast(whichColor || '#000', color));
    const max = Math.max(...contrastValues);
    const index = contrastValues.indexOf(max);
    return contrastColors[index];
};
const DecToHexaString = (val) => {
    const result = val.toString(16);
    return result.length < 2 ? '0' + result : result;
};
const HsvToCssHexaColor = (hue, x, y) => {
    const hsl = HsvToHsl(hue, x, y);
    const rgb = HslToRgb(hsl[0], hsl[1], hsl[2]);
    return `#${DecToHexaString(rgb[0])}${DecToHexaString(rgb[1])}${DecToHexaString(rgb[2])}`.toUpperCase();
};
const CssHexaColorToHsv = (cssHexa) => {
    const extract = cssHexa.replace('#', '');
    const colorParse = [];
    for (let i = 0; i < extract.length; i += 2) {
        colorParse.push(Number.parseInt(extract.slice(i, i + 2), 16));
    }
    const hsl = RgbToHsl(colorParse[0], colorParse[1], colorParse[2]);
    return HslToHsv(hsl[0], hsl[1], hsl[2]);
};
const CoordinateToHsv = (hue360, cursorX, cursorY, width, height) => {
    return [hue360 / 360, cursorX / width, (height - cursorY) / height];
};
const HsvToCoordinate = (hsv, width, height) => {
    return {
        hue360: hsv[0] * 360,
        cursorX: width * hsv[1],
        cursorY: height - hsv[2] * height,
    };
};

/**
 * return comparison of 2 dates or number, chars
 * a is less than b return -1
 * b is greater than a return 1
 * a equal b return 0
 * @param a Date | number | string
 * @param b Date | number | string
 * @returns {number}
 * @constructor
 */
const Compare = (a, b) => {
    return a < b ? -1 : a > b ? 1 : 0;
};
/**
 * return 0 if in range -1 if is min +1 if is max
 * @param {Date | number | string} which
 * @param {Date | number | string} [min]
 * @param {Date | number | string} [max]
 * @param {boolean} [strict]
 * @return {number}
 */
const RangeSign = (which, min, max, strict) => {
    let isOutMin = false;
    let isOutMax = false;
    if (min) {
        isOutMin = strict ? Compare(which, min) < 1 : Compare(which, min) === -1;
    }
    if (max) {
        isOutMax = strict ? Compare(which, max) > -1 : Compare(which, max) === 1;
    }
    return !(isOutMin || isOutMax) ? 0 : isOutMin ? -1 : isOutMax ? 1 : 0;
};
const setInRange = (value, min, max) => {
    if (typeof min !== 'undefined' && value < min) {
        return min;
    }
    if (typeof max !== 'undefined' && value > max) {
        return max;
    }
    return value;
};
const LoopInRange = (value, min, max) => {
    if (value < min) {
        return max;
    }
    if (value > max) {
        return min;
    }
    return value;
};
const SetInRange = (value, min, max) => {
    if (typeof min === 'number' && value < min) {
        return min;
    }
    if (typeof max === 'number' && value > max) {
        return max;
    }
    return value;
};
const Increment = (value, n, min, max, loop) => {
    const result = value + n;
    if (loop && typeof max === 'number' && typeof min === 'number') {
        return LoopInRange(result, min, max);
    }
    return SetInRange(result, min, max);
};
const Round = (n, precision) => {
    return Math.round(n * precision) / precision;
};
const Pad = (n, width, insert = '0') => {
    const str = n.toString(10);
    return str.length >= width ? str : new Array(width - str.length + 1).join(insert) + str;
};

const IsSameDay = (dateA, dateB) => {
    return (dateA.getFullYear() === dateB.getFullYear() &&
        dateA.getMonth() === dateB.getMonth() &&
        dateA.getDate() === dateB.getDate());
};
const MonthName = (date, locales) => {
    return date.toLocaleString(locales, { month: 'long' });
};
const DayName = (date, locales) => {
    return date.toLocaleString(locales, { weekday: 'long' });
};
const FirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1);
};
const LastDayOfMonth = (year, month) => {
    return new Date(year, month + 1, 0);
};
const getDayOfWeek = (index, offset) => {
    const today = new Date();
    let offsetDay = index + offset; // in js day begin by sunday
    offsetDay = offsetDay > 6 ? 0 : offsetDay < 0 ? 6 : offsetDay;
    return new Date(today.setDate(today.getDate() - today.getDay() + offsetDay));
};
const FirstMonDayOfWeek = (date, offset = 1) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + offset;
    if (diff > 1) {
        return new Date(d.setDate(diff - 7));
    }
    return new Date(d.setDate(diff));
};
const AddDay = (date, day) => {
    const d = new Date(date);
    const add = d.getDate() + day;
    return new Date(d.setDate(add));
};
const DaysName = (offset, locales) => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
        arr.push(DayName(getDayOfWeek(i, offset), locales));
    }
    return arr;
};
const MonthsNames = (locales) => {
    const arr = [];
    for (let i = 0; i < 12; i++) {
        arr.push(MonthName(new Date(new Date(new Date().setDate(1)).setMonth(i)), locales));
    }
    return arr;
};
const IsDisabledDate = (date, disabled) => {
    if (!disabled) {
        return false;
    }
    const { max, min, intervals, days, weekDays } = disabled;
    let val = false;
    const sign = RangeSign(date, min, max);
    if (sign !== 0) {
        val = true;
    }
    if (intervals) {
        intervals.forEach(([a, b]) => {
            const signInterval = RangeSign(date, a, b);
            if (signInterval === 0) {
                val = true;
            }
        });
    }
    if (days && days.some((dd) => IsSameDay(date, dd))) {
        val = true;
    }
    if (weekDays && weekDays.some((day) => date.getDay() === day)) {
        val = true;
    }
    return val;
};
const localToDateParams = (locale = navigator.language) => {
    switch (locale) {
        case 'fr':
        case 'fr_FR':
        case 'fr_CA':
            return {
                separator: '/',
                order: ['d', 'm', 'y'],
            };
        default:
        case 'en':
        case 'en_US':
        case 'en_CA':
        case 'en_AU':
            return {
                separator: '-',
                order: ['m', 'd', 'y'],
            };
    }
};
const dateToString = (date, locale) => {
    const params = localToDateParams(locale);
    let typeDate = '';
    if (date) {
        const mm = date.getMonth() + 1;
        const dd = date.getDate();
        params.order.forEach((val) => {
            switch (val) {
                case 'd':
                    typeDate += (dd > 9 ? '' : '0') + dd + params.separator;
                    break;
                case 'm':
                    typeDate += (mm > 9 ? '' : '0') + mm + params.separator;
                    break;
                case 'y':
                    typeDate += date.getFullYear();
                    break;
            }
        });
    }
    return typeDate;
};

const QueryChildrenByType = (children, type) => {
    const arrayChildren = React.Children.toArray(children);
    return arrayChildren.find((element) => {
        if (React.isValidElement(element)) {
            return element.type === type;
        }
        return false;
    });
};
const ExcludeChildrenByType = (children, types) => {
    const arrayChildren = React.Children.toArray(children);
    return arrayChildren.filter((element) => {
        if (React.isValidElement(element)) {
            return !types.some((type) => element.type === type);
        }
        return true;
    });
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const UpdateItemInObject = (current, key, value) => {
    return Object.assign(Object.assign({}, current), { [key]: value });
};
const RemoveItemInObject = (current, key) => {
    // eslint-disable-next-line
    const _a = current, _b = key; _a[_b]; const extract = __rest(_a, [typeof _b === "symbol" ? _b : _b + ""]);
    return extract;
};
const FindAndDeleteItemInArray = (current, key, value) => {
    const index = current.findIndex((c) => c[key] === value[key]);
    if (index < 0) {
        return current;
    }
    return RemoveItemInArray(current, index);
};
const FindAndReplaceItemInArray = (current, key, value) => {
    const index = current.findIndex((c) => c[key] === value[key]);
    if (index < 0) {
        return current;
    }
    return ReplaceItemInArray(current, index, value);
};
const ReplaceItemInArray = (current, index, value) => {
    return current.map((item, i) => (i === index ? value : item));
};
const MergeItemInArray = (current, index, value) => {
    return current.map((item, i) => (i === index ? Object.assign(Object.assign({}, item), value) : item));
};
const RemoveItemInArray = (current, index) => {
    return current.filter((_, i) => i !== index);
};
const TranslateItemInArray = (arr, from, to) => {
    if (to < 0) {
        return arr;
    }
    const copy = arr.slice();
    copy.splice(to, 0, copy.splice(from, 1)[0]);
    return copy;
};
const ReducerNumber = (current, action) => {
    if (typeof action.set === 'number') {
        return action.set;
    }
    if (typeof action.increment === 'number') {
        return current + action.increment;
    }
    if (typeof action.multiply === 'number') {
        return current * action.multiply;
    }
    if (typeof action.divide === 'number') {
        return current / action.divide;
    }
    return current;
};
const ReducerArray = (current, action) => {
    if (action.clean === true) {
        return [];
    }
    if (typeof action.findAnd !== 'undefined') {
        const findAction = action.findAnd.action;
        const findSelection = action.findAnd.selection;
        if (findAction.remove === true) {
            return current.filter((item, i) => {
                if (typeof findSelection.unique === 'number') {
                    return findSelection.unique !== i;
                }
                if (typeof findSelection.multiple !== 'undefined') {
                    const { key, equal } = findSelection.multiple;
                    return item[key] !== equal[key];
                }
                return true;
            });
        }
        if (typeof findAction.replace !== 'undefined' || typeof findAction.merge !== 'undefined') {
            return current.map((item, i) => {
                const newItem = typeof findAction.replace !== 'undefined'
                    ? findAction.replace
                    : typeof findAction.merge !== 'undefined'
                        ? Object.assign(Object.assign({}, item), findAction.merge) : item;
                if (typeof findSelection.unique === 'number') {
                    return findSelection.unique === i ? newItem : item;
                }
                if (typeof findSelection.multiple !== 'undefined') {
                    const { key, equal } = findSelection.multiple;
                    return item[key] === equal[key] ? newItem : item;
                }
                return item;
            });
        }
    }
    if (typeof action.mergeAfter !== 'undefined') {
        return [...current, ...action.mergeAfter];
    }
    if (typeof action.mergeBefore !== 'undefined') {
        return [...action.mergeBefore, ...current];
    }
    if (typeof action.push !== 'undefined') {
        return [...current, action.push];
    }
    if (typeof action.unshift !== 'undefined') {
        return [action.unshift, ...current];
    }
    if (typeof action.replace !== 'undefined') {
        return action.replace;
    }
    return current;
};
const ReducerObject = (current, action) => {
    if (action.replace) {
        return action.replace;
    }
    if (action.merge) {
        return Object.assign(Object.assign({}, current), action.merge);
    }
    return current;
};

const mergeStyled = (base, cssExtends) => {
    // @ts-ignore
    return cssExtends.reduce((prev, current) => {
        return styled(prev) `
      ${current}
    `;
    }, base);
};

/**
 *
 * @param str: string
 * @returns {string}
 */
const NormalizeText = (str) => {
    if (!str) {
        return '';
    }
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};
/**
 *
 * @param word
 * @param phrase
 * @returns {RegExpMatchArray}
 */
const MatchLike = (phrase, word) => {
    const sanitize = word.replace(/[#-.]|[[-^]|[?|{}]/g, '\\$&');
    const reg = phrase.toUpperCase().match(new RegExp(sanitize.toUpperCase()));
    if (reg) {
        return reg;
    }
};

const ReadFileToDataUrl = async (file, maxSize = 2) => {
    return new Promise((resolve, reject) => {
        const size = file.size / (1024 * 1024);
        if (size > maxSize) {
            return reject('fileSize');
        }
        const reader = new FileReader();
        reader.addEventListener('load', () => {
            resolve(reader.result);
        }, { once: true });
        reader.addEventListener('abort', reject, { once: true });
        reader.addEventListener('error', reject, { once: true });
        reader.readAsDataURL(file);
    });
};

const getFocusable = (element) => {
    return element.querySelectorAll(`
    input:not([tabindex^="-"]):not(:disabled),
    textarea:not([tabindex^="-"]):not(:disabled),
    select:not([tabindex^="-"]):not(:disabled),
    button:not([tabindex^="-"]):not(:disabled),
    a[href]:not([tabindex^="-"]):not(:disabled),
    *[tabindex]:not([tabindex^="-"]):not(:disabled)
  `);
};
const focusFirstFocusableElement = (element) => {
    const focusable = getFocusable(element);
    if (!focusable) {
        return;
    }
    if (focusable.length) {
        focusable[0].focus();
    }
};
const focusLastFocusableElement = (element) => {
    const focusable = getFocusable(element);
    if (!focusable) {
        return;
    }
    if (focusable.length) {
        focusable[focusable.length - 1].focus();
    }
};
/**
 *
 * @param handler
 * @param time
 * @param ref du timeout
 * @constructor
 * @return return the new ref
 */
const Debounce = (handler, time, ref) => {
    window.clearTimeout(ref);
    return window.setTimeout(handler, time);
};

const withIcon = css `
  ${({ iconPosition, small }) => iconPosition === 'right'
    ? `padding-right:${small ? '24' : '30'}px`
    : iconPosition === 'left'
        ? `padding-left:${small ? '24' : '30'}px`
        : ''};
`;
const shapeInput = css `
  ${({ small }) => (small ? Shapes.smallInput : Shapes.input)};
`;
const rightInput = css `
  ${({ right }) => (right ? 'text-align: right' : '')};
`;
const errorInput = css `
  border: 1px solid
    ${({ hasError, theme }) => (hasError ? theme.colors.danger : theme.colors.grey90)};

  &:focus:not(:disabled):not(:read-only) {
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.lightDanger : theme.colors.grey60};
  }

  &:hover:not(:disabled):not(:read-only) {
    border-color: ${({ hasError, theme }) => hasError ? theme.colors.lightDanger : theme.colors.grey70};
  }
`;
const themeInput = css `
  background: ${({ theme }) => theme.input.backgroundColor};
  color: ${({ theme }) => theme.input.color};
  border: ${({ theme }) => theme.input.borderColor} 1px solid;
  outline: none;
  width: 100%;
  margin: 0;
  transition: background ease-in-out 200ms, border-color ease-in-out 200ms, color ease-in-out 200ms,
    box-shadow ease-in-out 200ms;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey60};
  }

  &:focus:not(:disabled):not(:read-only) {
    background-color: ${({ theme }) => theme.colors.grey95};
    ${Shapes.shadow1};
  }

  &:hover:not(:disabled):not(:read-only) {
    background-color: ${({ theme }) => theme.colors.grey95};
  }

  &:disabled {
    border-color: ${({ theme }) => theme.colors.grey95};
    background-color: ${({ theme }) => theme.colors.grey95};
    cursor: not-allowed;

    &::placeholder {
      color: ${({ theme }) => theme.input.placeholderColor};
    }
  }

  &:read-only:not(:disabled) {
    background: transparent;
    margin-left: -1px;
    padding-left: 0;
    padding-right: 0;
    border-color: transparent;
  }
`;
const BaseInput = mergeStyled(styled.input ``, [
    getTypo('input'),
    shapeInput,
    withIcon,
    rightInput,
    themeInput,
    errorInput,
]);

const BaseInputImage = (props) => {
    const { maxSize, onChange, local, onLocalChange, onError } = props, rest = __rest(props, ["maxSize", "onChange", "local", "onLocalChange", "onError"]);
    const emitChange = (event) => {
        const target = event.target;
        onLocalChange(target.value);
        const files = target.files || new FileList();
        if (!files || !files.length) {
            return;
        }
        const file = files[0];
        if (file) {
            ReadFileToDataUrl(file, maxSize)
                .then((result) => {
                onChange({
                    stringFormat: result,
                    binary: file,
                });
            })
                .catch((error) => {
                onError(error);
            });
        }
    };
    return (React.createElement("input", Object.assign({ accept: ".gif,.jpg,.jpeg,.png" }, rest, { type: 'file', onChange: emitChange, value: local })));
};

const BaseInputNumber = (props) => {
    const { value, onChange } = props, rest = __rest(props, ["value", "onChange"]);
    const [getTemp, setTemp] = useState();
    const emitChange = (event) => {
        const target = event.target;
        if (!onChange) {
            console.warn('onChange is not defined');
            return;
        }
        if (typeof target.value === 'undefined' || target.value === '') {
            if (!target.validity.badInput) {
                onChange(null, event);
                return;
            }
        }
        const parsed = parseFloat(target.value);
        if (isNaN(parsed)) {
            setTemp(target.value);
            return;
        }
        setTemp(undefined);
        onChange(parsed, event);
    };
    return (React.createElement(BaseInput, Object.assign({}, rest, { type: 'number', value: typeof getTemp === 'undefined'
            ? typeof value === 'undefined' || value === null
                ? ''
                : value
            : getTemp, onChange: emitChange })));
};

const execAnimation = (element, keyframes, options) => {
    const option = Object.assign(Object.assign({}, options), { easing: options.easing || 'cubic-bezier(0.8, 0, 0.2, 1)', duration: options.duration || 250, delay: options.delay });
    return element.animate(keyframes, option);
};
const InOut = (props) => {
    const { children, show, startTriggering, enter, exit, style, autoScroll, startStyle, keepContent, } = props;
    const containerRef = useRef(null);
    const [getState, setState] = useState(show ? (startTriggering ? 'in' : 'shown') : 'out');
    const previous = useRef(startTriggering ? false : show);
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        if (!show) {
            return;
        }
        if (previous.current) {
            return;
        }
        previous.current = show;
        const anim = execAnimation(container, (enter === null || enter === undefined ? undefined : enter.keyframes) || Keyframes.fadeIn, (enter === null || enter === undefined ? undefined : enter.options) || {});
        setState('start');
        const onFinish = () => {
            flushSync(() => setState('shown'));
            if (autoScroll) {
                if (typeof autoScroll === 'boolean') {
                    container.scrollIntoView({
                        block: 'start',
                        behavior: 'smooth',
                    });
                }
                else {
                    container.scrollIntoView(autoScroll);
                }
            }
        };
        anim.addEventListener('finish', onFinish, { once: true });
        return () => {
            anim.finish();
        };
    }, [show, autoScroll, enter, exit]);
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        if (show) {
            return;
        }
        if (!previous.current) {
            return;
        }
        previous.current = show;
        const anim = execAnimation(container, (exit === null || exit === undefined ? undefined : exit.keyframes) || Keyframes.fadeOut, (exit === null || exit === undefined ? undefined : exit.options) || {});
        setState('end');
        const onFinish = () => {
            flushSync(() => setState('out'));
        };
        anim.addEventListener('finish', onFinish, { once: true });
        return () => {
            anim.finish();
        };
    }, [show, enter, exit, autoScroll]);
    return useMemo(() => {
        const hiddenStates = getState === 'in' || getState === 'out';
        return (React.createElement("div", { ref: containerRef, style: Object.assign(Object.assign({ visibility: hiddenStates ? 'hidden' : undefined, opacity: hiddenStates ? 0 : undefined, pointerEvents: hiddenStates ? 'none' : undefined }, (getState === 'start' ? startStyle || { opacity: 0 } : {})), style) }, hiddenStates && !keepContent ? null : children));
    }, [children, getState, keepContent, startStyle, style]);
};

const Wrapper$9 = styled.article `
  ${Shapes.card};
  background: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.card.padding};
  position: relative;
  transition: 200ms ease-in-out all;
  outline: none;
  border: 1px solid transparent;
  box-sizing: border-box;
  ${({ onClick, theme }) => onClick &&
    `
    cursor: pointer;
    &:hover,&:focus {
      ${Shapes.shadow2};
    }
    &:focus {
      border: 1px solid ${theme.colors.grey80};
    }
  `};
`;
const Card = (props) => {
    const { children, onClick } = props, rest = __rest(props, ["children", "onClick"]);
    return (React.createElement(Wrapper$9, Object.assign({}, rest, { tabIndex: onClick ? 0 : undefined, onClick: onClick }), children));
};

const offset = 130;
const dash = keyframes `
  0% {
    stroke-dashoffset: ${offset}; }
  50% {
    stroke-dashoffset: ${offset / 4};
    transform: rotate(135deg); 
    }
  100% {
    stroke-dashoffset: ${offset};
    transform: rotate(450deg); } 
`;
const rotateKeyframe = keyframes `
  0% {
    transform: rotate(0deg); }
  100% {
    transform: rotate(270deg); }
     
`;
const SpinnerGroup = styled.g `
  animation: ${rotateKeyframe} 1.4s linear infinite;
  -webkit-backface-visibility: hidden;
  transform: rotate(0deg);
  transform-origin: center;
`;
const Circle$1 = styled.circle `
  stroke-dasharray: ${offset};
  stroke-dashoffset: 0;
  transform-origin: center;
  animation: ${dash} 1.4s ease-in-out infinite;
  -webkit-backface-visibility: hidden;
`;
const Spinner = (React.createElement(SpinnerGroup, null,
    React.createElement(Circle$1, { fill: "none", strokeWidth: "6", cx: "24", cy: "24", r: "18" })));

const AnimatedIcons = {
    spinner: Spinner,
};

// Ce fichier a été généré par un script, toute modification sera effacée.
const IconsFrontNames = [
    'Filter01-outline',
    'Filter02-outline',
    'Layout2-outline',
    'Target-outline',
    'Valid-Contract-outline',
    'Valid-objo-outline',
    'activity-fill',
    'activity-outline',
    'alert-circle-fill',
    'alert-circle-outline',
    'alert-triangle-fill',
    'alert-triangle-outline',
    'archive-fill',
    'archive-outline',
    'arrow-back-fill',
    'arrow-back-outline',
    'arrow-circle-down-fill',
    'arrow-circle-down-outline',
    'arrow-circle-left-fill',
    'arrow-circle-left-outline',
    'arrow-circle-right-fill',
    'arrow-circle-right-outline',
    'arrow-circle-up-fill',
    'arrow-circle-up-outline',
    'arrow-down-fill',
    'arrow-down-outline',
    'arrow-downward-fill',
    'arrow-downward-outline',
    'arrow-forward-fill',
    'arrow-forward-outline',
    'arrow-ios-back-fill',
    'arrow-ios-back-outline',
    'arrow-ios-downward-fill',
    'arrow-ios-downward-outline',
    'arrow-ios-forward-fill',
    'arrow-ios-forward-outline',
    'arrow-ios-upward-fill',
    'arrow-ios-upward-outline',
    'arrow-left-fill',
    'arrow-left-outline',
    'arrow-right-fill',
    'arrow-right-outline',
    'arrow-split-fill',
    'arrow-up-fill',
    'arrow-up-outline',
    'arrow-upward-fill',
    'arrow-upward-outline',
    'arrowhead-down-fill',
    'arrowhead-down-outline',
    'arrowhead-left-fill',
    'arrowhead-left-outline',
    'arrowhead-right-fill',
    'arrowhead-right-outline',
    'arrowhead-up-fill',
    'arrowhead-up-outline',
    'at-fill',
    'at-outline',
    'attach-2-fill',
    'attach-2-outline',
    'attach-fill',
    'attach-outline',
    'award-fill',
    'award-outline',
    'backspace-fill',
    'backspace-outline',
    'bar-chart-2-fill',
    'bar-chart-2-outline',
    'bar-chart-fill',
    'bar-chart-outline',
    'battery-fill',
    'battery-outline',
    'behance-fill',
    'behance-outline',
    'bell-fill',
    'bell-off-fill',
    'bell-off-outline',
    'bell-outline',
    'bluetooth-fill',
    'bluetooth-outline',
    'book-fill',
    'book-open-fill',
    'book-open-outline',
    'book-outline',
    'bookmark-fill',
    'bookmark-outline',
    'briefcase-fill',
    'briefcase-outline',
    'browser-fill',
    'browser-outline',
    'brush-fill',
    'brush-outline',
    'bulb-fill',
    'bulb-outline',
    'calendar-fill',
    'calendar-outline',
    'camera-fill',
    'camera-outline',
    'car-fill',
    'car-outline',
    'cast-fill',
    'cast-outline',
    'charging-fill',
    'charging-outline',
    'checkmark-circle-2-fill',
    'checkmark-circle-2-outline',
    'checkmark-circle-fill',
    'checkmark-circle-outline',
    'checkmark-fill',
    'checkmark-outline',
    'checkmark-square-2-fill',
    'checkmark-square-2-outline',
    'checkmark-square-fill',
    'checkmark-square-outline',
    'chevron-down-circle',
    'chevron-down-fill',
    'chevron-down-outline',
    'chevron-left-fill',
    'chevron-left-outline',
    'chevron-right-fill',
    'chevron-right-outline',
    'chevron-top-circle',
    'chevron-up-fill',
    'chevron-up-outline',
    'clipboard-fill',
    'clipboard-outline',
    'clock-fill',
    'clock-outline',
    'close-circle-fill',
    'close-circle-outline',
    'close-fill',
    'close-outline',
    'close-square-fill',
    'close-square-outline',
    'cloud-download-fill',
    'cloud-download-outline',
    'cloud-upload-fill',
    'cloud-upload-outline',
    'code-download-fill',
    'code-download-outline',
    'code-fill',
    'code-outline',
    'code2-outline',
    'collapse-fill',
    'collapse-outline',
    'color-palette-fill',
    'color-palette-outline',
    'color-picker-fill',
    'color-picker-outline',
    'compass-fill',
    'compass-outline',
    'copy-fill',
    'copy-outline',
    'corner-down-left-fill',
    'corner-down-left-outline',
    'corner-down-right-fill',
    'corner-down-right-outline',
    'corner-left-down-fill',
    'corner-left-down-outline',
    'corner-left-up-fill',
    'corner-left-up-outline',
    'corner-right-down-fill',
    'corner-right-down-outline',
    'corner-right-up-fill',
    'corner-right-up-outline',
    'corner-up-left-fill',
    'corner-up-left-outline',
    'corner-up-right-fill',
    'corner-up-right-outline',
    'credit-card-fill',
    'credit-card-outline',
    'crop-fill',
    'crop-outline',
    'cube-fill',
    'cube-outline',
    'diagonal-arrow-left-down-fill',
    'diagonal-arrow-left-down-outline',
    'diagonal-arrow-left-up-fill',
    'diagonal-arrow-left-up-outline',
    'diagonal-arrow-right-down-fill',
    'diagonal-arrow-right-down-outline',
    'diagonal-arrow-right-up-fill',
    'diagonal-arrow-right-up-outline',
    'done-all-fill',
    'done-all-outline',
    'download-fill',
    'download-outline',
    'drag-outline',
    'drag2-outline',
    'droplet-fill',
    'droplet-off-fill',
    'droplet-off-outline',
    'droplet-outline',
    'edit-2-fill',
    'edit-2-outline',
    'edit-fill',
    'edit-outline',
    'email-fill',
    'email-outline',
    'expand-fill',
    'expand-outline',
    'external-link-fill',
    'external-link-outline',
    'eye-fill',
    'eye-off-2-fill',
    'eye-off-2-outline',
    'eye-off-fill',
    'eye-off-outline',
    'eye-outline',
    'eye2-off-outline',
    'eye2-outline',
    'facebook-fill',
    'facebook-outline',
    'file-add-fill',
    'file-add-outline',
    'file-fill',
    'file-outline',
    'file-remove-fill',
    'file-remove-outline',
    'file-text-fill',
    'file-text-outline',
    'film-fill',
    'film-outline',
    'flag-fill',
    'flag-outline',
    'flash-fill',
    'flash-off-fill',
    'flash-off-outline',
    'flash-outline',
    'flip-2-fill',
    'flip-2-outline',
    'flip-fill',
    'flip-outline',
    'folder-add-fill',
    'folder-add-outline',
    'folder-fill',
    'folder-outline',
    'folder-remove-fill',
    'folder-remove-outline',
    'funnel-fill',
    'funnel-outline',
    'gift-fill',
    'gift-outline',
    'github-fill',
    'github-outline',
    'globe-2-fill',
    'globe-2-outline',
    'globe-3-fill',
    'globe-fill',
    'globe-outline',
    'google-fill',
    'google-outline',
    'grid-fill',
    'grid-outline',
    'hard-drive-fill',
    'hard-drive-outline',
    'hash-fill',
    'hash-outline',
    'headphones-fill',
    'headphones-outline',
    'heart-fill',
    'heart-outline',
    'home-fill',
    'home-outline',
    'image-2-fill',
    'image-fill',
    'image-outline',
    'inbox-fill',
    'inbox-outline',
    'info-Price',
    'info-fill',
    'info-outline',
    'info-price',
    'keypad-fill',
    'keypad-outline',
    'layers-fill',
    'layers-outline',
    'layout-fill',
    'layout-left-outline',
    'layout-outline',
    'layout-top-outline',
    'layout-topleft-outline',
    'link-2-fill',
    'link-2-outline',
    'link-fill',
    'link-outline',
    'linkedin-fill',
    'linkedin-outline',
    'list-fill',
    'list-outline',
    'loader-outline',
    'lock-fill',
    'lock-outline',
    'log-in-fill',
    'log-in-outline',
    'log-out-fill',
    'log-out-outline',
    'map-fill',
    'map-outline',
    'marketing',
    'maximize-fill',
    'maximize-outline',
    'menu-2-fill',
    'menu-2-outline',
    'menu-3-outline',
    'menu-arrow-fill',
    'menu-arrow-outline',
    'menu-fill',
    'menu-outline',
    'message-circle-fill',
    'message-circle-outline',
    'message-square-fill',
    'message-square-outline',
    'mic-fill',
    'mic-off-fill',
    'mic-off-outline',
    'mic-outline',
    'minimize-fill',
    'minimize-outline',
    'minus-circle-fill',
    'minus-circle-outline',
    'minus-fill',
    'minus-outline',
    'minus-square-fill',
    'minus-square-outline',
    'monitor-fill',
    'monitor-outline',
    'moon-fill',
    'moon-outline',
    'more-horizontal-fill',
    'more-horizontal-outline',
    'more-vertical-fill',
    'more-vertical-outline',
    'move-fill',
    'move-outline',
    'music-fill',
    'music-outline',
    'navigation-2-fill',
    'navigation-2-outline',
    'navigation-fill',
    'navigation-outline',
    'note-outline',
    'npm-fill',
    'npm-outline',
    'options-2-fill',
    'options-2-outline',
    'options-fill',
    'options-outline',
    'pantone-fill',
    'pantone-outline',
    'paper-plane-fill',
    'paper-plane-outline',
    'pause-circle-fill',
    'pause-circle-outline',
    'people-fill',
    'people-outline',
    'percent-fill',
    'percent-outline',
    'person-add-fill',
    'person-add-outline',
    'person-delete-fill',
    'person-delete-outline',
    'person-done-fill',
    'person-done-outline',
    'person-fill',
    'person-outline',
    'person-remove-fill',
    'person-remove-outline',
    'phone-call-fill',
    'phone-call-outline',
    'phone-fill',
    'phone-missed-fill',
    'phone-missed-outline',
    'phone-off-fill',
    'phone-off-outline',
    'phone-outline',
    'pie-chart-2-fill',
    'pie-chart-fill',
    'pie-chart-outline',
    'pin-fill',
    'pin-outline',
    'play-circle-fill',
    'play-circle-outline',
    'plus-circle-fill',
    'plus-circle-outline',
    'plus-fill',
    'plus-outline',
    'plus-square-fill',
    'plus-square-outline',
    'power-fill',
    'power-outline',
    'pricetags-fill',
    'pricetags-outline',
    'printer-fill',
    'printer-outline',
    'question-mark-circle-fill',
    'question-mark-circle-outline',
    'question-mark-fill',
    'question-mark-outline',
    'radio-button-off-fill',
    'radio-button-off-outline',
    'radio-button-on-fill',
    'radio-button-on-outline',
    'radio-fill',
    'radio-outline',
    'recording-fill',
    'recording-outline',
    'refresh-fill',
    'refresh-outline',
    'repeat-fill',
    'repeat-outline',
    'rewind-left-fill',
    'rewind-left-outline',
    'rewind-right-fill',
    'rewind-right-outline',
    'save-fill',
    'save-outline',
    'scissors-fill',
    'scissors-outline',
    'search-fill',
    'search-outline',
    'settings-2-fill',
    'settings-2-outline',
    'settings-fill',
    'settings-outline',
    'shake-fill',
    'shake-outline',
    'share-fill',
    'share-outline',
    'shield-fill',
    'shield-off-fill',
    'shield-off-outline',
    'shield-outline',
    'shopping-bag-fill',
    'shopping-bag-outline',
    'shopping-cart-fill',
    'shopping-cart-outline',
    'shuffle-2-fill',
    'shuffle-2-outline',
    'shuffle-fill',
    'shuffle-outline',
    'skip-back-fill',
    'skip-back-outline',
    'skip-forward-fill',
    'skip-forward-outline',
    'slash-fill',
    'slash-outline',
    'smartphone-fill',
    'smartphone-outline',
    'speaker-fill',
    'speaker-outline',
    'square-fill',
    'square-outline',
    'star-fill',
    'star-outline',
    'stop-circle-fill',
    'stop-circle-outline',
    'sun-fill',
    'sun-outline',
    'swap-fill',
    'swap-outline',
    'sync-fill',
    'sync-outline',
    'text-fill',
    'text-outline',
    'thermometer-fill',
    'thermometer-minus-fill',
    'thermometer-minus-outline',
    'thermometer-outline',
    'thermometer-plus-fill',
    'thermometer-plus-outline',
    'thumb-outline',
    'toggle-left-fill',
    'toggle-left-outline',
    'toggle-right-fill',
    'toggle-right-outline',
    'trash-2-fill',
    'trash-2-outline',
    'trash-fill',
    'trash-outline',
    'trending-down-fill',
    'trending-down-outline',
    'trending-up-fill',
    'trending-up-outline',
    'tv-fill',
    'tv-outline',
    'twitter-fill',
    'twitter-outline',
    'umbrella-fill',
    'umbrella-outline',
    'undo-fill',
    'undo-outline',
    'unlock-fill',
    'unlock-outline',
    'upload-fill',
    'upload-outline',
    'video-fill',
    'video-off-fill',
    'video-off-outline',
    'video-outline',
    'volume-down-fill',
    'volume-down-outline',
    'volume-mute-fill',
    'volume-mute-outline',
    'volume-off-fill',
    'volume-off-outline',
    'volume-up-fill',
    'volume-up-outline',
    'wait-outline',
    'wait-outline2',
    'wifi-fill',
    'wifi-off-fill',
    'wifi-off-outline',
    'wifi-outline'
];
const IconsFrontPaths = {
    'Filter01-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M6 10a2 2 0 0 1 2-2h20a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM6 20a2 2 0 0 1 2-2h16a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM6 30a2 2 0 0 1 2-2h10a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM6 40a2 2 0 0 1 2-2h6a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM35 8a2 2 0 0 1 2 2v25.172l1.586-1.586a2 2 0 1 1 2.828 2.828l-5 5a2 2 0 0 1-2.828 0l-5-5a2 2 0 1 1 2.828-2.828L33 35.172V10a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'Filter02-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M6 40a2 2 0 0 1 2-2h20a2 2 0 0 1 0 4H8a2 2 0 0 1-2-2ZM6 30a2 2 0 0 1 2-2h16a2 2 0 0 1 0 4H8a2 2 0 0 1-2-2ZM6 20a2 2 0 0 1 2-2h10a2 2 0 0 1 0 4H8a2 2 0 0 1-2-2ZM6 10a2 2 0 0 1 2-2h6a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM35 42a2 2 0 0 1-2-2V14.828l-1.586 1.586a2 2 0 1 1-2.828-2.828l5-5a2 2 0 0 1 2.828 0l5 5a2 2 0 1 1-2.828 2.828L37 14.828V40a2 2 0 0 1-2 2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'Layout2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8ZM38 26h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8Z" }),
        React.createElement("path", { d: "M6 10a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4h-8a4 4 0 0 1-4-4V10Zm12 0h-8v28h8V10Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'Target-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22 6v2.124C14.763 9.026 9.026 14.763 8.124 22H6a2 2 0 1 0 0 4h2.124C9.026 33.237 14.763 38.974 22 39.876V42a2 2 0 1 0 4 0v-2.124c7.237-.902 12.974-6.639 13.876-13.876H42a2 2 0 1 0 0-4h-2.124C38.974 14.763 33.237 9.026 26 8.124V6a2 2 0 1 0-4 0Zm2 6c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'Valid-Contract-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 16.66-10.88-12A2 2 0 0 0 27.12 4h-14A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V18a2 2 0 0 0-.52-1.34ZM35.3 18h-7.88A1.58 1.58 0 0 1 26 16.3V8h.22l9.08 10Zm-.42 22H13.12A1.06 1.06 0 0 1 12 39V9a1.06 1.06 0 0 1 1.12-1H22v8.3a5.58 5.58 0 0 0 5.42 5.7H36v17a1.06 1.06 0 0 1-1.12 1Z" })),
    'Valid-objo-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.586 20.586a2 2 0 1 1 2.828 2.828l-6 6a2 2 0 0 1-2.828 0l-4-4a2 2 0 1 1 2.828-2.828L24 25.172l4.586-4.586Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M22 6v2.124C14.763 9.026 9.026 14.763 8.124 22H6a2 2 0 1 0 0 4h2.124C9.026 33.237 14.763 38.974 22 39.876V42a2 2 0 1 0 4 0v-2.124c7.237-.902 12.974-6.639 13.876-13.876H42a2 2 0 1 0 0-4h-2.124C38.974 14.763 33.237 9.026 26 8.124V6a2 2 0 1 0-4 0Zm2 6c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'activity-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.66 40h-.42a4 4 0 0 1-3.52-3.16L19.36 12l-5.52 12.8A2 2 0 0 1 12 26H6a2 2 0 0 1 0-4h4.68l5.02-11.58a4 4 0 0 1 7.58.76L28.64 36l5.52-12.76A2 2 0 0 1 36 22h6a2 2 0 0 1 0 4h-4.68L32.3 37.58A4 4 0 0 1 28.66 40Z" })),
    'activity-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.66 40h-.42a4 4 0 0 1-3.52-3.16L19.36 12l-5.52 12.8A1.999 1.999 0 0 1 12 26H6a2 2 0 1 1 0-4h4.68l5.02-11.58a4 4 0 0 1 7.58.76L28.64 36l5.52-12.76A2 2 0 0 1 36 22h6a2 2 0 0 1 0 4h-4.68L32.3 37.58A4 4 0 0 1 28.66 40Z" })),
    'alert-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 30a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm2-8a2 2 0 0 1-4 0V16a2 2 0 0 1 4 0v10Z" })),
    'alert-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 34a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 14a2 2 0 0 0-2 2v10a2 2 0 0 0 4 0V16a2 2 0 0 0-2-2Z" })),
    'alert-triangle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M45.12 32.6 29.78 7.16a6.86 6.86 0 0 0-11.56 0L2.88 32.6a6 6 0 0 0-.1 6A6.74 6.74 0 0 0 8.66 42h30.68a6.74 6.74 0 0 0 5.88-3.32 6 6 0 0 0-.1-6.08ZM24 34a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm2-8a2 2 0 0 1-4 0v-8a2 2 0 0 1 4 0v8Z" })),
    'alert-triangle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M45.12 32.6 29.78 7.16a6.86 6.86 0 0 0-11.56 0L2.88 32.6a6 6 0 0 0-.1 6A6.74 6.74 0 0 0 8.66 42h30.68a6.74 6.74 0 0 0 5.88-3.32 6 6 0 0 0-.1-6.08Zm-3.4 4.1a2.62 2.62 0 0 1-2.38 1.3H8.66a2.62 2.62 0 0 1-2.38-1.3 2 2 0 0 1 0-2L21.64 9.24a2.96 2.96 0 0 1 4.72 0L41.7 34.68a2 2 0 0 1 .02 2.02Z" }),
        React.createElement("path", { d: "M24 34a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 16a2 2 0 0 0-2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2Z" })),
    'archive-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-4 10.44V36a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16.44A6 6 0 0 0 36 6Zm-6 20.26A1.74 1.74 0 0 1 28.26 28h-8.52A1.74 1.74 0 0 1 18 26.26v-.52A1.74 1.74 0 0 1 19.74 24h8.52A1.74 1.74 0 0 1 30 25.74v.52ZM36 14H12a2 2 0 0 1 0-4h24a2 2 0 0 1 0 4Z" })),
    'archive-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12a6 6 0 0 0-6-6H12a6 6 0 0 0-4 10.44V36a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16.44A6 6 0 0 0 42 12Zm-30-2h24a2 2 0 0 1 0 4H12a2 2 0 0 1 0-4Zm24 26a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V18h24v18Z" }),
        React.createElement("path", { d: "M28.26 24h-8.52A1.74 1.74 0 0 0 18 25.74v.52c0 .961.779 1.74 1.74 1.74h8.52A1.74 1.74 0 0 0 30 26.26v-.52A1.74 1.74 0 0 0 28.26 24Z" })),
    'arrow-back-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 22H14.28l7.26-8.72a2.002 2.002 0 1 0-3.08-2.56l-10 12a2.39 2.39 0 0 0-.18.3c0 .1 0 .16-.14.26A2 2 0 0 0 8 24a2 2 0 0 0 .14.72c0 .1 0 .16.14.26.053.104.113.204.18.3l10 12A1.998 1.998 0 0 0 20 38a2 2 0 0 0 1.54-3.28L14.28 26H38a2 2 0 0 0 0-4Z" })),
    'arrow-back-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 22H14.28l7.26-8.72a2.002 2.002 0 1 0-3.08-2.56l-10 12a2.375 2.375 0 0 0-.18.3c0 .1 0 .16-.14.26A2 2 0 0 0 8 24a2 2 0 0 0 .14.72c0 .1 0 .16.14.26.053.104.113.205.18.3l10 12A2 2 0 0 0 20 38a2 2 0 0 0 1.54-3.28L14.28 26H38a2 2 0 0 0 0-4Z" })),
    'arrow-circle-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm7.38 23.72-6 5.72a.98.98 0 0 1-.3.2 1.08 1.08 0 0 1-.32.2 1.88 1.88 0 0 1-1.52 0 2 2 0 0 1-.66-.42l-6-6a2.008 2.008 0 1 1 2.84-2.84l2.58 2.6V16a2 2 0 0 1 4 0v11.32l2.62-2.5a2.002 2.002 0 0 1 2.76 2.9Z" })),
    'arrow-circle-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.62 24.82 26 27.32V16a2 2 0 0 0-4 0v11.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l6 6a2 2 0 0 0 .66.42 1.88 1.88 0 0 0 1.52 0 1.08 1.08 0 0 0 .32-.2.98.98 0 0 0 .3-.2l6-5.72a2.001 2.001 0 1 0-2.76-2.9Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" })),
    'arrow-circle-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 24a20 20 0 1 0-40 0 20 20 0 0 0 40 0Zm-23.72 7.38-5.72-6a.98.98 0 0 1-.2-.3 1.08 1.08 0 0 1-.2-.32 1.88 1.88 0 0 1 0-1.52 2 2 0 0 1 .42-.66l6-6a2.008 2.008 0 1 1 2.84 2.84L20.82 22H32a2 2 0 0 1 0 4H20.68l2.5 2.62a2.002 2.002 0 0 1-2.9 2.76Z" })),
    'arrow-circle-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 22H20.68l2.5-2.62a2.002 2.002 0 0 0-2.9-2.76l-5.72 6a1.993 1.993 0 0 0-.18.26 1.441 1.441 0 0 0-.22.38c-.07.179-.11.368-.12.56L14 24a2 2 0 0 0 .16.76 2 2 0 0 0 .42.64l6 6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L20.82 26H32a2 2 0 0 0 0-4Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" })),
    'arrow-circle-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M4 24a20 20 0 1 0 40 0 20 20 0 0 0-40 0Zm23.72-7.38 5.72 6a.98.98 0 0 1 .2.3c.085.094.153.202.2.32a1.88 1.88 0 0 1 0 1.52 2 2 0 0 1-.42.66l-6 6a2.008 2.008 0 1 1-2.84-2.84l2.6-2.58H16a2 2 0 0 1 0-4h11.32l-2.5-2.62a2.002 2.002 0 0 1 2.9-2.76Z" })),
    'arrow-circle-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 24v-.18a1.761 1.761 0 0 0-.12-.56 1.441 1.441 0 0 0-.22-.38 1.993 1.993 0 0 0-.18-.26l-5.72-6a2 2 0 1 0-2.9 2.76L27.32 22H16a2 2 0 0 0 0 4h11.18l-2.6 2.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l6-6a2 2 0 0 0 .42-.64c.105-.247.16-.512.16-.78Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" })),
    'arrow-circle-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a20 20 0 1 0 0-40 20 20 0 0 0 0 40Zm-7.38-23.72 6-5.72a.98.98 0 0 1 .3-.2 1.08 1.08 0 0 1 .32-.2 1.88 1.88 0 0 1 1.52 0 2 2 0 0 1 .66.42l6 6a2.008 2.008 0 1 1-2.84 2.84L26 20.82V32a2 2 0 1 1-4 0V20.68l-2.62 2.5a2.002 2.002 0 0 1-2.76-2.9Z" })),
    'arrow-circle-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.42 14.58a2 2 0 0 0-.64-.42A1.999 1.999 0 0 0 24 14h-.2a1.639 1.639 0 0 0-.92.34 1.993 1.993 0 0 0-.26.18l-6 5.72a2.002 2.002 0 0 0 2.76 2.9L22 20.68V32a2 2 0 0 0 4 0V20.82l2.58 2.6a2 2 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-6-6Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" })),
    'arrow-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 34a3.44 3.44 0 0 1-2.66-1.28l-8.42-10.2a4.2 4.2 0 0 1-.52-4.42 3.52 3.52 0 0 1 3.18-2.1h16.84a3.521 3.521 0 0 1 3.18 2.1 4.2 4.2 0 0 1-.52 4.42l-8.42 10.2A3.44 3.44 0 0 1 24 34Z" })),
    'arrow-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 34a3.44 3.44 0 0 1-2.66-1.28l-8.42-10.2a4.2 4.2 0 0 1-.52-4.42 3.52 3.52 0 0 1 3.18-2.1h16.84a3.521 3.521 0 0 1 3.18 2.1 4.2 4.2 0 0 1-.52 4.42l-8.42 10.2A3.44 3.44 0 0 1 24 34Zm-7.82-14L24 29.64 32 20H16.18Z" })),
    'arrow-downward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.54 26.72a2 2 0 0 0-2.82-.26L26 33.72V10a2 2 0 1 0-4 0v23.72l-8.72-7.26a2.002 2.002 0 1 0-2.56 3.08l12 10 .3.18.26.14a2 2 0 0 0 1.44 0l.26-.14.3-.18 12-10a2 2 0 0 0 .26-2.82Z" })),
    'arrow-downward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.54 26.72a2 2 0 0 0-2.82-.26L26 33.72V10a2 2 0 1 0-4 0v23.72l-8.72-7.26a2.002 2.002 0 1 0-2.56 3.08l12 10 .3.18.26.14a2 2 0 0 0 1.44 0l.26-.14.3-.18 12-10a2 2 0 0 0 .26-2.82Z" })),
    'arrow-forward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 26h23.72l-7.26 8.72a2.002 2.002 0 1 0 3.08 2.56l10-12c.067-.095.127-.196.18-.3 0-.1.1-.16.14-.26A2 2 0 0 0 40 24a2 2 0 0 0-.14-.72c0-.1-.1-.16-.14-.26a2.385 2.385 0 0 0-.18-.3l-10-12A2 2 0 0 0 28 10a2 2 0 0 0-1.54 3.28L33.72 22H10a2 2 0 1 0 0 4Z" })),
    'arrow-forward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 26h23.72l-7.26 8.72a2.002 2.002 0 1 0 3.08 2.56l10-12c.067-.095.127-.196.18-.3 0-.1.1-.16.14-.26A2 2 0 0 0 40 24a2 2 0 0 0-.14-.72c0-.1-.1-.16-.14-.26a2.378 2.378 0 0 0-.18-.3l-10-12A2 2 0 0 0 28 10a2 2 0 0 0-1.54 3.28L33.72 22H10a2 2 0 1 0 0 4Z" })),
    'arrow-ios-back-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.66 38a2 2 0 0 1-1.56-.74l-9.66-12a2 2 0 0 1 0-2.54l10-12a2.002 2.002 0 0 1 3.08 2.56L20.58 24l8.64 10.72A2 2 0 0 1 27.66 38Z" })),
    'arrow-ios-back-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.66 38a2 2 0 0 1-1.56-.74l-9.66-12a2 2 0 0 1 0-2.54l10-12a2.002 2.002 0 0 1 3.08 2.56L20.58 24l8.64 10.72A2 2 0 0 1 27.66 38Z" })),
    'arrow-ios-downward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 32a2 2 0 0 1-1.28-.46l-12-10a2.002 2.002 0 0 1 2.56-3.08L24 27.42l10.72-8.64a2.002 2.002 0 0 1 2.82.3 2 2 0 0 1-.28 2.92l-12 9.66A2 2 0 0 1 24 32Z" })),
    'arrow-ios-downward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 32a2 2 0 0 1-1.28-.46l-12-10a2.002 2.002 0 0 1 2.56-3.08L24 27.42l10.72-8.64a2.002 2.002 0 0 1 2.82.3 2 2 0 0 1-.28 2.92l-12 9.66A2 2 0 0 1 24 32Z" })),
    'arrow-ios-forward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20 38a2 2 0 0 1-1.54-3.28L27.42 24l-8.64-10.74a2 2 0 0 1 .3-2.82 2 2 0 0 1 2.92.3l9.66 12a2 2 0 0 1 0 2.54l-10 12A2 2 0 0 1 20 38Z" })),
    'arrow-ios-forward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20 38a2 2 0 0 1-1.54-3.28L27.42 24l-8.64-10.74a2 2 0 0 1 .3-2.82 2 2 0 0 1 2.92.3l9.66 12a2 2 0 0 1 0 2.54l-10 12A2.001 2.001 0 0 1 20 38Z" })),
    'arrow-ios-upward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 30a2 2 0 0 1-1.28-.46L24 20.58l-10.74 8.64a2 2 0 0 1-2.82-.3 2.002 2.002 0 0 1 .3-2.82l12-9.66a2 2 0 0 1 2.54 0l12 10a2 2 0 0 1 .26 2.82A2 2 0 0 1 36 30Z" })),
    'arrow-ios-upward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 30a2 2 0 0 1-1.28-.46L24 20.58l-10.74 8.64a2.002 2.002 0 0 1-3.185-.993 2.001 2.001 0 0 1 .665-2.127l12-9.66a2 2 0 0 1 2.54 0l12 10a2 2 0 0 1 .26 2.82A2 2 0 0 1 36 30Z" })),
    'arrow-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.08 36a4.12 4.12 0 0 1-2.6-.92l-10.2-8.42a3.4 3.4 0 0 1 0-5.32l10.2-8.42a4.2 4.2 0 0 1 4.42-.52 3.52 3.52 0 0 1 2.1 3.18v16.84a3.521 3.521 0 0 1-2.1 3.18 4.46 4.46 0 0 1-1.82.4Z" })),
    'arrow-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.08 36a4.12 4.12 0 0 1-2.6-.92l-10.2-8.42a3.4 3.4 0 0 1 0-5.32l10.2-8.42a4.2 4.2 0 0 1 4.42-.52 3.52 3.52 0 0 1 2.1 3.18v16.84a3.521 3.521 0 0 1-2.1 3.18 4.46 4.46 0 0 1-1.82.4Zm-9.72-12L27 32V16.18L17.36 24Z" })),
    'arrow-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20.92 36a4.46 4.46 0 0 1-1.82-.4 3.521 3.521 0 0 1-2.1-3.18V15.58a3.521 3.521 0 0 1 2.1-3.18 4.2 4.2 0 0 1 4.42.52l10.2 8.42a3.4 3.4 0 0 1 0 5.32l-10.2 8.42a4.12 4.12 0 0 1-2.6.92Z" })),
    'arrow-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20.92 36a4.46 4.46 0 0 1-1.82-.4 3.52 3.52 0 0 1-2.1-3.18V15.58a3.521 3.521 0 0 1 2.1-3.18 4.2 4.2 0 0 1 4.42.52l10.2 8.42a3.4 3.4 0 0 1 0 5.32l-10.2 8.42a4.12 4.12 0 0 1-2.6.92Zm0-20v15.8l9.72-7.8-9.72-8Z" })),
    'arrow-split-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.835 15.58v16.84a3.52 3.52 0 0 1-2 3.18 4.2 4.2 0 0 1-4.42-.52l-10.2-8.42a3.52 3.52 0 0 1 0-5.32l10.2-8.42a4.2 4.2 0 0 1 4.42-.52 3.52 3.52 0 0 1 2 3.18ZM28.165 32.42V15.58a3.52 3.52 0 0 1 2-3.18 4.2 4.2 0 0 1 4.42.52l10.2 8.42a3.521 3.521 0 0 1 0 5.32l-10.2 8.42a4.2 4.2 0 0 1-4.42.52 3.52 3.52 0 0 1-2-3.18Z" })),
    'arrow-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.42 32H15.58a3.52 3.52 0 0 1-3.18-2 4.2 4.2 0 0 1 .52-4.42l8.42-10.2a3.52 3.52 0 0 1 5.32 0l8.42 10.2A4.2 4.2 0 0 1 35.6 30a3.52 3.52 0 0 1-3.18 2Z" })),
    'arrow-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.42 32H15.58a3.52 3.52 0 0 1-3.18-2 4.2 4.2 0 0 1 .52-4.42l8.42-10.2a3.52 3.52 0 0 1 5.32 0l8.42 10.2A4.2 4.2 0 0 1 35.6 30a3.52 3.52 0 0 1-3.18 2ZM16 28h15.8L24 18.36 16 28Z" })),
    'arrow-upward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10.46 21.28a2 2 0 0 0 2.82.26L22 14.28V38a2 2 0 1 0 4 0V14.28l8.72 7.26a2.002 2.002 0 1 0 2.56-3.08l-12-10-.3-.18-.26-.14a2 2 0 0 0-1.44 0l-.26.14-.3.18-12 10a2 2 0 0 0-.26 2.82Z" })),
    'arrow-upward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10.46 21.28a2 2 0 0 0 2.82.26L22 14.28V38a2 2 0 0 0 4 0V14.28l8.72 7.26a2.002 2.002 0 1 0 2.56-3.08l-12-10-.3-.18-.26-.14a2 2 0 0 0-1.44 0l-.26.14-.3.18-12 10a2 2 0 0 0-.26 2.82Z" })),
    'arrowhead-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34.74 24.78 24 33.42l-10.72-8.96a2.002 2.002 0 1 0-2.56 3.08l12 10a2 2 0 0 0 2.54 0l12-9.66a1.999 1.999 0 1 0-2.52-3.1Z" }),
        React.createElement("path", { d: "M22.72 23.54a2 2 0 0 0 2.54 0l12-9.66a1.999 1.999 0 0 0 .3-2.82 1.999 1.999 0 0 0-2.82-.3L24 19.42l-10.72-8.96a2.002 2.002 0 1 0-2.56 3.08l12 10Z" })),
    'arrowhead-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34.74 24.78 24 33.42l-10.72-8.96a2.002 2.002 0 1 0-2.56 3.08l12 10a2 2 0 0 0 2.54 0l12-9.66a2 2 0 0 0-.304-3.318 2 2 0 0 0-2.216.218Z" }),
        React.createElement("path", { d: "M22.72 23.54a2 2 0 0 0 2.54 0l12-9.66a1.999 1.999 0 0 0 .3-2.82 1.999 1.999 0 0 0-2.82-.3L24 19.42l-10.72-8.96a2.002 2.002 0 1 0-2.56 3.08l12 10Z" })),
    'arrowhead-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M23.28 10.46a2 2 0 0 0-2.82.26l-10 12a2 2 0 0 0 0 2.54l9.66 12a1.999 1.999 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L14.58 24l8.96-10.74a2 2 0 0 0-.26-2.8Z" }),
        React.createElement("path", { d: "m28.58 24 8.96-10.74a2.002 2.002 0 1 0-3.08-2.56l-10 12a2 2 0 0 0 0 2.54l9.66 12a1.999 1.999 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L28.58 24Z" })),
    'arrowhead-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M23.28 10.46a2 2 0 0 0-2.82.26l-10 12a2 2 0 0 0 0 2.54l9.66 12a2 2 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L14.58 24l8.96-10.74a2 2 0 0 0-.26-2.8Z" }),
        React.createElement("path", { d: "m28.58 24 8.96-10.74a2.002 2.002 0 1 0-3.08-2.56l-10 12a2 2 0 0 0 0 2.54l9.66 12a2 2 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L28.58 24Z" })),
    'arrowhead-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m37.56 22.74-9.56-12a2.001 2.001 0 0 0-3.323.3 1.998 1.998 0 0 0 .203 2.22L33.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 26 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" }),
        React.createElement("path", { d: "M14 10.74a2.044 2.044 0 1 0-3.22 2.52L19.42 24l-8.96 10.72a2 2 0 0 0 2.39 3.092 2 2 0 0 0 .69-.532l10-12a2 2 0 0 0 0-2.54l-9.54-12Z" })),
    'arrowhead-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m37.56 22.74-9.56-12a1.999 1.999 0 0 0-2.82-.3 2 2 0 0 0-.3 2.82L33.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 26 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" }),
        React.createElement("path", { d: "M14 10.74a2.044 2.044 0 1 0-3.22 2.52L19.42 24l-8.96 10.72a2 2 0 0 0 2.39 3.092 2 2 0 0 0 .69-.532l10-12a2 2 0 0 0 0-2.54l-9.54-12Z" })),
    'arrowhead-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M13.26 23.22 24 14.58l10.74 8.96A2 2 0 0 0 36 24a2 2 0 0 0 1.28-3.54l-12-10a2 2 0 0 0-2.54 0l-12 9.66a2 2 0 0 0-.3 2.82 2 2 0 0 0 2.82.28Z" }),
        React.createElement("path", { d: "M25.28 24.46a2 2 0 0 0-2.54 0l-12 9.66a2 2 0 0 0-.3 2.82 2 2 0 0 0 2.82.3L24 28.58l10.74 8.96A2 2 0 0 0 36 38a2 2 0 0 0 1.28-3.54l-12-10Z" })),
    'arrowhead-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M13.26 23.22 24 14.58l10.74 8.96A2 2 0 0 0 36 24a2 2 0 0 0 1.28-3.54l-12-10a2 2 0 0 0-2.54 0l-12 9.66a2 2 0 0 0 .304 3.318 2 2 0 0 0 2.216-.218Z" }),
        React.createElement("path", { d: "M25.28 24.46a2 2 0 0 0-2.54 0l-12 9.66a2 2 0 0 0-.3 2.82 2 2 0 0 0 2.82.3L24 28.58l10.74 8.96A2 2 0 0 0 36 38a2 2 0 0 0 1.28-3.54l-12-10Z" })),
    'at-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 4a20 20 0 0 0-10 38.2 20.298 20.298 0 0 0 8 1.8 20 20 0 0 0 12.16-4.12 2 2 0 0 0 .38-2.8 2 2 0 0 0-2.82-.38A16 16 0 1 1 25.54 8 16.34 16.34 0 0 1 40 24.44v1.36a3.421 3.421 0 0 1-3.56 3.4 3.64 3.64 0 0 1-3.24-3.76V16.8a2 2 0 0 0-2-2 2 2 0 0 0-2 1.74 10 10 0 0 0-6.88-2.72A10.18 10.18 0 1 0 30.62 30a7.2 7.2 0 0 0 11.1 1.22A7.341 7.341 0 0 0 44 25.8v-1.36A20.4 20.4 0 0 0 26 4Zm-3.64 26.18A6.18 6.18 0 1 1 28.54 24a6.2 6.2 0 0 1-6.18 6.18Z" })),
    'at-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 4a20 20 0 0 0-10 38.2 20.302 20.302 0 0 0 8 1.8 20 20 0 0 0 12.16-4.12 2 2 0 0 0 .38-2.8 2 2 0 0 0-2.82-.38A16 16 0 1 1 25.54 8 16.34 16.34 0 0 1 40 24.44v1.36a3.42 3.42 0 0 1-3.56 3.4 3.64 3.64 0 0 1-3.24-3.76V16.8a2 2 0 0 0-2-2 2 2 0 0 0-2 1.74 10 10 0 0 0-6.88-2.72A10.18 10.18 0 1 0 30.62 30a7.2 7.2 0 0 0 11.1 1.22A7.341 7.341 0 0 0 44 25.8v-1.36A20.4 20.4 0 0 0 26 4Zm-3.64 26.18A6.18 6.18 0 1 1 28.54 24a6.2 6.2 0 0 1-6.18 6.18Z" })),
    'attach-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a11.72 11.72 0 0 1-12-11.4V12.26A8.48 8.48 0 0 1 20.66 4a8.48 8.48 0 0 1 8.68 8.26v20.36a5.34 5.34 0 0 1-10.66 0V13.84a2 2 0 1 1 4 0v18.78a1.34 1.34 0 0 0 2.66 0V12.26A4.5 4.5 0 0 0 20.66 8 4.5 4.5 0 0 0 16 12.26V32.6a7.72 7.72 0 0 0 8 7.4 7.72 7.72 0 0 0 8-7.4V12.26a2 2 0 1 1 4 0V32.6A11.72 11.72 0 0 1 24 44Z" })),
    'attach-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a11.72 11.72 0 0 1-12-11.4V12.26A8.48 8.48 0 0 1 20.66 4a8.48 8.48 0 0 1 8.68 8.26v20.36a5.34 5.34 0 0 1-10.66 0V13.84a2 2 0 0 1 4 0v18.78a1.34 1.34 0 0 0 2.66 0V12.26A4.5 4.5 0 0 0 20.66 8 4.5 4.5 0 0 0 16 12.26V32.6a7.72 7.72 0 0 0 8 7.4 7.72 7.72 0 0 0 8-7.4V12.26a2 2 0 0 1 4 0V32.6A11.72 11.72 0 0 1 24 44Z" })),
    'attach-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18.58 42a12.46 12.46 0 0 1-8.86-3.76 12 12 0 0 1-.44-16.98L24 6.4A8.22 8.22 0 0 1 30 4a8.96 8.96 0 0 1 6.38 2.7 8.72 8.72 0 0 1 .3 12.26l-14.8 14.86a5.08 5.08 0 0 1-3.62 1.5 5.44 5.44 0 0 1-3.9-1.64 5.36 5.36 0 0 1-.16-7.54l13.66-13.72a2 2 0 0 1 2.74 2.82L16.94 28.96a1.36 1.36 0 0 0 .16 1.9c.281.283.66.448 1.06.46a1.12 1.12 0 0 0 .8-.32l14.78-14.86a4.72 4.72 0 0 0-.3-6.62 4.76 4.76 0 0 0-6.54-.3L12.12 24a8.001 8.001 0 0 0 .44 11.34 8.44 8.44 0 0 0 6 2.58 7.34 7.34 0 0 0 5.22-2.12l14.78-14.86a2 2 0 1 1 2.84 2.82L26.62 38.62A11.3 11.3 0 0 1 18.58 42Z" })),
    'attach-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18.58 42a12.46 12.46 0 0 1-8.86-3.76 12 12 0 0 1-.44-16.98L24 6.4A8.22 8.22 0 0 1 30 4a8.96 8.96 0 0 1 6.38 2.7 8.72 8.72 0 0 1 .3 12.26l-14.8 14.86a5.08 5.08 0 0 1-3.62 1.5 5.44 5.44 0 0 1-3.9-1.64 5.36 5.36 0 0 1-.16-7.54l13.66-13.72a2 2 0 0 1 2.74 2.82L16.94 28.96a1.36 1.36 0 0 0 .16 1.9c.281.283.66.448 1.06.46a1.12 1.12 0 0 0 .8-.32l14.78-14.86a4.72 4.72 0 0 0-.3-6.62 4.76 4.76 0 0 0-6.54-.3L12.12 24a8 8 0 0 0 .44 11.34 8.44 8.44 0 0 0 6 2.58 7.341 7.341 0 0 0 5.22-2.12l14.78-14.86a2 2 0 1 1 2.84 2.82L26.62 38.62A11.3 11.3 0 0 1 18.58 42Z" })),
    'award-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m38 41.5-4.62-18A11.88 11.88 0 0 0 36 16a12 12 0 0 0-24 0 11.88 11.88 0 0 0 2.68 7.54L10 41.5a2 2 0 0 0 2.96 2.22l10.66-6.26 11.36 6.28A1.82 1.82 0 0 0 36 44a2 2 0 0 0 2-2.5ZM24 8a8 8 0 1 1 0 16 8 8 0 0 1 0-16Z" })),
    'award-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m38 41.5-4.62-18A11.88 11.88 0 0 0 36 16a12 12 0 0 0-24 0 11.88 11.88 0 0 0 2.68 7.54L10 41.5a2 2 0 0 0 2.96 2.22l10.66-6.26 11.36 6.28A1.82 1.82 0 0 0 36 44a2 2 0 0 0 2-2.5ZM24 8a8 8 0 1 1 0 16 8 8 0 0 1 0-16Zm.62 25.42a2 2 0 0 0-2 0l-7.5 4.4L18 26.42a11.88 11.88 0 0 0 11.84 0L32.9 38l-8.28-4.58Z" })),
    'backspace-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.28 8H20.74a6 6 0 0 0-4 1.56l-.2.22-12 14.96a2 2 0 0 0 .22 2.74l12 10.96a6 6 0 0 0 4 1.56H40.3a3.68 3.68 0 0 0 3.7-3.64V11.64A3.68 3.68 0 0 0 40.28 8Zm-6.86 18.58a1.998 1.998 0 0 1 0 2.84 1.999 1.999 0 0 1-2.84 0L28 26.82l-2.58 2.6a1.999 1.999 0 0 1-2.84 0 2 2 0 0 1 0-2.84l2.6-2.58-2.6-2.58a2.008 2.008 0 1 1 2.84-2.84l2.58 2.6 2.58-2.6a2.008 2.008 0 1 1 2.84 2.84L30.82 24l2.6 2.58Z" })),
    'backspace-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.28 8H20.74a6 6 0 0 0-4 1.56l-.2.22-12 14.96a2 2 0 0 0 .22 2.74l12 10.96a6 6 0 0 0 4 1.56H40.3a3.68 3.68 0 0 0 3.7-3.64V11.64A3.68 3.68 0 0 0 40.28 8ZM40 36H20.74a2 2 0 0 1-1.34-.52l-10.66-9.7L19.5 12.44a2 2 0 0 1 1.24-.44H40v24Z" }),
        React.createElement("path", { d: "M22.58 29.42a2 2 0 0 0 2.84 0l2.58-2.6 2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L30.82 24l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84L28 21.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l2.6 2.58-2.6 2.58a2 2 0 0 0 0 2.84Z" })),
    'bar-chart-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 16a2 2 0 0 0-2 2v22a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2ZM38 8a2 2 0 0 0-2 2v30a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2ZM10 24a2 2 0 0 0-2 2v14a2 2 0 0 0 4 0V26a2 2 0 0 0-2-2Z" })),
    'bar-chart-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 16a2 2 0 0 0-2 2v22a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2ZM38 8a2 2 0 0 0-2 2v30a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2ZM10 24a2 2 0 0 0-2 2v14a2 2 0 0 0 4 0V26a2 2 0 0 0-2-2Z" })),
    'bar-chart-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 8a2 2 0 0 0-2 2v30a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2ZM38 24a2 2 0 0 0-2 2v14a2 2 0 0 0 4 0V26a2 2 0 0 0-2-2ZM10 16a2 2 0 0 0-2 2v22a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2Z" })),
    'bar-chart-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 8a2 2 0 0 0-2 2v30a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2ZM38 24a2 2 0 0 0-2 2v14a2 2 0 0 0 4 0V26a2 2 0 0 0-2-2ZM10 16a2 2 0 0 0-2 2v22a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2Z" })),
    'battery-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M31.66 12H8.34A4.62 4.62 0 0 0 4 16.86v14.28A4.62 4.62 0 0 0 8.34 36h23.32A4.62 4.62 0 0 0 36 31.14V16.86A4.62 4.62 0 0 0 31.66 12ZM42 18a2 2 0 0 0-2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2Z" })),
    'battery-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M31.66 12H8.34A4.62 4.62 0 0 0 4 16.86v14.28A4.62 4.62 0 0 0 8.34 36h23.32A4.62 4.62 0 0 0 36 31.14V16.86A4.62 4.62 0 0 0 31.66 12ZM32 31.14a1.04 1.04 0 0 1-.34.86H8.36a1 1 0 0 1-.36-.86V16.86a1.06 1.06 0 0 1 .34-.86h23.3a1 1 0 0 1 .36.86v14.28ZM42 18a2 2 0 0 0-2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2Z" })),
    'behance-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M29.52 22.38a2 2 0 0 0-2 2.18h4.12a2.001 2.001 0 0 0-2.12-2.18ZM18.98 24.6h-2.46v3.88h2c2 0 2.88-.66 2.88-2 0-1.34-.92-1.88-2.42-1.88ZM20.72 21.04c0-1.06-.7-1.7-1.9-1.7h-2.3v3.48h1.7c1.78 0 2.5-.64 2.5-1.78Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm-4.6 26.4H14v-13h5.4c2.34 0 3.88 1.22 3.88 3.2a2.8 2.8 0 0 1-2.24 2.86A3.04 3.04 0 0 1 24 26.74c0 2.32-2 3.66-4.6 3.66Zm7.1-12h6v1h-6v-1Zm7.5 7.7h-6.6v.28a2.141 2.141 0 0 0 2.18 2.38 1.8 1.8 0 0 0 2-1.26H34a4 4 0 0 1-4.34 3.1 4.3 4.3 0 0 1-4.72-4.6v-.88a4.22 4.22 0 0 1 4.56-4.5 4.241 4.241 0 0 1 4.5 4.54v.94Z" })),
    'behance-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M21.04 23.56a2.8 2.8 0 0 0 2.24-2.86c0-2-1.54-3.2-3.88-3.2H14v13h5.4c2.6-.1 4.6-1.44 4.6-3.76a3.04 3.04 0 0 0-2.96-3.18Zm-4.52-4.22h2.3c1.2 0 1.9.64 1.9 1.7s-.76 1.78-2.5 1.78h-1.7v-3.48Zm2 9.14h-2V24.6h2.46c1.5 0 2.34.76 2.34 2s-.84 1.88-2.88 1.88h.08ZM29.5 20.6a4.22 4.22 0 0 0-4.56 4.5v.9a4.3 4.3 0 0 0 4.68 4.62A4 4 0 0 0 34 27.5h-2.42a1.8 1.8 0 0 1-2 1.26 2.14 2.14 0 0 1-2.18-2.38v-.28H34v-.94a4.241 4.241 0 0 0-4.5-4.56Zm2 4h-4.04a2 2 0 0 1 2-2.18 2 2 0 0 1 2 2.18h.04ZM32.5 18.4h-6v1h6v-1Z" })),
    'bell-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.04 30.42-3.6-3.62v-8.92A13.72 13.72 0 0 0 25.8 4.12a13.48 13.48 0 0 0-15.24 13.34v9.34l-3.6 3.62A3.28 3.28 0 0 0 9.28 36H16v.68A7.68 7.68 0 0 0 24 44a7.68 7.68 0 0 0 8-7.32V36h6.72a3.28 3.28 0 0 0 2.32-5.58ZM28 36.68A3.76 3.76 0 0 1 24 40a3.76 3.76 0 0 1-4-3.32V36h8v.68Z" })),
    'bell-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m31.76 37.42-1.18-1.18L28 33.56l-.14-.14-14.7-14.62-2.54-2.52c-.02.393-.02.787 0 1.18v9.34l-3.6 3.62A3.28 3.28 0 0 0 9.28 36H16v.68A7.68 7.68 0 0 0 24 44a7.76 7.76 0 0 0 8-6.44l-.24-.14ZM28 36.68A3.76 3.76 0 0 1 24 40a3.76 3.76 0 0 1-4-3.32V36h8v.68ZM14.26 8.6l2.92 2.92 19.06 19.06 4 4 .62.6c.392-.322.7-.733.9-1.2a3.24 3.24 0 0 0-.7-3.56l-3.6-3.62v-8.92A13.72 13.72 0 0 0 25.8 4.12a13.42 13.42 0 0 0-10.64 3.22c-.407.337-.794.698-1.16 1.08l.26.18ZM41.42 38.58 38.82 36l-4-4-19.04-19.06L12.84 10 9.42 6.58a2.008 2.008 0 0 0-2.84 2.84L11.06 14l3.5 3.4L29.18 32l.14.14L32 34.82 33.18 36l5.4 5.42a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" })),
    'bell-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.8 10.34A9.34 9.34 0 0 1 25.28 8a9.72 9.72 0 0 1 8.16 9.8v9c.005.401.072.8.2 1.18l7.2 7.2c.392-.322.7-.734.9-1.2a3.24 3.24 0 0 0-.7-3.56l-3.6-3.62v-8.92A13.72 13.72 0 0 0 25.8 4.12a13.42 13.42 0 0 0-10.64 3.22A13.76 13.76 0 0 0 14 8.42l2.94 2.86c.265-.332.553-.646.86-.94ZM28 33.72 26.34 32H11.02l2.36-2.36a4 4 0 0 0 1.18-2.84v-6.58l-4-4c-.02.393-.02.787 0 1.18v9.4l-3.6 3.62A3.26 3.26 0 0 0 9.28 36H16v.68A7.68 7.68 0 0 0 24 44a7.76 7.76 0 0 0 8-6.44L30.34 36 28 33.72ZM24 40a3.76 3.76 0 0 1-4-3.32V36h8v.68A3.76 3.76 0 0 1 24 40ZM41.42 38.58 38.82 36l-4-4-19.04-19.06L12.84 10 9.42 6.58a2.008 2.008 0 0 0-2.84 2.84L11.06 14l3.5 3.4L29.18 32l.14.14L32 34.82 33.18 36l5.4 5.42a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" })),
    'bell-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.04 30.42-3.6-3.62v-8.92A13.72 13.72 0 0 0 25.8 4.12a13.48 13.48 0 0 0-15.24 13.34v9.34l-3.6 3.62A3.28 3.28 0 0 0 9.28 36H16v.68A7.68 7.68 0 0 0 24 44a7.68 7.68 0 0 0 8-7.32V36h6.72a3.28 3.28 0 0 0 2.32-5.58ZM28 36.68A3.76 3.76 0 0 1 24 40a3.76 3.76 0 0 1-4-3.32V36h8v.68ZM11.02 32l2.36-2.36a4 4 0 0 0 1.18-2.84v-9.34a9.46 9.46 0 0 1 3.24-7.12A9.34 9.34 0 0 1 25.28 8a9.72 9.72 0 0 1 8.16 9.8v9a4 4 0 0 0 1.16 2.84L36.98 32H11.02Z" })),
    'bluetooth-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m27.26 24 8-7.58a2.28 2.28 0 0 0-.26-3.54l-9.34-6.46a2.34 2.34 0 0 0-2.42-.16 2.3 2.3 0 0 0-1.24 2v12.4l-6.38-8a2.03 2.03 0 1 0-3.12 2.6L19.44 24l-7 8.86a2 2 0 0 0 .32 2.8A2 2 0 0 0 14 36a2 2 0 0 0 1.56-.76L22 27.12V39.7a2.32 2.32 0 0 0 2.32 2.3 2.32 2.32 0 0 0 1.34-.42l9.28-6.36a2.34 2.34 0 0 0 .98-1.7 2.3 2.3 0 0 0-.68-1.82L27.26 24ZM26 11.52l5 3.46-5 4.72v-8.18Zm0 24.98v-8.14l4.94 4.76L26 36.5Z" })),
    'bluetooth-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m27.26 24 8-7.58a2.28 2.28 0 0 0-.26-3.54l-9.34-6.46a2.34 2.34 0 0 0-2.42-.16 2.3 2.3 0 0 0-1.24 2v12.4l-6.38-8a2.03 2.03 0 0 0-3.12 2.6L19.44 24l-7 8.86a2 2 0 0 0 .32 2.8A2 2 0 0 0 14 36a2 2 0 0 0 1.56-.76L22 27.12V39.7a2.32 2.32 0 0 0 2.32 2.3 2.32 2.32 0 0 0 1.34-.42l9.28-6.36a2.34 2.34 0 0 0 .98-1.7 2.3 2.3 0 0 0-.68-1.82L27.26 24ZM26 11.52l5 3.46-5 4.72v-8.18Zm0 24.98v-8.14l4.94 4.76L26 36.5Z" })),
    'book-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H14a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2ZM14 38a2 2 0 0 1 0-4h22v4H14Z" })),
    'book-open-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 8.68a2.48 2.48 0 0 0-2.16-.46L26 11.78v28.54l15.12-3.88A2.5 2.5 0 0 0 43 34V10.64a2.499 2.499 0 0 0-1-1.96ZM22 11.78 8.12 8.22A2.54 2.54 0 0 0 6 8.68a2.5 2.5 0 0 0-.96 2V34a2.5 2.5 0 0 0 1.88 2.42L22 40.32V11.78Z" })),
    'book-open-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.24 8.44a2 2 0 0 0-1.68-.4L24 11.54 8.44 8A2 2 0 0 0 6 10v24.4a2 2 0 0 0 1.56 2l16 3.6h.88l16-3.6a2 2 0 0 0 1.56-2V10a2 2 0 0 0-.76-1.56ZM10 12.5l12 2.7v20.3l-12-2.7V12.5Zm28 20.3-12 2.7V15.2l12-2.7v20.3Z" })),
    'book-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H14a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2Zm-24 4h22v20H14a6 6 0 0 0-2 .36V12a2 2 0 0 1 2-2Zm0 28a2 2 0 0 1 0-4h22v4H14Z" })),
    'bookmark-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 42a2 2 0 0 1-.98-.26A2 2 0 0 1 10 40V10.66A4.56 4.56 0 0 1 14.4 6h19.2a4.56 4.56 0 0 1 4.4 4.66V40a2 2 0 0 1-3 1.72L23.66 35.3 13 41.7a2 2 0 0 1-1 .3Z" })),
    'bookmark-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12.18 42.12a2 2 0 0 1-2-2l-.3-29.32a4.519 4.519 0 0 1 4.36-4.7L33.42 6a4.54 4.54 0 0 1 4.46 4.62l.28 29.32a2 2 0 0 1-.98 1.74 2 2 0 0 1-2 0l-11.4-6.32-10.58 6.46a2.4 2.4 0 0 1-1.02.3Zm11.52-11.1c.348 0 .69.083 1 .24l9.42 5.22-.24-25.9c0-.4-.26-.68-.42-.66l-19.2.18c-.16 0-.38.26-.38.66l.24 25.8 8.56-5.26a2.12 2.12 0 0 1 1.02-.28Z" })),
    'briefcase-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 42h20V14h-2v-3a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v3h-2v28Zm6-31a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3h-8v-3ZM38 14v28a6 6 0 0 0 6-6V20a6 6 0 0 0-6-6ZM10 14a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6V14Z" })),
    'briefcase-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 14h-6v-3a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v3h-6a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V20a6 6 0 0 0-6-6Zm-8 4v20H18V18h12Zm-10-7a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3h-8v-3ZM8 36V20a2 2 0 0 1 2-2h4v20h-4a2 2 0 0 1-2-2Zm32 0a2 2 0 0 1-2 2h-4V18h4a2 2 0 0 1 2 2v16Z" })),
    'browser-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-12 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm-8 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm22 24a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V22h28v14Z" })),
    'browser-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V22h28v14ZM10 18v-6a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v6H10Z" }),
        React.createElement("path", { d: "M16 16.06a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 16.06a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'brush-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14.24 25.1a8 8 0 0 0-6.14 7.72v6.22a.94.94 0 0 0 .96.96l6.48-.12a7.56 7.56 0 0 0 6.88-4.4 7.3 7.3 0 0 0-8.18-10.38ZM38.52 8.92a4.28 4.28 0 0 0-5.76.42L20 22.16a.94.94 0 0 0 0 1.32L24.5 28a.94.94 0 0 0 1.32 0L38.8 15.06a4.12 4.12 0 0 0 1.2-2.94 4 4 0 0 0-1.48-3.2Z" })),
    'brush-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 13.66a5.52 5.52 0 0 0-1.64-4 5.78 5.78 0 0 0-8 0l-13.2 13.2h-.44a8.84 8.84 0 0 0-8.6 8.62L8 38a2 2 0 0 0 .58 1.46A2.1 2.1 0 0 0 10 40l6.52-.12a8.84 8.84 0 0 0 8.62-8.6v-.46l13.22-13.2A5.48 5.48 0 0 0 40 13.66ZM16.5 35.88 12 36v-4.46a4.8 4.8 0 0 1 4.8-4.72 4.3 4.3 0 0 1 4.3 4.38 4.8 4.8 0 0 1-4.6 4.68Zm19.04-21.1L23.8 26.52a9.097 9.097 0 0 0-1.04-1.28c-.39-.39-.818-.74-1.28-1.04l11.74-11.72a1.68 1.68 0 0 1 2.32 0 1.619 1.619 0 0 1 .46 1.18 1.58 1.58 0 0 1-.46 1.12Z" })),
    'bulb-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14a10 10 0 0 0-6 18v8a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4v-8a10 10 0 0 0-6-18ZM24 12a2 2 0 0 0 2-2V6a2 2 0 1 0-4 0v4a2 2 0 0 0 2 2ZM42 22h-4a2 2 0 0 0 0 4h4a2 2 0 0 0 0-4ZM10 22H6a2 2 0 1 0 0 4h4a2 2 0 0 0 0-4ZM15.32 12.84 12.44 10a2.023 2.023 0 0 0-2.78 2.94l2.88 2.78a2 2 0 0 0 1.46.56 2 2 0 0 0 1.44-.62 2 2 0 0 0-.12-2.82ZM38.38 10.1a2 2 0 0 0-2.82 0l-2.88 2.74a2 2 0 0 0 0 2.82 2.002 2.002 0 0 0 1.44.62 2 2 0 0 0 1.38-.56l2.88-2.78a2 2 0 0 0 0-2.84Z" })),
    'bulb-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14a10 10 0 0 0-6 18v8a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4v-8a10 10 0 0 0-6-18Zm3 15.18a2 2 0 0 0-1 1.74V40h-4v-9.08a2 2 0 0 0-1-1.74A6 6 0 0 1 18 24a6 6 0 1 1 12 0 6 6 0 0 1-3 5.18ZM24 12a2 2 0 0 0 2-2V6a2 2 0 1 0-4 0v4a2 2 0 0 0 2 2ZM42 22h-4a2 2 0 0 0 0 4h4a2 2 0 0 0 0-4ZM10 22H6a2 2 0 1 0 0 4h4a2 2 0 0 0 0-4ZM15.32 12.84 12.44 10a2.023 2.023 0 0 0-2.78 2.94l2.88 2.78a2 2 0 0 0 1.46.56 2 2 0 0 0 1.44-.62 2 2 0 0 0-.12-2.82ZM38.38 10.1a2 2 0 0 0-2.82 0l-2.88 2.74a2 2 0 0 0 0 2.82 2.002 2.002 0 0 0 1.44.62 2 2 0 0 0 1.38-.56l2.88-2.78a2 2 0 0 0 0-2.84Z" })),
    'calendar-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 8h-2V6a2 2 0 1 0-4 0v2H18V6a2 2 0 1 0-4 0v2h-2a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V14a6 6 0 0 0-6-6ZM16 34a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm16 0h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4Zm6-12H10v-8a2 2 0 0 1 2-2h2v2a2 2 0 0 0 4 0v-2h12v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 1 2 2v8Z" })),
    'calendar-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 8h-2V6a2 2 0 1 0-4 0v2H18V6a2 2 0 1 0-4 0v2h-2a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V14a6 6 0 0 0-6-6Zm-24 4h2v2a2 2 0 0 0 4 0v-2h12v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 1 2 2v8H10v-8a2 2 0 0 1 2-2Zm24 28H12a2 2 0 0 1-2-2V26h28v12a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { d: "M16 34a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM32 30h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4Z" })),
    'camera-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 31a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        React.createElement("path", { d: "M38 14h-6v-3a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v3h-6a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V20a6 6 0 0 0-6-6Zm-18-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3h-8v-3Zm4 24a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'camera-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 14h-6v-3a5 5 0 0 0-5-5h-6a5 5 0 0 0-5 5v3h-6a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V20a6 6 0 0 0-6-6Zm-18-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3h-8v-3Zm20 25a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V20a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v16Z" }),
        React.createElement("path", { d: "M24 21a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 24 31Z" })),
    'car-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.2 22.44-9.2-7.4V10a3.82 3.82 0 0 0-3.62-4H7.58A3.82 3.82 0 0 0 4 10v20a4 4 0 0 0 2.4 3.76 6 6 0 1 0 11.2.24h12.72a6 6 0 1 0 11.28 0h.4a2 2 0 0 0 2-2v-8a1.998 1.998 0 0 0-.8-1.56ZM40 24.96V30h-6v-9.84l6 4.8ZM14 36a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm24 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" })),
    'car-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.2 22.44-9.2-7.4V10a3.82 3.82 0 0 0-3.62-4H7.58A3.82 3.82 0 0 0 4 10v20a4 4 0 0 0 2.4 3.76 6 6 0 1 0 11.2.24h12.72a6.001 6.001 0 0 0 8.41 7.37A6 6 0 0 0 41.6 34h.4a2 2 0 0 0 2-2v-8a2 2 0 0 0-.8-1.56ZM40 24.96V30h-6v-9.84l6 4.8ZM14 36a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm10-6H8V10h22v20h-6Zm14 6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z" })),
    'cast-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.8 6H11.2A5.4 5.4 0 0 0 6 11.56V14a2 2 0 0 0 4 0v-2.44A1.44 1.44 0 0 1 11.2 10h25.6a1.44 1.44 0 0 1 1.2 1.56v24.88A1.44 1.44 0 0 1 36.8 38H34a2 2 0 0 0 0 4h2.8a5.4 5.4 0 0 0 5.2-5.56V11.56A5.4 5.4 0 0 0 36.8 6ZM7.72 28A1.999 1.999 0 0 0 6 30.34 2 2 0 0 0 8.28 32a4.979 4.979 0 0 1 4.24 1.44 5.04 5.04 0 0 1 1.02 5.68 2 2 0 0 0 .96 2.66c.265.117.55.178.84.18a2 2 0 0 0 1.82-1.16A9.04 9.04 0 0 0 7.72 28Z" }),
        React.createElement("path", { d: "M7.72 20.16a2.02 2.02 0 0 0 .56 4 11.999 11.999 0 0 1 10.18 3.42 12 12 0 0 1 3.06 11.9A2 2 0 0 0 22.88 42c.186.03.375.03.56 0a2 2 0 0 0 2-1.44A16 16 0 0 0 7.8 20.16h-.08ZM8 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'cast-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.8 6H11.2A5.4 5.4 0 0 0 6 11.56V14a2 2 0 0 0 4 0v-2.44A1.44 1.44 0 0 1 11.2 10h25.6a1.44 1.44 0 0 1 1.2 1.56v24.88A1.44 1.44 0 0 1 36.8 38H34a2 2 0 0 0 0 4h2.8a5.4 5.4 0 0 0 5.2-5.56V11.56A5.4 5.4 0 0 0 36.8 6ZM7.72 28A2 2 0 0 0 6 30.34 2 2 0 0 0 8.28 32a4.979 4.979 0 0 1 4.24 1.44 5.04 5.04 0 0 1 1.02 5.68 2 2 0 0 0 .96 2.66c.265.117.55.178.84.18a2 2 0 0 0 1.82-1.16A9.04 9.04 0 0 0 7.72 28Z" }),
        React.createElement("path", { d: "M7.72 20.16a2.02 2.02 0 0 0 .56 4 11.999 11.999 0 0 1 10.18 3.42 11.999 11.999 0 0 1 3.06 11.9A2 2 0 0 0 22.88 42c.186.03.375.03.56 0a2 2 0 0 0 2-1.44A16 16 0 0 0 7.8 20.16h-.08ZM8 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'charging-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22.56 26H14a2 2 0 0 1-1.72-3l6.28-11H8.34A4.62 4.62 0 0 0 4 16.86v14.28A4.62 4.62 0 0 0 8.34 36h8.5l5.72-10Z" }),
        React.createElement("path", { d: "M31.66 12h-8.5l-5.72 10H26a2 2 0 0 1 1.72 3l-6.28 11h10.22A4.62 4.62 0 0 0 36 31.14V16.86A4.62 4.62 0 0 0 31.66 12ZM42 18a2 2 0 0 0-2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2Z" })),
    'charging-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 18a2 2 0 0 0-2 2v8a2 2 0 0 0 4 0v-8a2 2 0 0 0-2-2ZM31.66 12h-6.2l-2.28 4h8.46a1 1 0 0 1 .36.86v14.28a1.04 1.04 0 0 1-.34.86H26l-2.3 4h8a4.62 4.62 0 0 0 4.3-4.86V16.86A4.62 4.62 0 0 0 31.66 12ZM8 31.14V16.86a1.06 1.06 0 0 1 .34-.86H14l2.26-4h-8A4.62 4.62 0 0 0 4 16.86v14.28A4.62 4.62 0 0 0 8.34 36h6.2l2.28-4H8.36a1 1 0 0 1-.36-.86Z" }),
        React.createElement("path", { d: "M18 40a2 2 0 0 1-1.74-3l6.3-11H14a2 2 0 0 1-1.72-3l8-14a2.007 2.007 0 1 1 3.48 2l-6.32 11H26a2 2 0 0 1 1.72 3l-8 14A2 2 0 0 1 18 40Z" })),
    'checkmark-circle-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm8.6 15.22-9.14 12a2 2 0 0 1-1.58.78 2.001 2.001 0 0 1-1.58-.76l-4.88-6.22a2.003 2.003 0 0 1 3.16-2.46l3.26 4.16 7.56-10a2.011 2.011 0 1 1 3.2 2.44v.06Z" })),
    'checkmark-circle-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "m29.4 16.78-7.56 10-3.26-4.22a2.003 2.003 0 0 0-3.16 2.46l4.86 6.22a2 2 0 0 0 1.58.76 2 2 0 0 0 1.58-.78l9.14-12a2.012 2.012 0 1 0-3.2-2.44h.02Z" })),
    'checkmark-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.42 22.58a2.008 2.008 0 1 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 32a2 2 0 0 0 1.44-.68l14-16a2 2 0 0 0-3-2.64L24 27.08l-4.58-4.5Z" }),
        React.createElement("path", { d: "M42 22a2 2 0 0 0-2 2 16 16 0 0 1-16 16 16 16 0 0 1-11.34-27.28A15.861 15.861 0 0 1 24 8a17.58 17.58 0 0 1 3.8.44 2 2 0 1 0 .94-3.88A21.08 21.08 0 0 0 24 4a20 20 0 0 0-14 34.18A19.86 19.86 0 0 0 24 44a20 20 0 0 0 20-20 2 2 0 0 0-2-2Z" })),
    'checkmark-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.42 22.58a2.008 2.008 0 1 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 32a2 2 0 0 0 1.44-.68l14-16a2 2 0 0 0-3-2.64L24 27.08l-4.58-4.5Z" }),
        React.createElement("path", { d: "M42 22a2 2 0 0 0-2 2 16 16 0 0 1-16 16 16 16 0 0 1-11.34-27.28A15.861 15.861 0 0 1 24 8a17.58 17.58 0 0 1 3.8.44 2 2 0 1 0 .94-3.88A21.08 21.08 0 0 0 24 4a20 20 0 0 0-14 34.18A19.86 19.86 0 0 0 24 44a20 20 0 0 0 20-20 2 2 0 0 0-2-2Z" })),
    'checkmark-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.72 36a2 2 0 0 1-1.46-.64L8.54 25.02a2.002 2.002 0 1 1 2.92-2.74l8.24 8.78 16.82-18.4a1.999 1.999 0 1 1 2.96 2.68l-18.28 20a2 2 0 0 1-1.46.66h-.02Z" })),
    'checkmark-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.72 36a2 2 0 0 1-1.46-.64L8.54 25.02a2.002 2.002 0 1 1 2.92-2.74l8.24 8.78 16.82-18.4a1.999 1.999 0 1 1 2.96 2.68l-18.28 20a2 2 0 0 1-1.46.66h-.02Z" })),
    'checkmark-square-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-3.4 13.22-9.14 12a2 2 0 0 1-1.58.78 2.001 2.001 0 0 1-1.58-.76l-4.88-6.22a2.003 2.003 0 0 1 3.16-2.46l3.26 4.16 7.56-10a2.011 2.011 0 1 1 3.2 2.44v.06Z" })),
    'checkmark-square-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24Z" }),
        React.createElement("path", { d: "m29.4 16.78-7.56 10-3.26-4.22a2.002 2.002 0 1 0-3.16 2.46l4.86 6.22a1.999 1.999 0 0 0 1.58.76 2 2 0 0 0 1.58-.78l9.14-12a2.012 2.012 0 1 0-3.2-2.44h.02Z" })),
    'checkmark-square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 23.66a2 2 0 0 0-2 2V36.8a1.2 1.2 0 0 1-1.2 1.2H11.2a1.2 1.2 0 0 1-1.2-1.2V11.2a1.2 1.2 0 0 1 1.2-1.2h19.14a2 2 0 0 0 0-4H11.2A5.22 5.22 0 0 0 6 11.2v25.6a5.22 5.22 0 0 0 5.2 5.2h25.6a5.22 5.22 0 0 0 5.2-5.2V25.66a2 2 0 0 0-2-2Z" }),
        React.createElement("path", { d: "M21.44 22a2 2 0 0 0-2.88 2.76L23 29.42a2.002 2.002 0 0 0 2.88.02l13.56-14a2.008 2.008 0 0 0-2.88-2.8l-12.1 12.52L21.44 22Z" })),
    'checkmark-square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 23.66a2 2 0 0 0-2 2V36.8a1.2 1.2 0 0 1-1.2 1.2H11.2a1.2 1.2 0 0 1-1.2-1.2V11.2a1.2 1.2 0 0 1 1.2-1.2h19.14a2 2 0 0 0 0-4H11.2A5.22 5.22 0 0 0 6 11.2v25.6a5.22 5.22 0 0 0 5.2 5.2h25.6a5.22 5.22 0 0 0 5.2-5.2V25.66a2 2 0 0 0-2-2Z" }),
        React.createElement("path", { d: "M21.44 22a2 2 0 0 0-2.88 2.76L23 29.42a2.002 2.002 0 0 0 2.88.02l13.56-14a2.007 2.007 0 0 0-.698-3.266 2.009 2.009 0 0 0-2.182.466l-12.1 12.52L21.44 22Z" })),
    'chevron-down-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 30.008a2 2 0 0 1-1.42-.58l-6-6a2.008 2.008 0 0 1 2.84-2.84l4.58 4.62 4.6-4.36a2 2 0 1 1 2.76 2.88l-6 5.72a2 2 0 0 1-1.36.56Z" })),
    'chevron-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 31a2 2 0 0 1-1.42-.58l-8-8a2.008 2.008 0 0 1 2.84-2.84L24 26.2l6.6-6.36a2 2 0 1 1 2.76 2.88l-8 7.72A2 2 0 0 1 24 31Z" })),
    'chevron-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 31a2 2 0 0 1-1.42-.58l-8-8a2.008 2.008 0 0 1 2.84-2.84L24 26.2l6.6-6.36a2 2 0 1 1 2.76 2.88l-8 7.72A2 2 0 0 1 24 31Z" })),
    'chevron-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26.72 34a2 2 0 0 1-1.44-.62l-7.72-8a2 2 0 0 1 0-2.8l8-8a2.008 2.008 0 0 1 2.84 2.84L21.8 24l6.36 6.6a2 2 0 0 1 0 2.82 2 2 0 0 1-1.44.58Z" })),
    'chevron-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26.72 34a2 2 0 0 1-1.44-.62l-7.72-8a2 2 0 0 1 0-2.8l8-8a2.008 2.008 0 0 1 2.84 2.84L21.8 24l6.36 6.6a2 2 0 0 1 0 2.82 2 2 0 0 1-1.44.58Z" })),
    'chevron-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21 34a2 2 0 0 1-1.42-3.42L26.2 24l-6.36-6.62a2 2 0 1 1 2.84-2.82l7.72 8a2 2 0 0 1 0 2.8l-8 8A2 2 0 0 1 21 34Z" })),
    'chevron-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21 34a2.001 2.001 0 0 1-1.42-3.42L26.2 24l-6.36-6.62a2 2 0 1 1 2.84-2.82l7.72 8a2 2 0 0 1 0 2.8l-8 8A2 2 0 0 1 21 34Z" })),
    'chevron-top-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24.079 18a2.001 2.001 0 0 1 1.42.58l6 6a2.008 2.008 0 0 1-2.84 2.84l-4.58-4.62-4.6 4.36a2 2 0 1 1-2.76-2.88l6-5.72a2 2 0 0 1 1.36-.56Z" })),
    'chevron-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 29a2 2 0 0 1-1.42-.58L24 21.8l-6.6 6.36a2 2 0 1 1-2.82-2.84l8-7.72a2 2 0 0 1 2.8 0l8 8a2 2 0 0 1 0 2.84A2 2 0 0 1 32 29Z" })),
    'chevron-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 29a2 2 0 0 1-1.42-.58L24 21.8l-6.6 6.36a2 2 0 1 1-2.82-2.84l8-7.72a2 2 0 0 1 2.8 0l8 8a2 2 0 0 1 0 2.84A2 2 0 0 1 32 29Z" })),
    'clipboard-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 8v6a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V8a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V14a6 6 0 0 0-6-6Z" }),
        React.createElement("path", { d: "M32 4H16a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z" })),
    'clipboard-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 10V8a4 4 0 0 0-4-4H16a4 4 0 0 0-4 4v2a6 6 0 0 0-6 6v22a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V16a6 6 0 0 0-6-6ZM16 8h16v8H16V8Zm22 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V16a2 2 0 0 1 2-2v2a4 4 0 0 0 4 4h16a4 4 0 0 0 4-4v-2a2 2 0 0 1 2 2v22Z" })),
    'clock-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm8 22h-8a2 2 0 0 1-2-2v-8a2 2 0 0 1 4 0v6h6a2 2 0 1 1 0 4Z" })),
    'clock-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M32 22h-6v-6a2 2 0 0 0-4 0v8a2 2 0 0 0 2 2h8a2 2 0 0 0 0-4Z" })),
    'close-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm5.42 22.58a2 2 0 0 1 0 2.84 2 2 0 0 1-2.84 0L24 26.82l-2.58 2.6a2 2 0 0 1-2.84 0 1.999 1.999 0 0 1 0-2.84l2.6-2.58-2.6-2.58a2.008 2.008 0 1 1 2.84-2.84l2.58 2.6 2.58-2.6a2.008 2.008 0 1 1 2.84 2.84L26.82 24l2.6 2.58Z" })),
    'close-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M29.42 18.58a2 2 0 0 0-2.84 0L24 21.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l2.6 2.58-2.6 2.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l2.58-2.6 2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L26.82 24l2.6-2.58a2 2 0 0 0 0-2.84Z" })),
    'close-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m26.82 24 8.6-8.58a2.008 2.008 0 1 0-2.84-2.84L24 21.18l-8.58-8.6a2.008 2.008 0 1 0-2.84 2.84l8.6 8.58-8.6 8.58a2 2 0 0 0 0 2.84 1.999 1.999 0 0 0 2.84 0l8.58-8.6 8.58 8.6a1.999 1.999 0 0 0 2.84 0 2 2 0 0 0 0-2.84L26.82 24Z" })),
    'close-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m26.82 24 8.6-8.58a2.008 2.008 0 1 0-2.84-2.84L24 21.18l-8.58-8.6a2.008 2.008 0 1 0-2.84 2.84l8.6 8.58-8.6 8.58a2 2 0 0 0 0 2.84 1.999 1.999 0 0 0 2.84 0l8.58-8.6 8.58 8.6a1.999 1.999 0 0 0 2.84 0 2 2 0 0 0 0-2.84L26.82 24Z" })),
    'close-square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-6.58 20.58a2 2 0 0 1 0 2.84 2 2 0 0 1-2.84 0L24 26.82l-2.58 2.6a2 2 0 0 1-2.84 0 2 2 0 0 1 0-2.84l2.6-2.58-2.6-2.58a2.008 2.008 0 1 1 2.84-2.84l2.58 2.6 2.58-2.6a2.008 2.008 0 1 1 2.84 2.84L26.82 24l2.6 2.58Z" })),
    'close-square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24Z" }),
        React.createElement("path", { d: "M29.42 18.58a2 2 0 0 0-2.84 0L24 21.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l2.6 2.58-2.6 2.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l2.58-2.6 2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L26.82 24l2.6-2.58a2 2 0 0 0 0-2.84Z" })),
    'cloud-download-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.8 22c0-.22-.12-.44-.18-.66a8.347 8.347 0 0 0-.36-1.14c-.1-.24-.24-.48-.36-.74s-.3-.6-.48-.88S42 18.16 42 18c0-.16-.4-.5-.62-.74-.22-.24-.42-.4-.64-.6L40 16l-.72-.48a7.356 7.356 0 0 0-.88-.46l-.78-.36a8.258 8.258 0 0 0-1-.3 5.999 5.999 0 0 0-.82-.18h-.36A12 12 0 0 0 12.66 14h-.36c-.277.04-.551.1-.82.18-.34.079-.673.179-1 .3l-.78.36a7.363 7.363 0 0 0-.88.46l-.72.7-.74.62c-.22.2-.44.38-.64.6-.2.22-.42.5-.62.74s-.36.46-.52.72c-.16.26-.32.58-.48.88-.16.3-.26.5-.36.74-.147.37-.267.752-.36 1.14 0 .22-.14.44-.18.66A10.46 10.46 0 0 0 4 24a11 11 0 0 0 .18 1.82c0 .2.1.38.14.58.099.393.219.78.36 1.16l.24.58c.176.386.376.76.6 1.12l.28.44c.029.056.062.11.1.16L6 30a10 10 0 0 0 8 4 4 4 0 0 1 1.18-2.82A4 4 0 0 1 18 30a3.84 3.84 0 0 1 2 .54V24a4 4 0 1 1 8 0v6.74a4 4 0 0 1 2-.54 4.1 4.1 0 0 1 2.88 1.22A4 4 0 0 1 34 34a10 10 0 0 0 8-4l.1-.1c.038-.05.071-.104.1-.16l.28-.44c.224-.36.424-.734.6-1.12l.24-.58c.141-.38.261-.767.36-1.16 0-.2.1-.38.14-.58.113-.614.173-1.236.18-1.86a10.46 10.46 0 0 0-.2-2Z" }),
        React.createElement("path", { d: "M28.62 32.76 26 35.28V24a2 2 0 0 0-4 0v11.18l-2.58-2.6a2.008 2.008 0 0 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 42a2 2 0 0 0 1.38-.56l6-5.8a2 2 0 1 0-2.76-2.88Z" }),
        React.createElement("path", { d: "M43.8 22c0-.22-.12-.44-.18-.66a8.347 8.347 0 0 0-.36-1.14c-.1-.24-.24-.48-.36-.74s-.3-.6-.48-.88S42 18.16 42 18c0-.16-.4-.5-.62-.74-.22-.24-.42-.4-.64-.6L40 16l-.72-.48a7.356 7.356 0 0 0-.88-.46l-.78-.36a8.258 8.258 0 0 0-1-.3 5.999 5.999 0 0 0-.82-.18h-.36A12 12 0 0 0 12.66 14h-.36c-.277.04-.551.1-.82.18-.34.079-.673.179-1 .3l-.78.36a7.363 7.363 0 0 0-.88.46l-.72.7-.74.62c-.22.2-.44.38-.64.6-.2.22-.42.5-.62.74s-.36.46-.52.72c-.16.26-.32.58-.48.88-.16.3-.26.5-.36.74-.147.37-.267.752-.36 1.14 0 .22-.14.44-.18.66A10.46 10.46 0 0 0 4 24a11 11 0 0 0 .18 1.82c0 .2.1.38.14.58.099.393.219.78.36 1.16l.24.58c.176.386.376.76.6 1.12l.28.44c.029.056.062.11.1.16L6 30a10 10 0 0 0 8 4 4 4 0 0 1 1.18-2.82A4 4 0 0 1 18 30a3.84 3.84 0 0 1 2 .54V24a4 4 0 1 1 8 0v6.74a4 4 0 0 1 2-.54 4.1 4.1 0 0 1 2.88 1.22A4 4 0 0 1 34 34a10 10 0 0 0 8-4l.1-.1c.038-.05.071-.104.1-.16l.28-.44c.224-.36.424-.734.6-1.12l.24-.58c.141-.38.261-.767.36-1.16 0-.2.1-.38.14-.58.113-.614.173-1.236.18-1.86a10.46 10.46 0 0 0-.2-2Z" }),
        React.createElement("path", { d: "M28.62 32.76 26 35.28V24a2 2 0 0 0-4 0v11.18l-2.58-2.6a2.008 2.008 0 0 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 42a2 2 0 0 0 1.38-.56l6-5.8a2 2 0 1 0-2.76-2.88Z" })),
    'cloud-download-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.62 32.76 26 35.28V24a2 2 0 0 0-4 0v11.18l-2.58-2.6a2.008 2.008 0 0 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 42a2 2 0 0 0 1.38-.56l6-5.8a2 2 0 1 0-2.76-2.88Z" }),
        React.createElement("path", { d: "M35.34 14a12 12 0 0 0-22.68 0A10 10 0 0 0 6.5 30.54a2 2 0 1 0 3-2.54A6 6 0 0 1 14 18h.2a2 2 0 0 0 2-1.6 8 8 0 0 1 15.68 0 2 2 0 0 0 2 1.6H34a6 6 0 0 1 4.5 10 2 2 0 0 0 .18 2.84 2 2 0 0 0 2.143.322 2 2 0 0 0 .677-.502A10 10 0 0 0 35.34 14Z" })),
    'cloud-upload-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.8 24c0-.22-.12-.44-.18-.66a8.347 8.347 0 0 0-.36-1.14c-.1-.24-.24-.48-.36-.74s-.3-.6-.48-.88S42 20.16 42 20c0-.16-.4-.5-.62-.74-.22-.24-.42-.4-.64-.6L40 18l-.72-.48a7.356 7.356 0 0 0-.88-.46l-.78-.36a8.258 8.258 0 0 0-1-.3 5.999 5.999 0 0 0-.82-.18l-.46-.22a12 12 0 0 0-22.68 0l-.36.1c-.277.04-.551.1-.82.18-.34.079-.673.179-1 .3l-.78.36a7.363 7.363 0 0 0-.88.46l-.72.6-.74.62c-.22.2-.44.38-.64.6-.2.22-.42.5-.62.74s-.36.46-.52.72c-.16.26-.32.58-.48.88-.16.3-.26.5-.36.74-.147.37-.267.752-.36 1.14 0 .22-.14.44-.18.66A10.46 10.46 0 0 0 4 26a11 11 0 0 0 .18 1.82c0 .2.1.38.14.58.099.393.219.78.36 1.16l.24.58c.176.386.376.76.6 1.12l.28.44c.029.056.062.11.1.16L6 32a10 10 0 0 0 8 4h6v-2.74a4 4 0 0 1-2 .54 4.1 4.1 0 0 1-2.88-1.22 4.001 4.001 0 0 1 .1-5.66l6-5.8A4 4 0 0 1 24 20a4 4 0 0 1 2.82 1.18l6 6A4 4 0 0 1 30 34a3.84 3.84 0 0 1-2-.54V36h6a10 10 0 0 0 8-4l.1-.1c.038-.05.071-.104.1-.16l.28-.44c.224-.36.424-.734.6-1.12l.24-.58c.141-.38.261-.767.36-1.16 0-.2.1-.38.14-.58.113-.614.173-1.236.18-1.86a10.46 10.46 0 0 0-.2-2Z" }),
        React.createElement("path", { d: "M25.42 22.58a2 2 0 0 0-2.8 0l-6 5.8a2 2 0 1 0 2.76 2.88L22 28.72V40a2 2 0 0 0 4 0V28.82l2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84l-6-6Z" })),
    'cloud-upload-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.42 22.58a2 2 0 0 0-2.8 0l-6 5.8a2 2 0 1 0 2.76 2.88L22 28.72V40a2 2 0 1 0 4 0V28.82l2.58 2.6a1.999 1.999 0 0 0 2.84 0 2 2 0 0 0 0-2.84l-6-6Z" }),
        React.createElement("path", { d: "M35.34 14a12 12 0 0 0-22.68 0A10 10 0 0 0 6.5 30.54a2 2 0 1 0 3-2.54A6 6 0 0 1 14 18h.2a2 2 0 0 0 2-1.6 8 8 0 0 1 15.68 0 2 2 0 0 0 2 1.6H34a6 6 0 0 1 4.5 10 2 2 0 0 0 .18 2.84 2 2 0 0 0 2.143.322 2 2 0 0 0 .677-.502A10 10 0 0 0 35.34 14Z" })),
    'code-download-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m8.58 24 8.96-10.72a2.002 2.002 0 1 0-3.08-2.56l-10 12a2 2 0 0 0 0 2.54l9.66 12a2 2 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L8.58 24ZM43.56 22.74l-9.56-12a2.005 2.005 0 0 0-3.12 2.52L39.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 32 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" }),
        React.createElement("path", { d: "M31.44 22.82a2 2 0 0 0-2.82 0L26 25.28V16a2 2 0 0 0-4 0v9.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 32a2 2 0 0 0 1.38-.56l6-5.8a2 2 0 0 0 .06-2.82Z" })),
    'code-download-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m8.58 24 8.96-10.72a2.002 2.002 0 1 0-3.08-2.56l-10 12a2 2 0 0 0 0 2.54l9.66 12a2 2 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L8.58 24ZM43.56 22.74l-9.56-12a2.006 2.006 0 0 0-3.12 2.52L39.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 32 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" }),
        React.createElement("path", { d: "M31.44 22.82a2 2 0 0 0-2.82 0L26 25.28V16a2 2 0 0 0-4 0v9.18l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 32a2 2 0 0 0 1.38-.56l6-5.8a2 2 0 0 0 .06-2.82Z" })),
    'code-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.28 10.46a2 2 0 0 0-2.82.26l-10 12a2 2 0 0 0 0 2.54l9.66 12a2 2 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L8.58 24l8.96-10.72a2 2 0 0 0-.26-2.82ZM43.56 22.74l-9.56-12a2.001 2.001 0 0 0-3.323.3 1.998 1.998 0 0 0 .203 2.22L39.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 32 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" })),
    'code-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.28 10.46a2 2 0 0 0-2.82.26l-10 12a2 2 0 0 0 0 2.54l9.66 12a1.999 1.999 0 0 0 1.56.74 2 2 0 0 0 1.56-3.26L8.58 24l8.96-10.72a2 2 0 0 0-.26-2.82ZM43.56 22.74l-9.56-12a2 2 0 0 0-2.82-.3 2.001 2.001 0 0 0-.3 2.82L39.42 24l-8.96 10.74a2 2 0 0 0 .26 2.82A2 2 0 0 0 32 38a2 2 0 0 0 1.54-.72l10-12a2 2 0 0 0 .02-2.54Z" })),
    'code2-outline': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M37.541 17.271a2 2 0 0 0-.26-2.82l-12-10a2 2 0 0 0-2.54 0l-12 9.66a2.001 2.001 0 0 0-.74 1.56 2 2 0 0 0 3.26 1.56l10.74-8.66 10.72 8.96a2 2 0 0 0 2.82-.26ZM25.261 43.551l12-9.56a2 2 0 0 0 .3-2.82 2.002 2.002 0 0 0-2.82-.3l-10.74 8.54-10.74-8.96a2 2 0 0 0-2.82.26 2 2 0 0 0-.44 1.28 1.999 1.999 0 0 0 .72 1.54l12 10a2 2 0 0 0 2.54.02Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'collapse-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 18h-5.16l6.58-6.58a2.008 2.008 0 1 0-2.84-2.84L30 15.14V10a2 2 0 1 0-4 0v10a2 2 0 0 0 2 2h10a2 2 0 0 0 0-4ZM20 26H10a2 2 0 1 0 0 4h5.14l-6.56 6.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L18 32.84V38a2 2 0 0 0 4 0V28a2 2 0 0 0-2-2Z" })),
    'collapse-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 18h-5.16l6.58-6.58a2.008 2.008 0 1 0-2.84-2.84L30 15.14V10a2 2 0 1 0-4 0v10a2 2 0 0 0 2 2h10a2 2 0 0 0 0-4ZM20 26H10a2 2 0 1 0 0 4h5.14l-6.56 6.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L18 32.84V38a2 2 0 0 0 4 0V28a2 2 0 0 0-2-2Z" })),
    'color-palette-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39.08 10.16A21.22 21.22 0 0 0 23.82 4a20 20 0 0 0-.1 40 5.16 5.16 0 0 0 5.06-3.78 5.039 5.039 0 0 0-1.14-4.56 1 1 0 0 1 .74-1.66h3.3A12.3 12.3 0 0 0 44 22.66a16.959 16.959 0 0 0-4.92-12.5Zm-25.4 19.32a3 3 0 1 1 .8-4.16 2.98 2.98 0 0 1-.8 4.16ZM16.6 18.5a3 3 0 1 1-5.28-2.851A3 3 0 0 1 16.6 18.5ZM22 14a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm11.5 1.6a3 3 0 1 1-3.08-5.15 3 3 0 0 1 3.08 5.15Z" })),
    'color-palette-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39.08 10.16A21.22 21.22 0 0 0 23.82 4a20 20 0 0 0-.1 40 5.16 5.16 0 0 0 5.06-3.78 5.039 5.039 0 0 0-1.14-4.56 1 1 0 0 1 .74-1.66h3.3A12.3 12.3 0 0 0 44 22.66a16.959 16.959 0 0 0-4.92-12.5ZM31.76 30h-3.3a4.98 4.98 0 0 0-3.74 8.3.98.98 0 0 1 .24.98c-.1.42-.56.68-1.18.72A15.998 15.998 0 0 1 8.14 21.78 16.2 16.2 0 0 1 23.84 8H24a16.94 16.94 0 0 1 12.2 4.96A13 13 0 0 1 40 22.5a8.34 8.34 0 0 1-8.24 7.5Z" }),
        React.createElement("path", { d: "M24 16a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM30.5 14.4a3 3 0 1 0 3 5.195 3 3 0 0 0-3-5.195ZM17.5 14.4a3 3 0 1 0-2.995 5.198A3 3 0 0 0 17.5 14.4ZM12.32 22.52a3 3 0 1 0 4.16.8 2.98 2.98 0 0 0-4.16-.8Z" })),
    'color-picker-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.8 14.68 33.32 9.2A3.84 3.84 0 0 0 28 9.06l-4 4-2.58-2.48a2.007 2.007 0 1 0-2.84 2.84L21.06 16 10 27.06a4 4 0 0 0-1.14 2.42L8 37.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 40h.18l8.34-.76a4 4 0 0 0 2.42-1.14L32.1 26.94l2.48 2.48a2 2 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-2.48-2.48 4-4a3.84 3.84 0 0 0-.14-5.42Zm-26 15.2L24 18.72l5.38 5.4-5.58 5.58" })),
    'color-picker-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.8 14.68 33.32 9.2A3.84 3.84 0 0 0 28 9.06l-4 4-2.58-2.48a2.008 2.008 0 0 0-2.84 2.84L21.06 16 10 27.06a4 4 0 0 0-1.14 2.42L8 37.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 40h.18l8.34-.76a4 4 0 0 0 2.42-1.14L32.1 26.94l2.48 2.48a2.002 2.002 0 0 0 2.84 0 2.001 2.001 0 0 0 0-2.84l-2.48-2.48 4-4a3.84 3.84 0 0 0-.14-5.42ZM18.16 35.24l-6 .56.54-6L24 18.72l5.38 5.4-11.22 11.12Zm14-14L26.72 16l3.82-4L36 17.46l-3.84 3.78Z" })),
    'compass-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m21.6 26.42 3.38-1.36 1.42-3.48-3.38 1.36-1.42 3.48Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm7.86 14.84-3.5 8.52a2 2 0 0 1-1.1 1.1l-8.42 3.4A2 2 0 0 1 18 32a2 2 0 0 1-1.42-.62h-.1a2 2 0 0 1-.36-2l3.5-8.52a2 2 0 0 1 1.1-1.1l8.42-3.4a2 2 0 0 1 2.2.5 2 2 0 0 1 .52 1.98Z" })),
    'compass-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M31.36 16.64a2 2 0 0 0-2.2-.5l-8.42 3.4a2 2 0 0 0-1.1 1.1l-3.5 8.52a2 2 0 0 0 .36 2h.1A2.002 2.002 0 0 0 18 32a2 2 0 0 0 .76-.14l8.42-3.4a1.998 1.998 0 0 0 1.1-1.1l3.5-8.52a2 2 0 0 0-.42-2.2Zm-9.76 9.78 1.42-3.48 3.38-1.36-1.42 3.48-3.38 1.36Z" })),
    'copy-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 18h-6v-6.66A5.36 5.36 0 0 0 24.66 6H11.34A5.36 5.36 0 0 0 6 11.34v13.32A5.36 5.36 0 0 0 11.34 30H18v6a6 6 0 0 0 6 6h12a6 6 0 0 0 6-6V24a6 6 0 0 0-6-6Zm-18 6v2h-6.66A1.34 1.34 0 0 1 10 24.66V11.34A1.34 1.34 0 0 1 11.34 10h13.32A1.34 1.34 0 0 1 26 11.34V18h-2a6 6 0 0 0-6 6Z" })),
    'copy-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 42H24a6 6 0 0 1-6-6V24a6 6 0 0 1 6-6h12a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6ZM24 22a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V24a2 2 0 0 0-2-2H24Z" }),
        React.createElement("path", { d: "M19.46 30h-8.12A5.36 5.36 0 0 1 6 24.66V11.34A5.36 5.36 0 0 1 11.34 6h13.32A5.36 5.36 0 0 1 30 11.34v7.46h-4v-7.46A1.34 1.34 0 0 0 24.66 10H11.34A1.34 1.34 0 0 0 10 11.34v13.32A1.34 1.34 0 0 0 11.34 26h8.12v4Z" })),
    'corner-down-left-fill': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M39.834 11.95a1.992 1.992 0 1 0-3.983 0v9.959a1.992 1.992 0 0 1-.578 1.414 1.992 1.992 0 0 1-1.414.577H16.093l5.358-6.752a1.991 1.991 0 0 0-3.028-2.33l-7.967 9.959a1.992 1.992 0 0 0 0 2.49l7.967 9.958a1.991 1.991 0 0 0 2.789.299 1.99 1.99 0 0 0 .298-2.809l-5.298-6.692h17.766a5.975 5.975 0 0 0 5.975-5.975l-.119-10.098Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'corner-down-left-outline': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M39.834 11.95a1.992 1.992 0 1 0-3.983 0v9.959a1.992 1.992 0 0 1-.578 1.414 1.992 1.992 0 0 1-1.414.577H16.093l5.358-6.751a1.991 1.991 0 0 0-3.028-2.33l-7.967 9.958a1.992 1.992 0 0 0 0 2.49l7.967 9.958a1.993 1.993 0 0 0 1.554.737 1.992 1.992 0 0 0 1.533-3.247l-5.298-6.692h17.766a5.976 5.976 0 0 0 5.975-5.975l-.119-10.098Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'corner-down-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.56 24.76-8-10a2 2 0 0 0-3.12 2.48l5.4 6.76H16a2 2 0 0 1-2-2V12a2 2 0 0 0-4 0v10a6 6 0 0 0 6 6h17.84l-5.4 6.76A2 2 0 0 0 30 38a2 2 0 0 0 1.56-.76l8-10a2 2 0 0 0 0-2.48Z" })),
    'corner-down-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.56 24.76-8-10a2 2 0 0 0-3.12 2.48l5.4 6.76H16a2 2 0 0 1-2-2V12a2 2 0 0 0-4 0v10a6 6 0 0 0 6 6h17.84l-5.4 6.76A2 2 0 0 0 30 38a2 2 0 0 0 1.56-.76l8-10a2 2 0 0 0 0-2.48Z" })),
    'corner-left-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 10H26a6 6 0 0 0-6 6v17.84l-6.76-5.4a2 2 0 0 0-2.48 3.12l10 8a2 2 0 0 0 2.48 0l10-8a2 2 0 1 0-2.48-3.12L24 33.84V16a2 2 0 0 1 2-2h10a2 2 0 0 0 0-4Z" })),
    'corner-left-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 10H26a6 6 0 0 0-6 6v17.84l-6.76-5.4a2 2 0 0 0-2.48 3.12l10 8a2 2 0 0 0 2.48 0l10-8a2 2 0 1 0-2.48-3.12L24 33.84V16a2 2 0 0 1 2-2h10a2 2 0 0 0 0-4Z" })),
    'corner-left-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 34H26a2 2 0 0 1-2-2V14.16l6.76 5.4A2 2 0 0 0 32 20a2 2 0 0 0 1.24-3.56l-10-8a2 2 0 0 0-2.48 0l-10 8a2 2 0 0 0 2.48 3.12l6.76-5.4V32a6 6 0 0 0 6 6h10a2 2 0 0 0 0-4Z" })),
    'corner-left-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 34H26a2 2 0 0 1-2-2V14.16l6.76 5.4A2 2 0 0 0 32 20a2 2 0 0 0 1.24-3.56l-10-8a2 2 0 0 0-2.48 0l-10 8a2 2 0 0 0 2.48 3.12l6.76-5.4V32a6 6 0 0 0 6 6h10a2 2 0 0 0 0-4Z" })),
    'corner-right-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.56 28.76a2 2 0 0 0-2.8-.32L28 33.84V16a6 6 0 0 0-6-6H12a2 2 0 0 0 0 4h10a2 2 0 0 1 2 2v17.84l-6.76-5.4a2 2 0 0 0-2.48 3.12l10 8a2 2 0 0 0 2.48 0l10-8a2 2 0 0 0 .32-2.8Z" })),
    'corner-right-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.56 28.76a2 2 0 0 0-2.8-.32L28 33.84V16a6 6 0 0 0-6-6H12a2 2 0 0 0 0 4h10a2 2 0 0 1 2 2v17.84l-6.76-5.4a2 2 0 0 0-2.48 3.12l10 8a2 2 0 0 0 2.48 0l10-8a2 2 0 0 0 .32-2.8Z" })),
    'corner-right-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m37.24 16.44-10-8a2 2 0 0 0-2.48 0l-10 8a2 2 0 0 0 2.48 3.12l6.76-5.4V32a2 2 0 0 1-2 2H12a2 2 0 0 0 0 4h10a6 6 0 0 0 6-6V14.16l6.76 5.4A2 2 0 0 0 36 20a2 2 0 0 0 1.24-3.56Z" })),
    'corner-right-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m37.24 16.44-10-8a2 2 0 0 0-2.48 0l-10 8a2 2 0 0 0 2.48 3.12l6.76-5.4V32a2 2 0 0 1-2 2H12a2 2 0 0 0 0 4h10a6 6 0 0 0 6-6V14.16l6.76 5.4A2 2 0 0 0 36 20a2 2 0 0 0 1.24-3.56Z" })),
    'corner-up-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 20H14.16l5.4-6.76a2.001 2.001 0 0 0-1.766-3.348 2 2 0 0 0-1.354.868l-8 10a2 2 0 0 0 0 2.48l8 10A2 2 0 0 0 18 34a2 2 0 0 0 1.56-3.24L14.16 24H32a2 2 0 0 1 2 2v10a2 2 0 0 0 4 0V26a6 6 0 0 0-6-6Z" })),
    'corner-up-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 20H14.16l5.4-6.76a2.001 2.001 0 0 0-1.766-3.348 2 2 0 0 0-1.354.868l-8 10a2 2 0 0 0 0 2.48l8 10A2 2 0 0 0 18 34a2 2 0 0 0 1.56-3.24L14.16 24H32a2 2 0 0 1 2 2v10a2 2 0 0 0 4 0V26a6 6 0 0 0-6-6Z" })),
    'corner-up-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.56 20.76-8-10a2 2 0 0 0-3.12 2.48l5.4 6.76H16a6 6 0 0 0-6 6v10a2 2 0 0 0 4 0V26a2 2 0 0 1 2-2h17.84l-5.4 6.76A2 2 0 0 0 30 34a2 2 0 0 0 1.56-.76l8-10a2 2 0 0 0 0-2.48Z" })),
    'corner-up-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.56 20.76-8-10a2 2 0 0 0-3.12 2.48l5.4 6.76H16a6 6 0 0 0-6 6v10a2 2 0 0 0 4 0V26a2 2 0 0 1 2-2h17.84l-5.4 6.76A2 2 0 0 0 30 34a2 2 0 0 0 1.56-.76l8-10a2 2 0 0 0 0-2.48Z" })),
    'credit-card-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 10H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V16a6 6 0 0 0-6-6ZM22 30h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4Zm12 0h-4a2 2 0 0 1 0-4h4a2 2 0 0 1 0 4Zm6-12H8v-2a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v2Z" })),
    'credit-card-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 10H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V16a6 6 0 0 0-6-6ZM8 16a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v2H8v-2Zm32 16a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V22h32v10Z" }),
        React.createElement("path", { d: "M14 30h8a2 2 0 0 0 0-4h-8a2 2 0 0 0 0 4ZM30 30h4a2 2 0 0 0 0-4h-4a2 2 0 0 0 0 4Z" })),
    'crop-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 32h-6V17.12A5.12 5.12 0 0 0 30.88 12H16V6a2 2 0 1 0-4 0v6H6a2 2 0 1 0 0 4h6v14.88A5.12 5.12 0 0 0 17.12 36H32v6a2 2 0 0 0 4 0v-6h6a2 2 0 0 0 0-4Zm-24.88 0A1.12 1.12 0 0 1 16 30.88V16h14.88A1.12 1.12 0 0 1 32 17.12V32H17.12Z" })),
    'crop-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 32h-6V17.12A5.12 5.12 0 0 0 30.88 12H16V6a2 2 0 1 0-4 0v6H6a2 2 0 1 0 0 4h6v14.88A5.12 5.12 0 0 0 17.12 36H32v6a2 2 0 0 0 4 0v-6h6a2 2 0 0 0 0-4Zm-24.88 0A1.12 1.12 0 0 1 16 30.88V16h14.88A1.12 1.12 0 0 1 32 17.12V32H17.12Z" })),
    'cube-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22.5 23.66 6 16.72v15.46a3.38 3.38 0 0 0 2 3.04L22.38 42h.12V23.66ZM24 21l17.02-7.14A3.24 3.24 0 0 0 40 13.1L25.6 6.36a3.74 3.74 0 0 0-3.2 0L8 13.1a3.24 3.24 0 0 0-1.02.76L24 21ZM25.5 23.66V42h.1L40 35.22a3.379 3.379 0 0 0 2-3.02V16.72l-16.5 6.94Z" })),
    'cube-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.32 14.52c0-.14-.2-.28-.3-.42l-.18-.2a5 5 0 0 0-1.72-1.36l-12.8-6a5.4 5.4 0 0 0-4.52 0l-12.8 6a5.2 5.2 0 0 0-1.72 1.36l-.24.1c-.116.12-.217.255-.3.4A4.78 4.78 0 0 0 6 16.92v14.12a4.98 4.98 0 0 0 2.92 4.52l12.8 6a5.4 5.4 0 0 0 4.54 0l12.8-6A4.98 4.98 0 0 0 42 31.08V16.92a4.78 4.78 0 0 0-.68-2.4Zm-17.9-4.4a1.46 1.46 0 0 1 1.16 0l10.66 4.96L24 20.3l-11.24-5.22 10.66-4.96ZM10.6 32a.94.94 0 0 1-.6-.86V18.2l12 5.58v13.44L10.6 32Zm26.78 0L26 37.22V23.78l12-5.58v12.88a.96.96 0 0 1-.62.92Z" })),
    'diagonal-arrow-left-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.42 12.58a2 2 0 0 0-2.84 0L16 29.18V18a2 2 0 0 0-4 0v16a2 2 0 0 0 2 2h16a2 2 0 0 0 0-4H18.82l16.6-16.58a2 2 0 0 0 0-2.84Z" })),
    'diagonal-arrow-left-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.42 12.58a2 2 0 0 0-2.84 0L16 29.18V18a2 2 0 0 0-4 0v16a2 2 0 0 0 2 2h16a2 2 0 0 0 0-4H18.82l16.6-16.58a2 2 0 0 0 0-2.84Z" })),
    'diagonal-arrow-left-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.42 32.58 18.84 16H30a2 2 0 0 0 0-4H14.1a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2H14a2 2 0 0 0 2-2V18.9l16.52 16.52a2 2 0 0 0 2.84 0 2.001 2.001 0 0 0 .06-2.84Z" })),
    'diagonal-arrow-left-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.42 32.58 18.84 16H30a2 2 0 0 0 0-4H14.1a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2H14a2 2 0 0 0 2-2V18.9l16.52 16.52a2 2 0 0 0 2.84 0 2 2 0 0 0 .06-2.84Z" })),
    'diagonal-arrow-right-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 16a2 2 0 0 0-2 2v11.18l-16.58-16.6a2.008 2.008 0 1 0-2.84 2.84L29.18 32H18a2 2 0 0 0 0 4h16a2 2 0 0 0 2-2V18a2 2 0 0 0-2-2Z" })),
    'diagonal-arrow-right-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 16a2 2 0 0 0-2 2v11.18l-16.58-16.6a2.008 2.008 0 1 0-2.84 2.84L29.18 32H18a2 2 0 0 0 0 4h16a2 2 0 0 0 2-2V18a2 2 0 0 0-2-2Z" })),
    'diagonal-arrow-right-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 14.1a2 2 0 0 0-2-2L18 12a2 2 0 0 0 0 4h11.12L12.58 32.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L32 18.84V30a2 2 0 0 0 4 0V14.1Z" })),
    'diagonal-arrow-right-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 14.1a2 2 0 0 0-2-2L18 12a2 2 0 0 0 0 4h11.12L12.58 32.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L32 18.84V30a2 2 0 0 0 4 0V14.1Z" })),
    'done-all-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M33.24 12.42a2.001 2.001 0 0 0-2.82.34l-14 18-6.86-8.36a2 2 0 1 0-3.12 2.5l8.34 10.36a2 2 0 0 0 1.56.74 2 2 0 0 0 1.66-.76l15.66-20a2.003 2.003 0 0 0-.42-2.82ZM43.24 12.42a2 2 0 0 0-2.82.34l-14 18-1.22-1.5-2.52 3.24 2.2 2.74a2 2 0 0 0 3.12-.02l15.66-20a2.002 2.002 0 0 0-.42-2.8Z" }),
        React.createElement("path", { d: "M17.42 26.12 20 22.88l-.4-.48a1.999 1.999 0 0 0-2.86-.4 2 2 0 0 0-.3 2.82l.98 1.3Z" })),
    'done-all-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M33.24 12.42a2.001 2.001 0 0 0-2.82.34l-14 18-6.86-8.36a2 2 0 1 0-3.12 2.5l8.34 10.36a2.001 2.001 0 0 0 1.56.74 2 2 0 0 0 1.66-.76l15.66-20a2.002 2.002 0 0 0-.42-2.82ZM43.24 12.42a2 2 0 0 0-2.82.34l-14 18-1.22-1.5-2.52 3.24 2.2 2.74a2.001 2.001 0 0 0 2.427.538A2 2 0 0 0 28 35.22l15.66-20a2.003 2.003 0 0 0-.42-2.8Z" }),
        React.createElement("path", { d: "M17.42 26.12 20 22.88l-.4-.48a2 2 0 0 0-2.86-.4 2 2 0 0 0-.3 2.82l.98 1.3Z" })),
    'download-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 36H10a2 2 0 1 0 0 4h28a2 2 0 1 0 0-4Z" }),
        React.createElement("path", { d: "M8 34v4a2 2 0 1 0 4 0v-4a2 2 0 1 0-4 0ZM36 34v4a2 2 0 1 0 4 0v-4a2 2 0 1 0-4 0ZM24 30a2 2 0 0 1-1.16-.36l-8-5.64a2 2 0 1 1 2.32-3.26L24 25.52l6.8-5.12a2 2 0 1 1 2.4 3.2l-8 6a2 2 0 0 1-1.2.4Z" }),
        React.createElement("path", { d: "M24 26a2 2 0 0 1-2-2V8a2 2 0 1 1 4 0v16a2 2 0 0 1-2 2Z" })),
    'download-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 36H10a2 2 0 1 0 0 4h28a2 2 0 1 0 0-4Z" }),
        React.createElement("path", { d: "M8 34v4a2 2 0 1 0 4 0v-4a2 2 0 1 0-4 0ZM36 34v4a2 2 0 1 0 4 0v-4a2 2 0 1 0-4 0ZM24 30a2 2 0 0 1-1.16-.36l-8-5.64a2 2 0 1 1 2.32-3.26L24 25.52l6.8-5.12a2 2 0 1 1 2.4 3.2l-8 6a2 2 0 0 1-1.2.4Z" }),
        React.createElement("path", { d: "M24 26a2 2 0 0 1-2-2V8a2 2 0 1 1 4 0v16a2 2 0 0 1-2 2Z" })),
    'drag-outline': React.createElement(React.Fragment, null,
        React.createElement("circle", { cy: "12", cx: "20", r: "2" }),
        React.createElement("circle", { cy: "12", cx: "28", r: "2" }),
        React.createElement("circle", { cy: "20", cx: "20", r: "2" }),
        React.createElement("circle", { cy: "20", cx: "28", r: "2" }),
        React.createElement("circle", { cy: "28", cx: "20", r: "2" }),
        React.createElement("circle", { cy: "28", cx: "28", r: "2" }),
        React.createElement("circle", { cy: "36", cx: "20", r: "2" }),
        React.createElement("circle", { cy: "36", cx: "28", r: "2" })),
    'drag2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m35.172 21.994-3.65-3.65a2 2 0 1 1 2.83-2.828l7.07 7.071a2 2 0 0 1 0 2.829l-7.07 7.071a2 2 0 0 1-2.83-2.829l3.635-3.634-9.117-.014H12.837l3.648 3.649a2 2 0 1 1-2.828 2.828l-7.071-7.071a2 2 0 0 1 0-2.829l7.07-7.07a2 2 0 1 1 2.83 2.828l-3.635 3.634 14.092.015h8.229Z" })),
    'droplet-fill': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M23.802 41.851a14.677 14.677 0 0 1-10.473-4.522 15.332 15.332 0 0 1 .198-21.362l9.204-9.223a1.864 1.864 0 0 1 1.408-.595 1.983 1.983 0 0 1 1.408.615l9.045 9.362a15.332 15.332 0 0 1-.179 21.362 14.538 14.538 0 0 1-10.611 4.363Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'droplet-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 32.28A15.46 15.46 0 0 0 34.68 16l-9.12-9.38A2 2 0 0 0 24.14 6a2 2 0 0 0-1.44.6l-5.22 5.24L38 32.28ZM12 17.64a15.46 15.46 0 0 0 1.28 19.8A14.88 14.88 0 0 0 23.84 42a14.68 14.68 0 0 0 9.28-3.2L12 17.64ZM41.42 38.58l-32-32a2.008 2.008 0 1 0-2.84 2.84l32 32a2 2 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" })),
    'droplet-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 38a10.801 10.801 0 0 1-7.76-3.28 11.46 11.46 0 0 1-1.38-14.22L12 17.64a15.48 15.48 0 0 0 1.4 19.88A14.738 14.738 0 0 0 24 42a14.72 14.72 0 0 0 9.16-3.18L30.3 36a10.86 10.86 0 0 1-6.3 2ZM24 10.86l7.76 8a11.42 11.42 0 0 1 2.98 10.3L38 32.3A15.44 15.44 0 0 0 34.62 16l-9.2-9.4a2 2 0 0 0-2.86 0l-5.1 5.2 2.84 2.84 3.7-3.78ZM41.42 38.58l-32-32a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" })),
    'droplet-outline': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M23.802 41.851a14.677 14.677 0 0 1-10.473-4.522 15.332 15.332 0 0 1 .198-21.362l9.204-9.223a1.864 1.864 0 0 1 1.408-.595 1.983 1.983 0 0 1 1.408.615l9.045 9.362a15.332 15.332 0 0 1-.179 21.362 14.54 14.54 0 0 1-10.611 4.363Zm.258-30.882-7.716 7.775a11.385 11.385 0 0 0-.139 15.867 10.77 10.77 0 0 0 7.597 3.273 10.75 10.75 0 0 0 7.735-3.193 11.345 11.345 0 0 0 .12-15.868l-7.598-7.854Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'edit-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 40H10a2 2 0 1 0 0 4h28a2 2 0 0 0 0-4ZM10 36h.18l8.34-.76a4 4 0 0 0 2.42-1.14l18-18a3.84 3.84 0 0 0-.14-5.42L33.32 5.2A4 4 0 0 0 28 5.06l-18 18a4 4 0 0 0-1.14 2.42L8 33.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 36ZM30.54 8 36 13.46l-4 3.9L26.64 12l3.9-4Z" })),
    'edit-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 40H10a2 2 0 1 0 0 4h28a2 2 0 0 0 0-4ZM10 36h.18l8.34-.76a4 4 0 0 0 2.42-1.14l18-18a3.84 3.84 0 0 0-.14-5.42L33.32 5.2A4 4 0 0 0 28 5.06l-18 18a4 4 0 0 0-1.14 2.42L8 33.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 36ZM30.54 8 36 13.46l-4 3.9L26.64 12l3.9-4Zm-17.8 17.82L24 14.64l5.4 5.4-11.2 11.2-6 .56.54-5.98Z" })),
    'edit-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.8 14.68 33.32 9.2A4 4 0 0 0 28 9.06l-18 18a4 4 0 0 0-1.14 2.42L8 37.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 40h.18l8.34-.76a4 4 0 0 0 2.42-1.14l18-18a3.84 3.84 0 0 0-.14-5.42ZM32 21.36 26.64 16l3.9-4L36 17.46l-4 3.9Z" })),
    'edit-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.8 14.68 33.32 9.2A4 4 0 0 0 28 9.06l-18 18a4 4 0 0 0-1.14 2.42L8 37.82a2 2 0 0 0 1.232 2.032A2 2 0 0 0 10 40h.18l8.34-.76a4 4 0 0 0 2.42-1.14l18-18a3.84 3.84 0 0 0-.14-5.42ZM18.16 35.24l-6 .56.54-6L24 18.64l5.4 5.4-11.24 11.2ZM32 21.36 26.64 16l3.9-4L36 17.46l-4 3.9Z" })),
    'email-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 8H10a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V14a6 6 0 0 0-6-6Zm0 4-13 8.94a2 2 0 0 1-2 0L10 12h28Z" })),
    'email-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 8H10a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h28a6 6 0 0 0 6-6V14a6 6 0 0 0-6-6Zm-1.34 4L24 21.5 11.34 12h25.32ZM38 36H10a2 2 0 0 1-2-2V14.5l14.8 11.1a2 2 0 0 0 2.4 0L40 14.5V34a2 2 0 0 1-2 2Z" })),
    'expand-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 10a2 2 0 0 0-2-2H28a2 2 0 0 0 0 4h5.14l-6.56 6.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L36 14.84V20a2 2 0 0 0 4 0V10ZM21.42 26.58a2 2 0 0 0-2.84 0L12 33.14V28a2 2 0 0 0-4 0v10a2 2 0 0 0 2 2h10a2 2 0 0 0 0-4h-5.16l6.58-6.58a2 2 0 0 0 0-2.84Z" })),
    'expand-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 10a2 2 0 0 0-2-2H28a2 2 0 0 0 0 4h5.14l-6.56 6.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L36 14.84V20a2 2 0 0 0 4 0V10ZM21.42 26.58a2 2 0 0 0-2.84 0L12 33.14V28a2 2 0 0 0-4 0v10a2 2 0 0 0 2 2h10a2 2 0 0 0 0-4h-5.16l6.58-6.58a2 2 0 0 0 0-2.84Z" })),
    'external-link-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 22a2 2 0 0 0-2 2v12a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h12a2 2 0 0 0 0-4H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24a2 2 0 0 0-2-2Z" }),
        React.createElement("path", { d: "M32 10h3.16L22.58 22.56a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L38 12.84V16a2 2 0 0 0 4 0V8a2 2 0 0 0-2-2h-8a2 2 0 0 0 0 4Z" })),
    'external-link-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 22a2 2 0 0 0-2 2v12a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h12a2 2 0 0 0 0-4H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24a2 2 0 0 0-2-2Z" }),
        React.createElement("path", { d: "M32 10h3.16L22.58 22.56a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L38 12.84V16a2 2 0 0 0 4 0V8a2 2 0 0 0-2-2h-8a2 2 0 0 0 0 4Z" })),
    'eye-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        React.createElement("path", { d: "M43.74 23c-1.28-2.22-8.32-13.36-20.28-13C12.4 10.28 6 20 4.26 23a2 2 0 0 0 0 2c1.26 2.18 8 13 19.78 13h.5c11.06-.28 17.48-10 19.2-13a2 2 0 0 0 0-2ZM24 31a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'eye-off-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.62 26.78A17.86 17.86 0 0 0 42 15.24a2.014 2.014 0 0 0-4-.48 14.14 14.14 0 0 1-28 0 2.014 2.014 0 0 0-4 .48 17.86 17.86 0 0 0 6.36 11.54l-4.6 4.64a2 2 0 0 0 2.82 2.82l5.22-5.2a18.12 18.12 0 0 0 6.2 1.84V38a2 2 0 0 0 4 0v-7.12a18.12 18.12 0 0 0 6.2-1.84l5.22 5.2a2 2 0 0 0 2.82-2.82l-4.62-4.64Z" })),
    'eye-off-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.62 26.78A17.86 17.86 0 0 0 42 15.24a2.014 2.014 0 0 0-4-.48 14.14 14.14 0 0 1-28 0 2.014 2.014 0 0 0-4 .48 17.86 17.86 0 0 0 6.36 11.54l-4.6 4.64a2 2 0 0 0 2.82 2.82l5.22-5.2a18.12 18.12 0 0 0 6.2 1.84V38a2 2 0 0 0 4 0v-7.12a18.12 18.12 0 0 0 6.2-1.84l5.22 5.2a2 2 0 0 0 2.82-2.82l-4.62-4.64Z" })),
    'eye-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM30.58 36.24 28 33.56l-.14-.14-2.54-2.54c-.403.07-.81.11-1.22.12a6.998 6.998 0 0 1-7.1-7c.01-.41.05-.817.12-1.22l-4-4L10 15.74A31.78 31.78 0 0 0 4.26 23a2 2 0 0 0 0 2c1.26 2.18 8 13 19.78 13h.5A18.959 18.959 0 0 0 31 36.66l-.42-.42ZM17.18 11.52l5.6 5.6c.403-.07.81-.11 1.22-.12a7 7 0 0 1 7 7c-.01.41-.05.817-.12 1.22l5.36 5.36 1.68 1.68A31.782 31.782 0 0 0 43.74 25a2 2 0 0 0 0-2c-1.28-2.22-8.32-13.36-20.28-13-2.215.066-4.402.52-6.46 1.34l.18.18Z" }),
        React.createElement("path", { d: "M41.42 38.58 38.82 36l-4-4-19.04-19.06L12.84 10 9.42 6.58a2.008 2.008 0 1 0-2.84 2.84L11.06 14l3.5 3.4L29.18 32l.14.14L32 34.82 33.18 36l5.4 5.42a2 2 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" })),
    'eye-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9.42 6.58a2.008 2.008 0 0 0-2.84 2.84l11.26 11.26a7 7 0 0 0 9.48 9.48l11.26 11.26a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-32-32ZM24 27a3 3 0 0 1-3-3v-.14l3.12 3.12L24 27Z" }),
        React.createElement("path", { d: "M24.44 34c-8.6.2-14.24-7.18-16-10a27.399 27.399 0 0 1 4.48-5.44L10 15.74A31.78 31.78 0 0 0 4.26 23a2 2 0 0 0 0 2c1.26 2.18 8 13 19.78 13h.5A18.959 18.959 0 0 0 31 36.66l-3.16-3.16c-1.111.292-2.252.46-3.4.5ZM43.74 23c-1.28-2.22-8.34-13.36-20.28-13A18.96 18.96 0 0 0 17 11.34l3.16 3.16a15.473 15.473 0 0 1 3.4-.5c8.58-.22 14.22 7.18 16 10a27.4 27.4 0 0 1-4.58 5.44L38 32.26A31.782 31.782 0 0 0 43.82 25a2 2 0 0 0-.08-2Z" })),
    'eye-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.74 23c-1.28-2.22-8.32-13.36-20.28-13C12.4 10.28 6 20 4.26 23a2 2 0 0 0 0 2c1.26 2.18 8 13 19.78 13h.5c11.06-.28 17.48-10 19.2-13a2 2 0 0 0 0-2Zm-19.3 11c-8.62.2-14.24-7.18-16-10 2-3.22 7.22-9.8 15.22-10 8.58-.22 14.22 7.18 16 10-2.06 3.22-7.22 9.8-15.22 10Z" }),
        React.createElement("path", { d: "M24 17a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 24 27Z" })),
    'eye2-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M6.586 6.586a2 2 0 0 1 2.828 0l32 32a2 2 0 1 1-2.828 2.828l-32-32a2 2 0 0 1 0-2.828Z" }),
        React.createElement("path", { d: "M17.841 20.67a7 7 0 0 0 9.489 9.489l-3.163-3.163a3 3 0 0 1-3.162-3.162L17.84 20.67ZM39.965 24c-.423 2.025-1.696 4.438-3.724 6.584l2.829 2.829c2.634-2.76 4.397-6.05 4.903-9.084l.055-.329-.055-.329c-.59-3.544-2.896-7.435-6.3-10.424C34.232 10.227 29.526 8 24 8c-3.114 0-5.967.707-8.48 1.864l3.067 3.066c1.666-.59 3.48-.93 5.413-.93 4.414 0 8.209 1.773 11.033 4.253 2.734 2.4 4.43 5.346 4.932 7.747Z" }),
        React.createElement("path", { d: "M4.027 23.671c.506-3.035 2.27-6.324 4.903-9.084l2.83 2.83C9.73 19.561 8.457 21.974 8.034 24c.502 2.4 2.198 5.347 4.932 7.747C15.79 34.227 19.587 36 24 36c1.933 0 3.747-.34 5.413-.93l3.066 3.066A20.172 20.172 0 0 1 24 40c-5.527 0-10.232-2.227-13.672-5.247-3.405-2.99-5.71-6.88-6.3-10.424L3.971 24l.055-.329Z" })),
    'eye2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8.035 24c.502 2.4 2.198 5.347 4.932 7.747C15.79 34.227 19.587 36 24 36c4.414 0 8.209-1.773 11.033-4.253 2.734-2.4 4.43-5.346 4.932-7.747-.502-2.4-2.198-5.347-4.932-7.747C32.21 13.773 28.413 12 24 12c-4.414 0-8.209 1.773-11.033 4.253-2.734 2.4-4.43 5.346-4.932 7.747Zm2.293-10.753C13.768 10.227 18.473 8 24 8c5.527 0 10.233 2.227 13.672 5.247 3.405 2.99 5.71 6.88 6.3 10.424l.056.329-.055.329c-.59 3.544-2.896 7.435-6.3 10.424C34.232 37.773 29.526 40 24 40c-5.527 0-10.232-2.227-13.672-5.247-3.405-2.99-5.71-6.88-6.3-10.424L3.971 24l.055-.329c.59-3.544 2.896-7.435 6.3-10.424Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M24 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm0 4a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'facebook-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 7a1 1 0 0 0-1-1h-5a9.54 9.54 0 0 0-10 9v5.4h-5a1 1 0 0 0-1 1v5.2a1 1 0 0 0 1 1h5V41a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V27.6h5.24a1 1 0 0 0 .98-.74l1.44-5.2a1 1 0 0 0-.96-1.26H26V15a2 2 0 0 1 2-1.8h5a1 1 0 0 0 1-1V7Z" })),
    'facebook-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 44h-8a2 2 0 0 1-2-2V29.6h-4a2 2 0 0 1-2-2v-7.2a2 2 0 0 1 2-2h4V15A11.54 11.54 0 0 1 28 4h6a2 2 0 0 1 2 2v7.2a2 2 0 0 1-2 2h-6v3.2h6a1.998 1.998 0 0 1 1.6.78 2 2 0 0 1 .32 1.76l-2 7.2a2 2 0 0 1-2 1.46H28V42a2 2 0 0 1-2 2Zm-6-4h4V27.6a2 2 0 0 1 2-2h4.48l.88-3.2H26a2 2 0 0 1-2-2V15a4 4 0 0 1 4-3.8h4V8h-4a7.56 7.56 0 0 0-8 7v5.4a2 2 0 0 1-2 2h-4v3.2h4a2 2 0 0 1 2 2V40Z" })),
    'file-add-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 14.66-8.88-10A2 2 0 0 0 29.12 4h-16A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V16a2 2 0 0 0-.52-1.34ZM28 30h-2v2a2 2 0 0 1-4 0v-2h-2a2 2 0 0 1 0-4h2v-2a2 2 0 0 1 4 0v2h2a2 2 0 0 1 0 4Zm1.42-14A1.58 1.58 0 0 1 28 14.3V8l7.48 8h-6.06Z" })),
    'file-add-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 16.66-10.88-12A2 2 0 0 0 27.12 4h-14A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V18a2 2 0 0 0-.52-1.34ZM28 10l5.48 6h-4A1.582 1.582 0 0 1 28 14.3V10Zm6.88 30H13.12A1.06 1.06 0 0 1 12 39V9a1.06 1.06 0 0 1 1.12-1H24v6.3a5.58 5.58 0 0 0 5.42 5.7H36v19a1.06 1.06 0 0 1-1.12 1Z" }),
        React.createElement("path", { d: "M28 26h-2v-2a2 2 0 0 0-4 0v2h-2a2 2 0 0 0 0 4h2v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 0 0-4Z" })),
    'file-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 14.66-8.88-10A2 2 0 0 0 29.12 4h-16A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V16a2 2 0 0 0-.52-1.34ZM28 8l7.48 8h-6A1.58 1.58 0 0 1 28 14.3V8Z" })),
    'file-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 16.66-10.88-12A2 2 0 0 0 27.12 4h-14A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V18a2 2 0 0 0-.52-1.34ZM35.3 18h-7.88A1.58 1.58 0 0 1 26 16.3V8h.22l9.08 10Zm-.42 22H13.12A1.06 1.06 0 0 1 12 39V9a1.06 1.06 0 0 1 1.12-1H22v8.3a5.58 5.58 0 0 0 5.42 5.7H36v17a1.06 1.06 0 0 1-1.12 1Z" })),
    'file-remove-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 14.66-8.88-10A2 2 0 0 0 29.12 4h-16A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V16a2 2 0 0 0-.52-1.34ZM28 30h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4Zm1.42-14A1.58 1.58 0 0 1 28 14.3V8l7.48 8h-6.06Z" })),
    'file-remove-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 16.66-10.88-12A2 2 0 0 0 27.12 4h-14A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V18a2 2 0 0 0-.52-1.34ZM28 10l5.48 6h-4A1.582 1.582 0 0 1 28 14.3V10Zm6.88 30H13.12A1.06 1.06 0 0 1 12 39V9a1.06 1.06 0 0 1 1.12-1H24v6.3a5.58 5.58 0 0 0 5.42 5.7H36v19a1.06 1.06 0 0 1-1.12 1Z" }),
        React.createElement("path", { d: "M28 26h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4Z" })),
    'file-text-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.48 14.66-8.88-10A2 2 0 0 0 29.12 4h-16A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V16a2 2 0 0 0-.52-1.34ZM18 24h6a2 2 0 0 1 0 4h-6a2 2 0 0 1 0-4Zm12 12H18a2 2 0 0 1 0-4h12a2 2 0 0 1 0 4Zm-.58-20A1.58 1.58 0 0 1 28 14.3V8l7.48 8h-6.06Z" })),
    'file-text-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30 32H18a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4ZM18 28h6a2 2 0 0 0 0-4h-6a2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "m39.48 16.66-10.88-12A2 2 0 0 0 27.12 4h-14A5.06 5.06 0 0 0 8 9v30a5.06 5.06 0 0 0 5.12 5h21.76A5.06 5.06 0 0 0 40 39V18a2 2 0 0 0-.52-1.34ZM28 10l5.48 6h-4A1.582 1.582 0 0 1 28 14.3V10Zm6.88 30H13.12A1.06 1.06 0 0 1 12 39V9a1.06 1.06 0 0 1 1.12-1H24v6.3a5.58 5.58 0 0 0 5.42 5.7H36v19a1.06 1.06 0 0 1-1.12 1Z" })),
    'film-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.52 6H11.48A5.48 5.48 0 0 0 6 11.48v25.04A5.48 5.48 0 0 0 11.48 42h25.04A5.48 5.48 0 0 0 42 36.52V11.48A5.48 5.48 0 0 0 36.52 6ZM14 22h-4v-4h4v4Zm-4 4h4v4h-4v-4Zm28-4h-4v-4h4v4Zm-4 4h4v4h-4v-4Zm4-14.52V14h-4v-4h2.52A1.48 1.48 0 0 1 38 11.48ZM11.48 10H14v4h-4v-2.52A1.48 1.48 0 0 1 11.48 10ZM10 36.52V34h4v4h-2.52A1.48 1.48 0 0 1 10 36.52Zm28 0A1.48 1.48 0 0 1 36.52 38H34v-4h4v2.52Z" })),
    'film-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.52 6H11.48A5.48 5.48 0 0 0 6 11.48v25.04A5.48 5.48 0 0 0 11.48 42h25.04A5.48 5.48 0 0 0 42 36.52V11.48A5.48 5.48 0 0 0 36.52 6ZM14 22h-4v-4h4v4Zm-4 4h4v4h-4v-4Zm8-16h12v28H18V10Zm20 12h-4v-4h4v4Zm-4 4h4v4h-4v-4Zm4-14.52V14h-4v-4h2.52A1.48 1.48 0 0 1 38 11.48ZM11.48 10H14v4h-4v-2.52A1.48 1.48 0 0 1 11.48 10ZM10 36.52V34h4v4h-2.52A1.48 1.48 0 0 1 10 36.52Zm28 0A1.48 1.48 0 0 1 36.52 38H34v-4h4v2.52Z" })),
    'flag-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.54 9.36a3.58 3.58 0 0 0-3.2-.5 15.06 15.06 0 0 1-4.34.56 17.08 17.08 0 0 1-6.26-1.56A20.3 20.3 0 0 0 17 6c-5.78 0-8 2-8.4 2.28A2 2 0 0 0 8 9.72V40a2 2 0 0 0 4 0v-8.6c1.594-.61 3.295-.89 5-.82a17.08 17.08 0 0 1 6.26 1.56A20.3 20.3 0 0 0 31 34c2.411.091 4.81-.389 7-1.4a3.48 3.48 0 0 0 2-3.1V12.22a3.54 3.54 0 0 0-1.46-2.86Z" })),
    'flag-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.54 9.36a3.58 3.58 0 0 0-3.2-.5 15.06 15.06 0 0 1-4.34.56 17.08 17.08 0 0 1-6.26-1.56A20.3 20.3 0 0 0 17 6c-5.78 0-8 2-8.4 2.28A2 2 0 0 0 8 9.72V40a2 2 0 0 0 4 0v-8.6c1.594-.61 3.295-.89 5-.82a17.08 17.08 0 0 1 6.26 1.56A20.3 20.3 0 0 0 31 34c2.411.091 4.81-.389 7-1.4a3.48 3.48 0 0 0 2-3.1V12.22a3.54 3.54 0 0 0-1.46-2.86ZM36 29.18a12.64 12.64 0 0 1-5 .82 16.72 16.72 0 0 1-6.26-1.58A20.68 20.68 0 0 0 17 26.58a19.02 19.02 0 0 0-5 .58V10.84a12.26 12.26 0 0 1 5-.84 16.72 16.72 0 0 1 6.26 1.58A20.68 20.68 0 0 0 31 13.42a18.82 18.82 0 0 0 5-.6v16.36Z" })),
    'flash-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22.22 46a2 2 0 0 1-1.98-2.22l1.54-14.18H10a2 2 0 0 1-1.66-3.12l15.78-23.6a2 2 0 0 1 2.34-.76 2 2 0 0 1 1.3 2L26.22 18.4H38a2 2 0 0 1 1.66 3.12l-15.78 23.6a2.001 2.001 0 0 1-1.66.88Z" })),
    'flash-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m34.66 29 5-7.48A2 2 0 0 0 38 18.4H26.22l1.54-14.18a2 2 0 0 0-1.3-2 2 2 0 0 0-2.34.76l-6.24 9.24L34.66 29ZM13.34 19l-5 7.48A2 2 0 0 0 10 29.6h11.78l-1.54 14.18A2 2 0 0 0 22.22 46a2 2 0 0 0 1.66-.88l6.24-9.34L13.34 19ZM41.42 38.58l-32-32a2.008 2.008 0 1 0-2.84 2.84l32 32a2 2 0 0 0 2.84 0 1.998 1.998 0 0 0 0-2.84Z" })),
    'flash-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-32-32a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM25.08 36.12l.54-4.84L20 25.6h-6.26l2.48-3.72L13.34 19l-5 7.48A2 2 0 0 0 10 29.6h11.78l-1.54 14.18A2 2 0 0 0 22.22 46a2 2 0 0 0 1.66-.88l6.24-9.34-2.88-2.88-2.16 3.22ZM22.92 11.88l-.54 4.84L28 22.4h6.2l-2.48 3.72L34.6 29l5-7.48A2 2 0 0 0 38 18.4H26.22l1.54-14.18a2 2 0 0 0-1.3-2 2 2 0 0 0-2.34.76l-6.24 9.24 2.88 2.88 2.16-3.22Z" })),
    'flash-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22.22 46a2 2 0 0 1-1.98-2.22l1.54-14.18H10a2 2 0 0 1-1.66-3.12l15.78-23.6a2 2 0 0 1 2.34-.76 2 2 0 0 1 1.3 2L26.22 18.4H38a2 2 0 0 1 1.66 3.12l-15.78 23.6a2.001 2.001 0 0 1-1.66.88Zm-8.48-20.4H24a2 2 0 0 1 1.897 1.386 2 2 0 0 1 .083.834l-.9 8.3 9.18-13.72H24a2 2 0 0 1-2-2.22l.9-8.3-9.16 13.72Z" })),
    'flip-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12.18 38h24l-2.6 2.58a2.008 2.008 0 1 0 2.84 2.84l6-6a2 2 0 0 0 0-2.84l-6-6a2 2 0 0 0-2.84 0 2 2 0 0 0 0 2.84l2.6 2.58h-24A3.119 3.119 0 0 1 9 30.94V26a2 2 0 0 0-4 0v4.94A7.12 7.12 0 0 0 12.18 38ZM11.58 19.42a2.008 2.008 0 1 0 2.84-2.84L11.82 14h24A3.119 3.119 0 0 1 39 17.06V22a2 2 0 0 0 4 0v-4.94A7.12 7.12 0 0 0 35.82 10h-24l2.6-2.58a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0l-6 6a2 2 0 0 0 0 2.84l6 6Z" })),
    'flip-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12.18 38h24l-2.6 2.58a2.008 2.008 0 1 0 2.84 2.84l6-6a2 2 0 0 0 0-2.84l-6-6a2 2 0 0 0-2.84 0 2 2 0 0 0 0 2.84l2.6 2.58h-24A3.119 3.119 0 0 1 9 30.94V26a2 2 0 0 0-4 0v4.94A7.12 7.12 0 0 0 12.18 38ZM11.58 19.42a2.008 2.008 0 1 0 2.84-2.84L11.82 14h24A3.119 3.119 0 0 1 39 17.06V22a2 2 0 0 0 4 0v-4.94A7.12 7.12 0 0 0 35.82 10h-24l2.6-2.58a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0l-6 6a2 2 0 0 0 0 2.84l6 6Z" })),
    'flip-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 12.18v24l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l6 6a2 2 0 0 0 2.84 0l6-6a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0L14 36.18v-24A3.119 3.119 0 0 1 17.06 9H22a2 2 0 0 0 0-4h-4.94A7.12 7.12 0 0 0 10 12.18ZM28.58 11.58a2.008 2.008 0 1 0 2.84 2.84l2.58-2.6v24A3.119 3.119 0 0 1 30.94 39H26a2 2 0 0 0 0 4h4.94A7.12 7.12 0 0 0 38 35.82v-24l2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84l-6-6a2 2 0 0 0-2.84 0l-6 6Z" })),
    'flip-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 12.18v24l-2.58-2.6a2.008 2.008 0 1 0-2.84 2.84l6 6a2 2 0 0 0 2.84 0l6-6a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0L14 36.18v-24A3.119 3.119 0 0 1 17.06 9H22a2 2 0 0 0 0-4h-4.94A7.12 7.12 0 0 0 10 12.18ZM28.58 11.58a2.008 2.008 0 1 0 2.84 2.84l2.58-2.6v24A3.119 3.119 0 0 1 30.94 39H26a2 2 0 0 0 0 4h4.94A7.12 7.12 0 0 0 38 35.82v-24l2.58 2.6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84l-6-6a2 2 0 0 0-2.84 0l-6 6Z" })),
    'folder-add-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39 14.1H25l-5.26-6.36A2 2 0 0 0 18.2 7H9a4.94 4.94 0 0 0-5 4.86v24.28A4.94 4.94 0 0 0 9 41h30a4.94 4.94 0 0 0 5-4.86V18.96a4.94 4.94 0 0 0-5-4.86ZM28 30h-2v2a2 2 0 0 1-4 0v-2h-2a2 2 0 0 1 0-4h2v-2a2 2 0 0 1 4 0v2h2a2 2 0 0 1 0 4Z" })),
    'folder-add-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28 26h-2v-2a2 2 0 0 0-4 0v2h-2a2 2 0 0 0 0 4h2v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 0 0-4Z" }),
        React.createElement("path", { d: "M39 14.1H25l-5.26-6.36A2 2 0 0 0 18.2 7H9a4.94 4.94 0 0 0-5 4.86v24.28A4.94 4.94 0 0 0 9 41h30a4.94 4.94 0 0 0 5-4.86V18.96a4.94 4.94 0 0 0-5-4.86Zm1 22a.92.92 0 0 1-1 .86H9a.92.92 0 0 1-1-.86V11.86A.92.92 0 0 1 9 11h8.26l5.2 6.36a2 2 0 0 0 1.54.74h15a.92.92 0 0 1 1 .86V36.1Z" })),
    'folder-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39 41H9a4.94 4.94 0 0 1-5-4.86V11.86A4.94 4.94 0 0 1 9 7h9.2a2 2 0 0 1 1.54.74l5.2 6.36h14A4.939 4.939 0 0 1 44 18.96v17.18A4.94 4.94 0 0 1 39 41Z" })),
    'folder-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39 41H9a4.94 4.94 0 0 1-5-4.86V11.86A4.94 4.94 0 0 1 9 7h9.2a2 2 0 0 1 1.54.74l5.2 6.36h14A4.939 4.939 0 0 1 44 18.96v17.18A4.94 4.94 0 0 1 39 41ZM8 27.52v8.62A.92.92 0 0 0 9 37h30a.92.92 0 0 0 1-.86V18.96a.92.92 0 0 0-1-.86H24a1.999 1.999 0 0 1-1.54-.74L17.26 11H9a.92.92 0 0 0-1 .86v15.66Z" })),
    'folder-remove-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39 14.1H25l-5.26-6.36A2 2 0 0 0 18.2 7H9a4.94 4.94 0 0 0-5 4.86v24.28A4.94 4.94 0 0 0 9 41h30a4.94 4.94 0 0 0 5-4.86V18.96a4.94 4.94 0 0 0-5-4.86ZM28 30h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4Z" })),
    'folder-remove-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28 26h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4Z" }),
        React.createElement("path", { d: "M39 14.1H25l-5.26-6.36A2 2 0 0 0 18.2 7H9a4.94 4.94 0 0 0-5 4.86v24.28A4.94 4.94 0 0 0 9 41h30a4.94 4.94 0 0 0 5-4.86V18.96a4.94 4.94 0 0 0-5-4.86Zm1 22a.92.92 0 0 1-1 .86H9a.92.92 0 0 1-1-.86V11.86A.92.92 0 0 1 9 11h8.26l5.2 6.36a2 2 0 0 0 1.54.74h15a.92.92 0 0 1 1 .86V36.1Z" })),
    'funnel-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.8 44a2 2 0 0 1-1.2-.4l-8-6.1a2 2 0 0 1-.78-1.6v-6.54l-9.6-18.44A2 2 0 0 1 10 8h28a2 2 0 0 1 1.72.98 2 2 0 0 1 0 2l-10 18.42V42a2 2 0 0 1-1.1 1.8 2.001 2.001 0 0 1-.82.2Z" })),
    'funnel-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.8 44a2 2 0 0 1-1.2-.4l-8-6.1a2 2 0 0 1-.78-1.6v-6.54l-9.6-18.44A2 2 0 0 1 10 8h28a2 2 0 0 1 1.72.98 2 2 0 0 1 0 2l-10 18.42V42a2 2 0 0 1-1.1 1.8 2.001 2.001 0 0 1-.82.2Zm-6-9.08 4 3.06v-9.1A2 2 0 0 1 26 28l8.6-16H13.28l8.26 16a2 2 0 0 1 .22.92l.04 6Z" })),
    'gift-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9.28 30.54v9.64A1.84 1.84 0 0 0 11.12 42h11.24V30.54H9.28ZM25.64 42h11.24a1.84 1.84 0 0 0 1.84-1.82v-9.64H25.64V42ZM40.2 14.18h-3.68a5.64 5.64 0 0 0 .58-2.46A5.74 5.74 0 0 0 31.36 6 8.42 8.42 0 0 0 24 11.14 8.42 8.42 0 0 0 16.64 6a5.74 5.74 0 0 0-5.74 5.72 5.64 5.64 0 0 0 .58 2.46H7.8c-1 0-1.8 1.18-1.8 2.62v7.86c0 1.44.8 2.62 1.8 2.62h14.56v-13.1h3.28v13.1H40.2c1 0 1.8-1.18 1.8-2.62V16.8c0-1.44-.8-2.62-1.8-2.62Zm-23.56 0a2.46 2.46 0 1 1 0-4.9c2.8 0 4.38 2.88 5.16 4.9h-5.16Zm14.72 0H26.2c.78-2 2.36-4.9 5.16-4.9a2.46 2.46 0 1 1 0 4.9Z" })),
    'gift-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.4 14h-.78a6 6 0 0 0 .38-2 6.16 6.16 0 0 0-6.28-6A8.92 8.92 0 0 0 24 10.8 8.92 8.92 0 0 0 16.28 6 6.16 6.16 0 0 0 10 12a6 6 0 0 0 .38 2H9.6A4.001 4.001 0 0 0 6 18.4v7.2A4.16 4.16 0 0 0 9 30v8.74A3.5 3.5 0 0 0 12.62 42h22.76A3.5 3.5 0 0 0 39 38.66V30a4.16 4.16 0 0 0 3-4.4v-7.2a4.001 4.001 0 0 0-3.6-4.4Zm-.4 4.4v7.2a1.12 1.12 0 0 1 0 .4H26v-8h12a1.12 1.12 0 0 1 0 .4ZM31.72 10A2.16 2.16 0 0 1 34 12a2.16 2.16 0 0 1-2.28 2H26.8a5.86 5.86 0 0 1 4.92-4ZM14 12a2.16 2.16 0 0 1 2.28-2 5.86 5.86 0 0 1 4.9 4h-4.9A2.16 2.16 0 0 1 14 12Zm-4 6.4a1.12 1.12 0 0 1 0-.4h12v8H10a1.12 1.12 0 0 1 0-.4v-7.2ZM13 30h9v8h-9v-8Zm13 8v-8h9v8h-9Z" })),
    'github-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 2A21.78 21.78 0 0 0 2 23.54 21.58 21.58 0 0 0 17.04 44c1.1.2 1.5-.46 1.5-1.04V39.3c-6.12 1.3-7.42-2.88-7.42-2.88a5.72 5.72 0 0 0-2.44-3.16c-2-1.32.16-1.3.16-1.3a4.62 4.62 0 0 1 3.36 2.22 4.74 4.74 0 0 0 6.4 1.78 4.66 4.66 0 0 1 1.4-2.88c-4.88-.54-10-2.38-10-10.64a8.3 8.3 0 0 1 2.22-5.82 7.56 7.56 0 0 1 .22-5.68s1.86-.58 6 2.2a21.36 21.36 0 0 1 11 0c4.2-2.78 6-2.2 6-2.2a7.56 7.56 0 0 1 .22 5.68A8.3 8.3 0 0 1 38 22.4c0 8.28-5.16 10.1-10 10.64a5 5 0 0 1 1.5 4v5.9c0 .7.4 1.26 1.5 1.04a21.6 21.6 0 0 0 15-20.44A21.78 21.78 0 0 0 24 2Z" })),
    'github-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.48 44a2 2 0 0 1-2-2v-5.2a4.3 4.3 0 0 0-1.08-3.32 2 2 0 0 1 1.22-3.34C35.5 29.56 40 28 40 19.54a8 8 0 0 0-1.34-4.44 5.5 5.5 0 0 1-.82-4.12c.18-.931.18-1.889 0-2.82a15.3 15.3 0 0 0-4.18 2.18 2 2 0 0 1-1.68.3 20.3 20.3 0 0 0-11.04 0 2.001 2.001 0 0 1-1.68-.3 14.799 14.799 0 0 0-4.22-2.18 7.04 7.04 0 0 0 0 2.82 5.68 5.68 0 0 1-.86 4.16 8.14 8.14 0 0 0-1.34 4.46c0 7.78 3.76 9.86 9.4 10.58a2 2 0 0 1 1.64 1.32 1.999 1.999 0 0 1-.42 2 4.12 4.12 0 0 0-1.1 3.12V42a2 2 0 0 1-4 0v-1.14a12 12 0 0 1-10.54-4.18 7.8 7.8 0 0 0-2.32-1.76 2.005 2.005 0 0 1-1.101-3.144A2.005 2.005 0 0 1 6.5 31.04a9.86 9.86 0 0 1 4 2.72c2 2 4 3.76 7.8 3.04a7.78 7.78 0 0 1 .46-3.16c-4.12-1.04-10-4-10-14a12 12 0 0 1 2-6.66 1.7 1.7 0 0 0 .26-1.24 11.38 11.38 0 0 1 .66-6.42 2.001 2.001 0 0 1 1.26-1.14c.68-.2 3.12-.6 7.74 2.4 3.74-.9 7.64-.9 11.38 0 4.62-3 7.06-2.62 7.72-2.4a2 2 0 0 1 1.26 1.14 11.42 11.42 0 0 1 .66 6.44 1.502 1.502 0 0 0 .22 1.14 12 12 0 0 1 2 6.68c0 10.14-5.84 13.08-10 14a8.56 8.56 0 0 1 .44 3.34V42a2 2 0 0 1-1.88 2Z" })),
    'globe-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 4a16.38 16.38 0 0 1 3.58.42 5.22 5.22 0 0 1-1.56 2c-.44.34-.92.62-1.4.92a9.121 9.121 0 0 0-3.7 3.34 12.98 12.98 0 0 0-1.24 6.6c0 2.72 0 4.32-1.9 5.74-2.74 2.14-6.92.94-9.52-.14A16 16 0 0 1 24 8Zm9.78 28.64a13.584 13.584 0 0 0-1.26-2.28c-.22-.32-.44-.64-.64-.98-.78-1.36-.5-2 .76-4l.2-.34A9.54 9.54 0 0 0 34 24.18c-.002-.67.058-1.34.18-2 .32-1.46 3.42-1.86 5.34-2a15.88 15.88 0 0 1-5.72 16.56l-.02-.1Z" })),
    'globe-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 4a16.38 16.38 0 0 1 3.58.42 5.22 5.22 0 0 1-1.56 2c-.44.34-.92.62-1.4.92a9.121 9.121 0 0 0-3.7 3.34 12.98 12.98 0 0 0-1.24 6.6c0 2.72 0 4.32-1.9 5.74-2.74 2.14-6.92.94-9.52-.14A16 16 0 0 1 24 8ZM10 31.6c1.307.34 2.65.521 4 .54a10 10 0 0 0 6.28-2c3.42-2.68 3.42-6.12 3.42-8.88a9.52 9.52 0 0 1 .74-4.68 5.72 5.72 0 0 1 2.24-1.82c.636-.372 1.25-.779 1.84-1.22a9.1 9.1 0 0 0 2.8-3.74A16 16 0 0 1 38 16.24c-2.86.4-6.92 1.34-7.72 5.06-.18.889-.274 1.793-.28 2.7a5.86 5.86 0 0 1-.58 2.94l-.2.34c-1.3 2.16-2.76 4.62-.78 8 .24.42.5.82.76 1.22a4.58 4.58 0 0 1 1.04 2.16A15.78 15.78 0 0 1 24 40a16 16 0 0 1-14-8.4Zm23.86 5.04a13.584 13.584 0 0 0-1.26-2.28c-.22-.32-.44-.64-.64-.98-.78-1.36-.5-2 .76-4l.2-.34A9.54 9.54 0 0 0 34 24.18c-.002-.67.058-1.34.18-2 .32-1.46 3.42-1.86 5.34-2a15.88 15.88 0 0 1-5.72 16.56l.06-.1Z" })),
    'globe-3-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40ZM10 31.6c1.307.34 2.65.521 4 .54a10 10 0 0 0 6.28-2c3.42-2.68 3.42-6.12 3.42-8.88a9.52 9.52 0 0 1 .74-4.68 5.72 5.72 0 0 1 2.24-1.82c.636-.372 1.25-.779 1.84-1.22a9.1 9.1 0 0 0 2.8-3.74A16 16 0 0 1 38 16.24c-2.86.4-6.92 1.34-7.72 5.06-.18.889-.274 1.793-.28 2.7a5.86 5.86 0 0 1-.58 2.94l-.2.34c-1.3 2.16-2.76 4.62-.78 8 .24.42.5.82.76 1.22a4.58 4.58 0 0 1 1.04 2.16A15.78 15.78 0 0 1 24 40a16 16 0 0 1-14-8.4Z" })),
    'globe-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 24a20 20 0 1 0-40 0 20 20 0 0 0 40 0Zm-4.14-2H34a25.82 25.82 0 0 0-4.66-13.08A16 16 0 0 1 39.86 22Zm-21.7 4H30a22.88 22.88 0 0 1-6 13.22A22 22 0 0 1 18.16 26Zm0-4A22.8 22.8 0 0 1 24 8.8 22.38 22.38 0 0 1 30 22H18.16Zm.72-13.14A26.36 26.36 0 0 0 14.14 22h-6A16 16 0 0 1 18.88 8.86ZM8.14 26h6a25.72 25.72 0 0 0 4.7 13.12A16 16 0 0 1 8.14 26Zm21.1 13.1A26.28 26.28 0 0 0 34 26h5.9a16 16 0 0 1-10.66 13.1Z" })),
    'globe-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 24a20 20 0 1 0-40 0 20 20 0 0 0 40 0Zm-4.14-2H34a25.82 25.82 0 0 0-4.66-13.08A16 16 0 0 1 39.86 22Zm-21.7 4H30a22.88 22.88 0 0 1-6 13.22A22 22 0 0 1 18.16 26Zm0-4A22.8 22.8 0 0 1 24 8.8 22.38 22.38 0 0 1 30 22H18.16Zm.72-13.14A26.36 26.36 0 0 0 14.14 22h-6A16 16 0 0 1 18.88 8.86ZM8.14 26h6a25.72 25.72 0 0 0 4.7 13.12A16 16 0 0 1 8.14 26Zm21.1 13.1A26.28 26.28 0 0 0 34 26h5.9a16 16 0 0 1-10.66 13.1Z" })),
    'google-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35 28a11.02 11.02 0 0 1-9 7.86 12.3 12.3 0 0 1-14-10.9A12 12 0 0 1 24 12c1.555.002 3.096.3 4.54.88a1 1 0 0 0 1.28-.42l2.88-5.3a1.04 1.04 0 0 0-.46-1.4A20 20 0 0 0 4 24.58 20.24 20.24 0 0 0 23.14 44 20 20 0 0 0 44 25.04v-4a1.02 1.02 0 0 0-1-1H25a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h10" })),
    'google-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44h-.86A20.32 20.32 0 0 1 4 24.58 20 20 0 0 1 32.24 5.76a2.96 2.96 0 0 1 1.54 1.72 2.94 2.94 0 0 1-.2 2.32L31 14.56a2.88 2.88 0 0 1-3.66 1.28 9 9 0 0 0-9.8 2.16 8.82 8.82 0 0 0-2.32 6.68 8.72 8.72 0 0 0 3.32 6 9.04 9.04 0 0 0 6.9 2 7.78 7.78 0 0 0 5.26-3.14h-5.8a2.9 2.9 0 0 1-2.9-2.88V21.3a2.9 2.9 0 0 1 2.9-2.9h16.2a2.92 2.92 0 0 1 2.9 2.88v3.76A20 20 0 0 1 24 44Zm0-36A16 16 0 0 0 8 24.48 16.24 16.24 0 0 0 23.3 40 16 16 0 0 0 40 24.84V22.4H26v3.16h10.62l-.82 2.6a12 12 0 0 1-9.8 8.5A13.159 13.159 0 0 1 16 34a12.66 12.66 0 0 1-1.44-18.6A13.04 13.04 0 0 1 28 11.82l1.54-2.86A15.8 15.8 0 0 0 24 8Z" })),
    'grid-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 6h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4ZM38 6h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4ZM18 26h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4ZM38 26h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Z" })),
    'grid-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 6h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8ZM38 6h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8ZM18 26h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8ZM38 26h-8a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h8a4 4 0 0 0 4-4v-8a4 4 0 0 0-4-4Zm-8 12v-8h8v8h-8Z" })),
    'hard-drive-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.58 22.68 34.9 9.32A6 6 0 0 0 29.52 6H18.48a6 6 0 0 0-5.38 3.32L6.42 22.68a4 4 0 0 0-.42 1.8V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24.48a4 4 0 0 0-.42-1.8ZM16 34a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm16 0h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4ZM11.24 22l5.44-10.9a1.999 1.999 0 0 1 1.8-1.1h11.04a2 2 0 0 1 1.8 1.1L36.76 22H11.24Z" })),
    'hard-drive-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.58 22.68 34.9 9.32A6 6 0 0 0 29.52 6H18.48a6 6 0 0 0-5.38 3.32L6.42 22.68a4 4 0 0 0-.42 1.8V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24.48a4 4 0 0 0-.42-1.8ZM16.68 11.1a2 2 0 0 1 1.8-1.1h11.04a2 2 0 0 1 1.8 1.1L36.76 22H11.24l5.44-10.9ZM36 38H12a2 2 0 0 1-2-2V26h28v10a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { d: "M32 30h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4ZM16 34a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'hash-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 28h-8.6l1.46-8H40a2 2 0 0 0 0-4h-6.42l1.38-7.62A2 2 0 0 0 33.28 6a2 2 0 0 0-2.44 1.64L29.34 16h-7.76l1.38-7.62A2 2 0 0 0 21.28 6a2 2 0 0 0-2.44 1.64L17.34 16H8a2 2 0 1 0 0 4h8.6l-1.46 8H8a2 2 0 1 0 0 4h6.42l-1.38 7.62A2 2 0 0 0 14.72 42a2 2 0 0 0 2.44-1.64l1.5-8.36h7.76l-1.38 7.62A2 2 0 0 0 26.72 42a2 2 0 0 0 2.44-1.64l1.5-8.36H40a2 2 0 0 0 0-4Zm-20.6 0 1.46-8h7.74l-1.46 8H19.4Z" })),
    'hash-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 28h-8.6l1.46-8H40a2 2 0 0 0 0-4h-6.42l1.38-7.62A2 2 0 0 0 33.28 6a2 2 0 0 0-2.44 1.64L29.34 16h-7.76l1.38-7.62A2 2 0 0 0 21.28 6a2 2 0 0 0-2.44 1.64L17.34 16H8a2 2 0 1 0 0 4h8.6l-1.46 8H8a2 2 0 1 0 0 4h6.42l-1.38 7.62A2 2 0 0 0 14.72 42a2 2 0 0 0 2.44-1.64l1.5-8.36h7.76l-1.38 7.62A2 2 0 0 0 26.72 42a2 2 0 0 0 2.44-1.64l1.5-8.36H40a2 2 0 0 0 0-4Zm-20.6 0 1.46-8h7.74l-1.46 8H19.4Z" })),
    'headphones-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4A20.4 20.4 0 0 0 4 24.74V34a8 8 0 1 0 8-8 7.82 7.82 0 0 0-4 1.12v-2.38A16.4 16.4 0 0 1 24 8a16.4 16.4 0 0 1 16 16.74v2.38A7.82 7.82 0 0 0 36 26a8 8 0 1 0 8 8v-9.26A20.4 20.4 0 0 0 24 4Z" })),
    'headphones-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4A20.4 20.4 0 0 0 4 24.74V34a8 8 0 1 0 8-8 7.82 7.82 0 0 0-4 1.12v-2.38A16.4 16.4 0 0 1 24 8a16.4 16.4 0 0 1 16 16.74v2.38A7.82 7.82 0 0 0 36 26a8 8 0 1 0 8 8v-9.26A20.4 20.4 0 0 0 24 4ZM12 30a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm24 8a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" })),
    'heart-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 42a2 2 0 0 1-1.42-.58L7.04 25.86a10.52 10.52 0 0 1 0-14.8 10.48 10.48 0 0 1 14.8 0L24 13.22l2.16-2.16a10.48 10.48 0 0 1 14.8 0 10.52 10.52 0 0 1 0 14.8L25.42 41.42A1.999 1.999 0 0 1 24 42Z" })),
    'heart-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 42a2.001 2.001 0 0 1-1.42-.58L7.04 25.86a10.52 10.52 0 0 1 0-14.8 10.48 10.48 0 0 1 14.8 0L24 13.22l2.16-2.16a10.48 10.48 0 0 1 14.8 0 10.52 10.52 0 0 1 0 14.8L25.42 41.42A2 2 0 0 1 24 42Zm-9.56-30a6.4 6.4 0 0 0-4.56 1.88 6.48 6.48 0 0 0 0 9.14L24 37.16l14.12-14.14a6.48 6.48 0 0 0 0-9.14 6.64 6.64 0 0 0-9.12 0l-3.58 3.6a2.002 2.002 0 0 1-2.84 0L19 13.88A6.402 6.402 0 0 0 14.44 12Z" })),
    'home-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28 28h-8v14h8V28Z" }),
        React.createElement("path", { d: "M40.84 20.36 25.42 4.6a2 2 0 0 0-2.84 0L7.16 20.38A4 4 0 0 0 6 23.24V40a4 4 0 0 0 3.78 4H16V26a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v18h6.22A4 4 0 0 0 42 40V23.24a4.14 4.14 0 0 0-1.16-2.88Z" })),
    'home-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.84 20.36 25.42 4.6a2 2 0 0 0-2.84 0L7.16 20.38A4.001 4.001 0 0 0 6 23.24V40a4 4 0 0 0 3.78 4h28.44A4 4 0 0 0 42 40V23.24a4.14 4.14 0 0 0-1.16-2.88ZM20 40V28h8v12h-8Zm18 0h-6V26a2 2 0 0 0-2-2H18a2 2 0 0 0-2 2v14h-6V23.16l14-14.3 14 14.38V40Z" })),
    'image-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-20 8a3 3 0 1 1 0 5.999A3 3 0 0 1 16 14Zm22 21.66A2.18 2.18 0 0 1 36 38H12l15.14-13.64a1.38 1.38 0 0 1 1.86 0l9 8.96v2.34Z" })),
    'image-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-24 4h24a2 2 0 0 1 2 2v16.72l-6.4-5.46a5.54 5.54 0 0 0-7.04 0L10 35.4V12a2 2 0 0 1 2-2Z" }),
        React.createElement("path", { d: "M16 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'image-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-24 4h24a2 2 0 0 1 2 2v16.72l-6.4-5.46a5.54 5.54 0 0 0-7.04 0L10 35.4V12a2 2 0 0 1 2-2Zm24 28H13.12l14-11.68a1.56 1.56 0 0 1 1.86 0L38 34v2a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { d: "M16 20a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'inbox-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.58 22.68 34.9 9.32A6 6 0 0 0 29.52 6H18.48a6 6 0 0 0-5.38 3.32L6.42 22.68a4 4 0 0 0-.42 1.8V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24.48a4 4 0 0 0-.42-1.8ZM16.68 11.1a1.999 1.999 0 0 1 1.8-1.1h11.04a2 2 0 0 1 1.8 1.1L36.76 22H32a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-8a2 2 0 0 1-2-2v-4a2 2 0 0 0-2-2h-4.76l5.44-10.9Z" })),
    'inbox-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.58 22.68 34.9 9.32A6 6 0 0 0 29.52 6H18.48a6 6 0 0 0-5.38 3.32L6.42 22.68a4 4 0 0 0-.42 1.8V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V24.48a4 4 0 0 0-.42-1.8ZM16.68 11.1a2 2 0 0 1 1.8-1.1h11.04a2 2 0 0 1 1.8 1.1L36.76 22H32a2 2 0 0 0-2 2v6H18v-6a2 2 0 0 0-2-2h-4.76l5.44-10.9ZM36 38H12a2 2 0 0 1-2-2V26h4v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6h4v10a2 2 0 0 1-2 2Z" })),
    'info-Price': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M16 20a6 6 0 0 1 6-6h8a2 2 0 1 1 0 4h-8a2 2 0 1 0 0 4h4a6 6 0 0 1 0 12h-8a2 2 0 1 1 0-4h8a2 2 0 1 0 0-4h-4a6 6 0 0 1-6-6Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M24 10a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0v-4a2 2 0 0 1 2-2Zm0 20a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0v-4a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'info-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm2 28a2 2 0 0 1-4 0V22a2 2 0 0 1 4 0v10Zm-2-14a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'info-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 20a2 2 0 0 0-2 2v10a2 2 0 0 0 4 0V22a2 2 0 0 0-2-2Z" })),
    'info-price': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M16 20a6 6 0 0 1 6-6h8a2 2 0 1 1 0 4h-8a2 2 0 1 0 0 4h4a6 6 0 0 1 0 12h-8a2 2 0 1 1 0-4h8a2 2 0 1 0 0-4h-4a6 6 0 0 1-6-6Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M24 10a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0v-4a2 2 0 0 1 2-2Zm0 20a2 2 0 0 1 2 2v4a2 2 0 1 1-4 0v-4a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'keypad-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM24 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM38 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM10 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM24 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM38 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM10 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM24 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM38 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12Z" })),
    'keypad-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM24 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM38 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0-8a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM10 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM24 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM38 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM10 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM24 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM38 32a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'layers-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m6.48 14.58 17.04 9.26a1.02 1.02 0 0 0 .96 0l17.04-9.26a.88.88 0 0 0-.1-1.62L24.38 6a1 1 0 0 0-.76 0L6.58 12.96a.88.88 0 0 0-.1 1.62Z" }),
        React.createElement("path", { d: "m41.42 21.32-3.66-1.56-13.28 7.22a1.02 1.02 0 0 1-.96 0l-13.28-7.22-3.66 1.56a.96.96 0 0 0 0 1.7l17.04 9.8a.92.92 0 0 0 .96 0l17.04-9.8a.96.96 0 0 0-.2-1.7Z" }),
        React.createElement("path", { d: "m41.42 30.2-3.12-1.36-13.82 7.52a1.02 1.02 0 0 1-.96 0L9.7 28.84 6.58 30.2a.98.98 0 0 0 0 1.74l17.04 10a1.02 1.02 0 0 0 .96 0l17.04-10a.98.98 0 0 0-.2-1.74Z" })),
    'layers-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 22.7a1.999 1.999 0 0 0-1.22-1.72l-4.3-1.84 4.52-2.6a2 2 0 0 0 1-1.84 1.999 1.999 0 0 0-1.22-1.72l-16-6.82a2 2 0 0 0-1.56 0l-16 6.82A2 2 0 0 0 6 14.7a2 2 0 0 0 1 1.84l4.52 2.6-4.3 1.84A2 2 0 0 0 6 22.7a2 2 0 0 0 1 1.84l4.52 2.6-4.3 1.84A2 2 0 0 0 6 30.7a2 2 0 0 0 1 1.84l16 9.2a2 2 0 0 0 2 0l16-9.2a2 2 0 0 0 1-1.84 1.999 1.999 0 0 0-1.22-1.72l-4.3-1.84 4.52-2.6a2 2 0 0 0 1-1.84ZM24 10.18l11.52 4.9L24 21.7l-11.52-6.62L24 10.18Zm-1 15.56a2 2 0 0 0 2 0l7.14-4 3.38 1.44L24 29.7l-11.52-6.62 3.38-1.44 7.14 4.1Zm12.52 5.34L24 37.7l-11.52-6.62 3.38-1.44 7.14 4.1a2 2 0 0 0 2 0l7.14-4.1 3.38 1.44Z" })),
    'layout-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 16v-4a6 6 0 0 0-6-6H12a6 6 0 0 0-6 6v4h36ZM6 20v16a6 6 0 0 0 6 6h10V20H6ZM26 20v22h10a6 6 0 0 0 6-6V20H26Z" })),
    'layout-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6ZM10 36V11a1 1 0 0 1 1-1h5v28h-4a2 2 0 0 1-2-2Zm26 2H20V10h17a1 1 0 0 1 1 1v25a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { opacity: ".2", d: "M22 12H36V36H22z" })),
    'layout-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-24 4h24a2 2 0 0 1 2 2v4H10v-4a2 2 0 0 1 2-2Zm-2 26V20h12v18H12a2 2 0 0 1-2-2Zm26 2H26V20h12v16a2 2 0 0 1-2 2Z" })),
    'layout-top-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-24 4h24a2 2 0 0 1 2 2v4H10v-4a2 2 0 0 1 2-2Zm-2 26V20h28v17a1 1 0 0 1-1 1H12a2 2 0 0 1-2-2Z" }),
        React.createElement("path", { opacity: ".2", d: "M12 22H36V36H12z" })),
    'layout-topleft-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-24 4h24a2 2 0 0 1 2 2v4H10v-4a2 2 0 0 1 2-2Zm-2 26V20h6v18h-4a2 2 0 0 1-2-2Zm26 2H20V20h18v16a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { opacity: ".2", d: "M22 22H36V36H22z" })),
    'link-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m26.58 18.58-8 8a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l8-8a2.008 2.008 0 1 0-2.84-2.84Z" }),
        React.createElement("path", { d: "M24.56 34.8 22 37.34a8.4 8.4 0 0 1-11.16.8 8 8 0 0 1-.54-11.86l2.84-2.86a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0l-2.54 2.56a12.3 12.3 0 0 0-1.34 16.14 12.12 12.12 0 0 0 18.14 1.2l2.84-2.84a2.008 2.008 0 1 0-2.84-2.84ZM39.32 6.44A12.36 12.36 0 0 0 23.06 7.8L20.9 10a2.181 2.181 0 0 0-.34 3.22 1.999 1.999 0 0 0 2.84 0L26 10.6a8.34 8.34 0 0 1 11.14-.8 8 8 0 0 1 .54 11.9l-2.84 2.86a2 2 0 0 0 0 2.84 1.998 1.998 0 0 0 2.84 0l2.84-2.84a12.12 12.12 0 0 0-1.2-18.12Z" })),
    'link-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m26.58 18.58-8 8a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l8-8a2.008 2.008 0 1 0-2.84-2.84Z" }),
        React.createElement("path", { d: "M24.56 34.8 22 37.34a8.4 8.4 0 0 1-11.16.8 8 8 0 0 1-.54-11.86l2.84-2.86a2 2 0 0 0 0-2.84 2 2 0 0 0-2.84 0l-2.54 2.56a12.3 12.3 0 0 0-1.34 16.14 12.12 12.12 0 0 0 18.14 1.2l2.84-2.84a2.008 2.008 0 1 0-2.84-2.84ZM39.32 6.44A12.36 12.36 0 0 0 23.06 7.8L20.9 10a2.181 2.181 0 0 0-.34 3.22 1.999 1.999 0 0 0 2.84 0L26 10.6a8.34 8.34 0 0 1 11.14-.8 8 8 0 0 1 .54 11.9l-2.84 2.86a2 2 0 0 0 0 2.84 1.999 1.999 0 0 0 2.84 0l2.84-2.84a12.12 12.12 0 0 0-1.2-18.12Z" })),
    'link-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 24a2 2 0 0 0 2 2h12a2 2 0 0 0 0-4H18a2 2 0 0 0-2 2Z" }),
        React.createElement("path", { d: "M18 32h-3.58A8.26 8.26 0 0 1 6 24.74 8 8 0 0 1 14 16h4a2 2 0 0 0 0-4h-3.58A12.3 12.3 0 0 0 2.1 22.42 12 12 0 0 0 14 36h4a2 2 0 0 0 0-4ZM46 22.48A12.32 12.32 0 0 0 33.52 12H30.5c-1.62 0-2.5.9-2.5 2a2 2 0 0 0 2 2h3.58A8.26 8.26 0 0 1 42 23.26 8 8 0 0 1 34 32h-4a2 2 0 0 0 0 4h4a12 12 0 0 0 12-13.52Z" })),
    'link-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 24a2 2 0 0 0 2 2h12a2 2 0 0 0 0-4H18a2 2 0 0 0-2 2Z" }),
        React.createElement("path", { d: "M18 32h-3.58A8.26 8.26 0 0 1 6 24.74 8 8 0 0 1 14 16h4a2 2 0 0 0 0-4h-3.58A12.3 12.3 0 0 0 2.1 22.42 12 12 0 0 0 14 36h4a2 2 0 0 0 0-4ZM46 22.48A12.32 12.32 0 0 0 33.52 12H30.5c-1.62 0-2.5.9-2.5 2a2 2 0 0 0 2 2h3.58A8.26 8.26 0 0 1 42 23.26 8 8 0 0 1 34 32h-4a2 2 0 0 0 0 4h4a12 12 0 0 0 12-13.52Z" })),
    'linkedin-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30.3 16.8a11.66 11.66 0 0 0-11.7 11.64V40.2a1.8 1.8 0 0 0 1.8 1.8h4.2a1.8 1.8 0 0 0 1.8-1.8V28.44a3.879 3.879 0 0 1 4.3-3.86 4 4 0 0 1 3.5 4V40.2A1.8 1.8 0 0 0 36 42h4.2a1.8 1.8 0 0 0 1.8-1.8V28.44A11.66 11.66 0 0 0 30.3 16.8ZM13.2 18.6H7.8A1.8 1.8 0 0 0 6 20.4v19.8A1.8 1.8 0 0 0 7.8 42h5.4a1.8 1.8 0 0 0 1.8-1.8V20.4a1.8 1.8 0 0 0-1.8-1.8ZM10.5 15a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Z" })),
    'linkedin-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 44h-3.34a4 4 0 0 1-4-4V29.26a1.84 1.84 0 0 0-1.38-1.86 1.68 1.68 0 0 0-1.784.964 1.698 1.698 0 0 0-.156.716V40a4 4 0 0 1-4 4H22a4 4 0 0 1-4-4V29.08a13 13 0 1 1 26 0V40a4 4 0 0 1-4 4Zm-9-20.62c.313-.02.627-.02.94 0a5.82 5.82 0 0 1 4.72 5.8V40H40V29.08a9 9 0 0 0-18 0V40h3.34V29.08a5.7 5.7 0 0 1 5.66-5.7ZM12 44H8a4 4 0 0 1-4-4V20a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v20a4 4 0 0 1-4 4ZM8 20v20h4V20H8ZM10 14a6 6 0 1 1 0-12 6 6 0 0 1 0 12Zm0-8a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" })),
    'list-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 36a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM40.12 22H15.88A1.88 1.88 0 0 0 14 23.88v.24A1.88 1.88 0 0 0 15.88 26h24.24A1.88 1.88 0 0 0 42 24.12v-.24A1.88 1.88 0 0 0 40.12 22ZM40.12 32H15.88A1.88 1.88 0 0 0 14 33.88v.24A1.88 1.88 0 0 0 15.88 36h24.24A1.88 1.88 0 0 0 42 34.12v-.24A1.88 1.88 0 0 0 40.12 32ZM40.12 12H15.88A1.88 1.88 0 0 0 14 13.88v.24A1.88 1.88 0 0 0 15.88 16h24.24A1.88 1.88 0 0 0 42 14.12v-.24A1.88 1.88 0 0 0 40.12 12Z" })),
    'list-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM8 36a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM40.12 22H15.88A1.88 1.88 0 0 0 14 23.88v.24A1.88 1.88 0 0 0 15.88 26h24.24A1.88 1.88 0 0 0 42 24.12v-.24A1.88 1.88 0 0 0 40.12 22ZM40.12 32H15.88A1.88 1.88 0 0 0 14 33.88v.24A1.88 1.88 0 0 0 15.88 36h24.24A1.88 1.88 0 0 0 42 34.12v-.24A1.88 1.88 0 0 0 40.12 32ZM40.12 12H15.88A1.88 1.88 0 0 0 14 13.88v.24A1.88 1.88 0 0 0 15.88 16h24.24A1.88 1.88 0 0 0 42 14.12v-.24A1.88 1.88 0 0 0 40.12 12Z" })),
    'loader-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a2 2 0 0 0-2 2v4a2 2 0 0 0 4 0V6a2 2 0 0 0-2-2ZM42 22h-4a2 2 0 0 0 0 4h4a2 2 0 0 0 0-4ZM12 24a2 2 0 0 0-2-2H6a2 2 0 1 0 0 4h4a2 2 0 0 0 2-2ZM12.44 10a2.023 2.023 0 0 0-2.78 2.94l2.88 2.78a2 2 0 0 0 1.46.56 2 2 0 0 0 1.44-.62 2 2 0 0 0 0-2.82l-3-2.84ZM34 16.28a2 2 0 0 0 1.38-.56l2.88-2.78a2 2 0 0 0-2.7-2.94l-2.88 2.84a2 2 0 0 0 0 2.82 2 2 0 0 0 1.32.62ZM24 36a2 2 0 0 0-2 2v4a2 2 0 0 0 4 0v-4a2 2 0 0 0-2-2ZM35.46 32.28a2.001 2.001 0 1 0-2.78 2.88L35.56 38a2 2 0 0 0 2.16.406 2 2 0 0 0 .66-.446 2 2 0 0 0 0-2.84l-2.92-2.84ZM12.54 32.28l-2.88 2.78a2 2 0 0 0 0 2.84 2 2 0 0 0 1.44.6 2 2 0 0 0 1.34-.5l2.88-2.78a2.002 2.002 0 0 0-2.78-2.88v-.06Z" })),
    'lock-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 32a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M34 16h-2v-3.78a8 8 0 0 0-16 0V16h-2a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V22a6 6 0 0 0-6-6Zm-14-3.78A4.12 4.12 0 0 1 24 8a4.12 4.12 0 0 1 4 4.22V16h-8v-3.78ZM24 36a6 6 0 1 1 0-12 6 6 0 0 1 0 12Z" })),
    'lock-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 16h-2v-3.78a8 8 0 0 0-16 0V16h-2a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V22a6 6 0 0 0-6-6Zm-14-3.78A4.12 4.12 0 0 1 24 8a4.12 4.12 0 0 1 4 4.22V16h-8v-3.78ZM36 38a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V22a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2v16Z" }),
        React.createElement("path", { d: "M24 24a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'log-in-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 8h-4a2 2 0 0 0 0 4h2v24h-2a2 2 0 0 0 0 4h4a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2ZM23.6 14.8a2 2 0 0 0-3.2 2.4L24 22H8a2 2 0 1 0 0 4h16.18l-3.44 4.88a2 2 0 0 0 .48 2.8 2 2 0 0 0 1.16.36A2 2 0 0 0 24 33.2l5.64-8a2 2 0 0 0 0-2.36L23.6 14.8Z" })),
    'log-in-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 8h-4a2 2 0 0 0 0 4h2v24h-2a2 2 0 0 0 0 4h4a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2ZM23.6 14.8a2 2 0 0 0-3.2 2.4L24 22H8a2 2 0 1 0 0 4h16.18l-3.44 4.88a2 2 0 0 0 .48 2.8 2 2 0 0 0 1.16.36A2 2 0 0 0 24 33.2l5.64-8a2 2 0 0 0 0-2.36L23.6 14.8Z" })),
    'log-out-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 12a2 2 0 0 0 0-4h-4a2 2 0 0 0-2 2v28a2 2 0 0 0 2 2h4a2 2 0 0 0 0-4h-2V12h2ZM41.64 22.84l-5.64-8a2 2 0 1 0-3.26 2.32L36.18 22H20a2 2 0 0 0 0 4h16l-3.6 4.8a2 2 0 0 0 3.2 2.4l6-8a2 2 0 0 0 .04-2.36Z" })),
    'log-out-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 12a2 2 0 0 0 0-4h-4a2 2 0 0 0-2 2v28a2 2 0 0 0 2 2h4a2 2 0 0 0 0-4h-2V12h2ZM41.64 22.84l-5.64-8a2 2 0 1 0-3.26 2.32L36.18 22H20a2 2 0 0 0 0 4h16l-3.6 4.8a2 2 0 0 0 3.2 2.4l6-8a2 2 0 0 0 .04-2.36Z" })),
    'map-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.82 11.78-8-3.6h-1.64L24 11.4l-7.18-3.22h-.1L16.48 8h-1.2l-8 3.6a2 2 0 0 0-1.28 2V38a2 2 0 0 0 .92 1.68A2 2 0 0 0 8 40a2 2 0 0 0 .82-.18L16 36.6l7.18 3.22h.1a1.7 1.7 0 0 0 1.44 0h.1L32 36.6l7.18 3.22A2 2 0 0 0 40 40a2 2 0 0 0 1.08-.32A2 2 0 0 0 42 38V13.6a2.001 2.001 0 0 0-1.18-1.82ZM18 13.1l4 1.78v20l-4-1.78v-20Zm20 21.8-4-1.78v-20l4 1.78v20Z" })),
    'map-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.82 11.78-8-3.6h-1.64L24 11.4l-7.18-3.22h-.1L16.48 8h-1.2l-8 3.6a2 2 0 0 0-1.28 2V38a2 2 0 0 0 .92 1.68A2 2 0 0 0 8 40a2 2 0 0 0 .82-.18L16 36.6l7.18 3.22h.1a1.7 1.7 0 0 0 1.44 0h.1L32 36.6l7.18 3.22A2 2 0 0 0 40 40a2 2 0 0 0 1.08-.32A2 2 0 0 0 42 38V13.6a2.001 2.001 0 0 0-1.18-1.82ZM10 14.88l4-1.78v20l-4 1.78v-20Zm8-1.78 4 1.78v20l-4-1.78v-20Zm8 1.78 4-1.78v20l-4 1.78v-20Zm12 20-4-1.78v-20l4 1.78v20Z" })),
    'marketing': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h12v4h-8a2 2 0 0 0 0 4h20a2 2 0 0 0 0-4h-8v-4h12a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 22a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v16Z" }),
        React.createElement("path", { d: "M31 12a2 2 0 0 1 2 2v12a2 2 0 1 1-4 0V14a2 2 0 0 1 2-2Zm-14 4a2 2 0 0 1 2 2v8a2 2 0 1 1-4 0v-8a2 2 0 0 1 2-2Zm7 2a2 2 0 0 1 2 2v6a2 2 0 1 1-4 0v-6a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'maximize-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.999 1.999 0 0 0 2.84 0 2 2 0 0 0 0-2.84ZM26 24h-2v2a2 2 0 0 1-4 0v-2h-2a2 2 0 0 1 0-4h2v-2a2 2 0 0 1 4 0v2h2a2 2 0 0 1 0 4Z" })),
    'maximize-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM10 22a12 12 0 1 1 24 0 12 12 0 0 1-24 0Z" }),
        React.createElement("path", { d: "M26 20h-2v-2a2 2 0 0 0-4 0v2h-2a2 2 0 0 0 0 4h2v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 0 0-4Z" })),
    'menu-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM40.12 22H15.88A1.88 1.88 0 0 0 14 23.88v.24A1.88 1.88 0 0 0 15.88 26h24.24A1.88 1.88 0 0 0 42 24.12v-.24A1.88 1.88 0 0 0 40.12 22ZM40.12 32H7.88A1.88 1.88 0 0 0 6 33.88v.24A1.88 1.88 0 0 0 7.88 36h32.24A1.88 1.88 0 0 0 42 34.12v-.24A1.88 1.88 0 0 0 40.12 32ZM40.12 12H7.88A1.88 1.88 0 0 0 6 13.88v.24A1.88 1.88 0 0 0 7.88 16h32.24A1.88 1.88 0 0 0 42 14.12v-.24A1.88 1.88 0 0 0 40.12 12Z" })),
    'menu-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM40.12 22H15.88A1.88 1.88 0 0 0 14 23.88v.24A1.88 1.88 0 0 0 15.88 26h24.24A1.88 1.88 0 0 0 42 24.12v-.24A1.88 1.88 0 0 0 40.12 22ZM40.12 32H7.88A1.88 1.88 0 0 0 6 33.88v.24A1.88 1.88 0 0 0 7.88 36h32.24A1.88 1.88 0 0 0 42 34.12v-.24A1.88 1.88 0 0 0 40.12 32ZM40.12 12H7.88A1.88 1.88 0 0 0 6 13.88v.24A1.88 1.88 0 0 0 7.88 16h32.24A1.88 1.88 0 0 0 42 14.12v-.24A1.88 1.88 0 0 0 40.12 12Z" })),
    'menu-3-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 36a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM32 36a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM24 36a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM7.88 32H16a1.88 1.88 0 0 1 1.88 1.88v.24A1.88 1.88 0 0 1 16 36H7.88A1.88 1.88 0 0 1 6 34.12v-.24A1.88 1.88 0 0 1 7.88 32ZM40.12 22H7.88A1.88 1.88 0 0 0 6 23.88v.24A1.88 1.88 0 0 0 7.88 26h32.24A1.88 1.88 0 0 0 42 24.12v-.24A1.88 1.88 0 0 0 40.12 22ZM40.12 12H7.88A1.88 1.88 0 0 0 6 13.88v.24A1.88 1.88 0 0 0 7.88 16h32.24A1.88 1.88 0 0 0 42 14.12v-.24A1.88 1.88 0 0 0 40.12 12Z" })),
    'menu-arrow-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.1 22H11.82l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6a1.999 1.999 0 0 0 0 2.84l6 6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L11.82 26H40.1a2 2 0 0 0 1.9-1.9V24a2 2 0 0 0-1.9-2ZM40.1 34H7.9A1.9 1.9 0 0 0 6 35.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 10H7.9A1.9 1.9 0 0 0 6 11.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9Z" })),
    'menu-arrow-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.1 22H11.82l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6a1.999 1.999 0 0 0 0 2.84l6 6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L11.82 26H40.1a2 2 0 0 0 1.9-1.9V24a2 2 0 0 0-1.9-2ZM40.1 34H7.9A1.9 1.9 0 0 0 6 35.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 10H7.9A1.9 1.9 0 0 0 6 11.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9Z" })),
    'menu-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.1 22H7.9A1.9 1.9 0 0 0 6 23.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 32H7.9A1.9 1.9 0 0 0 6 33.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 12H7.9A1.9 1.9 0 0 0 6 13.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9Z" })),
    'menu-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.1 22H7.9A1.9 1.9 0 0 0 6 23.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 32H7.9A1.9 1.9 0 0 0 6 33.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9ZM40.1 12H7.9A1.9 1.9 0 0 0 6 13.9v.2c0 1.05.85 1.9 1.9 1.9h32.2a1.9 1.9 0 0 0 1.9-1.9v-.2a1.9 1.9 0 0 0-1.9-1.9Z" })),
    'message-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.14 9.86a20 20 0 0 0-32.56 22c.192.398.255.845.18 1.28L4 41.6a2 2 0 0 0 .54 1.82A2 2 0 0 0 6 44h.4l8.56-1.72a2.52 2.52 0 0 1 1.28.18 20 20 0 0 0 22-32.56l-.1-.04ZM16 26a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm8 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm8 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'message-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM32 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM16 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M38.14 9.86a20 20 0 0 0-32.56 22c.192.398.255.845.18 1.28L4 41.6a2 2 0 0 0 .54 1.82A1.999 1.999 0 0 0 6 44h.4l8.56-1.72a2.52 2.52 0 0 1 1.28.18 20 20 0 0 0 22-32.56l-.1-.04Zm1.66 16.72a16 16 0 0 1-22 12.16 6.519 6.519 0 0 0-2.5-.52 6.86 6.86 0 0 0-1.12.1l-5.64 1.14 1.14-5.64a6.18 6.18 0 0 0-.42-3.62A16 16 0 0 1 28.913 8.758 16 16 0 0 1 39.8 26.58Z" })),
    'message-square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H10a6 6 0 0 0-6 6v30a2 2 0 0 0 1.02 1.74A2 2 0 0 0 6 44a2 2 0 0 0 1.02-.28L16 38.28a2 2 0 0 1 1.1-.28H38a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6ZM16 24a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm8 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm8 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'message-square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 24a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM32 24a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM16 24a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M38 6H10a6 6 0 0 0-6 6v30a2 2 0 0 0 1.02 1.74A2 2 0 0 0 6 44a2 2 0 0 0 1.02-.28L16 38.28a2 2 0 0 1 1.1-.28H38a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 26a2 2 0 0 1-2 2H17.1a6 6 0 0 0-3.1.86l-6 3.6V12a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v20Z" })),
    'mic-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 30a8 8 0 0 0 8-8V12a8 8 0 0 0-16 0v10a8 8 0 0 0 8 8Z" }),
        React.createElement("path", { d: "M38 22a2 2 0 0 0-4 0 10 10 0 0 1-20 0 2 2 0 0 0-4 0 14 14 0 0 0 12 13.84V40h-4.22A1.78 1.78 0 0 0 16 41.78v.44A1.78 1.78 0 0 0 17.78 44h12.44A1.78 1.78 0 0 0 32 42.22v-.44A1.78 1.78 0 0 0 30.22 40H26v-4.16A14 14 0 0 0 38 22Z" })),
    'mic-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M31.16 25.5A8 8 0 0 0 32 22V12a8 8 0 0 0-15.84-1.5M38 22a2 2 0 0 0-4 0 9.72 9.72 0 0 1-1.38 4.96L35.56 30A14 14 0 0 0 38 22ZM24 30h.32L16 21.66V22a8 8 0 0 0 8 8ZM41.42 38.58l-32-32a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" }),
        React.createElement("path", { d: "M30 40h-4v-4.16a14.003 14.003 0 0 0 3.3-.88l-3.2-3.2A9.142 9.142 0 0 1 24 32a10 10 0 0 1-10-10 2 2 0 0 0-4 0 14 14 0 0 0 12 13.84V40h-4a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4Z" })),
    'mic-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20 12a4 4 0 1 1 8 0v10c.009.107.009.213 0 .32l3.2 3.18A8 8 0 0 0 32 22V12a8 8 0 0 0-15.84-1.5L20 14.34V12ZM38 22a2 2 0 0 0-4 0 9.72 9.72 0 0 1-1.38 4.96L35.56 30A14 14 0 0 0 38 22ZM24 30h.32L16 21.66V22a8 8 0 0 0 8 8ZM41.42 38.58l-32-32a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84Z" }),
        React.createElement("path", { d: "M30 40h-4v-4.16a14.003 14.003 0 0 0 3.3-.88l-3.2-3.2A9.142 9.142 0 0 1 24 32a10 10 0 0 1-10-10 2 2 0 0 0-4 0 14 14 0 0 0 12 13.84V40h-4a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4Z" })),
    'mic-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 30a8 8 0 0 0 8-8V12a8 8 0 0 0-16 0v10a8 8 0 0 0 8 8Zm-4-18a4 4 0 1 1 8 0v10a4 4 0 1 1-8 0V12Z" }),
        React.createElement("path", { d: "M38 22a2 2 0 0 0-4 0 10 10 0 0 1-20 0 2 2 0 0 0-4 0 14 14 0 0 0 12 13.84V40h-4.22A1.78 1.78 0 0 0 16 41.78v.44A1.78 1.78 0 0 0 17.78 44h12.44A1.78 1.78 0 0 0 32 42.22v-.44A1.78 1.78 0 0 0 30.22 40H26v-4.16A14 14 0 0 0 38 22Z" })),
    'minimize-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM26 24h-8a2 2 0 0 1 0-4h8a2 2 0 0 1 0 4Z" })),
    'minimize-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM10 22a12 12 0 1 1 24 0 12 12 0 0 1-24 0Z" }),
        React.createElement("path", { d: "M26 20h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4Z" })),
    'minus-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm6 22H18a2 2 0 0 1 0-4h12a2 2 0 0 1 0 4Z" })),
    'minus-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M30 22H18a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4Z" })),
    'minus-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 26H10a2 2 0 1 1 0-4h28a2 2 0 0 1 0 4Z" })),
    'minus-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 26H10a2 2 0 1 1 0-4h28a2 2 0 0 1 0 4Z" })),
    'minus-square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-6 20H18a2 2 0 0 1 0-4h12a2 2 0 0 1 0 4Z" })),
    'minus-square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24Z" }),
        React.createElement("path", { d: "M30 22H18a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4Z" })),
    'monitor-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H10a6 6 0 0 0-6 6v10h40V12a6 6 0 0 0-6-6ZM4 28a6 6 0 0 0 6 6h12v4h-8a2 2 0 0 0 0 4h20a2 2 0 0 0 0-4h-8v-4h12a6 6 0 0 0 6-6v-2H4v2Z" })),
    'monitor-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h12v4h-8a2 2 0 0 0 0 4h20a2 2 0 0 0 0-4h-8v-4h12a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 22a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v16Z" })),
    'moon-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.6 44h-.2a20.622 20.622 0 0 1-14.68-6.3 20.92 20.92 0 0 1-.52-28 20.26 20.26 0 0 1 8-5.48 2 2 0 0 1 2.12.44 2 2 0 0 1 .48 2 16.8 16.8 0 0 0 3.88 17.62 16.94 16.94 0 0 0 17.66 3.88 2 2 0 0 1 2.54 2.58A20.32 20.32 0 0 1 39.2 38a20.558 20.558 0 0 1-14.6 6Z" })),
    'moon-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.6 44h-.2a20.62 20.62 0 0 1-14.68-6.3 20.92 20.92 0 0 1-.52-28 20.26 20.26 0 0 1 8-5.48 2 2 0 0 1 2.12.44 2 2 0 0 1 .48 2 16.8 16.8 0 0 0 3.88 17.62 16.94 16.94 0 0 0 17.66 3.88 2 2 0 0 1 2.54 2.58A20.32 20.32 0 0 1 39.2 38a20.56 20.56 0 0 1-14.6 6ZM14.92 9.84a15.864 15.864 0 0 0-2.74 2.44 16.88 16.88 0 0 0 .4 22.64A16.58 16.58 0 0 0 24.44 40h.16a16.68 16.68 0 0 0 13.56-6.98A20.74 20.74 0 0 1 14.92 9.84Z" })),
    'more-horizontal-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM38 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM10 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" })),
    'more-horizontal-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM38 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM10 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" })),
    'more-vertical-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM24 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM24 42a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" })),
    'more-vertical-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 28a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM24 14a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM24 42a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" })),
    'move-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.42 22.62-6-6a2.008 2.008 0 1 0-2.84 2.84L37.16 22H26V10.82l2.58 2.6A1.999 1.999 0 0 0 30 14a2 2 0 0 0 1.42-3.42l-6-6a2 2 0 0 0-2.82 0l-6 6a2 2 0 1 0 2.82 2.84L22 10.84V22H10.82l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6A2 2 0 0 0 4 24a2 2 0 0 0 .58 1.42l6 6A1.999 1.999 0 0 0 12 32a2 2 0 0 0 1.42-3.42L10.84 26H22v11.18l-2.58-2.6a2.008 2.008 0 0 0-2.84 2.84l6 6A1.999 1.999 0 0 0 24 44a2 2 0 0 0 1.4-.58l6-6a2.008 2.008 0 1 0-2.84-2.84L26 37.16V26h11.18l-2.6 2.58A2 2 0 0 0 36 32a2 2 0 0 0 1.42-.58l6-6A1.999 1.999 0 0 0 44 24a2 2 0 0 0-.58-1.38Z" })),
    'move-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.42 22.62-6-6a2.008 2.008 0 1 0-2.84 2.84L37.16 22H26V10.82l2.58 2.6A2 2 0 0 0 30 14a2.001 2.001 0 0 0 1.42-3.42l-6-6a2 2 0 0 0-2.82 0l-6 6a2 2 0 0 0 1.403 3.421c.53.002 1.04-.207 1.417-.581L22 10.84V22H10.82l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6A2 2 0 0 0 4 24a2 2 0 0 0 .58 1.42l6 6A2 2 0 0 0 12 32a2.001 2.001 0 0 0 1.42-3.42L10.84 26H22v11.18l-2.58-2.6a2.008 2.008 0 0 0-2.84 2.84l6 6a2 2 0 0 0 2.82 0l6-6a2.008 2.008 0 1 0-2.84-2.84L26 37.16V26h11.18l-2.6 2.58A2 2 0 0 0 36 32a2.001 2.001 0 0 0 1.42-.58l6-6A1.999 1.999 0 0 0 44 24a2 2 0 0 0-.58-1.38Z" })),
    'music-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 30V8a2 2 0 0 0-.76-1.56 2 2 0 0 0-1.68-.4l-18 4A2 2 0 0 0 16 12v16.68a6.98 6.98 0 1 0 4 6.36c.02-.346.02-.694 0-1.04V13.6l14-3.1v14.18a6.98 6.98 0 1 0 4 6.34c.019-.34.019-.68 0-1.02Z" })),
    'music-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 30V8a2 2 0 0 0-.76-1.56 2 2 0 0 0-1.68-.4l-18 4A2 2 0 0 0 16 12v16.68a6.98 6.98 0 1 0 4 6.36c.02-.346.02-.694 0-1.04V13.6l14-3.1v14.18a6.98 6.98 0 1 0 4 6.34c.019-.34.019-.68 0-1.02Zm-24.92 8A2.98 2.98 0 1 1 16 34.42c.02.2.02.4 0 .6A2.98 2.98 0 0 1 13.08 38Zm18-4A3 3 0 1 1 34 30.42c.02.2.02.4 0 .6A3 3 0 0 1 31.02 34h.06Z" })),
    'navigation-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.34 44h-.12a2 2 0 0 1-1.84-1.6L22.3 27.26a2 2 0 0 0-1.56-1.56L5.6 22.62a2 2 0 0 1-.24-3.86l32-10.66A2 2 0 0 1 40 10.64l-10.66 32a2 2 0 0 1-2 1.36Z" })),
    'navigation-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.34 44h-.12a2 2 0 0 1-1.84-1.6L22 26 5.6 22.62a2 2 0 0 1-.24-3.86l32-10.66A2 2 0 0 1 40 10.64l-10.66 32a2 2 0 0 1-2 1.36Zm-13.6-23.8 10.38 2.12a2 2 0 0 1 1.58 1.56l2.1 10.38 7.04-21.1-21.1 7.04Z" })),
    'navigation-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 40a1.88 1.88 0 0 1-1.1-.34l-13.8-9.12a2 2 0 0 0-2.2 0L9.1 39.66a2 2 0 0 1-2.88-2.56l16-32a2 2 0 0 1 3.56 0l16 32A2 2 0 0 1 40 40Z" })),
    'navigation-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 40a1.88 1.88 0 0 1-1.1-.34L24 29.8 9.1 39.66a2 2 0 0 1-2.88-2.56l16-32a2 2 0 0 1 3.56 0l16 32A2 2 0 0 1 40 40ZM24 25.4a2 2 0 0 1 1.1.34l9.76 6.46L24 10.48 13.14 32.2l9.76-6.46a2 2 0 0 1 1.1-.34Z" })),
    'note-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M6 12a6 6 0 0 1 6-6h24a6 6 0 0 1 6 6v16c0 7.732-6.268 14-14 14H12a6 6 0 0 1-6-6V12Zm20.877 26a2 2 0 0 0 1.94-1.515l.37-1.478a8 8 0 0 1 5.82-5.82l1.478-.37A2 2 0 0 0 38 26.877V12a2 2 0 0 0-2-2H12a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2h14.877Zm9.854-5.121-.753.188a4 4 0 0 0-2.91 2.91l-.19.754a10.047 10.047 0 0 0 3.853-3.852ZM14 16a2 2 0 0 1 2-2h16a2 2 0 1 1 0 4H16a2 2 0 0 1-2-2Zm0 8a2 2 0 0 1 2-2h16a2 2 0 1 1 0 4H16a2 2 0 0 1-2-2Zm0 8a2 2 0 0 1 2-2h8a2 2 0 1 1 0 4h-8a2 2 0 0 1-2-2Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'npm-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h14V22h8v20h2a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Z" })),
    'npm-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 42H12a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6h24a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6ZM12 10a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2V12a2 2 0 0 0-2-2H12Z" }),
        React.createElement("path", { d: "M32 18h-8v20h8V18Z" })),
    'options-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 18a6 6 0 0 0-5.64 4H6a2 2 0 1 0 0 4h26.36A6 6 0 1 0 38 18ZM6 14h2.36a6 6 0 0 0 11.28 0H42a2 2 0 0 0 0-4H19.64a6 6 0 0 0-11.28 0H6a2 2 0 1 0 0 4ZM42 34H27.64a6 6 0 0 0-11.28 0H6a2 2 0 1 0 0 4h10.36a6 6 0 0 0 11.28 0H42a2 2 0 0 0 0-4Z" })),
    'options-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 18a6 6 0 0 0-5.64 4H6a2 2 0 1 0 0 4h26.36A6 6 0 1 0 38 18Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM6 14h2.36a6 6 0 0 0 11.28 0H42a2 2 0 0 0 0-4H19.64a6 6 0 0 0-11.28 0H6a2 2 0 0 0 0 4Zm8-4a2 2 0 1 1 0 3.999 2 2 0 0 1 0-4ZM42 34H27.64a6 6 0 0 0-11.28 0H6a2 2 0 0 0 0 4h10.36a6 6 0 0 0 11.28 0H42a2 2 0 0 0 0-4Zm-20 4a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'options-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 28.36V6a2 2 0 1 0-4 0v22.36a6 6 0 0 0 0 11.28V42a2 2 0 0 0 4 0v-2.36a6 6 0 0 0 0-11.28ZM42 26a6 6 0 0 0-4-5.64V6a2 2 0 1 0-4 0v14.36a6 6 0 0 0 0 11.28V42a2 2 0 0 0 4 0V31.64A6 6 0 0 0 42 26ZM30 10a6 6 0 1 0-8 5.64V42a2 2 0 0 0 4 0V15.64A6 6 0 0 0 30 10Z" })),
    'options-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 28.36V6a2 2 0 1 0-4 0v22.36a6 6 0 0 0 0 11.28V42a2 2 0 0 0 4 0v-2.36a6 6 0 0 0 0-11.28ZM12 36a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM42 26a6 6 0 0 0-4-5.64V6a2 2 0 1 0-4 0v14.36a6 6 0 0 0 0 11.28V42a2 2 0 0 0 4 0V31.64A6 6 0 0 0 42 26Zm-6 2a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM30 10a6 6 0 1 0-8 5.64V42a2 2 0 0 0 4 0V15.64A6 6 0 0 0 30 10Zm-6 2a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'pantone-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 26.36h-5.4l-3.72 4L23.76 38l-2.82 3.04L20 42h20a2 2 0 0 0 2-2V28.36a2 2 0 0 0-2-2ZM36.38 18.6l-8.28-7.72a1.78 1.78 0 0 0-1.42-.52 2 2 0 0 0-1.4.62l-1.64 1.78v21.42c-.02.382-.06.763-.12 1.14l12.96-13.9a2 2 0 0 0-.1-2.82ZM21.64 8a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v26.18a7.82 7.82 0 0 0 15.64 0V8Zm-4 26.18a3.82 3.82 0 0 1-7.64 0V30h7.64v4.18Zm0-8.18H10v-6h7.64v6Zm0-10H10v-6h7.64v6Z" })),
    'pantone-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 26.36h-8.12l4.6-4.94a2 2 0 0 0 0-2.82l-8.38-7.72a1.86 1.86 0 0 0-1.42-.52 2 2 0 0 0-1.4.62l-3.64 4V8a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v26.18A7.82 7.82 0 0 0 13.82 42H40a2 2 0 0 0 2-2V28.36a2 2 0 0 0-2-2ZM26.84 15.18l5.34 4.98-10.54 11.32V20.76l5.2-5.58ZM17.64 20v6H10v-6h7.64Zm0-10v6H10v-6h7.64ZM10 34.18V30h7.64v4.18a3.82 3.82 0 0 1-7.64 0ZM38 38H21.02l7.12-7.64H38V38Z" })),
    'paper-plane-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 8a2.62 2.62 0 0 0-.12-.54v-.18a2 2 0 0 0-.4-.6 2 2 0 0 0-.58-.38h-.18a1.72 1.72 0 0 0-.62-.3H40a2 2 0 0 0-.6 0l-36 12a2 2 0 0 0 0 3.8l17.06 5.68 5.68 17.06a1.999 1.999 0 0 0 3.8 0l12-36c.044-.176.065-.358.06-.54Zm-9.4 4.58L21.46 23.72 10.32 20l22.28-7.42ZM28 37.68l-3.72-11.14L35.42 15.4 28 37.68Z" })),
    'paper-plane-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 8a2.62 2.62 0 0 0-.12-.54v-.18a2 2 0 0 0-.4-.6 2 2 0 0 0-.58-.38h-.18a1.72 1.72 0 0 0-.62-.3H40a2 2 0 0 0-.6 0l-36 12a2.001 2.001 0 0 0 0 3.8l17.06 5.68 5.68 17.06a2 2 0 0 0 3.8 0l12-36c.044-.176.065-.358.06-.54Zm-9.4 4.58L21.46 23.72 10.32 20l22.28-7.42ZM28 37.68l-3.72-11.14L35.42 15.4 28 37.68Z" })),
    'pause-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm-4 26a2 2 0 0 1-4 0V18a2 2 0 0 1 4 0v12Zm12 0a2 2 0 0 1-4 0V18a2 2 0 0 1 4 0v12Z" })),
    'pause-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M30 16a2 2 0 0 0-2 2v12a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2ZM18 16a2 2 0 0 0-2 2v12a2 2 0 0 0 4 0V18a2 2 0 0 0-2-2Z" })),
    'people-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM34 26a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM42 40a2 2 0 0 0 2-2 10 10 0 0 0-16.12-7.9A14 14 0 0 0 4 40a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2" })),
    'people-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM34 26a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0-8a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM34 28a10 10 0 0 0-6.12 2.1A14 14 0 0 0 4 40a2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 13.8 13.8 0 0 0-1.72-6.7A6 6 0 0 1 40 38a2 2 0 0 0 4 0 10 10 0 0 0-10-10Z" })),
    'percent-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 22a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm0-10a3 3 0 1 1 0 6 3 3 0 0 1 0-6ZM32 28a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 32 38ZM39.48 8.52a1.78 1.78 0 0 0-2.52 0L8.52 36.96A1.82 1.82 0 0 0 8 38.22a1.78 1.78 0 0 0 3.04 1.26l28.44-28.44a1.78 1.78 0 0 0 0-2.52Z" })),
    'percent-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 22a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm0-10a3 3 0 1 1 0 6 3 3 0 0 1 0-6ZM32 28a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 32 38ZM39.48 8.52a1.78 1.78 0 0 0-2.52 0L8.52 36.96A1.82 1.82 0 0 0 8 38.22a1.78 1.78 0 0 0 3.04 1.26l28.44-28.44a1.78 1.78 0 0 0 0-2.52Z" })),
    'person-add-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12h-2v-2a2 2 0 1 0-4 0v2h-2a2 2 0 0 0 0 4h2v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 0 0-4ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM32 42a2 2 0 0 0 2-2 14 14 0 0 0-28 0 2 2 0 0 0 2 2" })),
    'person-add-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12h-2v-2a2 2 0 1 0-4 0v2h-2a2 2 0 0 0 0 4h2v2a2 2 0 0 0 4 0v-2h2a2 2 0 0 0 0-4ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM20 26A14 14 0 0 0 6 40a2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 14 14 0 0 0-14-14Z" })),
    'person-delete-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.94 15 1.46-1.46a2.079 2.079 0 0 0-2.94-2.94L38 12l-1.46-1.46a2.1 2.1 0 1 0-2.94 3L35.06 15l-1.46 1.46a2.079 2.079 0 1 0 2.94 2.94L38 18l1.46 1.46a2.1 2.1 0 0 0 2.94-3L40.94 15ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM32 42a2 2 0 0 0 2-2 14 14 0 0 0-28 0 2 2 0 0 0 2 2h24Z" })),
    'person-delete-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.94 15 1.46-1.46a2.079 2.079 0 1 0-2.94-2.94L38 12l-1.46-1.46a2.1 2.1 0 0 0-2.94 3L35.06 15l-1.46 1.46a2.079 2.079 0 0 0 2.94 2.94L38 18l1.46 1.46a2.1 2.1 0 1 0 2.94-3L40.94 15ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM20 26A14 14 0 0 0 6 40a2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 14 14 0 0 0-14-14Z" })),
    'person-done-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.32 8.5a2 2 0 0 0-2.82.18l-3.74 4.3-1.26-1.42a2.005 2.005 0 0 0-3 2.66l2.78 3.12a2 2 0 0 0 1.5.66 2.001 2.001 0 0 0 1.48-.68l5.22-6a2 2 0 0 0-.16-2.82ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM32 42a2 2 0 0 0 2-2 14 14 0 0 0-28 0 2 2 0 0 0 2 2" })),
    'person-done-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.32 8.5a2 2 0 0 0-2.82.18l-3.74 4.3-1.26-1.42a2.005 2.005 0 0 0-3 2.66l2.78 3.12a2.002 2.002 0 0 0 1.5.66 2.001 2.001 0 0 0 1.48-.68l5.22-6a2 2 0 0 0-.16-2.82ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM20 26A14 14 0 0 0 6 40a2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 14 14 0 0 0-14-14Z" })),
    'person-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM36 42a2 2 0 0 0 2-2 14 14 0 0 0-28 0 2 2 0 0 0 2 2h24Z" })),
    'person-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM24 26a14 14 0 0 0-14 14 2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 14 14 0 0 0-14-14Z" })),
    'person-remove-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM32 42a2 2 0 0 0 2-2 14 14 0 0 0-28 0 2 2 0 0 0 2 2" })),
    'person-remove-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12h-8a2 2 0 0 0 0 4h8a2 2 0 0 0 0-4ZM20 22a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a4 4 0 1 1 0 8 4 4 0 0 1 0-8ZM20 26A14 14 0 0 0 6 40a2 2 0 0 0 4 0 10 10 0 0 1 20 0 2 2 0 0 0 4 0 14 14 0 0 0-14-14Z" })),
    'phone-call-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 16a6 6 0 0 1 6 6 2 2 0 0 0 4 0 10 10 0 0 0-10-10 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M26 8a14 14 0 0 1 14 14 2 2 0 0 0 4 0A18 18 0 0 0 26 4a2 2 0 0 0 0 4ZM43.5 31.82a2 2 0 0 0-1.44-1.3l-12-2.74a1.999 1.999 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a19.82 19.82 0 0 1-9.74-9.78C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2 30.84 30.84 0 0 0 34.8 44a9.2 9.2 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52a8.683 8.683 0 0 0-.38-1.46Z" })),
    'phone-call-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 16a6 6 0 0 1 6 6 2 2 0 0 0 4 0 10 10 0 0 0-10-10 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M26 8a14 14 0 0 1 14 14 2 2 0 0 0 4 0A18 18 0 0 0 26 4a2 2 0 0 0 0 4ZM43.5 31.82a2 2 0 0 0-1.44-1.3l-12-2.74a1.999 1.999 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a19.82 19.82 0 0 1-9.74-9.78C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2 30.84 30.84 0 0 0 34.8 44a9.2 9.2 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52a8.683 8.683 0 0 0-.38-1.46ZM34.8 40A26.82 26.82 0 0 1 8 13.2 5.22 5.22 0 0 1 13.2 8h.66L16 17.28l-1.08.56c-1.72.9-3.08 1.62-2.36 3.18a23.698 23.698 0 0 0 14.36 14.42c1.68.68 2.34-.58 3.24-2.32l.58-1.1L40 34.14v.66a5.22 5.22 0 0 1-5.2 5.2Z" })),
    'phone-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34.8 44A30.84 30.84 0 0 1 4 13.2 9.2 9.2 0 0 1 13.2 4a7.88 7.88 0 0 1 1.54.14c.49.073.973.193 1.44.36a2 2 0 0 1 1.3 1.5l2.74 12a1.999 1.999 0 0 1-.52 1.84c-.26.28-.28.3-2.74 1.58a19.82 19.82 0 0 0 9.74 9.78c1.3-2.48 1.32-2.5 1.6-2.76a2 2 0 0 1 1.84-.52l12 2.74a2 2 0 0 1 1.44 1.3c.169.475.296.963.38 1.46.08.503.12 1.01.12 1.52A9.2 9.2 0 0 1 34.8 44Z" })),
    'phone-missed-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.88 33.28a8.683 8.683 0 0 0-.38-1.46 2 2 0 0 0-1.44-1.3l-12-2.74a1.999 1.999 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a20 20 0 0 1-9.76-9.78C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2 30.84 30.84 0 0 0 34.8 44a9.2 9.2 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52ZM31.6 17.4a2.1 2.1 0 0 0 2.94 0L36 16l1.46 1.46a2.1 2.1 0 0 0 2.94-3L38.94 13l1.46-1.46a2.078 2.078 0 0 0-2.94-2.94L36 10l-1.46-1.46a2.1 2.1 0 1 0-2.94 3L33.06 13l-1.46 1.46a2.1 2.1 0 0 0 0 2.94Z" })),
    'phone-missed-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.88 33.28a8.683 8.683 0 0 0-.38-1.46 2 2 0 0 0-1.44-1.3l-12-2.74a1.999 1.999 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a20 20 0 0 1-9.76-9.78C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2 30.84 30.84 0 0 0 34.8 44a9.2 9.2 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52ZM34.8 40A26.82 26.82 0 0 1 8 13.2 5.22 5.22 0 0 1 13.2 8h.66L16 17.28l-1.1.58c-1.74.9-3 1.56-2.34 3.16a23.698 23.698 0 0 0 14.36 14.42c1.68.68 2.34-.58 3.24-2.32l.58-1.1L40 34.14v.66a5.22 5.22 0 0 1-5.2 5.2ZM31.6 17.4a2.1 2.1 0 0 0 2.94 0L36 16l1.46 1.46a2.1 2.1 0 1 0 2.94-3L38.94 13l1.46-1.46a2.078 2.078 0 1 0-2.94-2.94L36 10l-1.46-1.46a2.1 2.1 0 1 0-2.94 3L33.06 13l-1.46 1.46a2.1 2.1 0 0 0 0 2.94Z" })),
    'phone-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18.54 24.12a20.74 20.74 0 0 1-1.6-2.84C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2a30.66 30.66 0 0 0 6.54 18.92l8-8ZM43.88 33.28a8.683 8.683 0 0 0-.38-1.46 2 2 0 0 0-1.44-1.3l-12-2.74a2 2 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a21.765 21.765 0 0 1-2.82-1.6l-8 8a30.66 30.66 0 0 0 19 6.54 9.201 9.201 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52ZM39.48 8.52a1.78 1.78 0 0 0-2.52 0L8.52 36.96A1.82 1.82 0 0 0 8 38.22a1.78 1.78 0 0 0 3.04 1.26l28.44-28.44a1.78 1.78 0 0 0 0-2.52Z" })),
    'phone-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39.48 8.52a1.78 1.78 0 0 0-2.52 0L8.52 36.96A1.82 1.82 0 0 0 8 38.22a1.78 1.78 0 0 0 3.04 1.26l28.44-28.44a1.78 1.78 0 0 0 0-2.52ZM13.4 29.26A26.58 26.58 0 0 1 8 13.2 5.22 5.22 0 0 1 13.2 8h.66L16 17.28l-1.1.58c-1.74.9-3 1.56-2.34 3.16a23.142 23.142 0 0 0 3.14 6l2.86-2.84a20.74 20.74 0 0 1-1.6-2.84C19.42 20 19.44 20 19.7 19.7a2 2 0 0 0 .52-1.84L17.48 6a2 2 0 0 0-1.3-1.44 7.581 7.581 0 0 0-1.44-.36A7.88 7.88 0 0 0 13.2 4 9.2 9.2 0 0 0 4 13.2a30.66 30.66 0 0 0 6.54 18.92l2.86-2.86ZM43.88 33.28a8.674 8.674 0 0 0-.38-1.46 2 2 0 0 0-1.44-1.3l-12-2.74a2 2 0 0 0-1.84.52c-.28.26-.3.28-1.6 2.76a21.765 21.765 0 0 1-2.82-1.6l-2.86 2.86a23.04 23.04 0 0 0 5.88 3.12c1.68.68 2.34-.58 3.24-2.32l.58-1.1L40 34.14v.66a5.22 5.22 0 0 1-5.2 5.2 26.581 26.581 0 0 1-16-5.4l-2.92 2.86A30.66 30.66 0 0 0 34.8 44a9.2 9.2 0 0 0 9.2-9.2c0-.51-.04-1.017-.12-1.52Z" })),
    'phone-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34.8 44A30.84 30.84 0 0 1 4 13.2 9.2 9.2 0 0 1 13.2 4a7.88 7.88 0 0 1 1.54.14c.49.073.973.193 1.44.36a2 2 0 0 1 1.3 1.5l2.74 12a1.999 1.999 0 0 1-.52 1.84c-.26.28-.28.3-2.74 1.58a19.82 19.82 0 0 0 9.74 9.78c1.3-2.48 1.32-2.5 1.6-2.76a2 2 0 0 1 1.84-.52l12 2.74a2 2 0 0 1 1.44 1.3c.169.475.296.963.38 1.46.08.503.12 1.01.12 1.52A9.2 9.2 0 0 1 34.8 44ZM13.2 8A5.22 5.22 0 0 0 8 13.2 26.82 26.82 0 0 0 34.8 40a5.22 5.22 0 0 0 5.2-5.2v-.66L30.72 32l-.58 1.1c-.9 1.74-1.56 3-3.24 2.32A23.7 23.7 0 0 1 12.54 21c-.72-1.56.64-2.28 2.36-3.18l1.1-.54L13.86 8h-.66Z" })),
    'pie-chart-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M29 20.66h13.34A1.66 1.66 0 0 0 44 19 15 15 0 0 0 29 4a1.66 1.66 0 0 0-1.66 1.66V19A1.66 1.66 0 0 0 29 20.66Zm1.66-13.2a11.66 11.66 0 0 1 9.88 9.88h-9.88V7.46Z" }),
        React.createElement("path", { d: "M42.16 24h-16.3a1.82 1.82 0 0 1-1.82-1.82V5.84A1.84 1.84 0 0 0 22 4a20 20 0 1 0 22 22 1.84 1.84 0 0 0-1.84-2Z" })),
    'pie-chart-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M29 20.66h13.34A1.66 1.66 0 0 0 44 19 15 15 0 0 0 29 4a1.66 1.66 0 0 0-1.66 1.66V19A1.66 1.66 0 0 0 29 20.66Z" }),
        React.createElement("path", { d: "M42.16 24h-16.3a1.82 1.82 0 0 1-1.82-1.82V5.84A1.84 1.84 0 0 0 22 4a20 20 0 1 0 22 22 1.84 1.84 0 0 0-1.84-2Z" })),
    'pie-chart-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 4a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2A18 18 0 0 0 26 4Zm2 16V8.14A14 14 0 0 1 39.86 20H28Z" }),
        React.createElement("path", { d: "M41.64 28.12a2 2 0 0 0-2.56 1.22A16 16 0 1 1 18.66 8.92a2.002 2.002 0 1 0-1.32-3.78 20 20 0 1 0 25.52 25.52 2 2 0 0 0-1.22-2.54Z" })),
    'pin-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        React.createElement("path", { d: "M24 4A16 16 0 0 0 8 19.84C8 30.8 22.1 43 22.7 43.52a2 2 0 0 0 2.6 0C26 43 40 30.8 40 19.84A16 16 0 0 0 24 4Zm0 22a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'pin-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4A16 16 0 0 0 8 19.84C8 30.8 22.1 43 22.7 43.52a2 2 0 0 0 2.6 0C26 43 40 30.8 40 19.84A16 16 0 0 0 24 4Zm0 35.3c-3.34-3.18-12-12-12-19.46a12 12 0 0 1 24 0c0 7.4-8.66 16.28-12 19.46Z" }),
        React.createElement("path", { d: "M24 12a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 24 22Z" })),
    'play-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m23 29.2 5.62-5.2L23 18.8v10.4Z" }),
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm8 22.36-7.28 6.74a3.48 3.48 0 0 1-2.32.9 3.36 3.36 0 0 1-1.38-.3 3.2 3.2 0 0 1-2-2.96V17.26a3.2 3.2 0 0 1 2-2.96 3.4 3.4 0 0 1 3.7.6L32 21.64a3.2 3.2 0 0 1 0 4.72Z" })),
    'play-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24.68 14.9a3.4 3.4 0 0 0-3.7-.6 3.2 3.2 0 0 0-2 2.96v13.48a3.2 3.2 0 0 0 2 2.96 3.36 3.36 0 0 0 1.38.3 3.48 3.48 0 0 0 2.32-.9L32 26.36a3.2 3.2 0 0 0 0-4.72l-7.32-6.74ZM23 29.2V18.8l5.62 5.2L23 29.2Z" })),
    'plus-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm6 22h-4v4a2 2 0 0 1-4 0v-4h-4a2 2 0 0 1 0-4h4v-4a2 2 0 0 1 4 0v4h4a2 2 0 0 1 0 4Z" })),
    'plus-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M30 22h-4v-4a2 2 0 0 0-4 0v4h-4a2 2 0 0 0 0 4h4v4a2 2 0 0 0 4 0v-4h4a2 2 0 0 0 0-4Z" })),
    'plus-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 22H26V10a2 2 0 1 0-4 0v12H10a2 2 0 1 0 0 4h12v12a2 2 0 0 0 4 0V26h12a2 2 0 0 0 0-4Z" })),
    'plus-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 22H26V10a2 2 0 1 0-4 0v12H10a2 2 0 1 0 0 4h12v12a2 2 0 0 0 4 0V26h12a2 2 0 0 0 0-4Z" })),
    'plus-square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm-6 20h-4v4a2 2 0 0 1-4 0v-4h-4a2 2 0 0 1 0-4h4v-4a2 2 0 0 1 4 0v4h4a2 2 0 0 1 0 4Z" })),
    'plus-square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6Zm2 30a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24Z" }),
        React.createElement("path", { d: "M30 22h-4v-4a2 2 0 0 0-4 0v4h-4a2 2 0 0 0 0 4h4v4a2 2 0 0 0 4 0v-4h4a2 2 0 0 0 0-4Z" })),
    'power-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 26a2 2 0 0 0 2-2V4a2 2 0 1 0-4 0v20a2 2 0 0 0 2 2Z" }),
        React.createElement("path", { d: "M33.18 6.22a2.004 2.004 0 0 0-1.84 3.56 16 16 0 1 1-14.68 0 2.003 2.003 0 1 0-1.84-3.56 20 20 0 1 0 18.36 0Z" })),
    'power-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 26a2 2 0 0 0 2-2V4a2 2 0 1 0-4 0v20a2 2 0 0 0 2 2Z" }),
        React.createElement("path", { d: "M33.18 6.22a2.004 2.004 0 0 0-1.84 3.56 16 16 0 1 1-14.68 0 2.003 2.003 0 1 0-1.84-3.56 20 20 0 1 0 18.36 0Z" })),
    'pricetags-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.94 23.16 30.1 10.34a2 2 0 0 0-1.22-.58L10.18 8a2 2 0 0 0-1.6.58 2 2 0 0 0-.58 1.6l1.76 18.7a2 2 0 0 0 .58 1.22l12.82 12.84A3.68 3.68 0 0 0 25.74 44a3.64 3.64 0 0 0 2.56-1.06L42.94 28.3a3.64 3.64 0 0 0 0-5.14Zm-19.82 0a3 3 0 1 1 0-4.24 2.98 2.98 0 0 1 0 4.2v.04Z" })),
    'pricetags-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.74 44a3.68 3.68 0 0 1-2.58-1.06L10.34 30.1a2 2 0 0 1-.58-1.22L8 10.18a2 2 0 0 1 .58-1.6 2 2 0 0 1 1.6-.58l18.7 1.76a2 2 0 0 1 1.22.58l12.84 12.82a3.64 3.64 0 0 1 0 5.14L28.3 42.94A3.64 3.64 0 0 1 25.74 44Zm-12-16.22 12 12 14.1-14.1-12-12-15.62-1.46 1.52 15.56Z" }),
        React.createElement("path", { d: "M21 24a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'printer-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.72 14H36v-4a3.84 3.84 0 0 0-3.66-4H15.66A3.84 3.84 0 0 0 12 10v4H9.28A5.32 5.32 0 0 0 4 19.34v13.32A5.32 5.32 0 0 0 9.28 38H11a4 4 0 0 0 4 4h18a4 4 0 0 0 4-4h1.72A5.32 5.32 0 0 0 44 32.66V19.34A5.32 5.32 0 0 0 38.72 14ZM16 10h16v4H16v-4Zm-1 28v-8h18v8H15Z" })),
    'printer-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.72 14H36v-4a3.84 3.84 0 0 0-3.66-4H15.66A3.84 3.84 0 0 0 12 10v4H9.28A5.32 5.32 0 0 0 4 19.34v13.32A5.32 5.32 0 0 0 9.28 38H11a4 4 0 0 0 4 4h18a4 4 0 0 0 4-4h1.72A5.32 5.32 0 0 0 44 32.66V19.34A5.32 5.32 0 0 0 38.72 14ZM16 10h16v4H16v-4Zm-1 28v-8h18v8H15Zm25-5.34A1.32 1.32 0 0 1 38.72 34H37v-4a4 4 0 0 0-4-4H15a4 4 0 0 0-4 4v4H9.28A1.32 1.32 0 0 1 8 32.66V19.34A1.32 1.32 0 0 1 9.28 18h29.44A1.32 1.32 0 0 1 40 19.34v13.32Z" })),
    'question-mark-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 32a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm2-10.32V28a2 2 0 0 1-4 0v-4a2 2 0 0 1 2-2 3 3 0 1 0-3-3 2 2 0 0 1-4 0 7 7 0 1 1 9 6.68Z" })),
    'question-mark-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 12a7 7 0 0 0-7 7 2 2 0 1 0 4 0 3 3 0 1 1 3 3 2 2 0 0 0-2 2v4a2 2 0 1 0 4 0v-2.32A6.98 6.98 0 0 0 24 12ZM24 36a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'question-mark-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 18a10 10 0 0 0-20 0 2 2 0 0 0 4 0 6 6 0 1 1 6 6 2 2 0 0 0-2 2v4a2 2 0 0 0 4 0v-2.2a10 10 0 0 0 8-9.8ZM24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'question-mark-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 18a10 10 0 0 0-20 0 2 2 0 0 0 4 0 6 6 0 1 1 6 6 2 2 0 0 0-2 2v4a2 2 0 0 0 4 0v-2.2a10 10 0 0 0 8-9.8ZM24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" })),
    'radio-button-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a20 20 0 1 1 0-40 20 20 0 0 1 0 40Zm0-36a16 16 0 1 0 0 32 16 16 0 0 0 0-32Z" })),
    'radio-button-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a20 20 0 1 1 0-40 20 20 0 0 1 0 40Zm0-36a16 16 0 1 0 0 32 16 16 0 0 0 0-32Z" })),
    'radio-button-on-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 14a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z" })),
    'radio-button-on-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M24 14a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 16a6 6 0 1 1 0-12 6 6 0 0 1 0 12Z" })),
    'radio-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 16a6 6 0 0 0-2 11.66 1.99 1.99 0 0 0 0 .34v12a2 2 0 0 0 4 0V28a2.01 2.01 0 0 0 0-.34A6 6 0 0 0 24 16ZM7 22a13.74 13.74 0 0 1 5.28-10.46 2.002 2.002 0 0 0-2.56-3.08A17.68 17.68 0 0 0 3 22a17.68 17.68 0 0 0 6.72 13.54 2.002 2.002 0 1 0 2.56-3.08A13.74 13.74 0 0 1 7 22Z" }),
        React.createElement("path", { d: "M33.28 12.48a2 2 0 0 0-2.56 3.04A8.56 8.56 0 0 1 34 22a8.56 8.56 0 0 1-3.28 6.48A2 2 0 0 0 32 32a2 2 0 0 0 1.28-.48A12.4 12.4 0 0 0 38 22a12.4 12.4 0 0 0-4.72-9.52ZM17.52 12.72a2 2 0 0 0-2.8-.24A12.4 12.4 0 0 0 10 22a12.4 12.4 0 0 0 4.72 9.52 2 2 0 0 0 2.8-.24 2 2 0 0 0-.24-2.8A8.56 8.56 0 0 1 14 22a8.56 8.56 0 0 1 3.28-6.48 2 2 0 0 0 .24-2.8Z" }),
        React.createElement("path", { d: "M38.28 8.46a2.002 2.002 0 1 0-2.56 3.08A13.74 13.74 0 0 1 41 22a13.74 13.74 0 0 1-5.28 10.46 2.002 2.002 0 0 0 2.56 3.08A17.68 17.68 0 0 0 45 22a17.68 17.68 0 0 0-6.72-13.54Z" })),
    'radio-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 16a6 6 0 0 0-2 11.66 1.99 1.99 0 0 0 0 .34v12a2 2 0 0 0 4 0V28a2.01 2.01 0 0 0 0-.34A6 6 0 0 0 24 16Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4ZM7 22a13.74 13.74 0 0 1 5.28-10.46 2.002 2.002 0 1 0-2.56-3.08A17.68 17.68 0 0 0 3 22a17.68 17.68 0 0 0 6.72 13.54 2.002 2.002 0 1 0 2.56-3.08A13.74 13.74 0 0 1 7 22Z" }),
        React.createElement("path", { d: "M33.28 12.48a2 2 0 0 0-2.56 3.04A8.56 8.56 0 0 1 34 22a8.56 8.56 0 0 1-3.28 6.48A2 2 0 0 0 32 32a2 2 0 0 0 1.28-.48A12.4 12.4 0 0 0 38 22a12.401 12.401 0 0 0-4.72-9.52ZM17.52 12.72a2 2 0 0 0-2.8-.24A12.4 12.4 0 0 0 10 22a12.4 12.4 0 0 0 4.72 9.52 2 2 0 0 0 2.8-.24 2 2 0 0 0-.24-2.8A8.558 8.558 0 0 1 14 22a8.56 8.56 0 0 1 3.28-6.48 2 2 0 0 0 .24-2.8Z" }),
        React.createElement("path", { d: "M38.28 8.46a2.002 2.002 0 1 0-2.56 3.08A13.74 13.74 0 0 1 41 22a13.74 13.74 0 0 1-5.28 10.46 2.002 2.002 0 1 0 2.56 3.08A17.681 17.681 0 0 0 45 22a17.68 17.68 0 0 0-6.72-13.54Z" })),
    'recording-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 16a8 8 0 0 0-8 8 7.82 7.82 0 0 0 1.12 4H18.88A7.82 7.82 0 0 0 20 24a8 8 0 1 0-8 8h24a8 8 0 1 0 0-16Z" })),
    'recording-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 16a8 8 0 0 0-8 8 7.82 7.82 0 0 0 1.12 4H18.88A7.82 7.82 0 0 0 20 24a8 8 0 1 0-8 8h24a8 8 0 1 0 0-16ZM8 24a4 4 0 1 1 8 0 4 4 0 0 1-8 0Zm28 4a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" })),
    'refresh-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.6 26.86a2 2 0 0 0-2.5 1.3A14.28 14.28 0 0 1 24.36 38 14.2 14.2 0 0 1 10 24a14.2 14.2 0 0 1 14.36-14 14.52 14.52 0 0 1 9.3 3.34l-4.34-.72a2 2 0 0 0-2.3 1.66 2 2 0 0 0 1.66 2.3l8.48 1.4h.34c.232 0 .462-.04.68-.12a.66.66 0 0 0 .2-.12 1.56 1.56 0 0 0 .4-.22l.18-.22c0-.1.18-.18.26-.3.08-.12 0-.2.1-.28a2.67 2.67 0 0 0 .14-.36l1.5-8a2.036 2.036 0 1 0-4-.76l-.54 2.9A18.42 18.42 0 0 0 24.36 6 18.2 18.2 0 0 0 6 24a18.2 18.2 0 0 0 18.36 18A18.24 18.24 0 0 0 42 29.36a1.998 1.998 0 0 0-1.4-2.5Z" })),
    'refresh-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.6 26.86a2 2 0 0 0-2.5 1.3A14.28 14.28 0 0 1 24.36 38 14.2 14.2 0 0 1 10 24a14.2 14.2 0 0 1 14.36-14 14.52 14.52 0 0 1 9.3 3.34l-4.34-.72a2 2 0 0 0-2.3 1.66 2 2 0 0 0 1.66 2.3l8.48 1.4h.34c.232 0 .462-.04.68-.12a.66.66 0 0 0 .2-.12 1.56 1.56 0 0 0 .4-.22l.18-.22c0-.1.18-.18.26-.3.08-.12 0-.2.1-.28a2.67 2.67 0 0 0 .14-.36l1.5-8a2.036 2.036 0 1 0-4-.76l-.54 2.9A18.42 18.42 0 0 0 24.36 6 18.2 18.2 0 0 0 6 24a18.2 18.2 0 0 0 18.36 18A18.24 18.24 0 0 0 42 29.36a1.998 1.998 0 0 0-1.4-2.5Z" })),
    'repeat-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.82 10h-24l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6a1.999 1.999 0 0 0 0 2.84l6 6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L11.82 14h24A3.119 3.119 0 0 1 39 17.06V22a2 2 0 0 0 4 0v-4.94A7.12 7.12 0 0 0 35.82 10ZM36.42 28.58a2.008 2.008 0 1 0-2.84 2.84l2.6 2.58h-24A3.119 3.119 0 0 1 9 30.94V26a2 2 0 0 0-4 0v4.94A7.12 7.12 0 0 0 12.18 38h24l-2.6 2.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l6-6a2 2 0 0 0 0-2.84l-6-6Z" })),
    'repeat-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.82 10h-24l2.6-2.58a2.008 2.008 0 1 0-2.84-2.84l-6 6a1.999 1.999 0 0 0 0 2.84l6 6a2 2 0 0 0 2.84 0 2 2 0 0 0 0-2.84L11.82 14h24A3.119 3.119 0 0 1 39 17.06V22a2 2 0 0 0 4 0v-4.94A7.12 7.12 0 0 0 35.82 10ZM36.42 28.58a2.008 2.008 0 1 0-2.84 2.84l2.6 2.58h-24A3.119 3.119 0 0 1 9 30.94V26a2 2 0 0 0-4 0v4.94A7.12 7.12 0 0 0 12.18 38h24l-2.6 2.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0l6-6a2 2 0 0 0 0-2.84l-6-6Z" })),
    'rewind-left-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.9 12.4a4.2 4.2 0 0 0-4.42.52L23 20.76v-5.18a3.52 3.52 0 0 0-2.1-3.18 4.2 4.2 0 0 0-4.42.52l-10.2 8.42a3.401 3.401 0 0 0 0 5.32l10.2 8.42a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18v-5.18l9.48 7.84a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18V15.58a3.52 3.52 0 0 0-2.1-3.18Z" })),
    'rewind-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.9 12.4a4.2 4.2 0 0 0-4.42.52L23 20.76v-5.18a3.52 3.52 0 0 0-2.1-3.18 4.2 4.2 0 0 0-4.42.52l-10.2 8.42a3.401 3.401 0 0 0 0 5.32l10.2 8.42a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18v-5.18l9.48 7.84a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18V15.58a3.52 3.52 0 0 0-2.1-3.18ZM19 32l-9.64-8L19 16.18V32Zm16 0-9.64-8L35 16.18V32Z" })),
    'rewind-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.72 21.34-10.2-8.42a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2.1 3.18v5.18l-9.48-7.84a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2 3.18v16.84a3.52 3.52 0 0 0 2 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92L25 27.24v5.18a3.52 3.52 0 0 0 2.1 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92l10.2-8.42a3.4 3.4 0 0 0 0-5.32Z" })),
    'rewind-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.72 21.34-10.2-8.42a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2.1 3.18v5.18l-9.48-7.84a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2 3.18v16.84a3.52 3.52 0 0 0 2 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92L25 27.24v5.18a3.52 3.52 0 0 0 2.1 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92l10.2-8.42a3.4 3.4 0 0 0 0-5.32ZM13 31.82V16l9.64 8L13 31.82Zm16 0V16l9.64 8L29 31.82Z" })),
    'save-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28 34h-8v8h8v-8Z" }),
        React.createElement("path", { d: "m40.24 17.42-9.66-9.66A6 6 0 0 0 26.34 6H20v12h10a2 2 0 0 1 0 4H18a2 2 0 0 1-2-2V6h-4a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h4v-8a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8h4a6 6 0 0 0 6-6V21.66a6 6 0 0 0-1.76-4.24Z" })),
    'save-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.24 17.42-9.66-9.66A6 6 0 0 0 26.34 6H12a6 6 0 0 0-6 6v24a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V21.66a6 6 0 0 0-1.76-4.24ZM20 38v-4h8v4h-8Zm18-2a2 2 0 0 1-2 2h-4v-6a2 2 0 0 0-2-2H18a2 2 0 0 0-2 2v6h-4a2 2 0 0 1-2-2V12a2 2 0 0 1 2-2h4v10a2 2 0 0 0 2 2h8a2 2 0 0 0 0-4h-6v-8h6.34a2.1 2.1 0 0 1 1.42.58l9.66 9.66a1.999 1.999 0 0 1 .58 1.42V36Z" })),
    'scissors-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.42 11.42a2.008 2.008 0 1 0-2.84-2.84L25.02 21.2l-6.6-6.62A6 6 0 0 0 19 12a6 6 0 1 0-6 6 6 6 0 0 0 2.58-.6L22.2 24l-6.58 6.6A6 6 0 1 0 19 36a6 6 0 0 0-.58-2.52l22-22.06ZM13 14a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm0 24a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" }),
        React.createElement("path", { d: "M30.42 26.58a2.008 2.008 0 1 0-2.84 2.84l10 10a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-10-10Z" })),
    'scissors-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.42 11.42a2.008 2.008 0 1 0-2.84-2.84L25.02 21.2l-6.6-6.62A6 6 0 0 0 19 12a6 6 0 1 0-6 6 6 6 0 0 0 2.58-.6L22.2 24l-6.58 6.6A6 6 0 1 0 19 36a6 6 0 0 0-.58-2.52l22-22.06ZM13 14a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm0 24a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" }),
        React.createElement("path", { d: "M30.42 26.58a2.008 2.008 0 1 0-2.84 2.84l10 10a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-10-10Z" })),
    'search-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM10 22a12 12 0 1 1 24 0 12 12 0 0 1-24 0Z" })),
    'search-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.42 38.58-6.8-6.78A15.84 15.84 0 0 0 38 22a16 16 0 1 0-16 16 15.84 15.84 0 0 0 9.8-3.38l6.78 6.8a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84ZM10 22a12 12 0 1 1 24 0 12 12 0 0 1-24 0Z" })),
    'settings-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        React.createElement("path", { d: "M40.64 18.74h-2.18c-.28 0-.48-.22-.6-.52a.68.68 0 0 1 0-.74L39.48 16a3.26 3.26 0 0 0 1-2.36 3.339 3.339 0 0 0-1-2.38L36.8 8.52a3.34 3.34 0 0 0-4.74 0L30.52 10a.76.76 0 0 1-.82 0 .68.68 0 0 1-.44-.58V7.36A3.36 3.36 0 0 0 26 4h-3.88a3.38 3.38 0 0 0-3.38 3.36v2.18c0 .28-.22.48-.52.6a.68.68 0 0 1-.74 0L16 8.52a3.44 3.44 0 0 0-2.38-1 3.3 3.3 0 0 0-2.36 1L8.52 11.2a3.34 3.34 0 0 0 0 4.8L10 17.48a.76.76 0 0 1 0 .82.68.68 0 0 1-.58.44H7.36A3.36 3.36 0 0 0 4 22.1v3.78a3.38 3.38 0 0 0 3.36 3.38h2.18c.28 0 .48.22.6.52a.68.68 0 0 1 0 .74L8.52 32a3.44 3.44 0 0 0-1 2.38 3.32 3.32 0 0 0 1 2.38l2.68 2.72a3.342 3.342 0 0 0 4.74 0L17.48 38a.76.76 0 0 1 .82 0 .68.68 0 0 1 .44.58v2.18A3.36 3.36 0 0 0 22.1 44h3.78a3.38 3.38 0 0 0 3.38-3.36v-2.18c0-.28.22-.48.52-.6a.68.68 0 0 1 .74 0l1.52 1.54a3.44 3.44 0 0 0 2.38 1 3.302 3.302 0 0 0 2.36-1l2.68-2.68a3.342 3.342 0 0 0 0-4.74L38 30.52a.68.68 0 0 1 0-.74.68.68 0 0 1 .58-.44h2.18A3.36 3.36 0 0 0 44 26v-3.88a3.38 3.38 0 0 0-3.36-3.38ZM24 31a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'settings-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.88 44H22.1a3.36 3.36 0 0 1-3.36-3.36v-2.18a.68.68 0 0 0-.44-.58.76.76 0 0 0-.82 0L16 39.48a3.342 3.342 0 0 1-4.74 0L8.52 36.8a3.32 3.32 0 0 1-1-2.38 3.44 3.44 0 0 1 1-2.42L10 30.52a.68.68 0 0 0 0-.74c-.12-.3-.32-.52-.6-.52H7.36A3.38 3.38 0 0 1 4 25.88V22.1a3.36 3.36 0 0 1 3.36-3.36h2.18a.68.68 0 0 0 .58-.44.76.76 0 0 0 0-.82L8.52 16a3.34 3.34 0 0 1 0-4.74l2.68-2.74a3.3 3.3 0 0 1 2.36-1 3.44 3.44 0 0 1 2.44 1L17.48 10a.68.68 0 0 0 .74 0c.3-.12.52-.32.52-.6V7.36A3.38 3.38 0 0 1 22.12 4H26a3.36 3.36 0 0 1 3.36 3.36v2.18a.68.68 0 0 0 .44.58.76.76 0 0 0 .82 0L32 8.52a3.34 3.34 0 0 1 4.74 0l2.74 2.68a3.338 3.338 0 0 1 1 2.38 3.26 3.26 0 0 1-1 2.42L38 17.48a.68.68 0 0 0 0 .74c.12.3.32.52.6.52h2.18A3.38 3.38 0 0 1 44 22.12V26a3.36 3.36 0 0 1-3.36 3.36h-2.18a.68.68 0 0 0-.58.44.68.68 0 0 0 0 .74l1.54 1.54a3.34 3.34 0 0 1 0 4.74l-2.62 2.66a3.3 3.3 0 0 1-2.36 1 3.44 3.44 0 0 1-2.38-1L30.52 38a.68.68 0 0 0-.74 0c-.3.12-.52.32-.52.6v2.18A3.38 3.38 0 0 1 25.88 44Zm-3.14-4h2.52v-1.54a4.66 4.66 0 0 1 2.92-4.28 4.72 4.72 0 0 1 5.18.94l1.08 1.08 1.76-1.76-1.08-1.1a4.68 4.68 0 0 1-.96-5.12 4.66 4.66 0 0 1 4.28-2.9H40v-2.58h-1.54a4.66 4.66 0 0 1-4.28-2.92 4.72 4.72 0 0 1 .94-5.18l1.08-1.08-1.76-1.76-1.1 1.08a4.78 4.78 0 0 1-8-3.34V8h-2.6v1.54a4.66 4.66 0 0 1-2.92 4.28 4.72 4.72 0 0 1-5.18-.94l-1.08-1.08-1.76 1.76 1.08 1.1a4.78 4.78 0 0 1-3.34 8H8v2.52h1.54a4.66 4.66 0 0 1 4.28 2.92 4.72 4.72 0 0 1-.94 5.18l-1.08 1.08 1.76 1.76 1.1-1.08a4.78 4.78 0 0 1 8 3.34l.08 1.62Z" }),
        React.createElement("path", { d: "M24 31a7 7 0 1 1 0-14 7 7 0 0 1 0 14Zm0-10a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" })),
    'settings-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" }),
        React.createElement("path", { d: "M43.78 20.64 42.2 15.6a4.521 4.521 0 0 0-5.76-3.02l-.68.22a3.48 3.48 0 0 1-3.18-.52l-.22-.16a3.521 3.521 0 0 1-1.38-2.86V8.7a4.74 4.74 0 0 0-1.36-3.36A4.52 4.52 0 0 0 26.42 4h-5.1a4.64 4.64 0 0 0-4.58 4.66v.48a3.88 3.88 0 0 1-1.46 3.02l-.26.2a3.86 3.86 0 0 1-3.56.58 4.282 4.282 0 0 0-3.36.24 4.36 4.36 0 0 0-2.24 2.66l-1.64 5.2a4.68 4.68 0 0 0 2.96 5.88h.32a3.661 3.661 0 0 1 2.24 2.44l.12.32a4.12 4.12 0 0 1-.46 3.72 4.74 4.74 0 0 0 .98 6.6l4.14 3.14a4.5 4.5 0 0 0 2.7.86c.26.025.52.025.78 0a4.5 4.5 0 0 0 2.94-2l.46-.66a3.6 3.6 0 0 1 2.86-1.54 3.5 3.5 0 0 1 3 1.56l.24.34a4.48 4.48 0 0 0 6.44 1.06L38 39.72a4.76 4.76 0 0 0 1-6.46l-.52-.76a4 4 0 0 1-.48-3.3 3.78 3.78 0 0 1 2.42-2.56l.4-.14a4.72 4.72 0 0 0 2.96-5.86ZM24 31a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'settings-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.22 44a4.5 4.5 0 0 1-2.7-.92L10.38 40a4.74 4.74 0 0 1-.98-6.44 4.12 4.12 0 0 0 .46-3.72l-.12-.32a3.66 3.66 0 0 0-2.24-2.44h-.32a4.68 4.68 0 0 1-2.96-5.88L5.86 16a4.36 4.36 0 0 1 2.24-2.82 4.28 4.28 0 0 1 3.36-.24 3.86 3.86 0 0 0 3.56-.58l.26-.2a3.88 3.88 0 0 0 1.46-3.02v-.48A4.64 4.64 0 0 1 21.32 4h5.1a4.52 4.52 0 0 1 3.2 1.34 4.74 4.74 0 0 1 1.36 3.36v.56a3.521 3.521 0 0 0 1.38 2.86l.22.16a3.48 3.48 0 0 0 3.18.52l.68-.22a4.519 4.519 0 0 1 5.76 3.02l1.58 5.04a4.72 4.72 0 0 1-2.92 5.86l-.4.14A3.78 3.78 0 0 0 38 29.2a4 4 0 0 0 .5 3.3l.52.76a4.76 4.76 0 0 1-1 6.46L34 42.82a4.48 4.48 0 0 1-6.44-1.06l-.24-.34a3.5 3.5 0 0 0-3-1.56 3.6 3.6 0 0 0-2.86 1.54l-.46.66A4.502 4.502 0 0 1 18 44c-.26.025-.52.025-.78 0ZM8.8 23.24a7.659 7.659 0 0 1 4.76 5v.24a8 8 0 0 1-.92 7.24.76.76 0 0 0 0 1.02l4.3 3.26a.5.5 0 0 0 .74-.14l.46-.66a7.54 7.54 0 0 1 12.4 0l.24.36a.6.6 0 0 0 .36.24.499.499 0 0 0 .38-.1l4.12-3.12a.72.72 0 0 0 .14-.98l-.52-.76A7.999 7.999 0 0 1 34.2 28a7.84 7.84 0 0 1 4.98-5.22l.4-.14a.68.68 0 0 0 .38-.88l-1.56-4.98a.7.7 0 0 0-.4-.38.42.42 0 0 0-.38 0l-.68.22a7.481 7.481 0 0 1-6.86-1.14L30 15.3a7.52 7.52 0 0 1-2.98-6v-.62a.74.74 0 0 0-.2-.52.62.62 0 0 0-.42-.16h-5.08a.62.62 0 0 0-.58.66v.5a7.8 7.8 0 0 1-3.04 6.18l-.26.2a7.819 7.819 0 0 1-7.26 1.18.44.44 0 0 0-.28 0 .56.56 0 0 0-.24.3L8 22.24a.72.72 0 0 0 .44.9l.36.1Z" }),
        React.createElement("path", { d: "M24 31a7 7 0 1 1 0-14 7 7 0 0 1 0 14Zm0-10a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" })),
    'shake-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M11 36a2 2 0 0 1-1.28-.48A17.62 17.62 0 0 1 3 22 17.62 17.62 0 0 1 9.72 8.48a2 2 0 1 1 2.56 3.04A13.8 13.8 0 0 0 7 22a13.8 13.8 0 0 0 5.28 10.48 2 2 0 0 1 .26 2.8A2 2 0 0 1 11 36ZM24 14a8.18 8.18 0 0 1 2 .28V6a2 2 0 1 0-4 0v8.28a8.18 8.18 0 0 1 2-.28ZM24 30a8.18 8.18 0 0 1-2-.28V40a2 2 0 0 0 4 0V29.72a8.18 8.18 0 0 1-2 .28ZM32 32a2 2 0 0 1-1.54-.72 2 2 0 0 1 .26-2.8A8.56 8.56 0 0 0 34 22a8.56 8.56 0 0 0-3.28-6.48 1.999 1.999 0 1 1 2.56-3.04A12.4 12.4 0 0 1 38 22a12.4 12.4 0 0 1-4.72 9.52A2 2 0 0 1 32 32Z" }),
        React.createElement("path", { d: "M16 32a2 2 0 0 1-1.28-.48A12.4 12.4 0 0 1 10 22a12.4 12.4 0 0 1 4.72-9.52 2 2 0 1 1 2.56 3.04A8.56 8.56 0 0 0 14 22a8.56 8.56 0 0 0 3.28 6.48 2 2 0 0 1 .26 2.8A2 2 0 0 1 16 32ZM37 36a2 2 0 0 1-1.54-.72 2 2 0 0 1 .26-2.8A13.8 13.8 0 0 0 41 22a13.8 13.8 0 0 0-5.28-10.48 1.999 1.999 0 1 1 2.56-3.04A17.62 17.62 0 0 1 45 22a17.62 17.62 0 0 1-6.72 13.52A2 2 0 0 1 37 36ZM24 24a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'shake-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M11 36a2 2 0 0 1-1.28-.48A17.62 17.62 0 0 1 3 22 17.62 17.62 0 0 1 9.72 8.48a2 2 0 1 1 2.56 3.04A13.8 13.8 0 0 0 7 22a13.8 13.8 0 0 0 5.28 10.48 2 2 0 0 1 .26 2.8A2 2 0 0 1 11 36ZM24 14a8.18 8.18 0 0 1 2 .28V6a2 2 0 1 0-4 0v8.28a8.18 8.18 0 0 1 2-.28ZM24 30a8.18 8.18 0 0 1-2-.28V40a2 2 0 0 0 4 0V29.72a8.18 8.18 0 0 1-2 .28ZM32 32a2 2 0 0 1-1.54-.72 2 2 0 0 1 .26-2.8A8.56 8.56 0 0 0 34 22a8.56 8.56 0 0 0-3.28-6.48 1.999 1.999 0 1 1 2.56-3.04A12.4 12.4 0 0 1 38 22a12.4 12.4 0 0 1-4.72 9.52A2 2 0 0 1 32 32Z" }),
        React.createElement("path", { d: "M16 32a2 2 0 0 1-1.28-.48A12.4 12.4 0 0 1 10 22a12.4 12.4 0 0 1 4.72-9.52 2 2 0 1 1 2.56 3.04A8.56 8.56 0 0 0 14 22a8.56 8.56 0 0 0 3.28 6.48 2 2 0 0 1 .26 2.8A2 2 0 0 1 16 32ZM37 36a2 2 0 0 1-1.54-.72 2 2 0 0 1 .26-2.8A13.8 13.8 0 0 0 41 22a13.8 13.8 0 0 0-5.28-10.48 1.999 1.999 0 1 1 2.56-3.04A17.62 17.62 0 0 1 45 22a17.62 17.62 0 0 1-6.72 13.52A2 2 0 0 1 37 36ZM24 24a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'share-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 30a6 6 0 0 0-4.2 1.72L16 24.68v-1.34l15.8-7.06A6 6 0 1 0 30 12v.68l-15.8 7.04a6 6 0 1 0 0 8.56L30 35.34V36a6 6 0 1 0 6-6Z" })),
    'share-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 30a6 6 0 0 0-4.2 1.72L16 24.68v-1.34l15.8-7.06A6 6 0 1 0 30 12v.68l-15.8 7.04a6 6 0 1 0 0 8.56L30 35.34V36a6 6 0 1 0 6-6Zm0-20a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM10 26a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm26 12a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'shield-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 43.7a4 4 0 0 1-2-.5l-.6-.34A30.34 30.34 0 0 1 6 16.46v-.28a4 4 0 0 1 2-3.5L22 4.8a4 4 0 0 1 4 0l14 7.88a4 4 0 0 1 2 3.5v.28a30.34 30.34 0 0 1-15.44 26.4l-.6.34a4 4 0 0 1-1.96.5Z" })),
    'shield-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M7.46 13.12A4 4 0 0 0 6 16.18v.28a30.34 30.34 0 0 0 15.44 26.4l.6.34a4 4 0 0 0 4 0l.6-.34a30.442 30.442 0 0 0 6-4.54L7.46 13.12ZM37.68 32A30.16 30.16 0 0 0 42 16.46v-.28a4 4 0 0 0-2-3.5L26 4.8a4 4 0 0 0-4 0l-7.36 4.18L37.68 32ZM9.42 6.58a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-32-32Z" })),
    'shield-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9.42 6.58a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-32-32ZM24.6 39.36l-.6.34-.6-.34A26.3 26.3 0 0 1 10 16.46v-.28l.32-.18-2.86-2.88A4 4 0 0 0 6 16.18v.28a30.34 30.34 0 0 0 15.44 26.4l.6.34a4 4 0 0 0 4 0l.6-.34a30.442 30.442 0 0 0 6-4.54l-2.84-2.84a25.118 25.118 0 0 1-5.2 3.88ZM40 12.68 26 4.8a4 4 0 0 0-4 0l-7.36 4.18L17.56 12 24 8.3l14 7.88v.28a26 26 0 0 1-3.26 12.62L37.68 32A30.16 30.16 0 0 0 42 16.46v-.28a4 4 0 0 0-2-3.5Z" })),
    'shield-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 43.7a4 4 0 0 1-2-.5l-.6-.34A30.34 30.34 0 0 1 6 16.46v-.28a4 4 0 0 1 2-3.5L22 4.8a4 4 0 0 1 4 0l14 7.88a4 4 0 0 1 2 3.5v.28a30.34 30.34 0 0 1-15.44 26.4l-.6.34a4 4 0 0 1-1.96.5Zm0-35.4-14 7.88v.28a26.3 26.3 0 0 0 13.4 22.9l.6.34.6-.34A26.3 26.3 0 0 0 38 16.46v-.28L24 8.3Z" })),
    'shopping-bag-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.24 13.42-5.66-5.66A6 6 0 0 0 30.34 6H17.66a6 6 0 0 0-4.24 1.76l-5.66 5.66A6 6 0 0 0 6 17.66V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V17.66a6 6 0 0 0-1.76-4.24ZM24 32a8 8 0 0 1-8-8 2 2 0 0 1 4 0 4 4 0 1 0 8 0 2 2 0 0 1 4 0 8 8 0 0 1-8 8ZM12.82 14l3.42-3.42a2.1 2.1 0 0 1 1.42-.58h12.68a2.1 2.1 0 0 1 1.42.58L35.18 14H12.82Z" })),
    'shopping-bag-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.24 13.42-5.66-5.66A6 6 0 0 0 30.34 6H17.66a6 6 0 0 0-4.24 1.76l-5.66 5.66A6 6 0 0 0 6 17.66V36a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V17.66a6 6 0 0 0-1.76-4.24Zm-24-2.84a2.1 2.1 0 0 1 1.42-.58h12.68a2.1 2.1 0 0 1 1.42.58L35.18 14H12.82l3.42-3.42ZM36 38H12a2 2 0 0 1-2-2V18h28v18a2 2 0 0 1-2 2Z" }),
        React.createElement("path", { d: "M30 22a2 2 0 0 0-2 2 4 4 0 1 1-8 0 2 2 0 0 0-4 0 8 8 0 0 0 16 0 2 2 0 0 0-2-2Z" })),
    'shopping-cart-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.16 14a4 4 0 0 0-3.4-2h-25.6L12 7.48A2 2 0 0 0 10 6H6a2 2 0 0 0 0 4h2.48L14 30.52A2 2 0 0 0 16 32h18a2 2 0 0 0 1.78-1.1l6.56-13.12a4 4 0 0 0-.18-3.78ZM15 42a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM35 42a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'shopping-cart-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.16 14a4 4 0 0 0-3.4-2h-25.6L12 7.48A2 2 0 0 0 10 6H6a2 2 0 0 0 0 4h2.48L14 30.52A2 2 0 0 0 16 32h18a2 2 0 0 0 1.78-1.1l6.56-13.12a4 4 0 0 0-.18-3.78Zm-9.4 14H17.52l-3.26-12h24.5l-6 12ZM15 42a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM35 42a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'shuffle-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.42 28.58a2.008 2.008 0 1 0-2.84 2.84l.58.58H32a8 8 0 1 1 0-16h3.18l-.6.58A2 2 0 0 0 36 20a2 2 0 0 0 1.42-.58l4-4A1.999 1.999 0 0 0 42 14a2 2 0 0 0-.58-1.42l-4-4a2.009 2.009 0 0 0-2.84 2.84l.58.58H32a12 12 0 0 0-10 5.38A12 12 0 0 0 12 12H8a2 2 0 1 0 0 4h4a8 8 0 0 1 0 16H8a2 2 0 1 0 0 4h4a11.999 11.999 0 0 0 10-5.38A12 12 0 0 0 32 36h3.18l-.6.58A2 2 0 0 0 36 40a2 2 0 0 0 1.42-.58l4-4A1.999 1.999 0 0 0 42 34a2 2 0 0 0-.58-1.42l-4-4Z" })),
    'shuffle-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.42 28.58a2.008 2.008 0 1 0-2.84 2.84l.58.58H32a8 8 0 1 1 0-16h3.18l-.6.58A2 2 0 0 0 36 20a2 2 0 0 0 1.42-.58l4-4A1.999 1.999 0 0 0 42 14a2 2 0 0 0-.58-1.42l-4-4a2.009 2.009 0 0 0-2.84 2.84l.58.58H32a12 12 0 0 0-10 5.38A12 12 0 0 0 12 12H8a2 2 0 1 0 0 4h4a8 8 0 0 1 0 16H8a2 2 0 1 0 0 4h4a11.999 11.999 0 0 0 10-5.38A12 12 0 0 0 32 36h3.18l-.6.58A2 2 0 0 0 36 40a2 2 0 0 0 1.42-.58l4-4A1.999 1.999 0 0 0 42 34a2 2 0 0 0-.58-1.42l-4-4Z" })),
    'shuffle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 18.62a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2h-8.6a2 2 0 0 0 0 4h3.78L24 21.18 12.32 9.52a2 2 0 0 0-2.82 2.82L21.16 24 8.58 36.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L36 14.84v3.78Z" }),
        React.createElement("path", { d: "M38 27.36a2 2 0 0 0-2 2v3.82l-5.56-5.58a2.008 2.008 0 1 0-2.84 2.84L33.14 36h-3.76a2 2 0 0 0 0 4H38a2 2 0 0 0 2-2.22v-8.42a2 2 0 0 0-2-2Z" })),
    'shuffle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 18.62a2 2 0 0 0 4 0V10a2 2 0 0 0-2-2h-8.6a2 2 0 0 0 0 4h3.78L24 21.18 12.32 9.52a2 2 0 0 0-2.82 2.82L21.16 24 8.58 36.58a2 2 0 0 0 0 2.84 2 2 0 0 0 2.84 0L36 14.84v3.78Z" }),
        React.createElement("path", { d: "M38 27.36a2 2 0 0 0-2 2v3.82l-5.56-5.58a2.008 2.008 0 1 0-2.84 2.84L33.14 36h-3.76a2 2 0 0 0 0 4H38a2 2 0 0 0 2-2.22v-8.42a2 2 0 0 0-2-2Z" })),
    'skip-back-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.9 12.4a4.2 4.2 0 0 0-4.42.52l-10.2 8.42-.28.3V14a2 2 0 0 0-4 0v20a2 2 0 1 0 4 0v-7.64l.28.3 10.2 8.42a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18V15.58a3.52 3.52 0 0 0-2.1-3.18Z" })),
    'skip-back-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.9 12.4a4.2 4.2 0 0 0-4.42.52l-10.2 8.42-.28.3V14a2 2 0 0 0-4 0v20a2 2 0 1 0 4 0v-7.64l.28.3 10.2 8.42a4.12 4.12 0 0 0 2.6.92 4.46 4.46 0 0 0 1.82-.4 3.52 3.52 0 0 0 2.1-3.18V15.58a3.52 3.52 0 0 0-2.1-3.18ZM31 32l-9.64-8L31 16.18V32Z" })),
    'skip-forward-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 12a2 2 0 0 0-2 2v7.64l-.28-.3-10.2-8.42a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2 3.18v16.84a3.52 3.52 0 0 0 2 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92l10.2-8.42.28-.3V34a2 2 0 1 0 4 0V14a2 2 0 0 0-2-2Z" })),
    'skip-forward-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 12a2 2 0 0 0-2 2v7.64l-.28-.3-10.2-8.42a4.2 4.2 0 0 0-4.42-.52 3.52 3.52 0 0 0-2 3.18v16.84a3.52 3.52 0 0 0 2 3.18 4.46 4.46 0 0 0 1.82.4 4.12 4.12 0 0 0 2.6-.92l10.2-8.42.28-.3V34a2 2 0 1 0 4 0V14a2 2 0 0 0-2-2ZM17 31.82V16l9.64 8L17 31.82Z" })),
    'slash-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm16 20a15.84 15.84 0 0 1-3.38 9.8L14.2 11.38A15.84 15.84 0 0 1 24 8a16 16 0 0 1 16 16ZM8 24a15.84 15.84 0 0 1 3.38-9.8L33.8 36.62A15.84 15.84 0 0 1 24 40 16 16 0 0 1 8 24Z" })),
    'slash-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm16 20a15.84 15.84 0 0 1-3.38 9.8L14.2 11.38A15.84 15.84 0 0 1 24 8a16 16 0 0 1 16 16ZM8 24a15.84 15.84 0 0 1 3.38-9.8L33.8 36.62A15.84 15.84 0 0 1 24 40 16 16 0 0 1 8 24Z" })),
    'smartphone-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 4H14a6 6 0 0 0-6 6v28a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V10a6 6 0 0 0-6-6ZM24 36a3 3 0 1 1 0-5.999A3 3 0 0 1 24 36Zm5-20H19a2 2 0 0 1 0-4h10a2 2 0 0 1 0 4Z" })),
    'smartphone-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 4H14a6 6 0 0 0-6 6v28a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V10a6 6 0 0 0-6-6Zm2 34a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2v28Z" }),
        React.createElement("path", { d: "M24 36a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM29 12H19a2 2 0 0 0 0 4h10a2 2 0 0 0 0-4Z" })),
    'speaker-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 34a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM24 18a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M34 4H14a6 6 0 0 0-6 6v28a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V10a6 6 0 0 0-6-6Zm-10 6a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm0 28a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z" })),
    'speaker-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 22a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0-8a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM24 24a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 10a3 3 0 1 1 0-5.999A3 3 0 0 1 24 34Z" }),
        React.createElement("path", { d: "M34 4H14a6 6 0 0 0-6 6v28a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V10a6 6 0 0 0-6-6Zm2 34a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2v28Z" })),
    'square-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 42H12a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6h24a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6ZM12 10a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2V12a2 2 0 0 0-2-2H12Z" })),
    'square-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 42H12a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6h24a6 6 0 0 1 6 6v24a6 6 0 0 1-6 6ZM12 10a2 2 0 0 0-2 2v24a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2V12a2 2 0 0 0-2-2H12Z" })),
    'star-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.12 42a2 2 0 0 1-.92-.22L24 36.44l-10.2 5.34a2 2 0 0 1-2.9-2.12l2-11.26-8.24-8a2 2 0 0 1-.5-2 2 2 0 0 1 1.62-1.36l11.4-1.66L22.2 5.12a2 2 0 0 1 3.6 0l5.08 10.24 11.4 1.66a2 2 0 0 1 1.62 1.36 2.001 2.001 0 0 1-.5 2l-8.24 8 2 11.26a2 2 0 0 1-.8 2 2 2 0 0 1-1.24.36Z" })),
    'star-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.12 42a2 2 0 0 1-.92-.22L24 36.44l-10.2 5.34a1.999 1.999 0 0 1-2.9-2.12l2-11.26-8.24-8a2 2 0 0 1-.5-2 2 2 0 0 1 1.62-1.36l11.4-1.66L22.2 5.12a2 2 0 0 1 3.6 0l5.08 10.24 11.4 1.66a2 2 0 0 1 1.62 1.36 2.001 2.001 0 0 1-.5 2l-8.24 8 2 11.26a2 2 0 0 1-.8 2 2 2 0 0 1-1.24.36ZM24 32.2c.32-.008.638.068.92.22l7.54 4L31.02 28a2 2 0 0 1 .58-1.78l6-5.86-8.4-1.24A2 2 0 0 1 27.78 18L24 10.5 20.22 18a2 2 0 0 1-1.5 1.08l-8.4 1.24 6 5.86a2 2 0 0 1 .58 1.78l-1.44 8.32 7.54-4c.32-.119.666-.146 1-.08Z" })),
    'stop-circle-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm8 25.5a2.5 2.5 0 0 1-2.5 2.5h-11a2.5 2.5 0 0 1-2.5-2.5v-11a2.5 2.5 0 0 1 2.5-2.5h11a2.5 2.5 0 0 1 2.5 2.5v11Z" }),
        React.createElement("path", { d: "M28 20h-8v8h8v-8Z" })),
    'stop-circle-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4a20 20 0 1 0 0 40 20 20 0 0 0 0-40Zm0 36a16 16 0 1 1 0-32 16 16 0 0 1 0 32Z" }),
        React.createElement("path", { d: "M29.5 16h-11a2.5 2.5 0 0 0-2.5 2.5v11a2.5 2.5 0 0 0 2.5 2.5h11a2.5 2.5 0 0 0 2.5-2.5v-11a2.5 2.5 0 0 0-2.5-2.5ZM28 28h-8v-8h8v8Z" })),
    'sun-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 12a2 2 0 0 0 2-2V6a2 2 0 1 0-4 0v4a2 2 0 0 0 2 2ZM42 22h-4a2 2 0 0 0 0 4h4a2 2 0 0 0 0-4ZM12 24a2 2 0 0 0-2-2H6a2 2 0 1 0 0 4h4a2 2 0 0 0 2-2ZM12.44 10a2.023 2.023 0 0 0-2.78 2.94l2.88 2.78a2 2 0 0 0 1.46.56 2 2 0 0 0 1.44-.62 2 2 0 0 0 0-2.82l-3-2.84ZM34 16.28a2 2 0 0 0 1.38-.56l2.88-2.78a2 2 0 0 0-2.7-2.94l-2.88 2.84a2 2 0 0 0 0 2.82 2 2 0 0 0 1.32.62ZM24 36a2 2 0 0 0-2 2v4a2 2 0 0 0 4 0v-4a2 2 0 0 0-2-2ZM35.46 32.28a2.001 2.001 0 1 0-2.78 2.88L35.56 38a2 2 0 0 0 2.16.406 2 2 0 0 0 .66-.446 2 2 0 0 0 0-2.84l-2.92-2.84ZM12.54 32.28l-2.88 2.78a2 2 0 0 0 0 2.84 2 2 0 0 0 1.44.6 2 2 0 0 0 1.34-.5l2.88-2.78a2.002 2.002 0 0 0-2.78-2.88v-.06ZM24 16a8 8 0 1 0 0 16.001A8 8 0 0 0 24 16Z" })),
    'sun-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 12a2 2 0 0 0 2-2V6a2 2 0 1 0-4 0v4a2 2 0 0 0 2 2ZM42 22h-4a2 2 0 0 0 0 4h4a2 2 0 0 0 0-4ZM12 24a2 2 0 0 0-2-2H6a2 2 0 1 0 0 4h4a2 2 0 0 0 2-2ZM12.44 10a2.023 2.023 0 0 0-2.78 2.94l2.88 2.78a2 2 0 0 0 1.46.56 2 2 0 0 0 1.44-.62 2 2 0 0 0 0-2.82l-3-2.84ZM34 16.28a2 2 0 0 0 1.38-.56l2.88-2.78a2 2 0 0 0-2.7-2.94l-2.88 2.84a2 2 0 0 0 0 2.82 2 2 0 0 0 1.32.62ZM24 36a2 2 0 0 0-2 2v4a2 2 0 0 0 4 0v-4a2 2 0 0 0-2-2ZM35.46 32.28a2.002 2.002 0 1 0-2.78 2.88L35.56 38a2 2 0 0 0 2.82-.04 2.002 2.002 0 0 0 0-2.84l-2.92-2.84ZM12.54 32.28l-2.88 2.78a2 2 0 0 0 0 2.84 2 2 0 0 0 1.44.6 2 2 0 0 0 1.34-.5l2.88-2.78a2.002 2.002 0 0 0-2.78-2.88v-.06ZM24 16a8 8 0 1 0 0 16.001A8 8 0 0 0 24 16Zm0 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z" })),
    'swap-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 18h26l-3.2 2.4a2 2 0 0 0 2.4 3.2l8-6a1.999 1.999 0 0 0 0-3.18l-7.72-6a2.002 2.002 0 1 0-2.46 3.16L34.16 14H8a2 2 0 1 0 0 4ZM40 32H14l3.2-2.4a2 2 0 0 0-2.4-3.2l-8 6a2 2 0 0 0 0 3.18l7.72 6a2 2 0 0 0 1.22.42 2 2 0 0 0 1.58-.78 2 2 0 0 0-.34-2.8L13.84 36H40a2 2 0 0 0 0-4Z" })),
    'swap-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 18h26l-3.2 2.4a2 2 0 0 0 2.4 3.2l8-6a1.999 1.999 0 0 0 0-3.18l-7.72-6a2.002 2.002 0 1 0-2.46 3.16L34.16 14H8a2 2 0 1 0 0 4ZM40 32H14l3.2-2.4a2 2 0 0 0-2.4-3.2l-8 6a2 2 0 0 0 0 3.18l7.72 6a2 2 0 0 0 1.22.42 2 2 0 0 0 1.58-.78 2 2 0 0 0-.34-2.8L13.84 36H40a2 2 0 0 0 0-4Z" })),
    'sync-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.32 20.74c.067-.118.114-.246.14-.38l1.5-8a2.032 2.032 0 0 0-4-.72l-.74 4a18.44 18.44 0 0 0-33.16 1.68 2 2 0 0 0 1.1 2.6 1.999 1.999 0 0 0 2.62-1.1A14.16 14.16 0 0 1 24.14 10a14.34 14.34 0 0 1 12.48 7.16l-3.3-.54a2.025 2.025 0 0 0-.64 4l8.5 1.42h.32a1.86 1.86 0 0 0 .68-.12.658.658 0 0 0 .2-.12 1.56 1.56 0 0 0 .4-.22l.16-.2c.104-.097.198-.204.28-.32a1.16 1.16 0 0 0 .1-.32ZM39.76 28.14a2 2 0 0 0-2.62 1.12A14.16 14.16 0 0 1 23.86 38a14.34 14.34 0 0 1-12.48-7.16l3.3.54H15a2.006 2.006 0 1 0 .32-4L6.82 26a1.821 1.821 0 0 0-.66 0H6a2.3 2.3 0 0 0-.64.28 2.003 2.003 0 0 0-.36.36l-.18.2c-.062.12-.109.248-.14.38a.88.88 0 0 0-.14.34l-1.5 8A2 2 0 0 0 4.64 38H5a2 2 0 0 0 2-1.64l.74-4A18.44 18.44 0 0 0 40.9 30.7a2 2 0 0 0-1.14-2.56Z" })),
    'sync-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.32 20.74c.067-.118.114-.246.14-.38l1.5-8a2.032 2.032 0 0 0-4-.72l-.74 4a18.44 18.44 0 0 0-33.16 1.68 2 2 0 0 0 1.1 2.6 1.999 1.999 0 0 0 2.62-1.1A14.16 14.16 0 0 1 24.14 10a14.34 14.34 0 0 1 12.48 7.16l-3.3-.54a2.026 2.026 0 0 0-.64 4l8.5 1.42h.32a1.86 1.86 0 0 0 .68-.12.66.66 0 0 0 .2-.12c.143-.053.278-.128.4-.22l.16-.2c.104-.097.198-.204.28-.32.049-.101.082-.21.1-.32ZM39.76 28.14a2 2 0 0 0-2.62 1.12A14.16 14.16 0 0 1 23.86 38a14.34 14.34 0 0 1-12.48-7.16l3.3.54H15a2.006 2.006 0 1 0 .32-4L6.82 26a1.821 1.821 0 0 0-.66 0H6a2.3 2.3 0 0 0-.64.28 2.003 2.003 0 0 0-.36.36l-.18.2c-.062.12-.109.248-.14.38a.88.88 0 0 0-.14.34l-1.5 8A2 2 0 0 0 4.64 38H5a2 2 0 0 0 2-1.64l.74-4A18.44 18.44 0 0 0 40.9 30.7a2 2 0 0 0-1.14-2.56Z" })),
    'text-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 8H8a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0v-4h12v26h-4a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4h-4V12h12v4a2 2 0 0 0 4 0v-6a2 2 0 0 0-2-2Z" })),
    'text-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 8H8a2 2 0 0 0-2 2v6a2 2 0 0 0 4 0v-4h12v26h-4a2 2 0 0 0 0 4h12a2 2 0 0 0 0-4h-4V12h12v4a2 2 0 0 0 4 0v-6a2 2 0 0 0-2-2Z" })),
    'thermometer-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm2-24.92V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 24 8a2 2 0 0 0-2 2v9.08h4Z" })),
    'thermometer-minus-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 10H6a2 2 0 1 0 0 4h8a2 2 0 1 0 0-4ZM28 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm2-24.92V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 28 8a2 2 0 0 0-2 2v9.08h4Z" })),
    'thermometer-minus-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 10H6a2 2 0 1 0 0 4h8a2 2 0 1 0 0-4ZM28 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm0-36a2 2 0 0 0-2 2v17.08a1.999 1.999 0 0 1-1 1.74A6 6 0 0 0 22 34a6 6 0 1 0 12 0 6 6 0 0 0-3-5.18 2 2 0 0 1-1-1.74V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 28 8Z" })),
    'thermometer-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm0-36a2 2 0 0 0-2 2v17.08a2 2 0 0 1-1 1.74A6 6 0 0 0 18 34a6 6 0 1 0 12 0 6 6 0 0 0-3-5.18 2 2 0 0 1-1-1.74V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 24 8Z" })),
    'thermometer-plus-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 10H6a2 2 0 1 0 0 4h8a2 2 0 1 0 0-4Z" }),
        React.createElement("path", { d: "M8 8v8a2 2 0 1 0 4 0V8a2 2 0 1 0-4 0ZM28 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm2-24.92V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 28 8a2 2 0 0 0-2 2v9.08h4Z" })),
    'thermometer-plus-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 10H6a2 2 0 1 0 0 4h8a2 2 0 1 0 0-4Z" }),
        React.createElement("path", { d: "M8 8v8a2 2 0 1 0 4 0V8a2 2 0 1 0-4 0ZM28 44a10 10 0 0 1-6-18V10a6 6 0 1 1 12 0v16a10 10 0 0 1-6 18Zm0-36a2 2 0 0 0-2 2v17.08a1.999 1.999 0 0 1-1 1.74A6 6 0 0 0 22 34a6 6 0 1 0 12 0 6 6 0 0 0-3-5.18 2 2 0 0 1-1-1.74V10a1.86 1.86 0 0 0-.58-1.38A2 2 0 0 0 28 8Z" })),
    'thumb-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20.572 7.188A2 2 0 0 1 22.4 6c2.576 0 4.41 1.007 5.525 2.559C28.957 9.996 29.2 11.663 29.2 12.8v4.4h6.138c3.677 0 6.489 3.278 5.93 6.912l-1.97 12.8A6 6 0 0 1 33.369 42H12a6 6 0 0 1-6-6v-9.6a6 6 0 0 1 6-6h2.7l5.872-13.212ZM14 24.4h-2a2 2 0 0 0-2 2V36a2 2 0 0 0 2 2h2V24.4ZM18 38h15.368a2 2 0 0 0 1.977-1.696l1.97-12.8a2 2 0 0 0-1.977-2.304H25.2v-8.4c0-.663-.157-1.396-.525-1.909-.183-.255-.482-.55-1.048-.728L18 22.824V38Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'toggle-left-fill': React.createElement(React.Fragment, null,
        React.createElement("g", { clipPath: "url(#a)" },
            React.createElement("path", { d: "M30 10H18a14 14 0 0 0 0 28h12a14 14 0 0 0 0-28ZM18 30a6 6 0 1 1 0-12 6 6 0 0 1 0 12Z" }),
            React.createElement("path", { d: "M18 22a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: "a" },
                React.createElement("path", { d: "M0 0H48V48H0z" })))),
    'toggle-left-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30 10H18a14 14 0 0 0 0 28h12a14 14 0 0 0 0-28Zm0 24H18a10 10 0 0 1 0-20h12a10 10 0 0 1 0 20Z" }),
        React.createElement("path", { d: "M18 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'toggle-right-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30 26a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M30 10H18a14 14 0 0 0 0 28h12a14 14 0 0 0 0-28Zm0 20a6 6 0 1 1 0-12 6 6 0 0 1 0 12Z" })),
    'toggle-right-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30 10H18a14 14 0 0 0 0 28h12a14 14 0 0 0 0-28Zm0 24H18a10 10 0 0 1 0-20h12a10 10 0 0 1 0 20Z" }),
        React.createElement("path", { d: "M30 18a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'trash-2-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12H32V8.66A4.84 4.84 0 0 0 27 4h-6a4.84 4.84 0 0 0-5 4.66V12H6a2 2 0 1 0 0 4h2v22a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16h2a2 2 0 0 0 0-4ZM20 32a2 2 0 1 1-4 0v-8a2 2 0 0 1 4 0v8Zm0-23.34c0-.32.42-.66 1-.66h6c.58 0 1 .34 1 .66V12h-8V8.66ZM32 32a2 2 0 1 1-4 0v-8a2 2 0 0 1 4 0v8Z" })),
    'trash-2-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12H32V8.66A4.84 4.84 0 0 0 27 4h-6a4.84 4.84 0 0 0-5 4.66V12H6a2 2 0 1 0 0 4h2v22a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16h2a2 2 0 0 0 0-4ZM20 8.66c0-.32.42-.66 1-.66h6c.58 0 1 .34 1 .66V12h-8V8.66ZM36 38a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V16h24v22Z" }),
        React.createElement("path", { d: "M18 34a2 2 0 0 0 2-2v-8a2 2 0 0 0-4 0v8a2 2 0 0 0 2 2ZM30 34a2 2 0 0 0 2-2v-8a2 2 0 0 0-4 0v8a2 2 0 0 0 2 2Z" })),
    'trash-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12H32V8.66A4.84 4.84 0 0 0 27 4h-6a4.84 4.84 0 0 0-5 4.66V12H6a2 2 0 1 0 0 4h2v22a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16h2a2 2 0 0 0 0-4ZM20 8.66c0-.32.42-.66 1-.66h6c.58 0 1 .34 1 .66V12h-8V8.66Z" })),
    'trash-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 12H32V8.66A4.84 4.84 0 0 0 27 4h-6a4.84 4.84 0 0 0-5 4.66V12H6a2 2 0 1 0 0 4h2v22a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V16h2a2 2 0 0 0 0-4ZM20 8.66c0-.32.42-.66 1-.66h6c.58 0 1 .34 1 .66V12h-8V8.66ZM36 38a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V16h24v22Z" })),
    'trending-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 24a2 2 0 0 0-4 0v4.6l-8.48-10a2 2 0 0 0-2.54-.42l-8.54 5.22-8.9-10.68a2.003 2.003 0 0 0-3.08 2.56l10 12a2 2 0 0 0 2.56.44l8.56-5.14 8 9.42H30a2 2 0 0 0 0 4h10c.246-.006.49-.053.72-.14l.28-.16c.104-.053.205-.113.3-.18a1.5 1.5 0 0 0 .28-.34 2.2 2.2 0 0 0 .18-.28c.049-.108.083-.223.1-.34A1.56 1.56 0 0 0 42 34V24Z" })),
    'trending-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 24a2 2 0 0 0-4 0v4.6l-8.48-10a2 2 0 0 0-2.54-.42l-8.54 5.22-8.9-10.68a2.003 2.003 0 0 0-3.08 2.56l10 12a2 2 0 0 0 2.56.44l8.56-5.14 8 9.42H30a2 2 0 0 0 0 4h10c.246-.006.49-.053.72-.14l.28-.16c.104-.053.205-.113.3-.18a1.5 1.5 0 0 0 .28-.34 2.2 2.2 0 0 0 .18-.28c.049-.108.083-.223.1-.34A1.56 1.56 0 0 0 42 34V24Z" })),
    'trending-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 14c.019-.14.019-.28 0-.42a1.28 1.28 0 0 0-.1-.34 2.2 2.2 0 0 0-.18-.28 1.498 1.498 0 0 0-.28-.34l-.24-.14a1.38 1.38 0 0 0-.38-.2h-.4A1.4 1.4 0 0 0 40 12H30a2 2 0 0 0 0 4h5.66l-8 9.42-8.64-5.14a2 2 0 0 0-2.56.44l-10 12A2 2 0 0 0 8 36a2 2 0 0 0 1.54-.72l8.9-10.68 8.54 5.12a2 2 0 0 0 2.54-.42L38 19.4V24a2 2 0 0 0 4 0V14Z" })),
    'trending-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 14c.019-.14.019-.28 0-.42a1.277 1.277 0 0 0-.1-.34 2.2 2.2 0 0 0-.18-.28 1.498 1.498 0 0 0-.28-.34l-.24-.14a1.378 1.378 0 0 0-.38-.2h-.4A1.4 1.4 0 0 0 40 12H30a2 2 0 0 0 0 4h5.66l-8 9.42-8.64-5.14a2 2 0 0 0-2.56.44l-10 12A2 2 0 0 0 8 36a2 2 0 0 0 1.54-.72l8.9-10.68 8.54 5.12a2 2 0 0 0 2.54-.42L38 19.4V24a2 2 0 0 0 4 0V14Z" })),
    'tv-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 12h-7.18l4.6-4.58a2.007 2.007 0 1 0-2.84-2.84L24 11.18l-6.58-6.6a2.009 2.009 0 0 0-2.84 2.84l4.6 4.58H12a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V18a6 6 0 0 0-6-6Zm2 26a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V24a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v14Z" })),
    'tv-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 12h-7.18l4.6-4.58a2.007 2.007 0 1 0-2.84-2.84L24 11.18l-6.58-6.6a2.009 2.009 0 0 0-2.84 2.84l4.6 4.58H12a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h24a6 6 0 0 0 6-6V18a6 6 0 0 0-6-6Zm2 26a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V18a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v20Z" })),
    'twitter-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16.16 40a22.139 22.139 0 0 0 22.88-22A16.179 16.179 0 0 0 42 12.32a.879.879 0 0 0-1.24-1.02 3.76 3.76 0 0 1-4.32-.76 7.78 7.78 0 0 0-11.16-.34 8.26 8.26 0 0 0-2.3 7.8c-6.7.4-11.3-2.78-14.98-7.14a.86.86 0 0 0-1.5.48 19.36 19.36 0 0 0 9.2 20.1A13.46 13.46 0 0 1 6.76 36a.9.9 0 0 0-.28 1.68A21.999 21.999 0 0 0 16.16 40Z" })),
    'twitter-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.02 40h-.16a21.74 21.74 0 0 1-9.3-2.18A2.76 2.76 0 0 1 6 34.94a2.82 2.82 0 0 1 2.32-2.36 13.26 13.26 0 0 0 5.08-1.78 18.98 18.98 0 0 1-7.02-18.14 2.82 2.82 0 0 1 2-2.3 2.7 2.7 0 0 1 2.86.82 14.18 14.18 0 0 0 9.76 5.5 9 9 0 0 1 2.82-6.2 8.94 8.94 0 0 1 12.74.38 1.4 1.4 0 0 0 1.56.2 2.78 2.78 0 0 1 3.88 3.2 13.32 13.32 0 0 1-2.56 5.2A21.58 21.58 0 0 1 17.02 40Zm0-4h.16a17.579 17.579 0 0 0 18.18-17.18 2.64 2.64 0 0 1 .74-1.7c.488-.617.904-1.289 1.24-2a5.118 5.118 0 0 1-3.82-1.7A4.9 4.9 0 0 0 30 12a5 5 0 0 0-3.58 1.38 5.06 5.06 0 0 0-1.44 4.84l.52 2.28-2.34.16a16.6 16.6 0 0 1-13.08-4.8 14.24 14.24 0 0 0 7.46 12.92l1.9 1.08-1.26 1.8a11.24 11.24 0 0 1-5.36 3.84c1.367.343 2.771.51 4.18.5h.02Z" })),
    'umbrella-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4A20 20 0 0 0 4 24a2 2 0 0 0 2 2h16v12a6 6 0 1 0 12 0 2 2 0 0 0-4 0 2 2 0 0 1-4 0V26h16a2 2 0 0 0 2-2A20 20 0 0 0 24 4Z" })),
    'umbrella-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4A20 20 0 0 0 4 24a2 2 0 0 0 2 2h16v12a6 6 0 1 0 12 0 2 2 0 0 0-4 0 2 2 0 0 1-4 0V26h16a2 2 0 0 0 2-2A20 20 0 0 0 24 4ZM8.12 22a16 16 0 0 1 31.76 0H8.12Z" })),
    'undo-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.44 42a2 2 0 0 1-2-1.52 17.82 17.82 0 0 0-15.6-13.38v2.24a3.56 3.56 0 0 1-2.18 3.28 4 4 0 0 1-4.3-.62L6.24 23.18a3.52 3.52 0 0 1 0-5.36l10.12-8.84a4 4 0 0 1 4.36-.6 3.56 3.56 0 0 1 2.18 3.28V14A21.78 21.78 0 0 1 43 35.5a20.58 20.58 0 0 1-.62 4.98 2 2 0 0 1-2 1.52h.06Z" })),
    'undo-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.44 42a2 2 0 0 1-2-1.52 17.821 17.821 0 0 0-15.6-13.38v2.24a3.56 3.56 0 0 1-2.18 3.28 4 4 0 0 1-4.3-.62L6.24 23.18a3.52 3.52 0 0 1 0-5.36l10.12-8.84a4 4 0 0 1 4.36-.6 3.56 3.56 0 0 1 2.18 3.28V14A21.78 21.78 0 0 1 43 35.5a20.585 20.585 0 0 1-.62 4.98 2 2 0 0 1-2 1.52h.06ZM20.9 23a22.14 22.14 0 0 1 17.62 8.52A18 18 0 0 0 20.9 18a2 2 0 0 1-2-2v-3.84L9.26 20.5l9.64 8.42v-4a2 2 0 0 1 2-1.92Z" })),
    'unlock-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 32a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M34 16H20v-4a4 4 0 1 1 8 0 2 2 0 0 0 4 0 8 8 0 0 0-16 0v4h-2a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V22a6 6 0 0 0-6-6ZM24 36a6 6 0 1 1 0-12 6 6 0 0 1 0 12Z" })),
    'unlock-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 16H20v-4a4 4 0 1 1 8 0 2 2 0 0 0 4 0 8 8 0 0 0-16 0v4h-2a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h20a6 6 0 0 0 6-6V22a6 6 0 0 0-6-6Zm2 22a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V22a2 2 0 0 1 2-2h20a2 2 0 0 1 2 2v16Z" }),
        React.createElement("path", { d: "M24 24a6 6 0 1 0 0 12 6 6 0 0 0 0-12Zm0 8a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" })),
    'upload-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 12h28a2 2 0 1 0 0-4H10a2 2 0 1 0 0 4Z" }),
        React.createElement("path", { d: "M40 14v-4a2 2 0 1 0-4 0v4a2 2 0 1 0 4 0ZM12 14v-4a2 2 0 1 0-4 0v4a2 2 0 1 0 4 0ZM16 28a2 2 0 0 1-1.2-3.6l8-6a2 2 0 0 1 2.36 0l8 5.64a2 2 0 1 1-2.32 3.26L24 22.48l-6.8 5.12a2 2 0 0 1-1.2.4Z" }),
        React.createElement("path", { d: "M24 42a2 2 0 0 1-2-2V24a2 2 0 0 1 4 0v16a2 2 0 0 1-2 2Z" })),
    'upload-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10 12h28a2 2 0 1 0 0-4H10a2 2 0 1 0 0 4Z" }),
        React.createElement("path", { d: "M40 14v-4a2 2 0 1 0-4 0v4a2 2 0 1 0 4 0ZM12 14v-4a2 2 0 1 0-4 0v4a2 2 0 1 0 4 0ZM16 28a2 2 0 0 1-1.2-3.6l8-6a2 2 0 0 1 2.36 0l8 5.64a2 2 0 1 1-2.32 3.26L24 22.48l-6.8 5.12a2 2 0 0 1-1.2.4Z" }),
        React.createElement("path", { d: "M24 42a2 2 0 0 1-2-2V24a2 2 0 0 1 4 0v16a2 2 0 0 1-2 2Z" })),
    'video-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 14.3a3.4 3.4 0 0 0-3.7.6l-4.3 4V16a6 6 0 0 0-6-6H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h18a6 6 0 0 0 6-6v-2.9l4.32 4a3.48 3.48 0 0 0 2.32.9 3.36 3.36 0 0 0 1.38-.3 3.2 3.2 0 0 0 2-2.96V17.26A3.2 3.2 0 0 0 42 14.3Z" })),
    'video-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.44 34.1 9.76 15.42 6.24 12 6 11.6A6 6 0 0 0 4 16v16a6 6 0 0 0 6 6h18a5.88 5.88 0 0 0 3.32-1.02l-2.88-2.88ZM42 14.3a3.4 3.4 0 0 0-3.7.6l-4.3 4V16a6 6 0 0 0-6-6H15.66l2.58 2.58 13.18 13.18 4 4 4 4a3.36 3.36 0 0 0 2.58-.08 3.2 3.2 0 0 0 2-2.96V17.26a3.2 3.2 0 0 0-2-2.96ZM34 31.18l-4-4L16.82 14l-4-4-3.4-3.42a2.008 2.008 0 0 0-2.84 2.84l1.08 1.06L11.18 14l18.68 18.68 2.92 2.92 5.8 5.82a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84L34 31.18Z" })),
    'video-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m34 31.18-4-4L16.82 14l-4-4-3.4-3.42a2.008 2.008 0 0 0-2.84 2.84l1.08 1.06L11.18 14l18.68 18.68 2.92 2.92 5.8 5.82a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84L34 31.18ZM28 34H10a2 2 0 0 1-2-2V16a2 2 0 0 1 .8-1.56L6 11.6A6 6 0 0 0 4 16v16a6 6 0 0 0 6 6h18a5.88 5.88 0 0 0 3.32-1.02L28.28 34c-.093.01-.187.01-.28 0ZM42 14.3a3.4 3.4 0 0 0-3.7.6l-4.3 4V16a6 6 0 0 0-6-6H15.66l4 4H28a2 2 0 0 1 2 2v8.34l9.44 9.44a3.36 3.36 0 0 0 2.58-.08 3.2 3.2 0 0 0 2-2.96V17.26A3.2 3.2 0 0 0 42 14.3Zm-2 14.9L34.38 24 40 18.8v10.4Z" })),
    'video-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 14.3a3.4 3.4 0 0 0-3.7.6l-4.3 4V16a6 6 0 0 0-6-6H10a6 6 0 0 0-6 6v16a6 6 0 0 0 6 6h18a6 6 0 0 0 6-6v-2.9l4.32 4a3.48 3.48 0 0 0 2.32.9 3.36 3.36 0 0 0 1.38-.3 3.2 3.2 0 0 0 2-2.96V17.26A3.2 3.2 0 0 0 42 14.3ZM30 32a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V16a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v16Zm10-2.8L34.38 24 40 18.8v10.4Z" })),
    'volume-down-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.56 16.74a2.005 2.005 0 1 0-3.12 2.52 8 8 0 0 1 0 9.48A2 2 0 0 0 40 32a2.001 2.001 0 0 0 1.56-.74 12 12 0 0 0 0-14.52ZM32.94 6.24a2 2 0 0 0-2 0L18 15.14H8a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8A2.12 2.12 0 0 0 32 42a2 2 0 0 0 2-2V8a2.001 2.001 0 0 0-1.06-1.76Z" })),
    'volume-down-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.56 16.74a2.005 2.005 0 0 0-3.12 2.52 8 8 0 0 1 0 9.48A2 2 0 0 0 40 32a2 2 0 0 0 1.56-.74 12 12 0 0 0 0-14.52ZM32.94 6.24a2 2 0 0 0-2 0L18 15.14H8a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8A2.12 2.12 0 0 0 32 42a2 2 0 0 0 2-2V8a2.001 2.001 0 0 0-1.06-1.76ZM30 36.2l-10.2-7a2 2 0 0 0-1.14-.34H10v-9.72h8.66a2 2 0 0 0 1.14-.34l10.2-7v24.4Z" })),
    'volume-mute-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 42a2.12 2.12 0 0 1-1.14-.34L20 32.86H10a2 2 0 0 1-2-2V17.14a2 2 0 0 1 2-2h10l12.82-8.8A2 2 0 0 1 36 8v32a2 2 0 0 1-2 2Z" })),
    'volume-mute-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 42a2.12 2.12 0 0 1-1.14-.34L20 32.86H10a2 2 0 0 1-2-2V17.14a2 2 0 0 1 2-2h10l12.82-8.8A2 2 0 0 1 36 8v32a2 2 0 0 1-2 2ZM12 28.86h8.66a2 2 0 0 1 1.14.34l10.2 7V11.8l-10.2 7a2 2 0 0 1-1.14.34H12v9.72Z" })),
    'volume-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m33.82 28.16 2.88 2.88a12 12 0 0 0-.14-14.3 2.005 2.005 0 1 0-3.12 2.52 8 8 0 0 1 .38 8.9Z" }),
        React.createElement("path", { d: "M42 24a13.02 13.02 0 0 1-3.56 8.78l2.84 2.84A17.06 17.06 0 0 0 46 24a17.5 17.5 0 0 0-6.72-13.54 2.004 2.004 0 0 0-2.56 3.08A13.599 13.599 0 0 1 42 24ZM30 24.34V8a2 2 0 0 0-3.14-1.66L18 12.4l12 11.94ZM9.48 15.14H4a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8A2.12 2.12 0 0 0 28 42a2 2 0 0 0 2-2v-4.34L9.48 15.14ZM9.42 6.58a2.008 2.008 0 0 0-2.84 2.84l32 32a1.999 1.999 0 0 0 2.84 0 2 2 0 0 0 0-2.84l-32-32Z" })),
    'volume-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9.42 6.58a2.008 2.008 0 0 0-2.84 2.84l32 32a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84l-32-32ZM33.82 28.16l2.88 2.88a12 12 0 0 0-.14-14.3 2.005 2.005 0 0 0-3.12 2.52 8 8 0 0 1 .38 8.9Z" }),
        React.createElement("path", { d: "M42 24a13.02 13.02 0 0 1-3.56 8.78l2.84 2.84A17.06 17.06 0 0 0 46 24a17.5 17.5 0 0 0-6.72-13.54 2.002 2.002 0 1 0-2.56 3.08A13.599 13.599 0 0 1 42 24ZM27 36.2l-10.2-7a2 2 0 0 0-1.14-.34H7v-9.72h6.48l-4-4H5a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8c.34.22.735.338 1.14.34a2 2 0 0 0 2-2v-3.34l-4-4L27 36.2ZM27 11.8v9.54l4 4V8a2 2 0 0 0-3.14-1.66l-9.4 6.46 2.88 2.88L27 11.8Z" })),
    'volume-up-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.56 16.74a2.005 2.005 0 1 0-3.12 2.52 8 8 0 0 1 0 9.48A2 2 0 0 0 35 32a2.001 2.001 0 0 0 1.56-.74 12 12 0 0 0 0-14.52Z" }),
        React.createElement("path", { d: "M39.28 10.46a2.004 2.004 0 0 0-2.56 3.08A13.599 13.599 0 0 1 42 24a13.599 13.599 0 0 1-5.28 10.46A2 2 0 0 0 38 38a2 2 0 0 0 1.28-.46A17.5 17.5 0 0 0 46 24a17.5 17.5 0 0 0-6.72-13.54ZM28.94 6.24a2 2 0 0 0-2 0L14 15.14H4a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8A2.12 2.12 0 0 0 28 42a2 2 0 0 0 2-2V8a2.001 2.001 0 0 0-1.06-1.76Z" })),
    'volume-up-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.56 16.74a2.005 2.005 0 0 0-3.12 2.52 8 8 0 0 1 0 9.48A2 2 0 0 0 35 32a2 2 0 0 0 1.56-.74 12 12 0 0 0 0-14.52Z" }),
        React.createElement("path", { d: "M39.28 10.46a2.002 2.002 0 1 0-2.56 3.08A13.599 13.599 0 0 1 42 24a13.6 13.6 0 0 1-5.28 10.46A2 2 0 0 0 38 38a2 2 0 0 0 1.28-.46A17.499 17.499 0 0 0 46 24a17.5 17.5 0 0 0-6.72-13.54ZM30 6.24a2 2 0 0 0-2 0l-12.96 8.9h-10a2 2 0 0 0-2 2v13.72a2 2 0 0 0 2 2h10l12.82 8.8c.34.22.735.338 1.14.34a2 2 0 0 0 2-2V8a2 2 0 0 0-1-1.76Zm-2.94 30L16.8 29.2a2 2 0 0 0-1.14-.34H7v-9.72h8.66a2 2 0 0 0 1.14-.34l10.2-7 .06 24.44Z" })),
    'wait-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 4a2 2 0 0 1 2 2v7.885c0 .545.213 1.048.568 1.4l.019.02 3.678 3.741A6.002 6.002 0 0 1 20 23.27v1.46c0 1.571-.61 3.105-1.743 4.232l-3.67 3.734c-.384.39-.587.89-.587 1.42V42a2 2 0 1 1-4 0v-7.885c0-1.591.624-3.094 1.735-4.224l3.697-3.76c.355-.352.568-.855.568-1.4V23.27c0-.53-.203-1.029-.587-1.42l-3.67-3.733A5.968 5.968 0 0 1 10 13.885V6a2 2 0 0 1 2-2Zm24 0a2 2 0 0 1 2 2v7.885c0 1.571-.61 3.104-1.743 4.232l-3.67 3.733c-.384.391-.587.89-.587 1.42v1.46c0 .546.213 1.049.568 1.4l.019.02 3.678 3.742A6.002 6.002 0 0 1 38 34.114V42a2 2 0 1 1-4 0v-7.885c0-.53-.203-1.028-.587-1.42l-3.67-3.733A5.968 5.968 0 0 1 28 24.73v-1.46c0-1.592.624-3.094 1.735-4.224l3.697-3.76c.355-.353.568-.856.568-1.401V6a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M6 6a2 2 0 0 1 2-2h32a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM6 42a2 2 0 0 1 2-2h32a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M16 36a2 2 0 0 1 2-2h12a2 2 0 1 1 0 4H18a2 2 0 0 1-2-2ZM19 16a2 2 0 0 1 2-2h6a2 2 0 1 1 0 4h-6a2 2 0 0 1-2-2Z" })),
    'wait-outline2': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 4a2 2 0 0 1 2 2v7.885c0 .545.213 1.048.568 1.4l.019.02 3.678 3.741A6.002 6.002 0 0 1 20 23.27v1.46c0 1.571-.61 3.105-1.743 4.232l-3.67 3.734c-.384.39-.587.89-.587 1.42V42a2 2 0 1 1-4 0v-7.885c0-1.591.624-3.094 1.735-4.224l3.697-3.76c.355-.352.568-.855.568-1.4V23.27c0-.53-.203-1.029-.587-1.42l-3.67-3.733A5.968 5.968 0 0 1 10 13.885V6a2 2 0 0 1 2-2Zm24 0a2 2 0 0 1 2 2v7.885c0 1.571-.61 3.104-1.743 4.232l-3.67 3.733c-.384.391-.587.89-.587 1.42v1.46c0 .546.213 1.049.568 1.4l.019.02 3.678 3.742A6.002 6.002 0 0 1 38 34.114V42a2 2 0 1 1-4 0v-7.885c0-.53-.203-1.028-.587-1.42l-3.67-3.733A5.968 5.968 0 0 1 28 24.73v-1.46c0-1.592.624-3.094 1.735-4.224l3.697-3.76c.355-.353.568-.856.568-1.401V6a2 2 0 0 1 2-2Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M6 6a2 2 0 0 1 2-2h32a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM6 42a2 2 0 0 1 2-2h32a2 2 0 1 1 0 4H8a2 2 0 0 1-2-2ZM24.633 34.319a2 2 0 0 0-1.265 0l-10.735 3.578a2 2 0 0 1-1.265-3.794l10.735-3.579a6 6 0 0 1 3.794 0l10.736 3.579a2 2 0 0 1-1.266 3.794L24.633 34.32Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'wifi-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 28a10 10 0 0 0-6.94 2.8 2.002 2.002 0 0 0 2.78 2.88 6.16 6.16 0 0 1 8.32 0 2.001 2.001 0 1 0 2.78-2.88A10 10 0 0 0 24 28ZM24 18a18 18 0 0 0-12.94 5.5A2.022 2.022 0 1 0 14 26.28a14 14 0 0 1 20.16 0 1.999 1.999 0 0 0 1.42.6 2 2 0 0 0 1.44-3.38A18 18 0 0 0 24 18Z" }),
        React.createElement("path", { d: "M43.44 15.86a28 28 0 0 0-38.88 0 2 2 0 0 0 2.76 2.88 24 24 0 0 1 33.36 0 2 2 0 0 0 1.38.56 2 2 0 0 0 1.38-3.44Z" })),
    'wifi-off-fill': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24.88 22l-3.8-3.78-4.92-4.88-3.1-3.1-3.64-3.66a2.008 2.008 0 0 0-2.84 2.84l2.76 2.74 2.92 2.92 4.46 4.48 3.1 3.08 5.48 5.48 5.58 5.6 7.7 7.7a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84L24.88 22ZM43.44 15.86A27.86 27.86 0 0 0 24 8a28.2 28.2 0 0 0-8.88 1.46l3.24 3.24a23.78 23.78 0 0 1 22.32 6 2 2 0 0 0 1.38.56 1.999 1.999 0 0 0 1.44-.62 2 2 0 0 0-.06-2.78ZM7.64 13.3a28.657 28.657 0 0 0-3.08 2.56 2 2 0 0 0 2.76 2.88 26.193 26.193 0 0 1 3.2-2.58L7.64 13.3ZM34 26.28a1.999 1.999 0 0 0 1.42.6 2 2 0 0 0 1.44-3.38A18 18 0 0 0 24 18h-.32l4.7 4.7A14 14 0 0 1 34 26.28ZM14.86 20.52a17.599 17.599 0 0 0-3.8 2.98A2.023 2.023 0 1 0 14 26.28a14.601 14.601 0 0 1 4-2.82l-3.14-2.94ZM17.06 30.8a2.002 2.002 0 0 0 2.78 2.88 6.12 6.12 0 0 1 7.68-.5l-5.04-5.04a10 10 0 0 0-5.42 2.66Z" })),
    'wifi-off-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24.88 22l-3.8-3.78-4.92-4.88-3.1-3.1-3.64-3.66a2.008 2.008 0 0 0-2.84 2.84l2.76 2.74 2.92 2.92 4.46 4.48 3.1 3.08 5.48 5.48 5.58 5.6 7.7 7.7a1.998 1.998 0 0 0 2.84 0 1.999 1.999 0 0 0 0-2.84L24.88 22ZM43.44 15.86A27.86 27.86 0 0 0 24 8a28.2 28.2 0 0 0-8.88 1.46l3.24 3.24a23.78 23.78 0 0 1 22.32 6 2 2 0 0 0 1.38.56 2 2 0 0 0 1.44-.62 2 2 0 0 0-.06-2.78ZM7.64 13.3a28.633 28.633 0 0 0-3.08 2.56 2 2 0 0 0 2.76 2.88 26.181 26.181 0 0 1 3.2-2.58L7.64 13.3ZM34 26.28a1.999 1.999 0 0 0 1.42.6 2 2 0 0 0 1.44-3.38A18 18 0 0 0 24 18h-.32l4.7 4.7A14 14 0 0 1 34 26.28ZM14.86 20.52a17.599 17.599 0 0 0-3.8 2.98A2.023 2.023 0 0 0 14 26.28a14.6 14.6 0 0 1 4-2.82l-3.14-2.94ZM17.06 30.8a2.001 2.001 0 1 0 2.78 2.88 6.12 6.12 0 0 1 7.68-.5l-5.04-5.04a10 10 0 0 0-5.42 2.66Z" })),
    'wifi-outline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 40a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM24 28a10 10 0 0 0-6.94 2.8 2.001 2.001 0 1 0 2.78 2.88 6.16 6.16 0 0 1 8.32 0 2.001 2.001 0 1 0 2.78-2.88A10 10 0 0 0 24 28ZM24 18a18 18 0 0 0-12.94 5.5A2.022 2.022 0 1 0 14 26.28a14 14 0 0 1 20.16 0 1.999 1.999 0 0 0 1.42.6 2 2 0 0 0 1.44-3.38A18 18 0 0 0 24 18Z" }),
        React.createElement("path", { d: "M43.44 15.86a28 28 0 0 0-38.88 0 2 2 0 0 0 2.76 2.88 24 24 0 0 1 33.36 0 2 2 0 0 0 1.38.56 2 2 0 0 0 1.38-3.44Z" }))
};

// Ce fichier a été généré par un script, toute modification sera effacée.
const IconsBackNames = [
    'SortAlphaAsc',
    'SortAlphaDesc',
    'SortAsc',
    'SortDesc',
    'SortInvert',
    'align-center',
    'align-justify',
    'align-left',
    'align-right',
    'alignment',
    'amazon',
    'anchor',
    'at',
    'attached',
    'automation',
    'back',
    'bag',
    'banner',
    'bike',
    'bold',
    'box',
    'brightness',
    'bullet-list',
    'burger-menu',
    'button',
    'calendar-cross',
    'calendar',
    'car',
    'cargo-bike',
    'cargo-bikeXL',
    'cart',
    'check',
    'check-circle',
    'checkbox',
    'click',
    'click-through-rate',
    'clock',
    'code',
    'color',
    'conflict',
    'confused',
    'contrast',
    'conversion-tracking',
    'copy',
    'copyright',
    'cost-per-click',
    'coupon',
    'crop',
    'cross',
    'cross-circle',
    'cutlery',
    'declination',
    'delete',
    'design',
    'desktop',
    'dish',
    'dollar',
    'down',
    'download',
    'dropdown-square',
    'duplicate',
    'edit',
    'effects',
    'event',
    'eye',
    'facebook--recommend',
    'facebook',
    'files-all',
    'flag',
    'folder',
    'forbidden-circle',
    'form',
    'gallery',
    'gallery-plus',
    'gear',
    'gift',
    'give',
    'group',
    'handle',
    'happy',
    'hard-drive',
    'heart',
    'help-circle',
    'help-hexa',
    'home',
    'image',
    'image-text',
    'information',
    'instagram',
    'italic',
    'left',
    'less-circle',
    'life-buoy',
    'link',
    'link-cross',
    'linkedin',
    'list',
    'locked',
    'loyalty-card',
    'magnify',
    'magnify-cross',
    'mail',
    'mail-open',
    'margin',
    'megaphone',
    'message',
    'message-check',
    'message-cross',
    'mobile',
    'money',
    'money-bag',
    'navigation',
    'network',
    'newsletter',
    'number-circle',
    'page',
    'page-plus',
    'pause',
    'pending-circle',
    'percent',
    'person',
    'person-check',
    'person-cross',
    'person-plus',
    'phone',
    'phone-arrow',
    'phone-cross',
    'pinterest',
    'play',
    'play-circle',
    'plus-circle',
    'pointer',
    'print',
    'puzzle',
    'quote',
    'quotemaker',
    'radio-button',
    'reactivity',
    'refresh',
    'report',
    'restaurant',
    'review',
    'right',
    'rotation',
    'save',
    'scale',
    'scooter',
    'separation',
    'share',
    'shop',
    'sitemap',
    'size',
    'skip-next',
    'skip-previous',
    'sms',
    'social-1',
    'social',
    'spacing',
    'spam',
    'star',
    'statistics',
    'stop',
    'stripe',
    'symmetry',
    'synchronize',
    'table',
    'tablet',
    'target',
    'template',
    'template-check',
    'template-cross',
    'text-area',
    'text-background',
    'text-bloc',
    'text-field',
    'text-format',
    'theme',
    'ticket',
    'transfer',
    'truck',
    'twitter',
    'unavailable',
    'underline',
    'unhappy',
    'unlocked',
    'unsubscribe',
    'up',
    'version',
    'vimeo',
    'warning-circle',
    'way',
    'web',
    'widget',
    'youtube'
];
const IconsBackPaths = {
    'SortAlphaAsc': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12.357 16.282h6.21l1.185 3.077H23L17.02 5h-3.058L8 19.359h3.172l1.185-3.077Zm3.114-8.02 2.121 5.497H13.35l2.121-5.497Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M21.497 43v-2.708h-7.605l7.319-9.497V28.64H9.956v2.708h7.185l-7.338 9.497V43h11.694ZM32.04 33.152a1.5 1.5 0 0 0 1.92 0l6-5-1.92-2.304-3.54 2.95V15h-3v13.797l-3.54-2.95-1.92 2.305 6 5Z" })),
    'SortAlphaDesc': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12.357 16.282h6.21l1.185 3.077H23L17.02 5h-3.058L8 19.359h3.172l1.185-3.077Zm3.114-8.02 2.121 5.497H13.35l2.121-5.497Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M21.497 43v-2.708h-7.605l7.319-9.497V28.64H9.956v2.708h7.185l-7.338 9.497V43h11.694ZM33.96 14.848a1.5 1.5 0 0 0-1.92 0l-6 5 1.92 2.304 3.54-2.95V33h3V19.203l3.54 2.95 1.92-2.305-6-5Z" })),
    'SortAsc': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 11H6V7h8v4ZM6 21v-4h16v4H6ZM6 31h24v-4H6v4ZM6 41v-4h32v4H6ZM34.58 25.152a1.5 1.5 0 0 0 1.92 0l6-5-1.92-2.304-3.54 2.95V7h-3v13.797l-3.54-2.95-1.92 2.305 6 5Z" })),
    'SortDesc': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14.009 37H6v4h8.009v-4ZM6 27v4h16.018v-4H6ZM6 17h24.026v4H6v-4ZM6 7v4h32.035V7H6ZM36.43 40.652a1.503 1.503 0 0 1-1.923 0l-6.007-5 1.923-2.304 3.543 2.95V22.5h3.004v13.797l3.543-2.95 1.923 2.305-6.007 5Z" })),
    'SortInvert': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m26 10.4-2.7-3.6L25.7 5l4.5 6a1.5 1.5 0 0 1 0 1.8l-4.5 6-2.4-1.8 2.7-3.6h-5.5a8.5 8.5 0 0 0-8.5 8.5v4H9v-4c0-6.351 5.149-11.5 11.5-11.5H26Zm9 15.5v-4h3v4c0 6.351-5.149 11.5-11.5 11.5h-5l2.7 3.6-2.4 1.8-5.175-6.9L21.8 29l2.4 1.8-2.7 3.6h5a8.5 8.5 0 0 0 8.5-8.5Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'align-center': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6v4h36V7ZM34 17H14v4h20v-4ZM42 27H6v4h36v-4ZM34 37H14v4h20v-4Z" })),
    'align-justify': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6v4h36V7ZM42 17H6v4h36v-4ZM42 27H6v4h36v-4ZM42 37H6v4h36v-4Z" })),
    'align-left': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6v4h36V7ZM24 17H6v4h18v-4ZM42 27H6v4h36v-4ZM24 37H6v4h18v-4Z" })),
    'align-right': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6v4h36V7ZM42 17H24v4h18v-4ZM42 27H6v4h36v-4ZM42 37H24v4h18v-4Z" })),
    'alignment': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.7 22.9 33.8 14 31 16.8l5.5 5.5H10.8l5.5-5.5-2.8-2.8-8.9 8.9c-.8.8-.8 2 0 2.8l8.9 8.9 2.8-2.8-5.5-5.5h25.6l-5.5 5.5 2.8 2.8 8.9-8.9c.9-.8.9-2 .1-2.8Z" })),
    'amazon': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.29 7c-4.7 0-9.9 1.8-11 7.6-.1.6.3.9.7 1l4.8.5c.4 0 .8-.5.9-.9.4-2 2.1-3 4-3 1 0 2.2.4 2.8 1.3.7 1 .6 2.4.6 3.6v.6c-2.9.3-6.6.5-9.3 1.7-3.1 1.3-5.3 4.1-5.3 8.1 0 5.1 3.2 7.7 7.4 7.7 3.5 0 5.4-.8 8.1-3.6.9 1.3 1.2 1.9 2.8 3.3.4.2.8.2 1.2-.1 1-.9 2.8-2.4 3.8-3.3.4-.3.3-.9 0-1.3-.9-1.2-1.9-2.3-1.9-4.6V18c0-3.3.2-6.3-2.2-8.5-1.9-1.9-5-2.5-7.4-2.5Zm2.7 14.7v1.1c0 1.9.1 3.5-.9 5.2-.8 1.4-2 2.2-3.4 2.2-1.9 0-3-1.4-3-3.6-.1-4.1 3.7-4.9 7.3-4.9Zm14.1 11.1c-1.4 0-3.1.3-4.3 1.2-.4.3-.3.7.1.6 1.4-.2 4.6-.5 5.1.2.6.7-.6 3.7-1.2 5-.2.4.2.6.5.3 2.4-2 3-6.1 2.5-6.7-.2-.3-1.3-.6-2.7-.6Zm-36.9.8c-.2.1-.3.4 0 .7 5.2 4.7 12.1 7.5 19.8 7.5 5.5 0 11.8-1.7 16.2-5 .7-.5.1-1.3-.6-1-5 2.1-10.3 3.2-15.2 3.2-7.2 0-14.2-2-19.8-5.3-.1-.1-.2-.1-.3-.1h-.1Z" })),
    'anchor': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 22v2c0 5.9-4.3 10.9-10 11.8V15.6c2.3-.8 4-3 4-5.6 0-3.3-2.7-6-6-6s-6 2.7-6 6c0 2.6 1.7 4.8 4 5.6v20.2c-5.7-1-10-5.9-10-11.8v-2H6v4h2.1C9 33.2 14.8 39 22 39.9V44h4v-4.1c7.2-.9 13-6.7 13.9-13.9H42v-4h-6ZM24 8c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Z" })),
    'at': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 21.8c0 2.3-.4 4.5-1.1 6.4-.8 1.9-1.8 3.5-3.2 4.6-1.4 1.1-3 1.7-4.8 1.7-1.2 0-2.3-.3-3.2-.9-.9-.6-1.6-1.3-2-2.3h-.4c-1.8 2.1-4 3.2-6.7 3.2-2.9 0-5.2-.9-6.8-2.6-1.7-1.7-2.5-4.1-2.5-7.1 0-3.4 1.1-6.2 3.3-8.3 2.2-2.1 5-3.2 8.6-3.2 1.4 0 2.9.1 4.6.4 1.7.3 3.1.6 4.2 1l-.6 12c0 2.3.6 3.4 1.9 3.4 1 0 1.9-.8 2.5-2.3.6-1.5.9-3.5.9-6 0-2.6-.5-4.9-1.6-7-1.1-2-2.6-3.6-4.6-4.6-2-1-4.2-1.6-6.8-1.6-3.3 0-6.2.7-8.6 2-2.6 1.4-4.4 3.4-5.7 5.9-1.3 2.6-1.9 5.5-1.9 8.8 0 4.5 1.2 8 3.6 10.4 2.4 2.4 5.9 3.6 10.5 3.6 1.7 0 3.6-.2 5.7-.6 2.1-.4 4.1-.9 6.1-1.7v4.7C31.8 43.3 28 44 23.7 44c-6.2 0-11-1.6-14.5-4.9C5.7 35.8 4 31.3 4 25.5c0-4 .9-7.7 2.7-11 1.8-3.3 4.3-5.9 7.6-7.7C17.6 5 21.2 4 25.5 4c3.6 0 6.8.7 9.6 2.2 2.8 1.5 5 3.6 6.5 6.3 1.6 2.7 2.4 5.8 2.4 9.3Zm-25.1 3.1c0 3.4 1.4 5.2 4.2 5.2 1.5 0 2.6-.5 3.4-1.6.8-1 1.2-2.7 1.4-5.1l.3-5.4c-.8-.2-1.8-.3-2.8-.3-2 0-3.6.6-4.8 1.9-1.1 1.4-1.7 3.1-1.7 5.3Z" })),
    'attached': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m38.538 17.963-18.3 18.2c-2.3 2.3-6.1 2.3-8.5 0-2.4-2.3-2.3-6.1 0-8.5l8.5-8.5.7-.7 7.8-7.8c.8-.8 2-.8 2.8 0 .8.8.8 2 0 2.8l-17 17 2.8 2.8 17-17c2.3-2.3 2.3-6.1 0-8.5-2.3-2.4-6.1-2.3-8.5 0l-7.8 7.8-.7.7-8.5 8.5c-3.8 4-3.8 10.4.1 14.3 3.9 3.9 10.2 3.9 14.1 0l18.3-18.3-2.8-2.8Z" })),
    'automation': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.32 20.104c-.3-.7-.7-1.4-1.3-1.9-.6-.5-1.2-.9-1.9-1.2.2-1.1.1-2.1-.3-3.1s-1.1-1.8-2.1-2.4h-.1l-.6-.3c-.3-.2-.6-.3-.9-.4l-1.8 1.7c.1.3.2.6.2.9 0 .5-.2 1.1-.5 1.5-.3.4-.8.8-1.3.9-.5.2-1.1.2-1.6 0s-1-.5-1.3-1c-.3-.4-.5-1-.4-1.5 0-.5.2-1.1.6-1.5.3-.4.8-.7 1.3-.9.5-.1 1.1-.1 1.6.1l1.6-1.6c-.1-.1-.2-.3-.3-.4-.2-.3-.5-.6-.8-.9-.8-.8-1.9-1.5-3-1.8-1.1-.3-2.3-.4-3.5-.2v14.9c.5.2 1 .6 1.2 1.2.3.5.3 1.1.2 1.7-.1.6-.4 1.1-.9 1.5-.5.4-1 .6-1.6.6-.6 0-1.1-.2-1.6-.6-.5-.4-.8-.9-.9-1.5-.1-.6 0-1.2.2-1.7.3-.5.7-.9 1.2-1.2v-14.9c-1.2-.2-2.3-.1-3.4.3s-2.1 1-2.9 1.8c-.3.3-.5.6-.8.9 0 .1-.1.3-.2.4l1.6 1.6c.3-.1.6-.1.9-.1.5 0 1 .2 1.4.5.4.3.7.7.9 1.1.2.5.2 1 .1 1.4-.1.5-.3.9-.7 1.3-.4.3-.8.6-1.3.7-.5.1-1 0-1.4-.1-.4-.1-.9-.5-1.1-.9-.4-.5-.5-1-.5-1.5 0-.3.1-.7.2-1l-1.8-1.7c-.3.1-.6.3-.9.4-.1.1-.3.2-.5.3-.9.6-1.6 1.4-2.1 2.4-.4 1-.5 2-.3 3.1-.7.2-1.4.7-2 1.2-.6.5-1 1.2-1.3 1.9-.4 1-.7 2-.6 3.1 0 1.1.3 2.1.7 3.1.4 1 1.2 1.9 2.2 2.4 0 0-1.4 5.3 2.9 7.4.3.1.6.3.9.4l5.2-5.1c-.1-.3-.2-.6-.2-.9 0-.3.1-.6.2-.9l-4-3.9c-.3.1-.6.2-.9.2-.5 0-1-.2-1.5-.5-.4-.4-.7-.9-.9-1.4-.1-.5-.1-1.1.1-1.6s.5-.9 1-1.2c.4-.3 1-.4 1.5-.4s1 .2 1.5.5c.4.3.7.8.9 1.3.1.5.1 1.1-.1 1.6l3.9 3.9c.3-.1.6-.2.9-.2.3 0 .7 0 1 .1.3.1.6.3.9.5.3.2.5.5.6.8.1.3.2.7.2 1 0 .3-.1.7-.2 1-.1.3-.3.6-.6.8-.3.2-.6.4-.9.5-.3.1-.7.2-1 .1-.3 0-.6-.1-.9-.2l-5 4.9c.1.3.3.6.5.9l.2.7c.5.7 1.1 1.3 1.8 1.8s1.5.8 2.4.9c1.1.3 2.3.3 3.4.1v-1.8c-.5-.2-1-.6-1.2-1.2-.3-.5-.3-1.1-.2-1.7.1-.6.4-1.1.9-1.5.5-.4 1-.6 1.6-.6.6 0 1.1.2 1.6.6.5.4.8.9.9 1.5.1.6 0 1.2-.2 1.7-.3.5-.7.9-1.2 1.2v1.9c1.1.2 2.3.1 3.4-.1.9-.1 1.7-.5 2.4-.9.7-.5 1.4-1.1 1.8-1.8.1-.2.2-.3.3-.5.1-.1.4-.6.5-.9l-5-4.9c-.3.1-.6.2-.9.2-.3 0-.7 0-1-.1-.3-.1-.6-.3-.9-.5-.3-.2-.5-.5-.6-.8-.1-.3-.2-.7-.2-1 0-.3.1-.7.2-1 .1-.3.3-.6.6-.8.3-.2.6-.4.9-.5.3-.1.7-.2 1-.1.3 0 .6.1.9.2l3.9-3.9c-.1-.3-.2-.6-.2-.9 0-.5.1-1 .4-1.4.3-.4.7-.8 1.1-.9.5-.2 1-.2 1.5-.2.5.1 1 .3 1.3.7.4.4.6.8.7 1.3.1.5.1 1-.1 1.5s-.5.9-.9 1.1c-.4.3-.9.4-1.4.4-.3 0-.6-.1-.9-.2l-3.8 3.6c.1.3.2.6.2.9 0 .3-.1.6-.2.9l5.1 5.1c.3-.1.6-.2.9-.4 4.3-2.1 2.9-7.4 2.9-7.4 1-.5 1.8-1.4 2.3-2.5.5-1 .7-2 .7-3.1.1-.9-.1-1.9-.5-2.9Z" })),
    'back': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9.2 24h3c0-7.7 6.3-14 14-14s14 6.3 14 14-6.3 14-14 14v-3.5c5.8 0 10.5-4.7 10.5-10.5S32 13.5 26.2 13.5 15.7 18.2 15.7 24h3.5c1.1 0 1.5.8 1 1.7l-5 8.5c-.6 1-1.5 1-2 0l-5-8.5c-.5-.9-.1-1.7 1-1.7Z" })),
    'bag': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m39.53 42-3.5-24c-.2-1.1-1.2-2-2.3-2h-2v-3.5c0-4.7-3.6-8.5-8-8.5s-8 3.8-8 8.5V16h-2c-1.1 0-2.1.9-2.3 2l-3.4 24c-.2 1.1.6 2 1.7 2h28c1.1 0 1.9-.9 1.8-2Zm-19.8-29.5c0-2.5 1.8-4.5 4-4.5s4 2 4 4.5V16h-8v-3.5Zm-2 11.5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm12 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'banner': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 16H4l6 8-6 8h40l-6-8 6-8Z" })),
    'bike': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M29.833 12.83c1.834 0 3.334-1.537 3.334-3.415 0-1.878-1.5-3.415-3.334-3.415C28 6 26.5 7.537 26.5 9.415c0 1.878 1.5 3.414 3.333 3.414Zm-17.5 11.097C7.667 23.927 4 27.683 4 32.463 4 37.243 7.667 41 12.333 41c4.667 0 8.334-3.756 8.334-8.537 0-4.78-3.667-8.536-8.334-8.536Zm0 14.512c-3.166 0-5.833-2.732-5.833-5.976 0-3.244 2.667-5.975 5.833-5.975 3.167 0 5.834 2.732 5.834 5.975 0 3.244-2.667 5.976-5.834 5.976ZM22 21.366l4-4.098 1.333 1.366c2.167 2.22 5 3.585 8.5 3.585v-3.414c-2.5 0-4.5-1.024-6-2.561L26.667 13C25.833 12.317 25 11.976 24 11.976c-1 0-1.833.341-2.333 1.024L17 17.78c-.667.683-1 1.537-1 2.39 0 1.025.333 1.879 1 2.391l5.333 4.78v8.537h3.334V25.293L22 21.366Zm13.667 2.56c-4.667 0-8.334 3.757-8.334 8.537C27.333 37.243 31 41 35.667 41 40.333 41 44 37.244 44 32.463c0-4.78-3.667-8.536-8.333-8.536Zm0 14.513c-3.167 0-5.834-2.732-5.834-5.976 0-3.244 2.667-5.975 5.834-5.975 3.166 0 5.833 2.732 5.833 5.975 0 3.244-2.667 5.976-5.833 5.976Z" })),
    'bold': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.4 42H10V6h13.6c2.4 0 4.4.3 6.1.9 1.7.6 3 1.4 3.9 2.4 1.7 1.8 2.4 3.9 2.4 6.2 0 2.8-.9 4.9-2.7 6.3-.6.5-1 .8-1.3.9-.2.1-.7.3-1.3.6 2.2.5 3.9 1.5 5.2 3 1.3 1.5 1.9 3.4 1.9 5.7 0 2.5-.8 4.7-2.5 6.6-1.7 2.3-5.1 3.4-9.9 3.4Zm-7.6-21.4h3.7c2.2 0 3.8-.2 4.8-.7 1.1-.5 1.6-1.5 1.6-3.1s-.5-2.7-1.5-3.2-2.6-.8-4.9-.8h-3.7v7.8Zm0 14.6h5.4c2.2 0 3.9-.3 5-.9 1.1-.6 1.7-1.7 1.7-3.3 0-1.7-.6-2.8-1.8-3.3-1.2-.5-3.1-.8-5.7-.8h-4.6v8.3Z" })),
    'box': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m15.5 18.475 15.1-8.4-4.9-2.7c-1-.5-2.5-.5-3.5 0l-12.5 7c-.2.1-.4.3-.6.5l6.4 3.6ZM17.6 19.575l6.4 3.6 14.9-8.3c-.2-.2-.4-.4-.7-.5l-5.6-3.1-15 8.3ZM23 24.875l-14.9-8.3c-.1.3-.1.5-.1.7v14c0 1.1.8 2.4 1.7 3l12.5 7c.2.1.5.2.7.3v-16.7h.1ZM25 24.875v16.7c.3-.1.5-.2.7-.3l12.5-7c1-.5 1.7-1.9 1.7-3v-14c0-.2 0-.5-.1-.7l-14.8 8.3Z" })),
    'brightness': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 9.6 28.2 4l1.5 6.8 6.1-3.4-1.3 6.9 6.9-.7-4 5.7 6.6 2.3-6 3.5 5.1 4.8-6.9.8 2.7 6.4-6.6-2-.1 6.9-5.3-4.6-2.9 6.4-2.9-6.4-5.3 4.6-.1-6.9-6.6 2 2.7-6.4-6.9-.8 5.1-4.8-6-3.5 6.6-2.3-4-5.7 6.9.7-1.3-6.9 6.1 3.4L19.8 4 24 9.6Z" })),
    'bullet-list': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9 14a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM9 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM9 40a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM40 8H18c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2ZM40 21H18c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2ZM40 34H18c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2Z" })),
    'burger-menu': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 8H8v4h32V8ZM40 22H8v4h32v-4ZM40 36H8v4h32v-4Z" })),
    'button': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 10H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12.9l-.8-2.2-.5-1.3c-.4-1.2 0-2.1.3-2.5.5-.6 1.2-1 2-1 .3 0 .7.1 1 .2l1 .3 3.1 1.1 10.2 3.6c.9.3 1.5 1 1.8 1.8h5c1.1 0 2-.9 2-2V12c0-1.1-.9-2-2-2Z" }),
        React.createElement("path", { d: "m31.7 30.5 3.1-3.1c.5-.5.4-1.1-.3-1.3l-.2-.1-9.9-3.5-3.1-1.1-1-.3c-.1-.1-.3-.1-.4-.1-.4 0-.6.3-.4.8l.5 1.3 2.7 7.5 1.9 5.4c.1.4.4.6.7.6.2 0 .4-.1.6-.3l3.1-3.1 4.4 4.4c.2.2.6.4.9.4.3 0 .7-.1.9-.4l.9-.9c.5-.5.5-1.3 0-1.8l-4.4-4.4Z" })),
    'calendar-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 12h-4V8c0-1.1-.9-2-2-2s-2 .9-2 2v4H18V8c0-1.1-.9-2-2-2s-2 .9-2 2v4h-4c-1.1 0-2 .9-2 2v26c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V14c0-1.1-.9-2-2-2Zm-6.6 19.6-2.8 2.8-4.6-4.6-4.6 4.6-2.8-2.8 4.6-4.6-4.6-4.6 2.8-2.8 4.6 4.6 4.6-4.6 2.8 2.8-4.6 4.6 4.6 4.6Z" })),
    'calendar': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 12h-4V8c0-1.1-.9-2-2-2s-2 .9-2 2v4H18V8c0-1.1-.9-2-2-2s-2 .9-2 2v4h-4c-1.1 0-2 .9-2 2v8h32v-8c0-1.1-.9-2-2-2ZM8 40c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V24H8v16Z" })),
    'car': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.84 10.02A2.988 2.988 0 0 0 35 8H13c-1.32 0-2.42.84-2.84 2.02L6.443 20.725A8 8 0 0 0 6 23.35V38c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2v-2h24v2c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2V23.35c0-.894-.15-1.78-.443-2.625L37.84 10.02ZM13 30c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3Zm22 0c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3ZM11.387 20a1 1 0 0 1-.948-1.316l2.105-6.317A2 2 0 0 1 14.441 11H33.56a2 2 0 0 1 1.897 1.367l2.105 6.317A1 1 0 0 1 36.613 20H11.387Z" })),
    'cargo-bike': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20.75 11.79c0-.691.56-1.25 1.25-1.25h2.331a2.25 2.25 0 0 1 2.21 1.822l1.33 6.888H41.5a2.25 2.25 0 0 1 2.25 2.25V29a2.25 2.25 0 0 1-2.25 2.25h-.705a7.503 7.503 0 0 1-14.59 0h-7.41a7.5 7.5 0 1 1-4.702-8.79l.733-.979-.718-3.231H13a1.25 1.25 0 1 1 0-2.5h5a1.25 1.25 0 0 1 0 2.5h-1.33l2.035 9.16c.087.299.155.605.204.919l.094.42h7.034a7.453 7.453 0 0 1 .659-2.409l-2.57-13.3H22c-.69 0-1.25-.56-1.25-1.25ZM11.5 24.5c.339 0 .67.034.99.098L10.2 27.65c-1.112 1.483-.054 3.6 1.8 3.6h4.185A5.002 5.002 0 0 1 6.5 29.5a5 5 0 0 1 5-5Zm1 4.25h3.942l-.157-.704a5.003 5.003 0 0 0-1.512-2.326L12.5 28.75Zm26 .75a5 5 0 1 1-10 0 5 5 0 0 1 10 0Zm-5 1.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'cargo-bikeXL': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M5.79 7.811A2 2 0 0 0 4 9.801V31a2 2 0 0 0 2 2h2.016a7.5 7.5 0 0 0 14.947.25h6.074a7.501 7.501 0 1 0 7.238-8.247l-1.732-8.655a2.25 2.25 0 0 0-2.206-1.809H30a1.25 1.25 0 0 0 0 2.5h2.132l1.691 8.453a7.515 7.515 0 0 0-4.618 5.258H23v-7.5h2a1.25 1.25 0 1 0 0-2.5h-2V8.222a2 2 0 0 0-2.21-1.99l-15 1.58ZM20.5 32.5a5 5 0 1 0-10 0 5 5 0 0 0 10 0Zm16 5a5 5 0 0 0 .276-9.992l.95 4.747a1.25 1.25 0 0 1-2.452.49l-.95-4.748A5 5 0 0 0 36.5 37.5Zm-21-3.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'cart': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.6 12.8c-.4-.5-1-.8-1.6-.8H17.3l-1.4-4.6c-.2-.8-1-1.4-1.9-1.4H6c-1.1 0-2 .9-2 2s.9 2 2 2h6.5l5.6 18.6c.3.8 1 1.4 1.9 1.4h18c.9 0 1.7-.6 1.9-1.5l4-14c.2-.6.1-1.2-.3-1.7ZM22 42a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM36 42a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" })),
    'check': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21.4 32.2 13 23.8l2.8-2.8 5.6 5.6L33 15l2.8 2.8-14.4 14.4Z" })),
    'check-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm-3 29.3-8.4-8.4 2.8-2.8 5.6 5.6 11.6-11.6 2.8 2.8L21 33.3Z" })),
    'checkbox': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2ZM21 32.6l-8.4-8.4 2.8-2.8L21 27l11.6-11.6 2.8 2.8L21 32.6Z" })),
    'click': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.6 37.4-6.9-6.9 4.8-4.8c.8-.8.6-1.7-.5-2.1l-22.2-8c-1-.4-1.6.2-1.2 1.2L23.5 39c.4 1 1.3 1.2 2.1.5l4.8-4.8 6.9 6.9c.8.8 2.1.8 2.8 0l1.4-1.4c.9-.8.9-2.1.1-2.8ZM9.627 8.212 8.213 9.627l4.03 4.03 1.415-1.414-4.03-4.03ZM11.7 14.9H6v2h5.7v-2ZM12.244 18.142l-4.03 4.03 1.413 1.414 4.03-4.03-1.413-1.414ZM22.244 8.142l-4.03 4.03 1.413 1.414 4.03-4.03-1.413-1.414ZM16.9 6h-2v5.7h2V6Z" })),
    'click-through-rate': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 31c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10c0 1-.2 2-.4 3l7 2.5C41.9 24.8 43 23 44 21 40 13 32.8 7 24 7S8 13 4 21c4 8 11.2 14 20 14 1.5 0 3-.2 4.4-.5L27 30.6c-1 .2-2 .4-3 .4Z" }),
        React.createElement("path", { d: "M27.4 21.9c.3 0 .7.1 1 .2l3.2 1.1c.2-.7.3-1.5.3-2.3 0-4.4-3.6-8-8-8s-8 3.6-8 8 3.6 8 8 8c.8 0 1.6-.1 2.3-.3l-1.1-3.2c-.4-1.2 0-2.1.3-2.5.5-.6 1.2-1 2-1Z" }),
        React.createElement("path", { d: "m39.2 33.5 3.1-3.1c.5-.5.4-1.1-.3-1.3l-5.8-2-5.4-1.9-3-1.2c-.1-.1-.3-.1-.4-.1-.4 0-.6.3-.4.8l1.1 3.1.8 2.2 3.2 8.9c.1.4.4.6.7.6.2 0 .4-.1.6-.3l3.1-3.1 4.4 4.4c.2.2.6.4.9.4.3 0 .7-.1.9-.4l.9-.9c.5-.5.5-1.3 0-1.8l-4.4-4.3Z" })),
    'clock': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm8 21h-9V8h2v15h7v2Z" })),
    'code': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m24.027 14.018-4.005 20.003 3.922.785 4.005-20.002-3.922-.786ZM13.6 14l-9 9c-.8.8-.8 2 0 2.8l9 9 2.8-2.8-7.6-7.6 7.6-7.6-2.8-2.8ZM43.4 23l-9-9-2.8 2.8 7.6 7.6-7.6 7.6 2.8 2.8 9-9c.8-.8.8-2 0-2.8Z" })),
    'color': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 43.775c8.3 0 15-6.7 15-15 0-9.9-13.9-24.1-13.9-24.1-.6-.9-1.6-.9-2.2 0 0 0-13.9 14.4-13.9 24.1 0 8.3 6.7 15 15 15Z" })),
    'conflict': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m12.9 14-2.8 2.8 5.6 5.6H4v4h11.7L10.1 32l2.8 2.8 9-9c.8-.8.8-2 0-2.8l-9-9ZM44 22.4H32.3l5.6-5.6-2.8-2.8-9 9c-.8.8-.8 2 0 2.8l9 9 2.8-2.8-5.6-5.6H44v-4Z" })),
    'confused': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm-6 12c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm14 16H16v-2h16v2Zm-2-12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'contrast': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm0 36c-8.8 0-16-7.2-16-16S15.2 8 24 8s16 7.2 16 16-7.2 16-16 16Z" }),
        React.createElement("path", { d: "M11 24c0 7.2 5.8 13 13 13V11c-7.2 0-13 5.8-13 13Z" })),
    'conversion-tracking': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.257 24h-3c0-7.7-6.3-14-14-14-2.2 0-4.3.5-6.2 1.5l1.6 3.1c1.4-.7 3-1.1 4.7-1.1 5.7 0 10.4 4.7 10.4 10.5h-3.5c-1.1 0-1.5.8-1 1.7l5 8.5c.6 1 1.5 1 2 0l5-8.5c.6-.9.1-1.7-1-1.7ZM24.257 34.5c-5.8 0-10.5-4.7-10.5-10.5h3.5c1.1 0 1.5-.8 1-1.7l-5-8.5c-.6-1-1.5-1-2 0l-5 8.5c-.6 1-.1 1.7 1 1.7h3c0 7.7 6.3 14 14 14 2.3 0 4.4-.5 6.3-1.5l-1.6-3.1c-1.4.7-3 1.1-4.7 1.1Z" }),
        React.createElement("path", { d: "M24.257 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" })),
    'copy': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 4H19c-1.1 0-2 .9-2 2v5h12c2 0 4 1.5 4 4v20h5c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2Z" }),
        React.createElement("path", { d: "M29 13H10c-1.1 0-2 .9-2 2v27c0 1.1.9 2 2 2h19c1.1 0 2-.9 2-2V15c0-1.1-.9-2-2-2Z" })),
    'copyright': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm0 36c-8.8 0-16-7.2-16-16S15.2 8 24 8s16 7.2 16 16-7.2 16-16 16Z" }),
        React.createElement("path", { d: "M24 18c2.1 0 3.9 1.1 5 2.7l3.3-2.2C30.5 15.8 27.5 14 24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10c3.5 0 6.5-1.8 8.3-4.5L29 27.3c-1.1 1.6-2.9 2.7-5 2.7-3.3 0-6-2.7-6-6s2.7-6 6-6Z" })),
    'cost-per-click': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M23 28h-1v2h-2v-2h-3v-2h6c-.2-1 .1-1.7.4-2 .3-.5.8-.8 1.3-.9-.3-.6-1-1-1.7-1h-3c-2.2 0-4-1.8-4-4s1.8-4 4-4V12h2v2h3v2h-5c-1.1 0-2 .9-2 2s.9 2 2 2h3c2 0 3.6 1.4 3.9 3.3l8.5 3c.6-1.6.9-3.4.9-5.2C36.3 12.8 29.5 6 21.2 6S6 12.8 6 21.2c0 8.4 6.8 15.2 15.2 15.2 1.8 0 3.6-.3 5.2-.9L23.7 28H23Z" }),
        React.createElement("path", { d: "m41.6 38.9-4.4-4.4 3.1-3.1c.5-.5.4-1.1-.3-1.3l-10.2-3.6-3.1-1.1-1-.3c-.1-.1-.3-.1-.4-.1-.4 0-.6.3-.4.8l.5 1.3 2.7 7.5L30 40c.1.4.4.6.7.6.2 0 .4-.1.6-.3l3.1-3.1 4.4 4.4c.2.2.6.4.9.4.3 0 .7-.1.9-.4l.9-.9c.6-.5.6-1.3.1-1.8Z" })),
    'coupon': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 10H16v4h-2v-4H6c-1.1 0-2 .9-2 2v5.1c2.4 1.4 4 4 4 6.9 0 2.9-1.6 5.5-4 6.9V36c0 1.1.9 2 2 2h8v-4h2v4h26c1.1 0 2-.9 2-2v-5.1c-2.4-1.4-4-4-4-6.9 0-2.9 1.6-5.5 4-6.9V12c0-1.1-.9-2-2-2ZM16 30h-2v-4h2v4Zm0-8h-2v-4h2v4Zm8-4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-1.3 11.1c-.4-.4-.4-1 0-1.4l9-9c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-9 9c-.4.4-1 .4-1.4 0Zm9.3.9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'crop': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 32h-6V14c0-.2 0-.3-.1-.5l4.8-4.8-1.4-1.4-4.8 4.8c-.2-.1-.3-.1-.5-.1H16V6h-4v6H6v4h6v18c0 1.1.9 2 2 2h18v6h4v-6h6v-4ZM30.6 16 16 30.6V16h14.6ZM17.4 32 32 17.4V32H17.4Z" })),
    'cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M33.8 31 31 33.8l-6.6-6.6-6.6 6.6L15 31l6.6-6.6-6.6-6.6 2.8-2.8 6.6 6.6L31 15l2.8 2.8-6.6 6.6 6.6 6.6Z" })),
    'cross-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm9.4 26.6-2.8 2.8-6.6-6.6-6.6 6.6-2.8-2.8 6.6-6.6-6.6-6.6 2.8-2.8 6.6 6.6 6.6-6.6 2.8 2.8-6.6 6.6 6.6 6.6Z" })),
    'cutlery': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 6c-3.3 0-6 3.6-6 8 0 3.5 1.7 6.4 4 7.5V40c0 1.1.9 2 2 2s2-.9 2-2V21.5c2.3-1.1 4-4.1 4-7.5 0-4.4-2.7-8-6-8ZM39 6h-.3c-1 0-1.8.7-2 1.6l-3.2 17c-.2 1.2.7 2.4 2 2.4H37v13c0 1.1.9 2 2 2s2-.9 2-2V8c0-1.1-.9-2-2-2ZM16 6c-.5 0-1 .5-1 1v9h-2V7c0-.5-.5-1-1-1s-1 .5-1 1v9H9V7c0-.5-.5-1-1-1s-1 .5-1 1v9c0 2.2 1.3 3.8 3 4.5V40c0 1.1.9 2 2 2s2-.9 2-2V20.5c1.7-.7 3-2.4 3-4.5V7c0-.5-.5-1-1-1Z" })),
    'declination': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M9 14a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM17 27a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM25 40a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM40 8H18c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h22c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2ZM40 21H26c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2ZM40 34h-6c-1.1 0-2 .9-2 2v2c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2v-2c0-1.1-.9-2-2-2Z" })),
    'delete': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 10h-6V8c0-2.2-1.8-4-4-4h-8c-2.2 0-4 1.8-4 4v2h-6c-1.1 0-2 .9-2 2s.9 2 2 2h28c1.1 0 2-.9 2-2s-.9-2-2-2Zm-18 0V8h8v2h-8ZM13.8 42c.1 1.1 1.1 2 2.2 2h16c1.1 0 2.1-.9 2.2-2l3.3-26h-27l3.3 26Z" })),
    'design': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21 27.1c-1.4-1.4-3.3-2.1-5.2-2.1-1.9 0-3.7.7-5.2 2.1-2.8 2.8-3.6 8.1-3.6 14 5.9 0 11.2-.8 14-3.7 2.8-2.9 2.8-7.5 0-10.3ZM21.4 20.9l-2.6 2.6c1.3.5 2.6 1.2 3.6 2.2 1 1 1.7 2.1 2.1 3.4l2.5-2.5-5.6-5.7ZM40.3 10.4l-2.8-2.8c-.8-.8-2.1-.8-2.8 0L22.8 19.5l5.7 5.7 11.8-12c.8-.7.8-2 0-2.8Z" })),
    'desktop': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6c-1.1 0-2 .9-2 2v24c0 1.1.9 2 2 2h12.9l-.6 4H14v2h20v-2h-4.3l-.6-4H42c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2ZM24 33c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'dish': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 14.1V12c0-1.1-.9-2-2-2s-2 .9-2 2v2.1c-9 1-16 8.6-16 17.9h36c0-9.3-7-16.9-16-17.9ZM42 34H6c-1.1 0-2 .9-2 2s.9 2 2 2h36c1.1 0 2-.9 2-2s-.9-2-2-2Z" })),
    'dollar': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.1 4c1.1 0 1.9.8 1.9 1.9v3c2.6.3 4.8 1.1 6.8 2.4.7.4 1.3 1.1 1.3 2.1 0 1.4-1.1 2.4-2.5 2.4-.4 0-.9-.1-1.3-.4-1.5-.9-3-1.6-4.5-1.9v8.4c6.7 1.7 9.5 4.3 9.5 9 0 4.8-3.8 8-9.3 8.6v2.6c0 1.1-.8 1.9-1.9 1.9-1.1 0-1.9-.8-1.9-1.9v-2.7c-3.3-.4-6.3-1.5-9-3.4-.8-.5-1.2-1.2-1.2-2.1 0-1.4 1.1-2.4 2.4-2.4.5 0 1.1.2 1.4.5 2.1 1.5 4.1 2.5 6.5 2.9v-8.6c-6.4-1.7-9.4-4.1-9.4-9 0-4.7 3.7-7.9 9.2-8.4v-3c.1-1.1 1-1.9 2-1.9Zm-1.7 17v-7.8c-2.9.3-4.3 1.8-4.3 3.7 0 1.8.9 3 4.3 4.1Zm3.4 6.1v8c2.8-.3 4.3-1.7 4.3-3.8.1-2-.9-3.2-4.3-4.2Z" })),
    'down': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.4 30.4c-.5 0-1-.2-1.4-.6l-9-9 2.8-2.8 7.6 7.6L32 18l2.8 2.8-9 9c-.4.4-.9.6-1.4.6Z" })),
    'download': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30.8 5.2 29.5 9C35.6 11.2 40 17.1 40 24c0 8.8-7.2 16-16 16S8 32.8 8 24c0-6.9 4.4-12.8 10.5-15l-1.4-3.8C9.5 8 4 15.4 4 24c0 11 9 20 20 20s20-9 20-20c0-8.6-5.5-16-13.2-18.8Z" }),
        React.createElement("path", { d: "M26 27.2V4h-4v23.2l-4.6-4.6-2.8 2.8 8 8c.8.8 2 .8 2.8 0l8-8-2.8-2.8-4.6 4.6Z" })),
    'dropdown-square': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2ZM25.4 29.6c-.4.4-.9.6-1.4.6-.5 0-1-.2-1.4-.6l-9-9 2.8-2.8 7.6 7.6 7.6-7.6 2.8 2.8-9 9Z" })),
    'duplicate': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 6H18c-2.2 0-4 1.8-4 4v2h4v-2h20v20h-2v4h2c2.2 0 4-1.8 4-4V10c0-2.2-1.8-4-4-4Z" }),
        React.createElement("path", { d: "M32 14H8c-1.1 0-2 .9-2 2v24c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V16c0-1.1-.9-2-2-2Zm-4 16h-6v6h-4v-6h-6v-4h6v-6h4v6h6v4Z" })),
    'edit': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.826 10.916 11.21 27.533l8.486 8.485L36.31 19.4l-8.485-8.485ZM9.734 29l-.9.9c-.8.8-1.6 2.3-1.8 3.4v.2l6.7 6.6h.3c1.1-.2 2.6-1 3.4-1.8l.9-.9-8.6-8.4ZM40.534 12.3l-5.7-5.7c-.8-.8-2.1-.8-2.8 0l-2.9 2.9 8.5 8.5 2.9-2.9c.8-.7.8-2 0-2.8ZM6.734 35.9l-.7 3.6c-.2 1.1.5 1.8 1.6 1.6l3.7-.6-4.6-4.6Z" })),
    'effects': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 19c0-8.3-6.7-15-15-15-6.8 0-12.5 4.5-14.3 10.6C8.5 16.5 4 22.2 4 29c0 8.3 6.7 15 15 15 6.8 0 12.5-4.5 14.4-10.7C39.5 31.5 44 25.8 44 19ZM19 40c-6.1 0-11-4.9-11-11 0-5.4 3.9-9.9 9.1-10.8l-2.9 2.9c.1.8.3 1.6.5 2.3l5.4-5.4c.8.1 1.6.2 2.3.5l-6.9 6.9c.3.6.6 1.2 1 1.8l7.9-7.9c.6.3 1.2.7 1.7 1.2l-8.3 8.3c.2.2.4.5.7.7.3.2.5.5.7.7l8.3-8.3c.4.5.8 1.1 1.2 1.7l-7.9 7.9c.6.4 1.2.7 1.8 1l6.9-6.9c.2.7.4 1.5.5 2.3l-5.4 5.4c.8.2 1.5.4 2.3.5l2.9-2.9C28.9 36.1 24.4 40 19 40Zm15-11.2c0-1-.1-2.1-.4-3.1l-.3-1.2c-.2-.7-.5-1.4-.8-2-.3-.6-.6-1.2-1-1.8-.4-.6-.8-1.1-1.2-1.6-.4-.5-.9-1-1.4-1.4-.5-.4-1-.9-1.6-1.2-.6-.4-1.2-.7-1.8-1-.7-.3-1.3-.6-2-.8l-1.2-.3c-.4-.1-.8-.1-1.2-.2-.6-.1-1.2-.1-1.9-.2 1.8-3.6 5.5-6 9.8-6 6.1 0 11 4.9 11 11 0 4.3-2.4 8-6 9.8Z" })),
    'event': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 12h-4V8c0-1.1-.9-2-2-2s-2 .9-2 2v4H18V8c0-1.1-.9-2-2-2s-2 .9-2 2v4h-4c-1.1 0-2 .9-2 2v26c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V14c0-1.1-.9-2-2-2Zm-5.2 13.4-4.1 4.1 1 5.8c.1.4-.4.8-.8.6l-5-2.7-5 2.7c-.4.2-.9-.1-.8-.6l1-5.8-4.1-4.1c-.3-.3-.1-.8.3-.9l5.6-.8 2.5-5.3c.3-.6.7-.6 1 0l2.5 5.3 5.6.8c.5.1.7.6.3.9Z" })),
    'eye': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 10c-8.8 0-16 6-20 14 4 8 11.2 14 20 14s16-6 20-14c-4-8-11.2-14-20-14Zm0 24c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10-4.5 10-10 10Z" }),
        React.createElement("path", { d: "M24 32a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" })),
    'facebook--recommend': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 6a6 6 0 0 0-6 6v16.125a6 6 0 0 0 6 6h3V42l11.25-7.875H36a6 6 0 0 0 6-6V12a6 6 0 0 0-6-6H12Zm7.584 21.938 4.405-2.442 4.444 2.402c.32.2.761-.08.681-.48l-.84-5.166 3.603-3.684c.24-.28.12-.761-.28-.801l-4.966-.721-2.202-4.686c-.2-.48-.6-.48-.84 0l-2.203 4.686-4.965.76c-.4.04-.56.521-.28.802l3.604 3.684-.841 5.166c-.08.36.32.68.68.48Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'facebook': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h17.2V28.1h-4.7v-5.4h4.7v-4c0-4.6 2.8-7.2 7-7.2 2 0 3.7.1 4.2.2v4.9h-2.9c-2.3 0-2.7 1.1-2.7 2.6v3.5h5.4l-.7 5.4h-4.7V42H40c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Z" })),
    'files-all': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21 16h6.6L20 8.4V15c0 .6.4 1 1 1ZM40.7 24H21.3c-.7 0-1.3.6-1.3 1.2v13.5c0 .7.6 1.2 1.3 1.2h19.5c.7 0 1.3-.6 1.3-1.2V25.2c-.1-.6-.7-1.2-1.4-1.2Zm-3.4 12H24.7c-.6 0-.9-.4-.6-.9l2.9-4.7c.2-.3.4-.4.6-.4.2 0 .4.1.6.4l2.3 3.7c.2.3.4.4.6.4.2 0 .4-.1.6-.4l2.3-3.7c.2-.3.4-.4.6-.4.2 0 .4.1.6.4l2.9 4.7c.1.5-.2.9-.8.9Z" }),
        React.createElement("path", { d: "M21.3 22H28v-4h-7c-1.7 0-3-1.3-3-3V8H7.6C6.7 8 6 8.7 6 9.6v28.8c0 .9.7 1.6 1.6 1.6h10.7c-.2-.4-.3-.8-.3-1.2V25.2c0-1.8 1.5-3.2 3.3-3.2Z" })),
    'flag': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.8 9.378c-14.8-6.5-14.8 3.5-28-2.2-1.3-.6-2.8.4-2.8 1.8v31.9h4v-11c12.1 5 13.2-4.4 25.2.8 1.3.6 2.8-.4 2.8-1.9v-17.6c0-.8-.5-1.5-1.2-1.8Z" })),
    'folder': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 20H12c-1.1 0-2.3.9-2.6 1.9L4.6 38.1c0 .1-.1.2-.1.4v.4c0 .1 0 .1.1.2v.1c0 .1.1.1.1.2 0 0 0 .1.1.1l.2.2c.1 0 .1.1.2.1 0 0 .1 0 .1.1.1 0 .2.1.3.1H36c1.1 0 2.3-.9 2.6-1.9l4.9-16.2c.2-1-.4-1.9-1.5-1.9Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M12 18h26v-3c0-1.1-.9-2-2-2H22v-3c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v23l3.5-11.7C8.1 19.4 10 18 12 18Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'forbidden-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36.7 8.5C33.2 5.7 28.8 4 24 4 13 4 4 13 4 24c0 4.8 1.7 9.2 4.5 12.7L36.7 8.5ZM39.5 11.3 11.3 39.5C14.8 42.3 19.2 44 24 44c11 0 20-9 20-20 0-4.8-1.7-9.2-4.5-12.7Z" })),
    'form': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M10.9 12.1 8.8 10 6 12.8l4.9 4.9 7.9-7.9L16 7l-5.1 5.1ZM41.9 9.8h-20v4h20v-4ZM10.9 24.1 8.8 22 6 24.8l4.9 4.9 7.9-7.9L16 19l-5.1 5.1ZM41.9 21.8h-20v4h20v-4ZM10.9 36.1 8.8 34 6 36.8l4.9 4.9 7.9-7.9L16 31l-5.1 5.1ZM41.9 33.8h-20v4h20v-4Z" })),
    'gallery': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 14H6c-1.1 0-2 .9-2 2v22c0 1.1.9 2 2 2h30c1.1 0 2-.9 2-2V16c0-1.1-.9-2-2-2Zm-5.1 20.5H11.1c-1 0-1.4-.7-.9-1.6l4.5-7.8c.5-.9 1.3-.9 1.8 0l3.6 6.2c.5.9 1.3.9 1.8 0l3.6-6.2c.5-.9 1.3-.9 1.8 0l4.5 7.8c.5.9.1 1.6-.9 1.6Z" }),
        React.createElement("path", { d: "M42 8H12c-1.1 0-2 .9-2 2v2h30v22h2c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'gallery-plus': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.6 34.5H11.1c-1 0-1.4-.7-.9-1.6l4.5-7.8c.5-.9 1.3-.9 1.8 0l3.6 6.2c.5.9 1.3.9 1.8 0l3.6-6.2C27 22 31.3 20 35 20c1 0 2 .2 3 .4V16c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v22c0 1.1.9 2 2 2h22.7c-1.9-1.3-3.4-3.3-4.1-5.5Z" }),
        React.createElement("path", { d: "M43.9 29.6c0-.2-.1-.5-.1-.7 0-.1-.1-.2-.1-.3-.1-.2-.1-.5-.2-.7 0-.1-.1-.2-.1-.3-.1-.3-.3-.6-.4-.9-.2-.3-.3-.6-.5-.8l-.2-.2c-.1-.2-.3-.4-.5-.6l-.2-.2c-.2-.2-.4-.4-.6-.5-.1 0-.1-.1-.2-.1-.8-.9-1.8-1.4-2.8-1.8-.2-.1-.4-.1-.7-.2h-.1c-.2-.1-.4-.1-.6-.1h-.3c-.2 0-.3 0-.5-.1H35c-.4 0-.7 0-1 .1h-.3c-.2 0-.5.1-.7.1-.1 0-.2.1-.3.1-.2.1-.5.1-.7.2-.1 0-.2.1-.3.1-.3.1-.6.2-.8.4h-.1c-.6.3-1.2.7-1.7 1.2l-.1.1c-.2.2-.5.4-.7.7l-.1.1c-.2.3-.4.5-.6.8-1 1.5-1.7 3.3-1.7 5.2 0 1.7.5 3.2 1.2 4.5 0 .1.1.2.1.2.4.7.9 1.2 1.5 1.8l.2.2c.6.5 1.2.9 1.9 1.3.1 0 .2.1.3.1.4.2.7.3 1.1.4l1.2.3h2.5c.3 0 .6-.1.9-.1C40.9 39 44 35.3 44 31c0-.4 0-.7-.1-1.1v-.3ZM36 32v4h-2v-4h-4v-2h4v-4h2v4h4v2h-4ZM42 8H12c-1.1 0-2 .9-2 2v2h30v9.2c1.6.8 3 2 4 3.5V10c0-1.1-.9-2-2-2Z" })),
    'gear': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 20h-3.6c-.2-.7-.5-1.3-.8-1.9l2.6-2.6c.8-.8.8-2.1 0-2.8l-2.8-2.8c-.8-.8-2.1-.8-2.8 0L30 12.5c-.6-.3-1.2-.6-1.9-.8V8c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v3.6c-.7.2-1.3.5-1.9.8l-2.7-2.5c-.8-.8-2.1-.8-2.8 0l-2.8 2.8c-.8.8-.8 2.1 0 2.8l2.6 2.6c-.3.6-.6 1.2-.8 1.9H8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h3.6c.2.7.5 1.3.8 1.9l-2.6 2.6c-.8.8-.8 2.1 0 2.8l2.8 2.8c.8.8 2.1.8 2.8 0l2.6-2.6c.6.3 1.2.6 1.9.8V40c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-3.6c.7-.2 1.3-.5 1.9-.8l2.6 2.6c.8.8 2.1.8 2.8 0l2.8-2.8c.8-.8.8-2.1 0-2.8L35.4 30c.3-.6.6-1.2.8-1.9H40c1.1 0 2-.9 2-2v-4c0-1.2-.9-2.1-2-2.1ZM24 30c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6Z" })),
    'gift': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 38c0 2.2 1.8 4 4 4h6V22H12v16ZM26 42h6c2.2 0 4-1.8 4-4V22H26v20ZM28 16c2.8 0 5-2.2 5-5s-2.2-5-5-5c-1.6 0-3.1.8-4 2-.9-1.2-2.4-2-4-2-2.8 0-5 2.2-5 5s2.2 5 5 5H8v4h32.1v-4H28Zm0-8c1.7 0 3 1.3 3 3s-1.3 3-3 3h-3v-3c0-1.7 1.3-3 3-3Zm-11 3c0-1.7 1.3-3 3-3s3 1.3 3 3v3h-3c-1.7 0-3-1.3-3-3Z" })),
    'give': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21.5 21.8c1.6 0 3.4.2 4.9.3 1.6.2 3.4.3 5 .3h.9c2.1 0 3.8 1.6 4.1 3.9v.6c1.4-.3 2.6-.6 3.3-.8.1 0 .2-.1.3-.1V10c0-1.1-.9-2-2-2H20c-1.1 0-2 .9-2 2v12.2c.5-.2 1-.3 1.4-.3.6-.1 1.3-.1 2.1-.1Z" }),
        React.createElement("path", { d: "M41.4 27.8c-.3 0-.7.1-1.1.2-.1 0-.2.1-.3.1l-.9.3c-.3.1-.7.2-1 .3H38c-.4.1-.7.2-1.1.2h-.1c-.4.1-.8.2-1.2.2-.1.2-.2.3-.3.4-.1.1-.2.3-.4.4-.8.7-1.9 1.3-3.3 1.5-.5.1-1 .1-1.6.1-1.4 0-3-.2-4.8-.4h-.4c-.5 0-.8-.5-.8-.9 0-.1 0-.2.1-.3 0-.1.1-.1.1-.2 0 0 0-.1.1-.1l.2-.2s.1 0 .1-.1c.1 0 .2-.1.3-.1h.5c1.7.2 3.4.4 4.7.4.5 0 .9 0 1.2-.1 2.6-.4 3.4-2 3.2-3.2-.1-1.1-.9-2.1-2.1-2.1h-1c-1.6 0-3.4-.2-5.2-.4-1.6-.2-3.3-.3-4.7-.3-.7 0-1.3 0-1.9.1-.2 0-.5.1-.7.2-1 .3-2.2.8-3.6 1.3-.4.2-.8.3-1.2.5v11.3c3.5 1.2 7.8 2.6 10.4 2.8h.6c5.3 0 14.8-5.5 17.6-7.2 2.4-1.4 1.2-4.7-1.3-4.7ZM10 24H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V26c0-1.1-.9-2-2-2Z" })),
    'group': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.096 33.9c-3.988-1-7.377-2.7-7.377-2.7-.599-1-.698-2.6-.698-2.6s1.794-3.1 1.993-4.5c.1.1.2.1.3.1.398 0 .897-.5 1.395-2.6.698-2.6 0-3.1-.598-3.1h-.299c1.695-7.3-1.894-7.6-2.492-7.6h-.1c-.2-1.4-1.894-2.9-3.788-2.9h-.1c-.199 0-.498 0-.698.1-.598.1-1.495.2-2.392.2h-.2c.2.3.4.5.599.8.698.2 1.595.7 2.293 1.6 1.196 1.5 1.495 3.7 1.096 6.6.1.1.1.2.2.2.996 1.3.697 3.1.299 4.6-.3 1.1-.798 3.1-2.194 3.8-.398 1.1-1.096 2.4-1.495 3.1 0 .3.1.5.2.7 1.096.5 3.588 1.6 6.38 2.3 3.09.8 4.585 2.9 4.486 6.2 0 .6-.2 1.2-.499 1.8h4.785c.897 0 1.794-.9 1.794-1.8.1-1.8-.299-3.6-2.89-4.3Z" }),
        React.createElement("path", { d: "M33.12 33.9c-3.987-1-7.376-2.7-7.376-2.7-.599-1-.698-2.6-.698-2.6s1.794-3.1 1.994-4.5c.1.1.199.1.299.1.398 0 .897-.5 1.395-2.6.698-2.6 0-3.1-.598-3.1h-.299c1.695-7.3-1.894-7.6-2.492-7.6h-.1c0-.2-.1-.3-.2-.5.1-.1.1-.1.1-.2-.1-.1-.1-.2-.199-.3 0-.1-.1-.1-.2-.2l-.199-.2c-.1-.1-.1-.2-.199-.2l-.2-.2c-.1-.1-.199-.2-.299-.2 0 0-.1 0-.1-.1-.597-.5-1.395-.8-2.292-.8h-.1c-.199 0-.498 0-.697.1-.599.1-1.496.2-2.393.2h-.1c-1.993 0-3.987-.2-3.987-.2-.1.9.698 1.5.698 1.5-4.387 2.7-2.592 8.8-2.592 8.8v.1h-.3c-.597 0-1.295.5-.597 3.1.498 2.1.997 2.6 1.395 2.7.1 0 .2 0 .3-.1.199 1.4.797 3.1 1.993 4.5 0 0-.1 1.9-.797 2.8 0 0-3.29 1.6-7.278 2.6-2.592.7-3.09 2.5-2.99 4.3.2.7 1.096 1.6 1.993 1.6h28.411c.898 0 1.795-.9 1.795-1.8-.1-1.8-.499-3.6-3.09-4.3Z" })),
    'handle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 34H8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM40 34h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM26 34h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM12 20H8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM40 20h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM26 20h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2v-4c0-1.1-.9-2-2-2ZM12 6H8c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2ZM40 6h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2ZM26 6h-4c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Z" })),
    'happy': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25 4C14 4 5 13 5 24s9 20 20 20 20-9 20-20S36 4 25 4Zm6 12c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-12 0c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm6 18c-4.1 0-7.6-2.5-9.2-6h18.3c-1.5 3.6-5 6-9.1 6Z" })),
    'hard-drive': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-25.9 8.1c5-5 12.8-5.4 18.3-1.3L26.6 21c-1.6-1.3-3.9-1.3-5.4.2-1.5 1.5-1.5 3.8-.2 5.4l-8.1 5.8c-4.2-5.5-3.8-13.3 1.2-18.3Zm19.8 19.8c-5 5-12.8 5.4-18.3 1.3l5.8-8.1c1.6 1.3 3.9 1.3 5.4-.2 1.5-1.5 1.5-3.8.2-5.4l8.1-5.8c4.2 5.4 3.8 13.2-1.2 18.2Z" })),
    'heart': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32 7c-4 0-6.7 2.6-8 5.8C22.7 9.6 20 7 16 7 10 7 6 11.9 6 18.7c0 7.2 10.2 14.4 16.5 21.6.8.9 2.3.9 3.1 0C31.8 33.1 42 25.9 42 18.7 42 11.9 38 7 32 7Z" })),
    'help-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm0 32c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm2-10.3V29h-4v-5c0-1.1.9-2 2-2 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3h-4c0-3.9 3.1-7 7-7s7 3.1 7 7c0 3.2-2.1 5.8-5 6.7Z" })),
    'help-hexa': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.3 12.675-14.6-8.3c-1-.5-2.5-.5-3.5 0l-14.5 8.3c-1 .5-1.7 1.9-1.7 3v16.5c0 1.1.8 2.4 1.7 3l14.5 8.3c1 .5 2.5.5 3.5 0l14.5-8.3c1-.5 1.7-1.9 1.7-3v-16.5c.1-1.1-.7-2.5-1.6-3ZM24 35.875c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm2-10.3v3.3h-4v-5c0-1.1.9-2 2-2 1.7 0 3-1.3 3-3s-1.3-3-3-3-3 1.3-3 3h-4c0-3.9 3.1-7 7-7s7 3.1 7 7c0 3.2-2.1 5.9-5 6.7Z" })),
    'home': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m40.652 21.8-15.2-15.2c-.8-.8-2.1-.8-2.8 0l-15.2 15.2c-.8.8-.5 1.4.6 1.4h4v16c0 1.1.9 2 2 2h7v-13h6v13h7c1.1 0 2-.9 2-2v-16h4c1.1 0 1.4-.7.6-1.4Z" })),
    'image': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M6 10c-1.1 0-2 .9-2 2v24c0 1.1.9 2 2 2h36c1.1 0 2-.9 2-2V12c0-1.1-.9-2-2-2H6Zm7 21c-1.1 0-1.5-.8-1-1.7l5-8.5c.6-1 1.5-1 2 0l4 6.8c.6 1 1.5 1 2 0l4-6.8c.6-1 1.5-1 2 0l5 8.5c.6 1 .1 1.7-1 1.7H13Z" })),
    'image-text': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V16c0-1.1-.9-2-2-2Zm-2.7 14H8.7c-.6 0-.9-.5-.6-1l2.9-5c.3-.6.8-.6 1.2 0l2.3 4c.3.6.8.6 1.2 0l2.3-4c.3-.6.8-.6 1.2 0l2.9 5c.1.5-.2 1-.8 1ZM44 14H30v2h14v-2ZM44 20H30v2h14v-2ZM44 26H30v2h14v-2ZM38 32h-8v2h8v-2Z" })),
    'information': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 44c11 0 20-9 20-20S35 4 24 4 4 13 4 24s9 20 20 20Zm0-30c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-2 8h4v12h-4V22Z" })),
    'instagram': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14c-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10-4.5-10-10-10Zm0 16c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6Z" }),
        React.createElement("path", { d: "M34 6H14c-4.4 0-8 3.6-8 8v20c0 4.4 3.6 8 8 8h20c4.4 0 8-3.6 8-8V14c0-4.4-3.6-8-8-8ZM14 38c-2.2 0-4-1.8-4-4V14c0-2.2 1.8-4 4-4h20c2.2 0 4 1.8 4 4v20c0 2.2-1.8 4-4 4H14Z" }),
        React.createElement("path", { d: "M34 12c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2Z" })),
    'italic': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 8V6H20v2h3.4c.6 0 1.1.6 1 1.2l-7.1 30c-.1.5-.5.8-1 .8H12v2h16v-2h-3.4c-.6 0-1.1-.6-1-1.2l7.1-30c.1-.5.5-.8 1-.8H36Z" })),
    'left': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m27.6 34.8-9-9c-.8-.8-.8-2 0-2.8l9-9 2.8 2.8-7.6 7.6 7.6 7.6-2.8 2.8Z" })),
    'less-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.05 9.925c-7.8-7.9-20.4-7.9-28.2 0-7.8 7.9-7.8 20.5 0 28.3 7.8 7.8 20.5 7.8 28.3 0 7.8-7.8 7.8-20.5-.1-28.3Zm-22.1 16.1v-4h16v4h-16Z" })),
    'life-buoy': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.518 16.4c-3.2-7.8-10.7-12.4-18.5-12.4-2.6 0-5.1.5-7.6 1.5-10.3 4.3-15.1 16-10.9 26.2 3.2 7.7 10.7 12.3 18.5 12.3 2.6 0 5.1-.5 7.6-1.5 10.3-4.2 15.1-15.9 10.9-26.1Zm-24.6-7.2 2.3 5.5c-2.6 1.1-4.4 3-5.4 5.4l-5.5-2.3c1.5-3.7 4.5-6.9 8.6-8.6Zm0 29.6c-3.8-1.6-7-4.6-8.7-8.7l5.5-2.3c1.1 2.6 3.1 4.4 5.4 5.4l-2.2 5.6Zm6.1-8.8c-2.4 0-4.6-1.5-5.5-3.7-.6-1.5-.6-3.1 0-4.6.6-1.5 1.8-2.6 3.2-3.2.7-.3 1.5-.5 2.3-.5 2.4 0 4.6 1.5 5.5 3.7 1.3 3.1-.2 6.6-3.2 7.8-.7.4-1.5.5-2.3.5Zm6.1 8.8-2.3-5.5c2.6-1.1 4.4-3 5.4-5.4l5.5 2.3c-1.5 3.7-4.5 6.9-8.6 8.6Zm3.1-18.6c-1.1-2.6-3.1-4.4-5.4-5.4l2.3-5.6c3.8 1.6 7 4.6 8.7 8.7l-5.6 2.3Z" })),
    'link': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 19h-2v2c0 .7-.1 1.4-.2 2H36c2.2 0 4 1.8 4 4v2c0 2.2-1.8 4-4 4H24c-2.2 0-4-1.8-4-4v-2c0-2.2 1.8-4 4-4 1.1 0 2-.9 2-2v-2h-2c-4.4 0-8 3.6-8 8v2c0 4.4 3.6 8 8 8h12c4.4 0 8-3.6 8-8v-2c0-4.4-3.6-8-8-8Z" }),
        React.createElement("path", { d: "M8 21v-2c0-2.2 1.8-4 4-4h12c2.2 0 4 1.8 4 4v2c0 2.2-1.8 4-4 4-1.1 0-2 .9-2 2v2h2c4.4 0 8-3.6 8-8v-2c0-4.4-3.6-8-8-8H12c-4.4 0-8 3.6-8 8v2c0 4.4 3.6 8 8 8h2v-2c0-.7.1-1.4.2-2H12c-2.2 0-4-1.8-4-4Z" })),
    'link-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 22.9h-2v2c0 .7-.1 1.4-.2 2H36c2.2 0 4 1.8 4 4v2c0 2.2-1.8 4-4 4H24c-2.2 0-4-1.8-4-4v-2c0-2.2 1.8-4 4-4 1.1 0 2-.9 2-2v-2h-2c-4.4 0-8 3.6-8 8v2c0 4.4 3.6 8 8 8h12c4.4 0 8-3.6 8-8v-2c0-4.4-3.6-8-8-8Z" }),
        React.createElement("path", { d: "M8 24.9v-2c0-2.2 1.8-4 4-4h12c.7 0 1.4.2 2 .6v-4.3c-.6-.2-1.3-.3-2-.3H12c-4.4 0-8 3.6-8 8v2c0 4.4 3.6 8 8 8h2v-2c0-.7.1-1.4.2-2H12c-2.2 0-4-1.8-4-4ZM22 30.9v2h2c4.4 0 8-3.6 8-8v-2h-4v2c0 2.2-1.8 4-4 4-1.1 0-2 .9-2 2ZM30.9 20.8l4.1-4.1 4.1 4.1 2.8-2.8-4.1-4.1 4.1-4.1L39.1 7 35 11.1 30.9 7l-2.8 2.8 4.1 4.1-4.1 4.1 2.8 2.8Z" })),
    'linkedin': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.7 6H9.3C7.5 6 6 7.5 6 9.3v29.5C6 40.5 7.5 42 9.3 42h29.5c1.8 0 3.3-1.5 3.3-3.3V9.3C42 7.5 40.5 6 38.7 6ZM17.1 37.1c0 .5-.4.9-.9.9h-4c-.5 0-.9-.4-.9-.9V20.2c0-.5.4-.9.9-.9h4c.5 0 .9.4.9.9v16.9Zm-2.9-19.5c-2.1 0-3.8-1.7-3.8-3.8 0-2.1 1.7-3.8 3.8-3.8 2.1 0 3.8 1.7 3.8 3.8 0 2.1-1.7 3.8-3.8 3.8Zm24 19.6c0 .5-.4.9-.9.9H33c-.5 0-.9-.4-.9-.9v-7.9c0-1.2.3-5.2-3.1-5.2-2.7 0-3.2 2.7-3.3 4v9.1c0 .5-.4.9-.9.9h-4.2c-.5 0-.9-.4-.9-.9V20.1c0-.5.4-.9.9-.9h4.2c.5 0 .9.4.9.9v1.5c1-1.5 2.5-2.6 5.6-2.6 6.9 0 6.9 6.5 6.9 10v8.2Z" })),
    'list': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 4H12c-1.1 0-2 .9-2 2v36c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2ZM17 38c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm0-8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm0-8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm0-8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm15 23H22v-2h10v2Zm0-8H22v-2h10v2Zm0-8H22v-2h10v2Zm0-8H22v-2h10v2Z" })),
    'locked': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 20h-4v-6c0-5.5-4.5-10-10-10S14 8.5 14 14v6h-4c-1.1 0-2 .9-2 2v20c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V22c0-1.1-.9-2-2-2ZM26 35c0 1.1-.9 2-2 2s-2-.9-2-2v-6c0-1.1.9-2 2-2s2 .9 2 2v6Zm4-15H18v-6c0-3.3 2.7-6 6-6s6 2.7 6 6v6Z" })),
    'loyalty-card': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 14v-4c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v4h40ZM4 16v22c0 1.1.9 2 2 2h36c1.1 0 2-.9 2-2V16H4Zm12 16h-6v-2h6v2Zm4-6H10v-2h10v2Zm8-4c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-1.3 11.1c-.4-.4-.4-1 0-1.4l9-9c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-9 9c-.4.4-1 .4-1.4 0Zm9.3.9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'magnify': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26.348 6a15.631 15.631 0 0 0-15.652 15.652c0 3.548 1.252 6.887 3.234 9.496l-7.304 7.304c-.835.835-.835 2.087 0 2.922.417.417.94.626 1.461.626a2.06 2.06 0 0 0 1.46-.626l7.305-7.304c2.609 1.982 5.948 3.234 9.496 3.234 8.66 0 15.652-6.991 15.652-15.652C42 12.992 35.009 6 26.348 6Zm0 27.13A11.445 11.445 0 0 1 14.87 21.652a11.445 11.445 0 0 1 11.478-11.478 11.445 11.445 0 0 1 11.478 11.478A11.445 11.445 0 0 1 26.348 33.13Z" })),
    'magnify-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26.5 7c-8.3 0-15 6.7-15 15 0 3.4 1.2 6.6 3.1 9.1l-7 7c-.8.8-.8 2 0 2.8.4.4.9.6 1.4.6.5 0 1-.2 1.4-.6l7-7c2.5 1.9 5.7 3.1 9.1 3.1 8.3 0 15-6.7 15-15s-6.7-15-15-15Zm0 26c-6.1 0-11-4.9-11-11s4.9-11 11-11 11 4.9 11 11-4.9 11-11 11Z" }),
        React.createElement("path", { d: "m30.1 15.6-3.6 3.6-3.6-3.6-2.8 2.8 3.6 3.6-3.6 3.6 2.8 2.8 3.6-3.6 3.6 3.6 2.8-2.8-3.6-3.6 3.6-3.6-2.8-2.8Z" })),
    'mail': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16.6 24 4 11.4v25.2L16.6 24ZM24.7 27.9 42.6 10H5.4l17.9 17.9c.4.4 1 .4 1.4 0Z" }),
        React.createElement("path", { d: "m30 25.4-3.9 3.9c-.6.6-1.3.9-2.1.9s-1.6-.3-2.1-.9L18 25.4 5.4 38h37.2L30 25.4ZM31.4 24 44 36.6V11.4L31.4 24Z" })),
    'mail-open': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.5 7.675c-.8-.9-2.2-.9-3 0l-4.7 3.3h12.3l-4.6-3.3ZM32.1 29.175l9.9 9.9v-17.2l-9.9 7.3ZM24 32.775l11.8-8.8h.2v-9c0-1.1-.9-2-2-2H14c-1.1 0-2 .9-2 2v9h.2l11.8 8.8ZM38 16.675v5.7l3.9-2.9-3.9-2.8ZM6 21.875v17.7l10.2-10.1-10.2-7.6Z" }),
        React.createElement("path", { d: "M24.6 34.775c-.2.2-.4.2-.6.2-.2 0-.4-.1-.6-.2l-5.6-4.2-11 11c.3.3.8.4 1.2.4h32c.6 0 1.1-.2 1.5-.6l-11-11-5.9 4.4ZM10 16.675l-3.9 2.8 3.9 2.9v-5.7Z" })),
    'margin': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m30.1 15.7-2.8 2.8 3.5 3.5H17.2l3.5-3.5-2.8-2.8-6.9 6.9c-.8.8-.8 2 0 2.8l6.9 6.9 2.8-2.8-3.5-3.5h13.6l-3.5 3.5 2.8 2.8 6.9-6.9c.8-.8.8-2 0-2.8l-6.9-6.9ZM8 8H4v32h4V8ZM44 8h-4v32h4V8Z" })),
    'megaphone': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.2 6.2 18.5 17.4c-.2 0-.3-.1-.5-.1H8c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h4.2c-.1.3-.1.7-.1 1 0 .3-.1.7-.1 1 0 5.5 4.5 10 10 10 .4 0 .9 0 1.3-.1.1 0 .2 0 .3-.1.4-.1.7-.1 1.1-.2h.2c.4-.1.8-.3 1.2-.5.1 0 .1-.1.2-.1.4-.2.7-.4 1-.6 0 0 .1 0 .1-.1.8-.5 1.4-1.1 2-1.8.6-.7 1.1-1.5 1.5-2.3l9.2 4.7c1 .5 1.8 0 1.8-1.1v-32c.1-.9-.7-1.4-1.7-.9ZM16.4 29.3H18c.2 0 .4 0 .5-.1l8.9 4.6c-1 2.1-3 3.5-5.4 3.5-3.3 0-6-2.7-6-6 0-.7.1-1.4.4-2Z" })),
    'message': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v26c0 1.1.9 2 2 2h12l4 6 4-6h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-6 22H14v-2h20v2Zm0-6H14v-2h20v2Zm0-6H14v-2h20v2Z" })),
    'message-check': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26.1 24.5c.4-.5.7-.9 1.1-1.3.3-.3.6-.5.9-.8.1 0 .1-.1.2-.1.1-.1.3-.2.4-.3H10v-2h20v1.2c.1 0 .2-.1.2-.1 1.5-.7 3.1-1.1 4.7-1.1h1V10c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v22c0 1.1.9 2 2 2h10l4 6 4-6h.4v-.1c-.1-.4-.2-.8-.2-1.2v-.2c-.1-.4-.1-.8-.1-1.2 0-.9.1-1.8.3-2.7 0-.2.1-.4.2-.6H10v-2h15.2c.2-.5.5-.9.8-1.3 0-.1.1-.1.1-.2ZM10 14h20v2H10v-2Z" }),
        React.createElement("path", { d: "M41.4 24.6C39.6 22.9 37.3 22 35 22c-1.3 0-2.7.3-3.9.9-.3.1-.5.3-.8.4 0 0-.1 0-.1.1-.2.1-.5.3-.7.5l-.1.1c-.3.2-.5.4-.7.7-.3.3-.6.7-.9 1l-.1.1c-.5.7-.8 1.4-1.1 2.2-.1.3-.2.7-.3 1-.2.7-.2 1.5-.2 2.2 0 .3 0 .7.1 1v.1c.1.3.1.7.2 1v.1c.1.2.1.4.2.6.1.2.1.3.2.5v.1c.1.3.3.6.4.8 0 .1.1.2.2.3.1.2.3.4.4.6.1.1.1.2.2.3.2.3.5.6.7.8l.7.7.1.1c.2.2.4.3.7.5 0 0 .1 0 .1.1.3.2.5.3.8.4 1.2.6 2.6.9 3.9.9 2.3 0 4.6-.9 6.4-2.6 3.5-3.7 3.5-9.3 0-12.9Zm-5.5 8.1-2.4 2.4-3.4-3.4 1.4-1.4 1.9 2 4.9-4.9 1.4 1.4-3.8 3.9Z" })),
    'message-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.4 24.6C39.6 22.9 37.3 22 35 22c-1.3 0-2.7.3-3.9.9-.3.1-.5.3-.8.4 0 0-.1 0-.1.1-.2.1-.5.3-.7.5l-.1.1c-.3.2-.5.4-.7.7-.3.3-.6.7-.9 1l-.1.1c-.5.7-.8 1.4-1.1 2.2-.1.3-.2.7-.3 1-.2.7-.2 1.5-.2 2.2 0 .3 0 .7.1 1v.1c.1.3.1.7.2 1v.1c.1.2.1.4.2.6.1.2.1.3.2.5v.1c.1.3.3.6.4.8 0 .1.1.2.2.3.1.2.3.4.4.6.1.1.1.2.2.3.2.3.5.6.7.8l.7.7.1.1c.2.2.4.3.7.5 0 0 .1 0 .1.1.3.2.5.3.8.4 1.2.6 2.6.9 3.9.9 2.3 0 4.6-.9 6.4-2.6 3.5-3.7 3.5-9.3 0-12.9Zm-2.2 9.2-1.4 1.4-2.8-2.8-2.8 2.8-1.3-1.2-.2-.2 2.8-2.8-1.5-1.6-1.2-1.2 1.5-1.5 2.8 2.8 2.8-2.8 1.4 1.4-2.9 2.9 2.8 2.8Z" }),
        React.createElement("path", { d: "M26.1 24.5c.4-.5.7-.9 1.1-1.3.3-.3.6-.5.9-.8.1 0 .1-.1.2-.1.1-.1.3-.2.4-.3H10v-2h20v1.2c.1 0 .2-.1.3-.1 1.5-.7 3.1-1.1 4.7-1.1h1V10c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v22c0 1.1.9 2 2 2h10l4 6 4-6h.4v-.1c-.1-.4-.2-.8-.2-1.2v-.2c-.1-.4-.1-.8-.1-1.2 0-.9.1-1.8.3-2.7 0-.2.1-.4.2-.6H10v-2h15.2c.2-.5.5-.9.8-1.3 0-.1.1-.1.1-.2ZM10 14h20v2H10v-2Z" })),
    'mobile': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M33 4H15c-1.1 0-2 .9-2 2v36c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2Zm-9 36c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'money': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 38h8v-8c-4.4 0-8 3.6-8 8ZM4 30v8h8c0-4.4-3.6-8-8-8ZM12 10H4v8c4.4 0 8-3.6 8-8ZM36 10c0 4.4 3.6 8 8 8v-8h-8Z" }),
        React.createElement("path", { d: "M34 10H14c0 5.5-4.5 10-10 10v8c5.5 0 10 4.5 10 10h20c0-5.5 4.5-10 10-10v-8c-5.5 0-10-4.5-10-10ZM24 32c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8Z" })),
    'money-bag': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 24c0-5.5-4.5-10-10-10h-.4s3.2-5.6 3.9-7.6c.4-1.2-.7-2.4-2-2.4h-11c-1.3 0-2.4 1.2-2 2.4.7 2 3.9 7.6 3.9 7.6H20c-5.5 0-10 4.5-10 10L8 42c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2l-2-18ZM26 36h-1v2h-2v-2h-3v-2h6c1.1 0 2-.9 2-2s-.9-2-2-2h-3c-2.2 0-4-1.8-4-4s1.8-4 4-4v-2h2v2h3v2h-5c-1.1 0-2 .9-2 2s.9 2 2 2h3c2.2 0 4 1.8 4 4s-1.8 4-4 4Z" })),
    'navigation': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42.048 24c0-1.7-1.3-3-3-3-1.6 0-3 1.3-3 3v-2c0-1.7-1.3-3-3-3-1.6 0-3 1.3-3 3v-2c0-1.7-1.3-3-3-3-1.6 0-3 1.3-3 3V7c0-1.7-1.3-3-3-3-1.6 0-3 1.3-3 3v20.8l-5.9-7c-1.1-1.3-3.1-1.7-4.6-.9-1.5.8-2 2.7-1.1 4.1l7.6 13c2.8 4.9 4 7 8 7h14c5.6 0 6-5.3 6-11.8V24Z" })),
    'network': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" }),
        React.createElement("path", { d: "M31 24c0-.6-.1-1.1-.2-1.6l3.2-1.9c1.6 1.1 3.7 1.3 5.5.3 2.4-1.4 3.2-4.4 1.8-6.8-1.4-2.4-4.4-3.2-6.8-1.8-1.8 1-2.7 3-2.5 4.9L28.8 19c-1.3-1.3-2.9-2-4.8-2s-3.5.7-4.8 1.9L16 17.1c.2-1.9-.7-3.9-2.5-4.9-2.4-1.4-5.4-.6-6.8 1.8-1.4 2.4-.6 5.4 1.8 6.8 1.8 1 3.9.8 5.5-.3l3.2 1.9c-.1.5-.2 1-.2 1.6 0 3.2 2.1 5.8 5 6.7v3.7c-1.8.8-3 2.5-3 4.6 0 2.8 2.2 5 5 5s5-2.2 5-5c0-2-1.2-3.8-3-4.6v-3.7c2.9-.9 5-3.5 5-6.7Z" }),
        React.createElement("path", { d: "M41.782 32.9a5 5 0 1 0-9.59-2.838 5 5 0 0 0 9.59 2.838ZM11.473 36.477a5 5 0 1 0-.945-9.955 5 5 0 0 0 .945 9.955Z" })),
    'newsletter': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.2 23.394-31.4-15.2c-1-.5-1.8 0-1.8 1.1v9.3l-5 1.4c-.5.2-1 .7-1 1.3 0 .6.4 1 1 1.1l5 .3 26 1.5c1.1.1 1.1.2 0 .2l-26 1.5-5 .3c-.5 0-1 .5-1 1.1 0 .6.4 1.1 1 1.3l5 1.4v9.3c0 1.1.8 1.6 1.8 1.1l31.4-15.3c1-.4 1-1.2 0-1.7Z" })),
    'number-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.05 9.925c-7.8-7.9-20.4-7.9-28.2 0-7.8 7.9-7.8 20.5 0 28.3 7.8 7.8 20.5 7.8 28.3 0 7.8-7.9 7.8-20.5-.1-28.3Zm-12.1 24.1h-4v-16h-3v-4h7v20Z" })),
    'page': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 14V4H12c-1.1 0-2 .9-2 2v36c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V18H28c-2.2 0-4-1.8-4-4Z" }),
        React.createElement("path", { d: "M28 16h10L26 4v10c0 1.1.9 2 2 2Z" })),
    'page-plus': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 16h8L32 6v8c0 1.1.9 2 2 2Z" }),
        React.createElement("path", { d: "M34 18c-2.2 0-4-1.8-4-4V6H20c-1.1 0-2 .9-2 2v6.4c4.6 1.3 8 5.5 8 10.6s-3.4 9.3-8 10.6V40c0 1.1.9 2 2 2h20c1.1 0 2-.9 2-2V18h-8Z" }),
        React.createElement("path", { d: "M15 16c-5 0-9 4-9 9s4 9 9 9 9-4 9-9-4-9-9-9Zm5 10h-4v4h-2v-4h-4v-2h4v-4h2v4h4v2Z" })),
    'pause': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 8h-4c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2ZM34 8h-4c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'pending-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm-8 22c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm8 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm8 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'percent': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39.4 8.6c-.8-.8-2-.8-2.8 0l-28 28c-.8.8-.8 2 0 2.8.4.4.9.6 1.4.6.5 0 1-.2 1.4-.6l28-28c.8-.8.8-2 0-2.8ZM15 22c3.9 0 7-3.1 7-7s-3.1-7-7-7-7 3.1-7 7 3.1 7 7 7Zm0-10c1.7 0 3 1.3 3 3s-1.3 3-3 3-3-1.3-3-3 1.3-3 3-3ZM33 26c-3.9 0-7 3.1-7 7s3.1 7 7 7 7-3.1 7-7-3.1-7-7-7Zm0 10c-1.7 0-3-1.3-3-3s1.3-3 3-3 3 1.3 3 3-1.3 3-3 3Z" })),
    'person': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 40c0-2-.5-4.1-3.4-4.8-4.5-1.1-8.4-3.1-8.4-3.1-.6-1.1-.7-3-.7-3s2-3.5 2.2-5.1c.1.1.2.2.4.2.4 0 1-.6 1.6-3 .8-2.9.1-3.5-.6-3.5h-.3c2-8.2-2.1-8.5-2.8-8.5h-.1c-.3-1.5-2.3-3.1-4.4-3.2h-.1c-.3 0-.5 0-.8.1-.7.1-1.7.2-2.6.2h-.1c-2.2 0-4.5-.3-4.5-.3-.1 1 .8 1.7.8 1.7-5 3-2.9 9.9-2.9 9.9v.1H15c-.7 0-1.4.6-.6 3.5.6 2.3 1.2 3 1.6 3 .1 0 .2 0 .3-.1.3 1.6.9 3.5 2.3 5 0 0-.1 2.1-.9 3.1 0 0-3.7 1.7-8.3 2.9C6.5 35.9 6 38 6 40c0 1 1 2 2 2h32c1 0 2-1 2-2Z" })),
    'person-check': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.212 31c0-2.9 1.1-5.7 3.2-7.8.4-.4.8-.7 1.2-1 .1-.2.1-.4.2-.6.7-2.6 0-3.1-.6-3.1h-.3c1.7-7.3-1.9-7.6-2.5-7.6h-.1c0-.2-.1-.3-.2-.5.1-.1.1-.1.1-.2-.1-.1-.1-.2-.2-.3 0-.1-.1-.1-.2-.2l-.2-.2c-.1-.1-.1-.2-.2-.2l-.2-.2c-.1-.1-.2-.2-.3-.2 0 0-.1 0-.1-.1-.6-.5-1.4-.8-2.3-.8h-.1c-.2 0-.5 0-.7.1-.6.1-1.5.2-2.4.2h-.1c-2 0-4-.2-4-.2-.1.9.7 1.5.7 1.5-4.4 2.7-2.6 8.8-2.6 8.8v.1h-.3c-.6 0-1.3.5-.6 3.1.5 2.1 1 2.6 1.4 2.7.1 0 .2 0 .3-.1.2 1.4.8 3.1 2 4.5 0 0-.1 1.9-.8 2.8 0 0-3.3 1.6-7.3 2.6-2.6.7-3.1 2.5-3 4.3.2.7 1.1 1.6 2 1.6h22.9l-1.5-1.2c-2.1-2.1-3.2-4.9-3.2-7.8Z" }),
        React.createElement("path", { d: "M41.612 24.6c-1.8-1.7-4.1-2.6-6.4-2.6-2.3 0-4.6.9-6.4 2.6-3.5 3.5-3.5 9.2 0 12.7 1.8 1.8 4.1 2.6 6.4 2.6 2.3 0 4.6-.9 6.4-2.6 3.5-3.5 3.5-9.1 0-12.7Zm-7.6 10.6-3.8-3.8 1.4-1.4 2.4 2.4 4.8-4.8 1.4 1.4-6.2 6.2Z" })),
    'person-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M27.412 23.2c.4-.4.8-.7 1.2-1 .1-.2.1-.4.2-.6.7-2.6 0-3.1-.6-3.1h-.3c1.7-7.3-1.9-7.6-2.5-7.6h-.1c0-.2-.1-.3-.2-.5.1-.1.1-.1.1-.2-.1-.1-.1-.2-.2-.3 0-.1-.1-.1-.2-.2l-.2-.2c-.1-.1-.1-.2-.2-.2l-.2-.2c-.1-.1-.2-.2-.3-.2 0 0-.1 0-.1-.1-.6-.5-1.4-.8-2.3-.8h-.1c-.2 0-.5 0-.7.1-.6.1-1.5.2-2.4.2h-.1c-2 0-4-.2-4-.2-.1.9.7 1.5.7 1.5-4.4 2.7-2.6 8.8-2.6 8.8v.1h-.3c-.6 0-1.3.5-.6 3.1.5 2.1 1 2.6 1.4 2.7.1 0 .2 0 .3-.1.2 1.4.8 3.1 2 4.5 0 0-.1 1.9-.8 2.8 0 0-3.3 1.6-7.3 2.6-2.6.7-3.1 2.5-3 4.3.2.7 1.1 1.6 2 1.6h22.9l-1.5-1.2c-4.3-4.3-4.3-11.3 0-15.6Z" }),
        React.createElement("path", { d: "M41.612 24.6c-1.8-1.7-4.1-2.6-6.4-2.6-2.3 0-4.6.9-6.4 2.6-3.5 3.5-3.5 9.2 0 12.7 1.8 1.8 4.1 2.6 6.4 2.6 2.3 0 4.6-.9 6.4-2.6 3.5-3.5 3.5-9.1 0-12.7Zm-2.2 9.2-1.4 1.4-2.8-2.8-2.8 2.8-1.4-1.4 2.8-2.8-2.8-2.8 1.4-1.4 2.8 2.8 2.8-2.8 1.4 1.4-2.8 2.8 2.8 2.8Z" })),
    'person-plus': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24.212 31c0-3.6 1.7-6.8 4.4-8.8.1-.2.1-.4.2-.7.7-2.6 0-3.1-.6-3.1h-.3c1.7-7.3-1.9-7.6-2.5-7.6h-.1c0-.2-.1-.3-.2-.5.1 0 .1 0 .1-.1-.1-.1-.1-.2-.2-.3 0-.1-.1-.1-.2-.2l-.2-.2c-.1-.1-.1-.2-.2-.2l-.2-.2c-.1-.1-.2-.2-.3-.2 0 0-.1 0-.1-.1-.6-.5-1.4-.8-2.3-.8h-.1c-.2 0-.5 0-.7.1-.6.1-1.5.2-2.4.2h-.1c-2 0-4-.2-4-.2-.1.9.7 1.5.7 1.5-4.4 2.7-2.6 8.8-2.6 8.8v.1h-.3c-.6 0-1.3.5-.6 3.1.5 2.1 1 2.6 1.4 2.7.1 0 .2 0 .3-.1.2 1.4.8 3.1 2 4.5 0 0-.1 1.9-.8 2.8 0 0-3.3 1.6-7.3 2.6-2.6.7-3.1 2.5-3 4.3.2.7 1.1 1.6 2 1.6h22.9c-2.8-2-4.7-5.3-4.7-9Z" }),
        React.createElement("path", { d: "M35.212 22c-5 0-9 4-9 9s4 9 9 9 9-4 9-9-4-9-9-9Zm5 10h-4v4h-2v-4h-4v-2h4v-4h2v4h4v2Z" })),
    'phone': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 28h-8.6c-.8 0-1.6.5-1.9 1.3l-2.4 6c-.4.9-1.3 1.4-2.3 1.2-6.5-1.6-11.7-6.7-13.3-13.3-.2-1 .3-1.9 1.2-2.3l6-2.4c.8-.3 1.3-1 1.3-1.9V8c0-1.1-.9-2-2-2h-3c-3.2.1-6.3 2.3-7.5 5.2-1.2 3.3-1.8 6.8-1.4 10.6 1.1 10.5 9.6 19 20.1 20.1 3.7.4 7.3-.1 10.5-1.4 2.9-1.1 5.1-4.3 5.2-7.5v-3c.1-1.1-.8-2-1.9-2Z" })),
    'phone-arrow': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.023 28h-8.6c-.8 0-1.6.5-1.9 1.3l-2.4 6c-.4.9-1.3 1.4-2.3 1.2-6.5-1.6-11.7-6.7-13.3-13.3-.2-1 .3-1.9 1.2-2.3l6-2.4c.8-.3 1.3-1 1.3-1.9V8c0-1.1-.9-2-2-2h-3c-3.2.1-6.3 2.3-7.5 5.2-1.2 3.3-1.8 6.8-1.4 10.6 1.1 10.5 9.6 19 20.1 20.1 3.7.4 7.3-.1 10.5-1.4 2.9-1.1 5.1-4.3 5.2-7.5v-3c.1-1.1-.8-2-1.9-2Z" }),
        React.createElement("path", { d: "M26.023 18h8.8l-2.5 2.5 2.8 2.8 5.9-5.9c.8-.8.8-2 0-2.8l-5.9-5.9-2.8 2.8 2.5 2.5h-6.8V6h-4v10c0 1.1.9 2 2 2Z" })),
    'phone-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m31.923 18.9 3.6-3.6 3.6 3.6 2.8-2.8-3.6-3.6 3.6-3.6-2.8-2.8-3.6 3.6-3.6-3.6-2.8 2.8 3.6 3.6-3.6 3.6 2.8 2.8Z" }),
        React.createElement("path", { d: "M40.023 28h-8.6c-.8 0-1.6.5-1.9 1.3l-2.4 6c-.4.9-1.3 1.4-2.3 1.2-6.5-1.6-11.7-6.7-13.3-13.3-.2-1 .3-1.9 1.2-2.3l6-2.4c.8-.3 1.3-1 1.3-1.9V8c0-1.1-.9-2-2-2h-3c-3.2.1-6.3 2.3-7.5 5.2-1.2 3.3-1.8 6.8-1.4 10.6 1.1 10.5 9.6 19 20.1 20.1 3.7.4 7.3-.1 10.5-1.4 2.9-1.1 5.1-4.3 5.2-7.5v-3c.1-1.1-.8-2-1.9-2Z" })),
    'pinterest': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 24.603c.3-11.4-8.9-20.8-20.3-20.6-10.9.2-19.8 9.2-19.7 20.2.1 7.8 4.6 14.6 11.3 17.8.4.2.8-.1.8-.5 0-1.3 0-2.7.3-3.8.3-1.1 1.2-5.3 1.9-8.1.3-1.3.4-2.7.1-4.1-.1-.4-.1-1-.1-1.6 0-3 1.7-5.2 3.8-5.2 1.8 0 2.7 1.4 2.7 3 0 1.8-1.2 4.6-1.8 7.1-.5 2.1 1.1 3.8 3.1 3.8 3.8 0 6.7-4 6.7-9.7 0-5.1-3.7-8.6-8.9-8.6-6 0-9.6 4.5-9.6 9.2 0 1.8.7 3.8 1.6 4.9.2.2.2.4.1.6-.2.7-.5 2.1-.6 2.4-.1.4-.3.5-.7.3-2.7-1.2-4.3-5.1-4.3-8.2 0-6.7 4.9-12.9 14-12.9 7.4 0 13.1 5.3 13.1 12.3 0 7.3-4.6 13.2-11 13.2-1.7 0-3.3-.7-4.3-1.6-.3-.3-.8-.2-.9.2-.3 1.3-.9 3.3-1 4-.3 1.2-1 2.6-1.6 3.8-.2.3 0 .7.4.8 1.6.4 3.3.6 5.1.6 10.5 0 19.5-8.8 19.8-19.3Z" })),
    'play': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.3 25.2c1-.5 1-1.4 0-2L9.7 7.2c-.9-.5-1.7-.1-1.7 1v32c0 1.1.8 1.6 1.7 1l28.6-16Z" })),
    'play-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm8.3 21.1-12.7 8.5c-.9.6-1.7.2-1.7-.9V15.3c0-1.1.7-1.5 1.7-.9l12.7 8.5c1 .6 1 1.6 0 2.2Z" })),
    'plus-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.05 9.925c-7.8-7.9-20.4-7.9-28.2 0-7.8 7.9-7.8 20.5 0 28.3 7.8 7.8 20.5 7.8 28.3 0 7.8-7.8 7.8-20.5-.1-28.3Zm-12.1 24.1h-4v-8h-8v-4h8v-8h4v8h8v4h-8v8Z" })),
    'pointer': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C15.7 4 9 10.7 9 19c0 9.9 13.9 24.1 13.9 24.1.6.9 1.6.9 2.2 0 0 0 13.9-14.5 13.9-24.1 0-8.3-6.7-15-15-15Zm0 20c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5Z" })),
    'print': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 14H6c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h6V22c0-1.1.9-2 2-2h20c1.1 0 2 .9 2 2v14h6c1.1 0 2-.9 2-2V16c0-1.1-.9-2-2-2ZM34 4H14v8h20V4Z" }),
        React.createElement("path", { d: "M14 44h20V22H14v22Zm4-18h12v2H18v-2Zm0 6h12v2H18v-2Zm0 6h12v2H18v-2Z" })),
    'puzzle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38.1 24.092c-1.6-.3-3 .1-4.1.9l-.4.3a1 1 0 0 1-1.6-.8v-8.5h-8.5c-.8 0-1.3-.9-.8-1.6l.3-.4c.8-1.1 1.2-2.5.9-4.1-.4-1.9-2-3.4-3.9-3.8-3.2-.6-6 1.8-6 4.9 0 1.1.4 2.2 1 3l.3.4a1 1 0 0 1-.8 1.6H6v8.5c0 .8.9 1.3 1.6.8l.4-.3c1.1-.8 2.5-1.2 4.1-.9 1.9.4 3.4 2 3.8 3.9.6 3.2-1.8 6-4.9 6-1.1 0-2.2-.4-3-1l-.4-.3a1 1 0 0 0-1.6.8v8.5h26v-8.5c0-.8.9-1.3 1.6-.8l.4.3c.8.6 1.9 1 3 1 3.1 0 5.5-2.8 4.9-6-.4-1.9-1.9-3.5-3.8-3.9Z" })),
    'quote': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14.9 12c-2 0-3.7.7-5.2 2.2-1.3 1.4-2.1 3.2-2.1 5.3 0 1.6.4 3.1 1.3 4.2.8 1.2 2.1 2.2 3.8 3L6 36h6.2l6.5-9c1.4-2 2.4-3.5 2.8-4.5.4-1 .7-2 .7-3 0-2.1-.7-3.9-2.1-5.4-1.4-1.4-3.1-2.1-5.2-2.1ZM39.9 14.1c-1.3-1.4-3-2.1-5.1-2.1-2 0-3.8.7-5.2 2.2-1.4 1.5-2.2 3.2-2.2 5.3 0 1.6.4 3.1 1.3 4.2.8 1.2 2.1 2.2 3.8 3L25.9 36h6.2l6.5-9c1.4-1.9 2.3-3.4 2.7-4.4.5-1 .7-2.1.7-3.1 0-2.2-.7-3.9-2.1-5.4Z" })),
    'quotemaker': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M7.571 6H30.43C31.293 6 32 6.72 32 7.6v32.8c0 .88-.707 1.6-1.571 1.6H7.57C6.707 42 6 41.28 6 40.4V7.6C6 6.72 6.707 6 7.571 6Zm29.79 2h3.28c.778 0 1.41.632 1.362 1.361V12H36V9.361C35.951 8.631 36.583 8 37.361 8ZM36 35.013V14h6.003v21.013H36ZM36 37h6.003L39.8 39.934a1 1 0 0 1-1.6 0L36 37ZM19.674 12.265a.99.99 0 0 0-1.348 0l-6.02 5.636c-.64.598-.209 1.656.673 1.656h.157v6.483c0 .53.438.96.978.96h9.772c.54 0 .978-.43.978-.96v-6.483h.157c.882 0 1.313-1.058.674-1.656l-6.02-5.636ZM12 31a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H13a1 1 0 0 1-1-1Zm0 4a1 1 0 0 1 1-1h6a1 1 0 1 1 0 2h-6a1 1 0 0 1-1-1Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'radio-button': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm0 36c-8.8 0-16-7.2-16-16S15.2 8 24 8s16 7.2 16 16-7.2 16-16 16Z" }),
        React.createElement("path", { d: "M24 34c5.523 0 10-4.477 10-10s-4.477-10-10-10-10 4.477-10 10 4.477 10 10 10Z" })),
    'reactivity': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M12 20c0 4.4 3.6 8 8 8s8-3.6 8-8-3.6-8-8-8-8 3.6-8 8Zm12 0c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4 4 1.8 4 4Z" }),
        React.createElement("path", { d: "M20 32c-6.6 0-12-5.4-12-12S13.4 8 20 8s12 5.4 12 12c0 2.1-.6 4.1-1.5 5.9l2 .7 1.8.6C35.4 25 36 22.6 36 20c0-8.8-7.2-16-16-16S4 11.2 4 20s7.2 16 16 16c2.6 0 5-.6 7.2-1.7l-1.4-3.8c-1.7.9-3.7 1.5-5.8 1.5Z" }),
        React.createElement("path", { d: "m43.6 40.9-4.4-4.4 3.1-3.1c.5-.5.4-1.1-.3-1.3L33.2 29l-1.4-.5-3.1-1.1-1-.3c-.1-.1-.3-.1-.4-.1-.4 0-.6.3-.4.8l.5 1.3.1.2.3.7 2.3 6.5 1.9 5.4c.1.4.4.6.7.6.2 0 .4-.1.6-.3l3.1-3.1 4.4 4.4c.2.2.6.4.9.4.3 0 .7-.1.9-.4l.9-.9c.6-.4.6-1.2.1-1.7Z" })),
    'refresh': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M39 24h-3c0-7.7-6.3-14-14-14S8 16.3 8 24s6.3 14 14 14v-3.5c-5.8 0-10.5-4.7-10.5-10.5S16.2 13.5 22 13.5 32.5 18.2 32.5 24H29c-1.1 0-1.5.8-1 1.7l5 8.5c.6 1 1.5 1 2 0l5-8.5c.5-.9.1-1.7-1-1.7Z" })),
    'report': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M36 4H12c-1.1 0-2 .9-2 2v36c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2ZM19.7 15.3l2.3 2.3 5.3-5.3 1.4 1.4-6.7 6.7-3.7-3.7 1.4-1.4ZM30 36H18v-2h12v2Zm0-8H18v-2h12v2Z" })),
    'restaurant': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35 10c-.6 0-1.2.1-1.8.2C31.7 6.6 28.2 4 24 4c-4.2 0-7.7 2.6-9.2 6.2-.6-.1-1.2-.2-1.8-.2-5 0-9 4-9 9s4 9 9 9v8h22v-8c5 0 9-4 9-9s-4-9-9-9ZM13 42c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2v-4H13v4Z" })),
    'review': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m23.952 37.7-11 6.1c-.9.5-1.9-.3-1.7-1.2l2.1-12.9-9-9.2c-.7-.7-.3-1.9.7-2l12.4-1.9 5.5-11.7c.6-1.2 1.6-1.2 2.1 0l5.5 11.7 12.4 1.8c1 .1 1.3 1.3.7 2l-9 9.2 2.1 12.9c.2 1-.9 1.7-1.7 1.2l-11.1-6Z" })),
    'right': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M20.8 34.8 18 32l7.6-7.6-7.6-7.6 2.8-2.8 9 9c.8.8.8 2 0 2.8l-9 9Z" })),
    'rotation': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.3 15-7.9-8-2.8 2.8 4.5 4.5H33c-14.9 0-27 12.1-27 27h4c0-12.7 10.3-23 23-23h2.1l-4.5 4.5 2.8 2.8 7.9-8c.8-.6.8-1.9 0-2.6Z" })),
    'save': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m41.4 16.6-10-10C31 6.2 30.5 6 30 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V18c0-.5-.2-1-.6-1.4ZM38 38H10V10h12v17.2l-4.6-4.6-2.8 2.8 8 8c.8.8 2 .8 2.8 0l8-8-2.8-2.8-4.6 4.6V10h3.2l8.8 8.8V38Z" })),
    'scale': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 30h-2V8H8v22H6c-1.1 0-2 .9-2 2s.9 2 2 2h2v6h4v-6h2c1.1 0 2-.9 2-2s-.9-2-2-2ZM42 14h-2V8h-4v6h-2c-1.1 0-2 .9-2 2s.9 2 2 2h2v22h4V18h2c1.1 0 2-.9 2-2s-.9-2-2-2ZM28 22h-2V8h-4v14h-2c-1.1 0-2 .9-2 2s.9 2 2 2h2v14h4V26h2c1.1 0 2-.9 2-2s-.9-2-2-2Z" })),
    'scooter': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M43.8 32.4c0-.1 0-.1-.1-.2-.1-.4-.2-.8-.4-1.2-.1-.2-.2-.3-.2-.5-.1-.3-.3-.6-.4-.8-.1-.2-.2-.3-.3-.5-.2-.2-.4-.5-.6-.7l-.4-.4c-.2-.2-.4-.4-.7-.5-.2-.1-.3-.2-.5-.3-.3-.2-.5-.3-.8-.4-.2-.1-.3-.2-.5-.2l-.9-.3h-.1c-.4-3.5-1.7-8.6-3.7-12.3H36c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2h-3c-.2 0-.4 0-.6.1h-.2c-.1 0-.2.1-.4.1-.1 0-.1.1-.2.1s-.2.1-.3.2c-.1 0-.1.1-.2.1-.1.1-.2.1-.2.2l-.2.2c-.1.1-.1.2-.2.3 0 .1-.1.1-.1.2-.1.1-.1.2-.2.3 0 .1-.1.1-.1.2v.1H27c-.5 0-1 .5-1 1s.5 1 1 1h3c.2.2.3.4.5.6l.1.1C34.9 18.9 33.7 30 25 30h-2l-.3-.4c-1.6-2.1-1.6-5.1 0-7.2l.9-1.2c1-1.3 0-3.2-1.6-3.2h-8C8.5 18 4 22.5 4 28v4c0 1.1.9 2 2 2h2c0 3.3 2.7 6 6 6s6-2.7 6-6h10c0 3.3 2.7 6 6 6s6-2.7 6-6h.5c.8 0 1.5-.8 1.3-1.6ZM36 36c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2Z" })),
    'separation': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 22H4v4h40v-4ZM38 6c0-1.1-.9-2-2-2H12c-1.1 0-2 .9-2 2v12h28V6ZM10 42c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V30H10v12Z" })),
    'share': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M34 18h-4v4h4v18H14V22h4v-4h-4c-2.2 0-4 1.8-4 4v18c0 2.2 1.8 4 4 4h20c2.2 0 4-1.8 4-4V22c0-2.2-1.8-4-4-4Z" }),
        React.createElement("path", { d: "M22 10.8V32h4V10.8l4.6 4.6 2.8-2.8-8-8c-.8-.8-2-.8-2.8 0l-8 8 2.8 2.8 4.6-4.6Z" })),
    'shop': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 7h-3.9c-.7 0-1.3.4-1.7 1L5 17h8l3-10ZM23 17V7h-5l-3 10h8ZM30 7h-5v10h8L30 7ZM43 17l-5.4-9c-.4-.6-1-1-1.7-1H32l3 10h8ZM8 24.9c.1 0 .2.1.4.1H9c2.2 0 4-1.8 4-4v-2H5v2c0 1.9 1.3 3.4 3 3.9ZM34 24.3c-1.1 1.6-2.9 2.7-5 2.7s-3.9-1.1-5-2.7c-1.1 1.6-2.9 2.7-5 2.7s-3.9-1.1-5-2.7c-1.1 1.6-2.9 2.7-5 2.7-.3 0-.7 0-1-.1V39c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V26.9c-.3.1-.7.1-1 .1-2.1 0-3.9-1.1-5-2.7Z" }),
        React.createElement("path", { d: "M29 25c2.2 0 4-1.8 4-4v-2h-8v2c0 2.2 1.8 4 4 4ZM15 21c0 2.2 1.8 4 4 4s4-1.8 4-4v-2h-8v2ZM35 21c0 2.2 1.8 4 4 4 .2 0 .4 0 .6-.1.1 0 .3 0 .4-.1 1.7-.4 3-2 3-3.9v-2h-8V21Z" })),
    'sitemap': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.4 26c.8 1.8 2.5 3 4.6 3 2.8 0 5-2.2 5-5s-2.2-5-5-5c-2 0-3.8 1.2-4.6 3h-8.9v-1c0-4.4 3.6-8 8-8h.9c.8 1.8 2.5 3 4.6 3 2.8 0 5-2.2 5-5s-2.2-5-5-5c-2 0-3.8 1.2-4.6 3h-.9c-6.6 0-12 5.4-12 12v1h-3.9c-.8-1.8-2.6-3-4.6-3-2.8 0-5 2.2-5 5s2.2 5 5 5c2 0 3.8-1.2 4.6-3h3.9v1c0 6.6 5.4 12 12 12h.9c.8 1.8 2.5 3 4.6 3 2.8 0 5-2.2 5-5s-2.2-5-5-5c-2 0-3.8 1.2-4.6 3h-.9c-4.4 0-8-3.6-8-8v-1h8.9Z" })),
    'size': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 6H26v4h9.2L10 35.2V26H6v14c0 1.1.9 2 2 2h14v-4h-9.2L38 12.8V22h4V8c0-1.1-.9-2-2-2Z" })),
    'skip-next': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M32.3 23 7.7 9C6.8 8.4 6 8.9 6 10v28c0 1.1.8 1.6 1.7 1l24.5-14c1-.6 1-1.4.1-2ZM40 8h-2c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'skip-previous': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40.3 9 15.7 23c-1 .5-1 1.4 0 2l24.5 14c1 .5 1.7.1 1.7-1V10c.1-1.1-.7-1.6-1.6-1ZM10 8H8c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h2c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'sms': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 26V14c0-2.2 1.8-4 4-4h12V6c0-1.1-.9-2-2-2H10c-1.1 0-2 .9-2 2v36c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V30H18c-2.2 0-4-1.8-4-4Zm7 12c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2Z" }),
        React.createElement("path", { d: "M38 12H18c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h18l4 4V14c0-1.1-.9-2-2-2ZM22 22c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm6 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm6 0c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Z" })),
    'social-1': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M14 6c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8Zm.9 5.4c-.2 0-.3.1-.3.4v.9h1.3l-.2 1.3h-1.1v4h-1.7v-4h-.8v-1.4h.8v-.9c0-.7.3-1.7 1.7-1.7h1.3v1.4h-1ZM14 26c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8Zm3.2 6.4v.2c0 2.2-1.7 4.6-4.6 4.6-.9 0-1.8-.2-2.5-.8h.4c.8 0 1.5-.3 2-.7-.7 0-1.3-.5-1.5-1.2H11.3c.1 0 .3 0 .4-.1-.7.1-1.3-.6-1.3-1.4.2.1.4.2.7.2-.4-.3-.7-.8-.7-1.4 0-.3.1-.6.2-.8.8 1 2 1.6 3.4 1.7-.1-.1-.1-.2-.1-.4 0-.9.8-1.6 1.6-1.6.5 0 .9.2 1.2.5.3 0 .7-.2 1-.4-.1.4-.4.7-.7.9.4 0 .7-.1 1-.3-.2.5-.5.8-.8 1ZM34 26c-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8-3.5-8-8-8Zm4 9h-3v3h-2v-3h-3v-2h3v-3h2v3h3v2ZM34 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" }),
        React.createElement("path", { d: "M34 22c4.4 0 8-3.6 8-8s-3.5-8-8-8-8 3.6-8 8 3.6 8 8 8Zm-4.5-10c0-1.4 1.1-2.5 2.5-2.5h4c1.4 0 2.5 1.1 2.5 2.5v4c0 1.4-1.1 2.5-2.5 2.5h-4c-1.4 0-2.5-1.1-2.5-2.5v-4Z" })),
    'social': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M33 28c-1.8 0-3.4.7-4.7 1.8l-6.6-4c.2-.6.2-1.2.2-1.8 0-.6-.1-1.2-.2-1.8l6.6-4c1.2 1.1 2.9 1.8 4.7 1.8 3.9 0 7-3.1 7-7s-3.1-7-7-7-7 3.1-7 7c0 .6.1 1.2.2 1.8l-6.6 4C18.4 17.7 16.8 17 15 17c-3.9 0-7 3.1-7 7s3.1 7 7 7c1.8 0 3.4-.7 4.7-1.8l6.6 4c-.2.6-.3 1.2-.3 1.8 0 3.9 3.1 7 7 7s7-3.1 7-7-3.1-7-7-7Z" })),
    'spacing': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 37.2V30h-4v7.2l-4.6-4.6-2.8 2.8 8 8c.8.8 2 .8 2.8 0l8-8-2.8-2.8-4.6 4.6ZM22 10.8V18h4v-7.2l4.6 4.6 2.8-2.8-8-8c-.8-.8-2-.8-2.8 0l-8 8 2.8 2.8 4.6-4.6ZM40 22H8v4h32v-4Z" })),
    'spam': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m43.45 22.3-8-13.6c-.6-.9-1.9-1.7-3-1.7h-16c-1.1 0-2.5.8-3 1.7l-8 13.6c-.6.9-.6 2.5 0 3.4l8 13.6c.6.9 1.9 1.7 3 1.7h16c1.1 0 2.5-.8 3-1.7l8-13.6c.5-.9.5-2.5 0-3.4Zm-19 11.7c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm2-8h-4V14h4v12Z" })),
    'star': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m23.952 37.7-11 6.1c-.9.5-1.9-.3-1.7-1.2l2.1-12.9-9-9.2c-.7-.7-.3-1.9.7-2l12.4-1.9 5.5-11.7c.6-1.2 1.6-1.2 2.1 0l5.5 11.7 12.4 1.8c1 .1 1.3 1.3.7 2l-9 9.2 2.1 12.9c.2 1-.9 1.7-1.7 1.2l-11.1-6Z" })),
    'statistics': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M13 24H7c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V26c0-1.1-.9-2-2-2ZM27 8h-6c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2ZM41 16h-6c-1.1 0-2 .9-2 2v20c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2V18c0-1.1-.9-2-2-2Z" })),
    'stop': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M38 8H10c-1.1 0-2 .9-2 2v28c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'stripe': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 24.6c0-2.8-1.4-5.1-4-5.1s-4.2 2.2-4.2 5.1c0 3.3 1.9 5 4.6 5 1.3 0 2.3-.3 3.1-.7v-2.2c-.8.4-1.6.6-2.7.6s-2-.4-2.2-1.7H44v-1Zm-5.5-1.1c0-1.3.8-1.8 1.5-1.8s1.4.5 1.4 1.8h-2.9ZM31.5 19.5c-1.1 0-1.8.5-2.2.9l-.1-.7h-2.4v13l2.8-.6V29c.4.3 1 .7 2 .7 2 0 3.8-1.6 3.8-5.1-.2-3.3-2-5.1-3.9-5.1Zm-.7 7.7c-.7 0-1-.2-1.3-.5v-4.1c.3-.4.7-.6 1.3-.6 1 0 1.7 1.1 1.7 2.6s-.7 2.6-1.7 2.6ZM22.9 18.9l2.8-.6V16l-2.8.6v2.3Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M25.7 19.7h-2.8v9.7h2.8v-9.7Z" }),
        React.createElement("path", { d: "m19.9 20.5-.2-.8h-2.4v9.7h2.8v-6.6c.7-.9 1.8-.7 2.1-.6v-2.5c-.4-.1-1.7-.4-2.3.8ZM14.3 17.3l-2.7.6v8.9c0 1.6 1.2 2.9 2.9 2.9.9 0 1.6-.2 1.9-.4V27c-.4.1-2.1.7-2.1-1v-3.9h2.1v-2.4h-2.1v-2.4ZM6.8 22.5c0-.4.4-.6.9-.6.8 0 1.9.3 2.8.7V20c-.9-.3-1.8-.5-2.7-.5-2.3 0-3.8 1.2-3.8 3.2 0 3.1 4.2 2.6 4.2 3.9 0 .5-.4.7-1.1.7-.9 0-2.1-.4-3-.9V29c1 .4 2.1.6 3 .6 2.3 0 3.9-1.1 3.9-3.1 0-3.3-4.2-2.7-4.2-4Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'symmetry': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M26 6h-4v36h4V6ZM9.4 14.6c-.6-.5-1.4-.7-2.2-.4-.7.3-1.2 1-1.2 1.8v16c0 .8.5 1.5 1.2 1.8.3.1.5.2.8.2.5 0 1-.2 1.4-.6l7.9-8c.8-.8.8-2 0-2.8l-7.9-8Zm.6 12.5v-6.2l3.1 3.1-3.1 3.1ZM40.8 14.2c-.7-.3-1.6-.1-2.2.4l-8 8c-.8.8-.8 2 0 2.8l8 8c.4.3.9.6 1.4.6.3 0 .5 0 .8-.2.7-.3 1.2-1 1.2-1.8V16c0-.8-.4-1.5-1.2-1.8ZM38 27.1 34.9 24l3.1-3.1v6.2Z" })),
    'synchronize': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.257 24h-3c0-7.7-6.3-14-14-14-2.2 0-4.3.5-6.2 1.5l1.6 3.1c1.4-.7 3-1.1 4.7-1.1 5.7 0 10.4 4.7 10.4 10.5h-3.5c-1.1 0-1.5.8-1 1.7l5 8.5c.6 1 1.5 1 2 0l5-8.5c.6-.9.1-1.7-1-1.7ZM24.257 34.5c-5.8 0-10.5-4.7-10.5-10.5h3.5c1.1 0 1.5-.8 1-1.7l-5-8.5c-.6-1-1.5-1-2 0l-5 8.5c-.6 1-.1 1.7 1 1.7h3c0 7.7 6.3 14 14 14 2.3 0 4.4-.5 6.3-1.5l-1.6-3.1c-1.4.7-3 1.1-4.7 1.1Z" })),
    'table': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 16H8V8c0-1.1.9-2 2-2h28c1.1 0 2 .9 2 2v8ZM8 40V20h8v22h-6c-1.1 0-2-.9-2-2ZM28 42V20h-8v22h8ZM32 42V20h8v20c0 1.1-.9 2-2 2h-6Z" })),
    'tablet': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M44 37V11c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v26c0 1.1.9 2 2 2h36c1.1 0 2-.9 2-2ZM8 24c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2Z" })),
    'target': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m20 10.6-1.1-3.8c-5.7 1.6-10.1 6-12 11.5l3.8 1.3c1.5-4.3 4.9-7.7 9.3-9ZM37.4 20l3.8-1.1c-1.7-5.6-6-10.1-11.5-11.9l-1.3 3.8c4.3 1.4 7.7 4.8 9 9.2ZM10.6 28l-3.8 1.1c1.7 5.6 6 10.1 11.5 11.9l1.3-3.8c-4.3-1.4-7.7-4.8-9-9.2ZM28 37.4l1.1 3.8c5.6-1.7 10.1-6 11.9-11.5l-3.8-1.3c-1.4 4.3-4.8 7.7-9.2 9Z" }),
        React.createElement("path", { d: "M33.8 22c-.8-3.9-3.9-7-7.8-7.8V6h-4v8.2c-3.9.8-7 3.9-7.8 7.8H6v4h8.2c.8 3.9 3.9 7 7.8 7.8V42h4v-8.2c3.9-.8 7-3.9 7.8-7.8H42v-4h-8.2ZM24 30c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6Z" })),
    'template': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 4H8v16h32V4ZM22 24H8v20h14V24ZM40 24H26v8h14v-8ZM40 36H26v8h14v-8Z" })),
    'template-check': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 22H4v8h12v-8ZM16 32H4v8h12v-8ZM24 24c0-.7.1-1.3.2-2H18v18h14v-5.4c-1.8-.5-3.4-1.5-4.8-2.8-2.1-2.1-3.2-4.9-3.2-7.8ZM27.2 16.2c1.3-1.3 3-2.3 4.8-2.8V8H4v12h20.8c.5-1.4 1.3-2.7 2.4-3.8Z" }),
        React.createElement("path", { d: "M41.4 17.6C39.6 15.9 37.3 15 35 15c-1.3 0-2.7.3-3.9.9-.3.1-.5.3-.8.4 0 0-.1 0-.1.1-.2.1-.5.3-.7.5l-.1.1c-.3.2-.5.4-.7.7-.3.3-.6.7-.9 1l-.1.1c-.7 1-1.2 2.1-1.4 3.2-.2.7-.2 1.5-.2 2.2 0 .3 0 .7.1 1v.1c.1.3.1.7.2 1v.1c.1.3.2.7.4 1v.1c.1.3.3.6.4.8 0 .1.1.2.2.3.1.2.3.4.4.6.1.1.1.2.2.3.2.3.5.6.7.8l.7.7.1.1c.2.2.4.3.7.5 0 0 .1 0 .1.1.3.2.5.3.8.4 1.2.6 2.6.9 3.9.9 2.3 0 4.6-.9 6.4-2.6 3.5-3.6 3.5-9.2 0-12.8Zm-7.9 10.5-3.4-3.4 1.4-1.4 1.9 2 4.9-5 1.4 1.4-6.2 6.4Z" })),
    'template-cross': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M16 22H4v8h12v-8ZM16 32H4v8h12v-8ZM24 24c0-.7.1-1.3.2-2H18v18h14v-5.4c-1.8-.5-3.4-1.5-4.8-2.8-2.1-2.1-3.2-4.9-3.2-7.8ZM27.2 16.2c1.3-1.3 3-2.3 4.8-2.8V8H4v12h20.8c.5-1.4 1.3-2.7 2.4-3.8Z" }),
        React.createElement("path", { d: "M41.4 17.6C39.6 15.9 37.3 15 35 15c-1.3 0-2.7.3-3.9.9-.3.1-.5.3-.8.4 0 0-.1 0-.1.1-.2.1-.5.3-.7.5l-.1.1c-.3.2-.5.4-.7.7-.3.3-.6.7-.9 1l-.1.1c-.7 1-1.2 2.1-1.4 3.2-.2.7-.2 1.5-.2 2.2 0 .3 0 .7.1 1v.1c.1.3.1.7.2 1v.1c.1.3.2.7.4 1v.1c.1.3.3.6.4.8 0 .1.1.2.2.3.1.2.3.4.4.6.1.1.1.2.2.3.2.3.5.6.7.8l.7.7.1.1c.2.2.4.3.7.5 0 0 .1 0 .1.1.3.2.5.3.8.4 1.2.6 2.6.9 3.9.9 2.3 0 4.6-.9 6.4-2.6 3.5-3.6 3.5-9.2 0-12.8Zm-2.2 9.2-1.4 1.4-2.8-2.8-2.8 2.8-1.4-1.4 2.8-2.8-2.8-2.8 1.5-1.5 2.8 2.8 2.8-2.8 1.4 1.4-2.9 2.9 2.8 2.8Z" })),
    'text-area': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M18 34h4V18h6v-4H12v4h6v16Z" }),
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-2 32H10V10h28v28Z" }),
        React.createElement("path", { d: "M24 24h4v10h4V24h4v-4H24v4Z" })),
    'text-background': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M19.3 26.9h9.4L24 16.7l-4.7 10.2Z" }),
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-7.1 30-2.3-5.1H17.4L15.1 36h-4.4l11.1-24h4.5l11.1 24h-4.5Z" })),
    'text-bloc': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 7H6v4h36V7ZM42 17H6v4h36v-4ZM42 27H6v4h36v-4ZM31.2 37H6v4h25.2v-4Z" })),
    'text-field': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M22 34h4V18h8v-4H14v4h8v16Z" }),
        React.createElement("path", { d: "M40 6H8c-1.1 0-2 .9-2 2v32c0 1.1.9 2 2 2h32c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2Zm-2 32H10V10h28v28Z" })),
    'text-format': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 36.8H8v4h32v-4ZM17.4 26.8h13.1l2.8 6h4.4L25.8 7h-3.6L10.3 32.8h4.4l2.7-6ZM24 12.6l4.7 10.2h-9.4L24 12.6Z" })),
    'theme': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M25.1 4.7c-.6-.9-1.6-.9-2.2 0C22.9 4.7 9 19.1 9 28.8c0 8.3 6.7 15 15 15s15-6.7 15-15c0-9.9-13.9-24.1-13.9-24.1Zm4.7 29.2-1.9-4H20l-1.9 4h-4.4L22.2 16h3.6l8.5 17.9h-4.5Z" }),
        React.createElement("path", { d: "M21.9 25.9h4.2L24 21.6l-2.1 4.3Z" })),
    'ticket': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M40 33.9V4H8v40h2v-1c0-1.1.9-2 2-2s2 .9 2 2v1h2v-1c0-1.1.9-2 2-2s2 .9 2 2v1h2v-1c0-1.1.9-2 2-2s2 .9 2 2v1h2v-1c0-1.1.9-2 2-2s2 .9 2 2v1h2v-1c0-1.1.9-2 2-2s2 .9 2 2v1h2V33.9Zm-6 .1H14v-2h20v2Zm0-7H14v-2h20v2Zm0-7H14v-2h20v2Zm0-7H14v-2h20v2Z" })),
    'transfer': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M42 21 26 9v6.1C13.6 16.1 6.4 26.9 6 38c0 .6.4 1 1 1 .5 0 1-.4 1-.9.6-10 11.5-11 18-11.1v6l16-12Z" })),
    'truck': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M37.9 34v-.1c-.1-.3-.1-.5-.2-.8 0-.1-.1-.2-.1-.3-.1-.2-.2-.4-.3-.5-.1-.1-.1-.2-.2-.3-.1-.1-.2-.3-.3-.4l-.3-.3c-.1-.1-.3-.2-.4-.3-.1-.1-.2-.1-.3-.2-.2-.1-.3-.2-.5-.3-.1-.1-.2-.1-.3-.2-.2-.1-.4-.1-.6-.2-.1 0-.2-.1-.3-.1H33c-.3 0-.6 0-1 .1-.1 0-.2.1-.3.1-.2.1-.4.1-.6.2-.1 0-.2.1-.3.2-.2.1-.3.2-.5.3-.1.1-.2.1-.3.2-.1.1-.3.2-.4.3l-.3.3c-.1.1-.2.3-.3.4-.1.1-.1.2-.2.3-.1.2-.2.3-.3.5 0 .1-.1.2-.1.3-.1.3-.2.5-.2.8v.1c-.2.2-.2.6-.2.9 0 2.8 2.2 5 5 5s5-2.2 5-5c0-.3 0-.7-.1-1ZM34 35c0 .6-.4 1-1 1s-1-.4-1-1 .4-1 1-1 1 .4 1 1ZM19.9 34v-.1c-.1-.3-.1-.5-.2-.8 0-.1-.1-.2-.1-.3-.1-.2-.2-.4-.3-.5-.1-.1-.1-.2-.2-.3-.1-.1-.2-.3-.3-.4l-.3-.3c-.1-.1-.3-.2-.4-.3-.1-.1-.2-.1-.3-.2-.2-.1-.3-.2-.5-.3-.1-.1-.2-.1-.3-.2-.2-.1-.4-.1-.6-.2-.1 0-.2-.1-.3-.1H15c-.3 0-.6 0-1 .1-.1 0-.2.1-.3.1-.2.1-.4.1-.6.2-.1 0-.2.1-.3.2-.2.1-.3.2-.5.3-.1-.1-.2 0-.3.1-.1.1-.3.2-.4.3l-.3.3c-.1.1-.2.3-.3.4-.1.1-.1.2-.2.3-.1.2-.2.3-.3.5 0 .1-.1.2-.1.3-.1.3-.2.5-.2.8v.1c-.2.3-.2.7-.2 1 0 2.8 2.2 5 5 5s5-2.2 5-5c0-.3 0-.7-.1-1ZM15 36c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1Z" }),
        React.createElement("path", { d: "M43.9 24c-.5-6.7-6.1-12-12.9-12h-1v-2c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v22c0 1.1.9 2 2 2h2.1c.5-3.4 3.4-6 6.9-6 3.5 0 6.4 2.6 6.9 6h4.2c.5-3.4 3.4-6 6.9-6 3.5 0 6.4 2.6 6.9 6H42c1.1 0 2-.9 2-2v-6l-.1-2ZM38 20c-1.1 0-2 .9-2 2v2h-6v-8h1c3.1 0 5.9 1.6 7.5 4H38Z" })),
    'twitter': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M41.4 12.5c-.8.4-1.6.6-2.5.8.9-.9 1.6-2 2-3.3.1-.4-.3-.7-.6-.5-1.2.7-2.5 1.2-3.8 1.6h-.2c-.2 0-.5-.1-.7-.3-1.3-1.1-3.1-1.8-5-1.8-.8 0-1.6.1-2.5.4-2.5.8-4.5 2.9-5.1 5.4-.2 1-.3 1.9-.2 2.9 0 .1 0 .2-.1.2l-.1.1c-5.6-.5-10.5-3.2-14-7.5-.2-.2-.6-.2-.7 0-.7 1.2-1.1 2.6-1.1 3.9 0 2.1.8 4.1 2.3 5.5-.6-.1-1.2-.4-1.8-.7-.3-.1-.6 0-.6.3 0 3.1 1.8 5.9 4.5 7.2H11c-.4 0-.9 0-1.3-.1-.3-.1-.6.2-.5.5 1.1 2.9 3.5 4.9 6.3 5.4-2.4 1.6-5.1 2.4-8 2.4h-.9c-.3 0-.5.2-.6.4-.1.3.1.5.3.7 3.2 1.9 6.9 2.9 10.7 2.9 3.3 0 6.4-.7 9.1-1.9 2.5-1.2 4.8-2.9 6.7-5 1.8-2 3.1-4.3 4.1-6.9.9-2.4 1.4-5 1.4-7.5v-.1c0-.4.2-.8.5-1 1.2-1 2.2-2.1 3.1-3.4.3-.4-.1-.8-.5-.6Z" })),
    'unavailable': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M21.4 32.4c.8.4 1.7.6 2.6.6 3.3 0 6-2.7 6-6 0-.9-.2-1.8-.6-2.6l-8 8ZM24 21c-3.3 0-6 2.7-6 6 0 .9.2 1.8.6 2.6l8-8c-.8-.4-1.7-.6-2.6-.6Z" }),
        React.createElement("path", { d: "M38 12h-4V8c0-1.1-.9-2-2-2s-2 .9-2 2v4H18V8c0-1.1-.9-2-2-2s-2 .9-2 2v4h-4c-1.1 0-2 .9-2 2v26c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V14c0-1.1-.9-2-2-2ZM24 37c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10-4.5 10-10 10Z" })),
    'underline': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 33c3.3 0 5.9-1 8-3 2.1-2 3.1-4.9 3.1-8.6V7h-5.8v14.2c0 2.1-.5 3.7-1.4 4.9-1 1.2-2.3 1.8-3.9 1.8s-3-.6-3.9-1.8c-.9-1.2-1.5-2.8-1.5-4.9V7h-5.8v14.4c0 3.7 1.1 6.6 3.2 8.6s4.8 3 8 3ZM36 37H12v4h24v-4Z" })),
    'unhappy': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm6 12c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-12 0c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2Zm-3.2 17c1.6-3.5 5.1-6 9.1-6 4.1 0 7.6 2.5 9.2 6H14.8Z" })),
    'unlocked': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M8 22v20c0 1.1.9 2 2 2h28c1.1 0 2-.9 2-2V22c0-1.1-.9-2-2-2h-4v-6c0-5.5-4.5-10-10-10S14 8.5 14 14h4c0-3.3 2.7-6 6-6s6 2.7 6 6v6H10c-1.1 0-2 .9-2 2Zm14 7c0-1.1.9-2 2-2s2 .9 2 2v6c0 1.1-.9 2-2 2s-2-.9-2-2v-6Z" })),
    'unsubscribe': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m21.53 23.818 1.335-1.246 3.55 3.55A10.953 10.953 0 0 0 24 33H7a3 3 0 0 1-.871-.129l9.482-9.482.459.429a4 4 0 0 0 5.46 0ZM35 22c-2.752 0-5.268 1.01-7.197 2.681l-3.476-3.475 9.885-9.233A2.99 2.99 0 0 1 35 14v8ZM4.505 31.666l9.643-9.643-9.891-9.24A2.99 2.99 0 0 0 4 14v16c0 .617.186 1.19.505 1.666ZM5.63 11.33c.41-.21.877-.33 1.37-.33h25c.103 0 .206.005.307.015L20.165 22.356a2 2 0 0 1-2.73 0L5.63 11.33ZM44 33a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-10.207.207L31 36l1.414 1.414 2.793-2.793L38 37.414 39.414 36l-2.793-2.793 2.793-2.793L38 29l-2.793 2.793L32.414 29 31 30.414l2.793 2.793Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'up': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "m32 30.4-7.6-7.6-7.6 7.6-2.8-2.8 9-9c.8-.8 2-.8 2.8 0l9 9-2.8 2.8Z" })),
    'version': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M30 20H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h24c1.1 0 2-.9 2-2V22c0-1.1-.9-2-2-2Z" }),
        React.createElement("path", { d: "M36 14H12c-1.1 0-2 .9-2 2v2h24v16h2c1.1 0 2-.9 2-2V16c0-1.1-.9-2-2-2Z" }),
        React.createElement("path", { d: "M42 8H18c-1.1 0-2 .9-2 2v2h24v16h2c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2Z" })),
    'vimeo': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-11.1-9-20-20-20Zm11.9 14.6c-1.3 7.5-8.8 14-11.1 15.4-2.2 1.5-4.3-.6-5.1-2.1-.9-1.8-3.4-11.3-4.1-12.1-.6-.9-2.6.7-2.6.7l-1-1.3s4.1-4.9 7.2-5.5c3.3-.6 3.3 5.1 4.1 8.2.8 3.1 1.3 4.8 2 4.8s2-1.7 3.4-4.3c1.4-2.6-.1-4.9-2.8-3.3 1-6.5 11.3-8.1 10-.5Z" })),
    'warning-circle': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4Zm0 30c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2Zm2-8h-4V14h4v12Z" })),
    'way': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M17.52 6a2 2 0 0 0-1.927 1.465l-8.889 32A2 2 0 0 0 8.631 42H20l.59-7.083a1 1 0 0 1 .997-.917h4.826a1 1 0 0 1 .997.917L28 42h11.369a2 2 0 0 0 1.927-2.535l-8.89-32A2 2 0 0 0 30.48 6H17.52Zm5.237 2.917A1 1 0 0 1 23.753 8h.494a1 1 0 0 1 .996.917l.083 1A1 1 0 0 1 24.33 11h-.66a1 1 0 0 1-.996-1.083l.083-1ZM24.747 14a1 1 0 0 1 .996.917l.167 2A1 1 0 0 1 24.913 18h-1.826a1 1 0 0 1-.997-1.083l.167-2a1 1 0 0 1 .996-.917h1.494Zm1.663 8.917a1 1 0 0 0-.997-.917h-2.826a1 1 0 0 0-.997.917l-.333 4A1 1 0 0 0 22.253 28h3.494a1 1 0 0 0 .996-1.083l-.333-4Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'web': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M28.595 39.33c-1.15.344-2.353.563-3.595.64V33h6.744c-.468 1.557-1.05 2.963-1.718 4.178-.457.83-.937 1.546-1.43 2.152ZM32.263 31H25v-6h7.986a32.282 32.282 0 0 1-.723 6Zm1.563 2c-.504 1.815-1.155 3.471-1.926 4.917A16.079 16.079 0 0 0 37.23 33h-3.404Zm4.566-2h-4.085c.387-1.882.623-3.898.68-6h4.982a15.908 15.908 0 0 1-1.578 6ZM23 31v-6h-7.986c.061 2.128.315 4.146.723 6H23Zm-9.307 0a34.527 34.527 0 0 1-.68-6H8.032a15.907 15.907 0 0 0 1.577 6h4.085Zm-2.924 2h3.405c.504 1.815 1.155 3.471 1.926 4.917A16.079 16.079 0 0 1 10.77 33Zm5.487 0H23v6.97a15.949 15.949 0 0 1-3.595-.64 14.362 14.362 0 0 1-1.43-2.152c-.67-1.215-1.251-2.621-1.72-4.178Zm16.73-10H25v-6h7.263c.408 1.854.662 3.872.723 6ZM25 8.03V15h6.744c-.468-1.557-1.05-2.963-1.718-4.178a14.364 14.364 0 0 0-1.43-2.152A15.95 15.95 0 0 0 25 8.03ZM34.986 23a34.527 34.527 0 0 0-.679-6h4.084a15.908 15.908 0 0 1 1.578 6h-4.983Zm-1.16-8h3.405a16.079 16.079 0 0 0-5.33-4.917c.77 1.446 1.421 3.102 1.925 4.917Zm-17.57 0H23V8.03a15.95 15.95 0 0 0-3.595.64 14.364 14.364 0 0 0-1.43 2.152c-.67 1.215-1.251 2.621-1.72 4.178ZM23 17h-7.263a32.282 32.282 0 0 0-.723 6H23v-6Zm-6.9-6.917c-.77 1.446-1.422 3.102-1.926 4.917h-3.405a16.079 16.079 0 0 1 5.33-4.917ZM13.693 17H9.608a15.907 15.907 0 0 0-1.577 6h4.982c.057-2.102.293-4.118.68-6ZM24 4c11.046 0 20 8.954 20 20s-8.954 20-20 20S4 35.046 4 24 12.954 4 24 4Z", fillRule: "evenodd", clipRule: "evenodd" })),
    'widget': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M0 3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3ZM22 3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H25a3 3 0 0 1-3-3V3Z" }),
        React.createElement("path", { d: "M37 24H25a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V25a1 1 0 0 0-1-1Zm-12-2a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V25a3 3 0 0 0-3-3H25Z", fillRule: "evenodd", clipRule: "evenodd" }),
        React.createElement("path", { d: "M0 25a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V25ZM30 32h-2a1 1 0 1 1 0-2h2v-2a1 1 0 1 1 2 0v2h2a1 1 0 1 1 0 2h-2v2a1 1 0 1 1-2 0v-2Z" })),
    'youtube': React.createElement(React.Fragment, null,
        React.createElement("path", { d: "M35.7 10H12.3C7.7 10 4 13.7 4 18.3V30c0 4.6 3.7 8.3 8.3 8.3h23.4c4.6 0 8.3-3.7 8.3-8.3V18.3c0-4.6-3.7-8.3-8.3-8.3Zm-5.6 14.7-10.9 5.2c-.3.1-.6-.1-.6-.4V18.8c0-.3.3-.5.6-.4l10.9 5.5c.3.2.3.7 0 .8Z" }))
};

const Svg = (props) => {
    const { icon, animatedIcon, children } = props, rest = __rest(props, ["icon", "animatedIcon", "children"]);
    const theme = useContext(ThemeContext);
    return (React.createElement("svg", Object.assign({ width: 48, height: 48 }, rest, { viewBox: '0 0 48 48', xmlns: "http://www.w3.org/2000/svg" }),
        icon
            ? theme.icons === 'front'
                ? IconsFrontPaths[icon]
                : IconsBackPaths[icon]
            : null,
        animatedIcon ? AnimatedIcons[animatedIcon] : null,
        children));
};

const BoxHeader = styled.header `
  position: relative;
  padding: 40px 40px 15px;
`;
const BoxClose = styled.div `
  position: absolute;
  top: 40px;
  right: 40px;
`;
const BoxTextClose = styled(BaseButton) `
  background: transparent;
  position: absolute;
  top: 40px;
  right: 40px;
  text-align: right;
`;
const Box = (props) => {
    const { title, subtitle, children, closeText, hide, onClose } = props;
    const theme = useContext(ThemeContext);
    return (React.createElement(InOut, { exit: { keyframes: Keyframes.slideOutToTop }, show: !hide },
        React.createElement(Card, { style: { padding: '0px' } },
            React.createElement(BoxHeader, null,
                React.createElement(CardTitle, null, title),
                React.createElement(CardSubtitle, null, subtitle),
                closeText && (React.createElement(BoxTextClose, { onClick: () => onClose && onClose() },
                    React.createElement(SpanLink, null, closeText))),
                !closeText && (React.createElement(BoxClose, null,
                    React.createElement(BaseButton, { onClick: () => onClose && onClose() },
                        React.createElement(Svg, { icon: "cross", fill: theme.colors.grey60, width: 24, height: 24 }))))),
            children)));
};

const Bubble = styled.section `
  ${Shapes.bubble};
  padding: 24px;
  margin: 16px;
`;
const Subtitle$1 = styled.span `
  ${getTypo('subtitle2')};
  margin-right: 16px;
`;
const Copy = styled.p `
  ${getTypo('body1')};
  color: ${({ theme }) => theme.colors.grey40};
  margin: 0;
  padding: 0;
`;
const Header = styled.header `
  margin-bottom: 16px;
`;
const LabelDate = styled.span `
  ${getTypo('caption1')};
  color: ${({ theme }) => theme.colors.grey50};
  white-space: nowrap;
`;

class BubbleComponent extends React.PureComponent {
    render() {
        return (React.createElement(Bubble, { className: 'lnk-bubble' },
            this.renderHeader(),
            React.createElement(Copy, { className: 'lnk-bubble__copy' }, this.props.children)));
    }
    renderDate() {
        if (this.props.date) {
            return React.createElement(LabelDate, { className: 'lnk-bubble__label-date' }, this.props.date);
        }
    }
    renderHeader() {
        if (this.props.blockTitle || this.props.date) {
            return (React.createElement(Header, { className: 'lnk-bubble__header' },
                this.renderTitle(),
                this.renderDate()));
        }
    }
    renderTitle() {
        if (this.props.blockTitle) {
            return React.createElement(Subtitle$1, { className: 'lnk-bubble__subtitle' }, this.props.blockTitle);
        }
    }
}

const rippleEffect = keyframes `
    0% {
      opacity: 1;
      transform: scale(1, 1);
    }
    100%{
      opacity: 0;
      transform: scale(20, 20);
    }
`;
const RIPPLE_DURATION = 1000;
const RippleContainer = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
`;
const RippleSubContainer = styled.span `
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  border-radius: inherit;
`;
const RippleColor = css `
  &:not(svg) {
    background: ${({ color, theme }) => (color ? rgba(color, 0.2) : theme.colors.light)};
  }

  &.svg {
    fill: ${({ color, theme }) => (color ? rgba(color, 0.2) : theme.colors.light)};
  }
`;
const RippleObject = mergeStyled(styled.span `
    position: absolute;
    opacity: 0;
    transform: scale(10, 10);
    transition: opacity 0.3s ease-in-out;
    transform-origin: center;
    animation: ${rippleEffect} ${RIPPLE_DURATION}ms ease-out;
    animation-delay: 10ms;
    border-radius: inherit;

    &:not(svg) {
      width: 20px;
      height: 20px;
      border-radius: 50%;
    }
  `, [RippleColor]);
const Ripple = (props) => {
    const { color } = props;
    const refContainer = useRef(null);
    const [getActives, setActives] = useState([]);
    const addRipple = (event) => {
        const current = refContainer.current;
        if (!current) {
            return;
        }
        const rect = current.getBoundingClientRect();
        setActives([
            {
                x: event.clientX - rect.left,
                y: event.clientY - rect.top,
                rand: Math.random(),
            },
        ]);
    };
    return (React.createElement(RippleContainer, { ref: refContainer, onMouseDown: addRipple }, getActives.map((ripple) => (React.createElement(RippleSubContainer, { key: ripple.rand },
        React.createElement(RippleObject, { style: {
                top: `${ripple.y - 10}px`,
                left: `${ripple.x - 10}px`,
            }, color: color }))))));
};

const normalBtnSize = css `
  ${getTypo('fontButton')};
  ${Shapes.button};
`;
const smallBtnSize = css `
  ${getTypo('smallFontButton')};
  ${Shapes.smallButton};
`;
const ContentButton = styled.span `
  position: relative;
  transition: ease opacity 200ms;
  opacity: ${({ show }) => (show ? 1 : 0)};
`;
const buttonShape = css `
  ${({ small }) => (small ? smallBtnSize : normalBtnSize)};
`;
const buttonBackground = css `
  background-color: ${({ backgroundColor }) => backgroundColor};

  &:hover:not(:disabled) {
    background-color: ${({ backgroundColor }) => lighten(0.03, backgroundColor)};
  }

  &:focus:not(:disabled) {
    background-color: ${({ backgroundColor }) => lighten(0.03, backgroundColor)};
  }
`;
const buttonBorder = css `
  border-color: ${({ borderColor }) => borderColor};

  &:hover:not(:disabled) {
    border-color: ${({ borderColor }) => lighten(0.03, borderColor)};
  }

  &:focus:not(:disabled) {
    border-color: ${({ borderColor }) => lighten(0.03, borderColor)};
  }

  &:active:not(:disabled) {
    border-color: ${({ borderColor }) => lighten(0.03, borderColor)};
  }
`;
const buttonFontColor = css `
  color: ${({ fontColor }) => fontColor};
`;
const ButtonStyle = css `
  position: relative;
  transition: background-color ease-in-out 200ms, ease-in-out border-color 200ms,
    ease-in-out color 200ms, ease-in-out box-shadow 200ms;
  transform-origin: center;

  &:focus:not(:disabled) {
    ${Shapes.shadow1};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.grey80};
    border-color: ${({ theme }) => theme.colors.grey80};
    color: ${({ theme }) => theme.colors.light};
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    ${Shapes.shadow2};
  }
`;
const ButtonContainer = mergeStyled(styled(BaseButton) ``, [
    buttonShape,
    ButtonStyle,
    buttonBackground,
    buttonFontColor,
    buttonBorder,
]);
const IconContainer = styled.div `
  display: inline-block;
  line-height: 0.83;
  vertical-align: middle;
  ${({ marginSide }) => 'margin-' + marginSide + ': 4px'};
  margin-top: -2px;
  margin-bottom: -1px;
`;
const LoaderContainerButton = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  height: 100%;
  width: 100%;
  display: flex;
  opacity: ${({ hide }) => (hide ? 0 : 1)};
  align-items: center;
  justify-content: center;
`;
const Button = (props) => {
    const { loading, small, noRipple, children, disabled, colorType, icon, iconRight, backgroundColor, borderColor, fontColor, onClick, select } = props, rest = __rest(props, ["loading", "small", "noRipple", "children", "disabled", "colorType", "icon", "iconRight", "backgroundColor", "borderColor", "fontColor", "onClick", "select"]);
    const theme = useContext(ThemeContext);
    const iconSize = small ? '16px' : '24px';
    const getPartColor = () => {
        switch (colorType) {
            case 'outlineSecondary':
                return {
                    FC: theme.colors.secondary,
                    BGC: 'transparent',
                    BC: theme.colors.secondary,
                };
            case 'outline':
                return {
                    FC: theme.colors.primary,
                    BGC: 'transparent',
                    BC: theme.colors.primary,
                };
            case 'primary':
                return {
                    FC: theme.colors.invertPrimary,
                    BGC: theme.colors.primary,
                    BC: theme.colors.primary,
                };
            case 'secondary':
                return {
                    FC: theme.colors.invertSecondary,
                    BGC: theme.colors.secondary,
                    BC: theme.colors.secondary,
                };
            default:
                return {
                    FC: theme.colors.grey10,
                    BGC: theme.colors.grey90,
                    BC: theme.colors.grey90,
                };
        }
    };
    const { FC, BGC, BC } = getPartColor();
    const renderIcon = (marginSide, select) => {
        if (!icon) {
            return;
        }
        return select ? (iconRight ? (React.createElement(Svg, { icon: icon, width: iconSize, height: iconSize, style: { position: 'absolute', top: '50%', right: '5px', transform: 'translateY(-50%)' }, fill: fontColor || FC })) : (React.createElement(Svg, { icon: icon, width: iconSize, height: iconSize, style: { position: 'absolute', top: '50%', left: '5px', transform: 'translateY(-50%)' }, fill: fontColor || FC }))) : (React.createElement(IconContainer, { marginSide: marginSide },
            React.createElement(Svg, { icon: icon, width: iconSize, height: iconSize, fill: fontColor || FC })));
    };
    return (React.createElement(ButtonContainer, Object.assign({}, rest, { small: small, disabled: disabled, backgroundColor: backgroundColor || BGC, borderColor: borderColor || BC, fontColor: fontColor || FC, onClick: (e) => (onClick && !loading ? onClick(e) : null) }),
        !loading && !iconRight ? renderIcon('right', select) : undefined,
        React.createElement(ContentButton, { className: 'lnk-button__content', show: !loading }, children),
        !loading && iconRight ? renderIcon('left', select) : undefined,
        React.createElement(LoaderContainerButton, { hide: !loading },
            React.createElement(Svg, { animatedIcon: 'spinner', stroke: fontColor || FC, width: iconSize, height: iconSize })),
        !noRipple && !disabled ? React.createElement(Ripple, { color: fontColor || FC }) : ''));
};

const DropdownMask = styled.div `
  position: absolute;
  z-index: 10;
  box-sizing: border-box;
  pointer-events: none;
  ${({ right }) => (right ? 'right' : 'left')}: 0;
  ${({ fulfilled }) => (fulfilled ? 'width: 100%' : '')};
`;

const Dropdown = (props) => {
    const { fulfilled, children, onClose, wrapperRef, right, show, offset } = props;
    const [getWrapperHeight, setWrapperHeight] = useState(0);
    const [getDirection, setDirection] = useState('top');
    useEffect(() => {
        if (!wrapperRef) {
            return;
        }
        const elem = wrapperRef.current;
        const onScroll = () => {
            const rect = elem.getBoundingClientRect();
            const ratio = rect.y / window.innerHeight;
            const limit = 0.4;
            if (ratio < -0.5 || ratio > 1.5) {
                if (show) {
                    onClose();
                }
            }
            else if (ratio > limit && getDirection === 'top') {
                setDirection('bottom');
            }
            else if (ratio <= limit && getDirection === 'bottom') {
                setDirection('top');
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, [wrapperRef, getDirection, onClose, show]);
    useEffect(() => {
        const cleanUp = () => {
            window.removeEventListener('click', autoClose);
            if (!wrapperRef.current) {
                return;
            }
            wrapperRef.current.removeEventListener('keydown', keyPress, true);
        };
        const autoClose = (event) => {
            if (!wrapperRef.current) {
                return;
            }
            const path = event.composedPath();
            if (!path.includes(wrapperRef.current)) {
                onClose();
            }
        };
        const keyPress = (event) => {
            if (event.key === 'Tab' || event.code === 'Tab' || event.which === 9) {
                setTimeout(() => {
                    if (!wrapperRef.current) {
                        return;
                    }
                    const stillBlurInto = wrapperRef.current.contains(document.activeElement);
                    if (!stillBlurInto) {
                        onClose();
                    }
                }, 1);
                return;
            }
            if (event.key === 'Escape' || event.code === 'Escape' || event.which === 27) {
                onClose();
            }
        };
        if (wrapperRef.current) {
            wrapperRef.current.style.position = 'relative';
            setWrapperHeight(wrapperRef.current.clientHeight);
            if (show) {
                window.addEventListener('click', autoClose);
                wrapperRef.current.addEventListener('keydown', keyPress, true);
            }
            else {
                cleanUp();
            }
        }
        return () => {
            cleanUp();
        };
    }, [onClose, wrapperRef, show]);
    return (React.createElement(DropdownMask, { style: { [getDirection]: `${getWrapperHeight - (offset || 0)}px` }, fulfilled: fulfilled, right: right },
        React.createElement("div", { style: { pointerEvents: 'initial' } },
            React.createElement(InOut, { show: show, enter: { keyframes: Keyframes.slideInFromTop }, exit: { keyframes: [], options: { duration: 1 } } }, children))));
};

const FlexWidth = (device, columns) => {
    if (!columns) {
        return;
    }
    const result = PickChoiceByDevice(device, columns);
    return result < 0 ? result : (100 * result) / 12;
};
const FlexColToCss = (device, col) => {
    const res = FlexWidth(device, col);
    if (typeof res === 'undefined') {
        return '';
    }
    return res < 0 ? 'width: initial;' : `width: ${res}%;`;
};
const FlexCss = (device, prefix, choices) => {
    if (!choices) {
        return '';
    }
    const selection = PickChoiceByDevice(device, choices);
    if (!selection) {
        return '';
    }
    return prefix + selection + ';';
};

const ResponsiveWrap = css `
  ${({ wraps }) => typeof wraps === 'string'
    ? `flex-wrap: ${wraps};`
    : wraps
        ? DEVICES.slice(0, wraps.length).map((device) => MakeCssMediaQuery(FlexCss(device, 'flex-wrap: ', wraps), device))
        : 'flex-wrap: wrap'};
`;
const ResponsiveAlignItems = css `
  ${({ alignItems }) => typeof alignItems === 'string'
    ? `align-items: ${alignItems};`
    : alignItems
        ? DEVICES.slice(0, alignItems.length).map((device) => MakeCssMediaQuery(FlexCss(device, 'align-items: ', alignItems), device))
        : ''};
`;
const ResponsiveJustifyContent = css `
  ${({ justifyContent }) => typeof justifyContent === 'string'
    ? `justify-content: ${justifyContent};`
    : justifyContent
        ? DEVICES.slice(0, justifyContent.length).map((device) => MakeCssMediaQuery(FlexCss(device, 'justify-content: ', justifyContent), device))
        : ''};
`;
const ResponsiveDirection = css `
  ${({ direction }) => typeof direction === 'string'
    ? `flex-direction: ${direction};`
    : direction
        ? DEVICES.slice(0, direction.length).map((device) => MakeCssMediaQuery(FlexCss(device, 'flex-direction: ', direction), device))
        : ''};
`;
const ResponsiveCol = css `
  ${({ columns }) => typeof columns === 'number'
    ? columns < 0
        ? ''
        : `width: ${(100 * columns) / 12}%;`
    : columns
        ? DEVICES.slice(0, columns.length).map((device) => MakeCssMediaQuery(FlexColToCss(device, columns), device))
        : ''};
`;
const Flex = mergeStyled(styled.div `
    display: flex;
    position: relative;
  `, [
    ResponsiveWrap,
    ResponsiveAlignItems,
    ResponsiveJustifyContent,
    ResponsiveDirection,
    ResponsiveCol,
]);

const RowCss = (val) => {
    return `
  width: calc(100% + ${val * 2}px);
  margin-left: calc(-${val}px);
  margin-right: calc(-${val}px);
  `;
};
const ResponsiveRowGap = css `
  ${({ gapColumn }) => typeof gapColumn === 'number'
    ? RowCss(gapColumn)
    : gapColumn
        ? DEVICES.slice(0, gapColumn.length).map((device) => MakeCssMediaQuery(RowCss(PickChoiceByDevice(device, gapColumn)), device))
        : RowCss(16)};
`;
const RowStyle = mergeStyled(styled.div `
    display: flex;
    position: relative;
  `, [
    ResponsiveRowGap,
    ResponsiveWrap,
    ResponsiveAlignItems,
    ResponsiveJustifyContent,
    ResponsiveDirection,
]);
const RowContext = createContext({});
const useRow = () => useContext(RowContext);
const Row = (_a) => {
    var { gapColumn, children } = _a, props = __rest(_a, ["gapColumn", "children"]);
    return (React__default.createElement(RowContext.Provider, { value: { gapColumn } },
        React__default.createElement(RowStyle, Object.assign({ gapColumn: gapColumn }, props), children)));
};

const CollCss = (val) => {
    return `padding: 0 ${val}px;`;
};
const ResponsiveColGap = css `
  ${({ gapColumn }) => typeof gapColumn === 'number'
    ? CollCss(gapColumn)
    : gapColumn
        ? DEVICES.slice(0, gapColumn.length).map((device) => MakeCssMediaQuery(CollCss(PickChoiceByDevice(device, gapColumn)), device))
        : CollCss(16)};
`;
const ColStyle = mergeStyled(styled.div `
    box-sizing: border-box;
    position: relative;
  `, [ResponsiveCol, ResponsiveColGap]);
const Col = (_a) => {
    var { children } = _a, props = __rest(_a, ["children"]);
    const { gapColumn } = useRow();
    return (React__default.createElement(ColStyle, Object.assign({ gapColumn: gapColumn }, props), children));
};

const MenuContainer = styled.ol `
  ${Shapes.shadow1};
  position: relative;
  background-color: ${({ theme }) => theme.colors.light};
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  list-style: none;
  display: block;
  border-radius: 4px;
  max-height: 25vh;
`;
const Item$1 = styled.li `
  margin: 0;
  padding: 0;
  cursor: pointer;
`;
const MenuItem = styled.div `
  ${getTypo('body1')};
  color: ${({ theme }) => theme.colors.grey50};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid transparent;
  background: ${({ selected, theme }) => selected ? theme.colors.grey95 : theme.colors.light};

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.grey20};
    background: ${({ theme }) => theme.colors.grey95};
  }
`;
const Highlight = styled.span `
  color: ${({ theme }) => theme.colors.dark};
`;
const Title$3 = styled.div `
  ${getTypo('body2')};
  padding: 8px;
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colors.light};
  z-index: 1;
  border-radius: 4px;
`;
const HighLighter = (props) => {
    const { label, search } = props;
    const normalizeItem = NormalizeText(label);
    const normalizeHighlight = NormalizeText(search);
    const match = MatchLike(normalizeItem, normalizeHighlight);
    if (!match || typeof match.index === 'undefined') {
        return React.createElement(React.Fragment, null, label);
    }
    const start = match.index || 0;
    const end = start + match[0].length;
    return (React.createElement(React.Fragment, null,
        label.slice(0, start),
        React.createElement(Highlight, null, label.slice(start, end)),
        label.slice(end)));
};
const SelectMenu = function (props) {
    const { onSelect, selected, items, highlight, title, icon } = props;
    const theme = React.useContext(ThemeContext);
    const refList = useRef(null);
    useEffect(() => {
        const parent = refList.current;
        if (!parent) {
            return;
        }
        if (!selected) {
            return;
        }
        const selectedIndex = items.findIndex((f) => f.uid && selected.uid ? f.uid === (selected === null || selected === undefined ? undefined : selected.uid) : f.label === selected.label);
        if (selectedIndex < 0) {
            return;
        }
        const childTarget = parent.childNodes[selectedIndex];
        if (!childTarget)
            return;
        parent.scrollTo({
            behavior: 'smooth',
            top: childTarget.offsetTop - parent.clientHeight / 3,
        });
    }, [selected, refList, items]);
    return (React.createElement(MenuContainer, { ref: refList },
        title && React.createElement(Title$3, null, title),
        items.map((item) => (React.createElement(Item$1, { key: item.uid || item.label, title: item.label, "data-selected": (selected === null || selected === undefined ? undefined : selected.uid) && item.uid
                ? selected.uid === item.uid
                    ? true
                    : null
                : (selected === null || selected === undefined ? undefined : selected.label) === item.label
                    ? true
                    : null },
            React.createElement(MenuItem, { onClick: () => onSelect(item), selected: (selected === null || selected === undefined ? undefined : selected.label) === item.label },
                React.createElement(Row, { alignItems: 'center', justifyContent: 'space-between', wraps: 'nowrap' },
                    React.createElement(Col, { style: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' } },
                        React.createElement(HighLighter, { label: item.label, search: highlight }, item.label)),
                    icon && (React.createElement(Col, null,
                        React.createElement(Svg, { icon: icon, height: 26, width: 26, color: theme.colors.grey20 })))),
                item.caption ? (React.createElement(React.Fragment, null,
                    React.createElement("br", null),
                    React.createElement(SpanCaption1, null, item.caption))) : null))))));
};

function ButtonSelect(props) {
    const { onSelect, selected, disabled, options, readOnly, small, right, children } = props, rest = __rest(props, ["onSelect", "selected", "disabled", "options", "readOnly", "small", "right", "children"]);
    const [getSelIndex, setSelIndex] = useState(null);
    useEffect(() => {
        if (!selected) {
            return;
        }
        const find = options.findIndex((f) => f.uid && selected.uid ? f.uid === selected.uid : f.label === selected.label);
        if (find < 0) {
            return;
        }
        setSelIndex(find);
    }, [selected, options]);
    const [getOpen, setOpen] = useState(false);
    const refWrapper = useRef(null);
    const emitSelection = (item) => {
        if (!item) {
            return;
        }
        if (onSelect) {
            onSelect(item);
        }
        setOpen(false);
    };
    const selectDown = () => {
        if (getSelIndex === null) {
            setSelIndex(0);
            return;
        }
        let newIndex = getSelIndex + 1;
        if (newIndex > options.length - 1) {
            newIndex = 0;
        }
        setSelIndex(newIndex);
    };
    const selectUp = () => {
        if (getSelIndex === null) {
            setSelIndex(options.length - 1);
            return;
        }
        let newIndex = getSelIndex - 1;
        if (newIndex < 0) {
            newIndex = options.length - 1;
        }
        setSelIndex(newIndex);
    };
    const onKeyDown = (event) => {
        if (readOnly) {
            return;
        }
        switch (event.keyCode) {
            case 40: // down
                selectDown();
                setOpen(true);
                break;
            case 38: // up
                selectUp();
                setOpen(true);
                break;
            case 27: // escape
                setOpen(false);
                break;
            case 13: // enter
                if (getSelIndex !== null) {
                    emitSelection(options[getSelIndex]);
                }
                break;
            case 9: // tab
                break;
            default: {
                const filterPropositions = options
                    .map((m, i) => (Object.assign(Object.assign({}, m), { originIndex: i })))
                    .filter((f) => f.label.toLowerCase().startsWith(String.fromCharCode(event.keyCode).toLowerCase()));
                if (!filterPropositions.length) {
                    return;
                }
                const filterIndex = filterPropositions.findIndex((fi) => fi.originIndex === getSelIndex);
                if (filterIndex > -1) {
                    let newIndex = filterIndex + 1;
                    if (newIndex > filterPropositions.length - 1) {
                        newIndex = 0;
                    }
                    setSelIndex(filterPropositions[newIndex].originIndex);
                }
                else {
                    setSelIndex(filterPropositions[0].originIndex);
                }
            }
        }
    };
    const theme = useContext(ThemeContext);
    return (React.createElement("div", { style: { position: 'relative', maxWidth: '100%' }, ref: refWrapper },
        React.createElement(Button, Object.assign({}, Object.assign({ disabled, small }, rest), { onFocus: readOnly ? undefined : () => setOpen(true), onTouchStart: readOnly ? undefined : () => setOpen(true), onClick: readOnly ? undefined : () => setOpen(true), onKeyDown: onKeyDown, style: { maxWidth: '100%', position: 'relative' }, select: true, icon: readOnly ? undefined : theme.icons === 'front' ? 'arrow-ios-downward-outline' : 'down', iconRight: right }),
            React.createElement("div", { style: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'start',
                    padding: right ? '0 15px 0 0' : '0 0 0 15px',
                } }, (selected === null || selected === undefined ? undefined : selected.label) || children)),
        refWrapper && (React.createElement(Dropdown, { wrapperRef: refWrapper, onClose: () => setOpen(false), show: getOpen, fulfilled: true, offset: -12, right: right },
            React.createElement(SelectMenu, { onSelect: emitSelection, selected: getSelIndex !== null ? options[getSelIndex] : undefined, items: options })))));
}

const CalendarContainer = styled.article `
  ${Shapes.card};
  background: ${({ theme }) => theme.colors.light};
  position: relative;
  width: 320px;
  min-height: 320px;
  padding: 10px;
  user-select: none;
`;
const NavContainer = styled.nav `
  display: flex;
  justify-content: space-between;
`;
const DayCell = styled(BaseButton) `
  ${getTypo('body1')};
  width: 38px;
  height: 38px;
  margin: 2.2px;
  text-align: center;
  padding: 6.5px 0;
  border-radius: ${({ theme }) => theme.button.small.borderRadius};
  color: ${({ isFade, theme }) => (isFade ? theme.colors.grey70 : '')};
  border-color: ${({ isToday, theme }) => (isToday ? theme.colors.primary : '')};
  background-color: ${({ isSelected, theme }) => (isSelected ? theme.colors.primary : '')};
  color: ${({ isSelected, theme }) => (isSelected ? theme.colors.light : '')};

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: ${({ theme, isSelected }) => isSelected ? '' : lighten(0.2, theme.colors.primary)};
  }
`;
const MonthYearCell = styled(BaseButton) `
  ${getTypo('body1')};
  text-align: center;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  margin: 2px;
  border-radius: 4px;
  border-color: ${({ isToday, theme }) => (isToday ? theme.colors.primary : 'transparent')};

  &:disabled {
    color: ${({ theme }) => theme.colors.grey60};
  }
`;
const NavButton = styled(BaseButton) `
  ${getTypo('h6')};
  padding: 8.5px 11px;
  border-radius: 4px;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.grey60};

  &:not(:disabled) {
    cursor: pointer;
  }

  &:focus:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.grey80};
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.grey90};
  }

  svg {
    fill: ${({ theme }) => theme.colors.dark};
  }

  &:disabled {
    svg {
      fill: ${({ theme }) => theme.colors.grey80};
    }
  }
`;
const NavDay = styled.div `
  ${getTypo('caption1')};
  display: inline-block;
  text-transform: capitalize;
  text-align: center;
  padding: 5px 0;
  width: 36.5px;
  margin: 0 3px;
  box-sizing: border-box;
  opacity: ${({ hide }) => (hide ? '0' : '1')};
`;
const CalendarHeader = (props) => {
    const { onPrevious, onNext, text, onChangeScale } = props;
    const theme = useContext(ThemeContext);
    return (React.createElement("header", null,
        React.createElement(NavContainer, null,
            React.createElement(NavButton, { onClick: onPrevious },
                React.createElement(Svg, { style: { position: 'relative', top: '3px' }, width: 24, height: 24, icon: theme.icons === 'back' ? 'left' : 'arrow-ios-back-fill' })),
            React.createElement(NavButton, { onClick: onChangeScale }, text),
            React.createElement(NavButton, { onClick: onNext },
                React.createElement(Svg, { style: { position: 'relative', top: '3px' }, width: 24, height: 24, icon: theme.icons === 'back' ? 'right' : 'arrow-ios-forward-fill' })))));
};
const CalendarMonths = (props) => {
    const { onViewChange, view, locale, min, max } = props;
    const today = new Date();
    const isDisabled = (month) => {
        const signMin = RangeSign(new Date(view.year, month + 1, -1, 23, 59, 59), min, null);
        const signMax = RangeSign(new Date(view.year, month, 0, 0, 0, 0), null, max);
        return signMin === -1 || signMax === 1;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(CalendarHeader, { onPrevious: () => onViewChange(Object.assign(Object.assign({}, view), { year: view.year - 1 })), onNext: () => onViewChange(Object.assign(Object.assign({}, view), { year: view.year + 1 })), onChangeScale: () => onViewChange(Object.assign(Object.assign({}, view), { scale: 3 })), text: view.year + '' }),
        MonthsNames(locale).map((m, i) => (React.createElement(MonthYearCell, { onClick: () => onViewChange({ month: i, year: view.year, scale: 1 }), key: m + view.year, width: 94, height: 71, disabled: isDisabled(i), isToday: IsSameDay(new Date(view.year, i, today.getDate()), today) }, m)))));
};
const CalendarYears = (props) => {
    const { onViewChange, view, max, min } = props;
    const today = new Date();
    const years = new Array(12).fill(0).map((_, i) => view.year - 5 + i);
    const isDisabled = (year) => {
        return RangeSign(year, min === null || min === undefined ? undefined : min.getFullYear(), max === null || max === undefined ? undefined : max.getFullYear()) !== 0;
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(CalendarHeader, { onPrevious: () => onViewChange(Object.assign(Object.assign({}, view), { year: view.year - 10 })), onNext: () => onViewChange(Object.assign(Object.assign({}, view), { year: view.year + 10 })), onChangeScale: () => onViewChange(Object.assign(Object.assign({}, view), { scale: 3 })), text: view.year - 5 + '-' + (view.year + 6) }),
        years.map((y) => (React.createElement(MonthYearCell, { onClick: () => onViewChange({ month: view.month, year: y, scale: 2 }), disabled: isDisabled(y), width: 71, height: 71, key: y, isToday: IsSameDay(new Date(y, today.getMonth(), today.getDate()), today) }, y)))));
};
const CalendarDays = (props) => {
    const { firstDayOfWeekOffset, disabledDays, value, onChange, onViewChange, view, locale } = props;
    const [getDays, setDays] = useState([]);
    useEffect(() => {
        const firstDayOfMonth = FirstDayOfMonth(view.year, view.month);
        const firstDayOfWeek = FirstMonDayOfWeek(firstDayOfMonth, firstDayOfWeekOffset);
        const testLastDay = AddDay(firstDayOfWeek, 35);
        const daysCellCount = testLastDay.getMonth() === view.month ? 42 : 35;
        const days = new Array(daysCellCount)
            .fill(undefined)
            .map((_, i) => AddDay(firstDayOfWeek, i));
        setDays(days);
    }, [view, firstDayOfWeekOffset]);
    const previousMonth = () => {
        const month = view.month - 1;
        const year = view.year;
        onViewChange({
            year: month < 0 ? year - 1 : year,
            month: month < 0 ? 11 : month,
            scale: 1,
        });
    };
    const nextMonth = () => {
        const month = view.month + 1;
        const year = view.year;
        onViewChange({
            year: month > 11 ? year + 1 : year,
            month: month > 11 ? 0 : month,
            scale: 1,
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(CalendarHeader, { onPrevious: previousMonth, onNext: nextMonth, onChangeScale: () => onViewChange(Object.assign(Object.assign({}, view), { scale: 2 })), text: `${MonthsNames(locale)[view.month]} ${view.year}` }),
        DaysName(typeof firstDayOfWeekOffset === 'undefined' ? 1 : firstDayOfWeekOffset, locale).map((name) => (React.createElement(NavDay, { key: name }, name.substring(0, 3)))),
        getDays.map((d) => (React.createElement(DayCell, { disabled: IsDisabledDate(d, disabledDays), isFade: d.getMonth() !== view.month || IsDisabledDate(d, disabledDays), key: view.month + d.toISOString(), isToday: IsSameDay(d, new Date()), isSelected: IsSameDay(d, value || new Date()), onClick: () => onChange(d) }, d.getDate())))));
};
const Calendar = (props) => {
    const { onChange, value, locale, offsetWeekDay, disabledDays } = props;
    const [getView, setView] = useState({
        month: 0,
        year: 0,
        scale: 1,
    });
    useEffect(() => {
        const date = value || new Date();
        setView({
            month: date.getMonth(),
            year: date.getFullYear(),
            scale: 1,
        });
    }, [value]);
    return (React.createElement(CalendarContainer, null,
        React.createElement(InOut, { show: getView.scale === 1, keepContent: true, style: { display: getView.scale === 1 ? 'block' : 'none' } },
            React.createElement(CalendarDays, { disabledDays: disabledDays, value: value, locale: locale, firstDayOfWeekOffset: offsetWeekDay, onChange: onChange, view: getView, onViewChange: setView })),
        React.createElement(InOut, { show: getView.scale === 2, keepContent: true, style: { display: getView.scale === 2 ? 'block' : 'none' } },
            React.createElement(CalendarMonths, { locale: locale, view: getView, min: disabledDays === null || disabledDays === undefined ? undefined : disabledDays.min, max: disabledDays === null || disabledDays === undefined ? undefined : disabledDays.max, onViewChange: setView })),
        React.createElement(InOut, { show: getView.scale === 3, keepContent: true, style: { display: getView.scale === 3 ? 'block' : 'none' } },
            React.createElement(CalendarYears, { view: getView, min: disabledDays === null || disabledDays === undefined ? undefined : disabledDays.min, max: disabledDays === null || disabledDays === undefined ? undefined : disabledDays.max, onViewChange: setView }))));
};

const SmallContainer = css `
  margin-top: ${({ small }) => (small ? '8px' : '16px')};
  margin-bottom: ${({ small }) => (small ? '8px' : '16px')};
`;
const DisabledContainer = css `
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;
const ErrorLabel = css `
  ${({ theme, hasError }) => hasError && `color:${theme.colors.danger}`};
`;
const PositionLabel = css `
  ${({ right }) => (right ? 'text-align: right' : '')};
`;
const RequiredLabel = css `
  &:after {
    ${({ required }) => (required ? 'content: " *"' : '')};
  }
`;
const LabelWrapperContainer = mergeStyled(styled.div `
    position: relative;
    width: 100%;
    box-sizing: border-box;
  `, [SmallContainer, DisabledContainer, PositionLabel]);
const LabelText = mergeStyled(styled(SpanSubtitle1) ``, [
    ErrorLabel,
    PositionLabel,
    RequiredLabel,
]);
const LabelSmallText = mergeStyled(styled(SpanCaption1) ``, [
    ErrorLabel,
    PositionLabel,
    RequiredLabel,
]);
const CaptionText = mergeStyled(styled(SpanCaption1) ``, [
    ErrorLabel,
    PositionLabel,
]);
const OverFlow = styled.label `
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin: ${({ small }) => (small ? '4px 0' : '8px 0')};
`;
const LabelWrapper = (props) => {
    const { children, label, caption, right, disabled, hasError, required, small } = props;
    return (React.createElement(LabelWrapperContainer, { disabled: disabled, small: small, right: right },
        React.createElement(InOut, { show: !!label },
            React.createElement(OverFlow, { small: small, htmlFor: 'switch-select' }, small ? (React.createElement(LabelSmallText, Object.assign({}, { right, hasError, required }), label)) : (React.createElement(LabelText, Object.assign({}, { right, hasError, required }), label)))),
        children,
        React.createElement(InOut, { show: !!caption },
            React.createElement(CaptionText, Object.assign({ as: 'div' }, { right, hasError }), caption))));
};

const InvisibleInput$1 = styled.input `
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;
const Label$1 = styled.label `
  display: inline-block;
  white-space: nowrap;
`;
const Square = styled.span `
  display: inline-block;
  vertical-align: top;
  position: relative;
  width: 23px;
  height: 23px;
  box-sizing: border-box;
  border-radius: 4px;
  background: ${({ checked, theme }) => checked ? theme.colors.primary : theme.colors.grey95};
  border: 1px solid
    ${({ checked, theme, hasError }) => hasError ? theme.colors.danger : checked ? theme.colors.primary : theme.colors.grey80};
  overflow: hidden;
  transition: background-color 200ms ease-in-out;

  svg {
    position: relative;
    top: -1px;
    left: -2px;
    transform: scale(1.4, 1.4);
    transition: opacity 200ms ease-in-out;
    opacity: ${({ checked }) => (checked ? 1 : 0)};
  }

  ${Label$1}:focus-within & {
    ${({ disabled }) => (disabled ? '' : Shapes.shadow1)};
    border-color: ${({ theme }) => theme.colors.grey40};
  }
`;
const Title$2 = styled(SpanBody1) `
  display: inline-block;
  white-space: normal;
  transition: color ease-in-out 200ms;
  color: ${({ checked, theme }) => (checked ? theme.colors.grey10 : theme.colors.grey60)};

  ${Label$1}:hover & {
    ${({ disabled, theme }) => (disabled ? '' : 'color: ' + theme.colors.grey40)};
  }
`;
const Checkbox = (props) => {
    const theme = useContext(ThemeContext);
    const { onChange, value, children, label, right, hasError, caption, disabled, required, small } = props, rest = __rest(props, ["onChange", "value", "children", "label", "right", "hasError", "caption", "disabled", "required", "small"]);
    const changeCheckbox = (event) => {
        const target = event.target;
        onChange(target.checked, event);
    };
    const renderTitle = (React.createElement(Title$2, { checked: value, disabled: disabled, style: right
            ? {
                textAlign: 'right',
                marginRight: '5px',
            }
            : {
                textAlign: 'left',
                marginLeft: '5px',
            } }, children));
    return (React.createElement(LabelWrapper, Object.assign({}, { label, caption, required, hasError, right, disabled, small }),
        React.createElement(Label$1, null,
            React.createElement(InvisibleInput$1, Object.assign({}, rest, { disabled, required }, { onChange: changeCheckbox, checked: value, type: 'checkbox' })),
            right && renderTitle,
            React.createElement(Square, { checked: value, hasError: hasError, right: right, disabled: disabled },
                React.createElement(Svg, { fill: theme.colors.light, width: 24, height: 24, icon: theme.icons === 'back' ? 'check' : 'checkmark-outline' })),
            !right && renderTitle)));
};

const ColorSelectContainer = styled.div `
  ${Shapes.card};
  display: inline-block;
  padding: 20px;
  background: ${({ theme }) => theme.colors.grey95};
  border: 1px solid ${({ theme }) => theme.colors.grey60};
  position: relative;
  user-select: none;
`;
const ColorSelectGrid = styled.div `
  position: relative;
  margin-bottom: 18px;
`;
const ColorSelectGradientContainer = styled.div `
  width: 100%;
  height: 100%;
  background-blend-mode: luminosity;
  mix-blend-mode: normal;
  border-radius: 4px;

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: linear-gradient(180deg, #ffffff 0%, #000000 100%);
    background-blend-mode: luminosity;
    mix-blend-mode: multiply;
    border-radius: 4px;
  }
`;
const ColorSelectPointer = styled(BaseButton) `
  position: absolute;
  width: 20px;
  height: 20px;
  top: -10px;
  left: -10px;
  border-radius: 50%;
  box-sizing: content-box;
  border: 2px solid white;
  transition: border-color 200ms ease-in-out;
`;
const ColorSelectHuePicker = styled.input `
  appearance: none;
  position: relative;
  height: 10px;
  width: 100%;
  background: linear-gradient(
      90deg,
      #ff0000 0%,
      #faff00 15.63%,
      #14ff00 31.25%,
      #00ffd1 45.83%,
      #0066ff 58.33%,
      #9e00ff 71.87%,
      #d0007b 85.42%,
      #ff0000 100%
    ),
    #c4c4c4;
  border-radius: 5px;
  border: none;
  padding: 0;
  margin: 0;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    box-sizing: content-box;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid white;
    background-color: var(--hue);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    box-sizing: content-box;
    border-radius: 50%;
    cursor: pointer;
    border: 2px solid white;
    background-color: var(--hue);
  }
`;
const ColorSelect = (props) => {
    const { value, onChange, width, height, onClose } = props;
    const theme = useContext(ThemeContext);
    const getWidth = width || 200;
    const getHeight = height || 200;
    const [getHue, setHue] = useState(0);
    const [getDragged, setDragged] = useState(false);
    const refColorSelectGradient = useRef(null);
    const refColorSelectPointer = useRef(null);
    const refColorSelectHuePicker = useRef(null);
    const data = useRef({
        cursorX: 0,
        cursorY: 0,
    });
    const updatePointerPosition = useCallback((x, y) => {
        const pointer = refColorSelectPointer.current;
        if (!pointer) {
            return;
        }
        pointer.style.transform = `translate(${x}px, ${y}px)`;
    }, [refColorSelectPointer]);
    const updatePointerColor = useCallback((hue, saturation, lightness) => {
        const pointer = refColorSelectPointer.current;
        if (!pointer) {
            return;
        }
        pointer.style.backgroundColor = HslToCss(hue, saturation, lightness);
    }, [refColorSelectPointer]);
    const updateGradiant = useCallback((hue) => {
        const grad = refColorSelectGradient.current;
        if (!grad) {
            return;
        }
        grad.style.background = ColorSelectGradientToCss(hue);
        const picker = refColorSelectHuePicker.current;
        if (!picker) {
            return;
        }
        picker.style.setProperty('--hue', HslToCss(hue, 100, 50));
    }, [refColorSelectGradient, refColorSelectHuePicker]);
    const changeHue = (event) => {
        const hue = parseInt(event.target.value, 10);
        onChange(CoordinateToHsv(hue, data.current.cursorX, data.current.cursorY, getWidth, getHeight));
    };
    const mousePosInContainer = (x, y) => {
        const container = refColorSelectGradient.current;
        if (!container) {
            return { x: 0, y: 0 };
        }
        const rect = container.getBoundingClientRect();
        return {
            x: setInRange(x - rect.x, 0, getWidth),
            y: setInRange(y - rect.y, 0, getHeight),
        };
    };
    const dragStart = (event) => {
        setDragged(true);
        const relativePos = mousePosInContainer(event.clientX, event.clientY);
        updatePointerPosition(relativePos.x, relativePos.y);
        data.current.cursorX = relativePos.x;
        data.current.cursorY = relativePos.y;
    };
    const dragEnd = () => {
        if (!getDragged) {
            return;
        }
        setDragged(false);
        onChange(CoordinateToHsv(getHue, data.current.cursorX, data.current.cursorY, getWidth, getHeight));
    };
    const mouseMove = (event) => {
        if (!getDragged) {
            return;
        }
        const relativePos = mousePosInContainer(event.clientX, event.clientY);
        updatePointerPosition(relativePos.x, relativePos.y);
        data.current.cursorX = relativePos.x;
        data.current.cursorY = relativePos.y;
        const coo = CoordinateToHsv(getHue, data.current.cursorX, data.current.cursorY, getWidth, getHeight);
        const hsl = HsvToHsl(coo[0], coo[1], coo[2]);
        updatePointerColor(getHue, hsl[1] * 100, hsl[2] * 100);
    };
    useEffect(() => {
        const coo = HsvToCoordinate(value, getWidth, getHeight);
        data.current.cursorX = coo.cursorX;
        data.current.cursorY = coo.cursorY;
        const hsl = HsvToHsl(value[0], value[1], value[2]);
        const hue = hsl[0] * 360;
        setHue(hue);
        updatePointerColor(hue, hsl[1] * 100, hsl[2] * 100);
        updatePointerPosition(coo.cursorX, coo.cursorY);
        updateGradiant(coo.hue360);
    }, [value, updatePointerPosition, updateGradiant, updatePointerColor, getWidth, getHeight]);
    useEffect(() => {
        updateGradiant(getHue);
    }, [getHue, updateGradiant]);
    return (React.createElement(ColorSelectContainer, { onMouseMove: mouseMove, onMouseLeave: dragEnd },
        onClose && (React.createElement(Svg, { icon: 'cross', height: 24, width: 24, fill: theme.colors.dark, style: { cursor: 'pointer', position: 'absolute', top: 0, right: 0 }, onClick: onClose })),
        React.createElement(ColorSelectGrid, { style: { width: getWidth, height: getHeight }, onMouseDown: dragStart, onMouseUp: dragEnd },
            React.createElement(ColorSelectGradientContainer, { ref: refColorSelectGradient }),
            React.createElement(ColorSelectPointer, { ref: refColorSelectPointer, onKeyDown: () => { } })),
        React.createElement(ColorSelectHuePicker, { ref: refColorSelectHuePicker, type: "range", value: getHue, min: 0, max: 360, onChange: changeHue })));
};

const Input = (props) => {
    const { required, disabled, label, caption, hasError, small, value, onChange, right, beforeChild, afterChild, icon, iconPosition, onIconClick } = props, rest = __rest(props, ["required", "disabled", "label", "caption", "hasError", "small", "value", "onChange", "right", "beforeChild", "afterChild", "icon", "iconPosition", "onIconClick"]);
    const emitChange = (event) => {
        if (onChange) {
            const target = event.target;
            onChange(target.value);
        }
    };
    const theme = useContext(ThemeContext);
    const styleIconWrapper = {
        position: 'absolute',
        inset: `${small ? '4px' : '7px'} ${iconPosition === 'left' ? 'auto' : '2px'} 0 ${iconPosition === 'left' ? '2px' : 'auto'}`,
    };
    return (React.createElement(LabelWrapper, Object.assign({}, { required, disabled, caption, label, hasError, small, right }),
        beforeChild,
        React.createElement("div", { style: { position: 'relative' } },
            React.createElement(BaseInput, Object.assign({}, rest, { required, disabled, hasError, small, right }, { iconPosition: iconPosition ? iconPosition : icon ? 'right' : undefined, value: typeof value === 'undefined' ? '' : value, onChange: emitChange })),
            icon && onIconClick ? (React.createElement(BaseButton, { onClick: onIconClick, style: Object.assign(Object.assign({}, styleIconWrapper), { marginTop: '-2px' }) },
                React.createElement(Svg, { icon: icon, width: small ? 24 : 28, height: small ? 24 : 28, fill: theme.input.placeholderColor }))) : (React.createElement("div", { style: Object.assign(Object.assign({}, styleIconWrapper), { pointerEvents: 'none' }) },
                React.createElement(Svg, { icon: icon, width: small ? 24 : 28, height: small ? 24 : 28, fill: theme.input.placeholderColor })))),
        afterChild));
};

const PreviewError = css `
  border: 1px solid
    ${({ theme, hasError }) => (hasError ? theme.colors.danger : theme.colors.grey60)};
`;
const PreviewDisabled = css `
  opacity: ${({ disabled }) => (disabled ? '0.5' : '1')};
`;
const PreviewHasColor = css `
  &:after {
    opacity: ${({ hasColor }) => (hasColor ? 0 : 1)};
  }
`;
const PreviewStyle = css `
  width: 40px;
  height: 40px;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.grey95};
  position: relative;
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    display: inline-block;
    width: 1px;
    height: 200%;
    top: -50%;

    left: 50%;
    transform: rotate(45deg);
    transform-origin: center;
    background-color: ${({ theme }) => theme.colors.danger};
  }
`;
const PreviewColor = mergeStyled(styled.div ``, [PreviewError, PreviewDisabled, PreviewHasColor, PreviewStyle]);
const ColorPicker = (props) => {
    const { hasError, label, caption, disabled, required, value, onChange, right, small } = props, rest = __rest(props, ["hasError", "label", "caption", "disabled", "required", "value", "onChange", "right", "small"]);
    const [open, setOpen] = useState(false);
    const [hasColor, setHasColor] = useState(false);
    const [getHSV, setHSV] = useState([0, 0, 0]);
    const refPreviewColor = useRef();
    const refColorPicker = useRef(null);
    const [inputValue, setInputValue] = useState('');
    useEffect(() => {
        if (typeof value === 'undefined') {
            return;
        }
        setHasColor(true);
        setInputValue(value ? value.toUpperCase() : '');
        updatePreview(value);
        setHSV(CssHexaColorToHsv(value));
    }, [value]);
    const updatePreview = (cssColor) => {
        const preview = refPreviewColor.current;
        if (preview) {
            setHasColor(!!cssColor);
            preview.style.backgroundColor = cssColor;
        }
    };
    const updateHex = (hsv) => {
        const cssHexaColor = HsvToCssHexaColor(hsv[0], hsv[1], hsv[2]);
        onChange(cssHexaColor);
        updatePreview(cssHexaColor);
        setInputValue(cssHexaColor);
    };
    const debounceTimeout = useRef();
    const changeValue = (hsv) => {
        setHSV(hsv);
        window.clearTimeout(debounceTimeout.current);
        debounceTimeout.current = window.setTimeout(() => {
            updateHex(hsv);
            debounceTimeout.current = undefined;
        }, 300);
    };
    const changeInput = (targetValue) => {
        const regColor = /^#([0-9a-f]{1,6})$/gim;
        if (targetValue.length >= 8) {
            return;
        }
        setInputValue(targetValue ? targetValue.toUpperCase() : '');
        const result = regColor.exec(targetValue);
        if (!result) {
            return onChange(targetValue ? targetValue.toUpperCase() : '');
        }
        if (targetValue.length <= 6) {
            return;
        }
        onChange(targetValue);
        updatePreview(targetValue);
        const hsv = CssHexaColorToHsv(targetValue);
        setHSV(hsv);
    };
    return (React.createElement("div", { ref: refColorPicker },
        React.createElement(Row, { style: { flexDirection: right ? 'row-reverse' : 'row' } },
            React.createElement(Col, { style: { paddingTop: label ? '43px' : '16px' } },
                React.createElement(PreviewColor, Object.assign({ onClick: () => setOpen(true) }, { disabled, hasError, hasColor }, { ref: refPreviewColor }))),
            React.createElement(Col, { style: { flex: '1' } },
                React.createElement(Input, Object.assign({}, Object.assign({ hasError, label, caption, disabled, required, right, small }, rest), { onFocus: () => setOpen(true), value: inputValue, onChange: changeInput, afterChild: React.createElement(Dropdown, { show: open, onClose: () => setOpen(false), wrapperRef: refColorPicker, offset: 16, right: right },
                        React.createElement(ColorSelect, { value: getHSV, onChange: changeValue, onClose: !disabled ? () => setOpen(false) : undefined })) }))))));
};

const ContainerSizeSM = 'sm';
const ContainerSizeMD = 'md';
const ContainerSizeLG = 'lg';
const ContainerSizeFluid = 'fluid';
const ContainerSizes = [
    ContainerSizeSM,
    ContainerSizeMD,
    ContainerSizeLG,
    ContainerSizeFluid,
];
const MaxWidth = (size) => {
    switch (size) {
        case 'lg':
            return '992px';
        case 'md':
            return '768px';
        case 'sm':
            return '576px';
        case 'fluid':
            return 'initial';
        default:
            return size + 'px';
    }
};
const Container$4 = styled.div `
  width: 100%;
  max-width: ${(props) => MaxWidth(props.size)};
  margin: 0 auto;
  padding: 32px max(10px, min(2vw, 20px));
  box-sizing: border-box;
  position: relative;
`;

const DashboardCardStyled = styled.div `
  ${Shapes.card};
  background: ${({ theme }) => theme.colors.light};
  padding: ${({ noPadding }) => (noPadding ? 0 : 30)}px;
  width: ${({ col }) => 310 * (col || 1) + 30 * ((col || 1) - 1)}px;
  height: ${({ row }) => 310 * (row || 1) + 30 * ((row || 1) - 1)}px;
  margin: 0 16px 32px;
  position: relative;
`;
const DashboardCard = (props) => {
    const rest = __rest(props, []);
    return React.createElement(DashboardCardStyled, Object.assign({}, rest));
};

const ImgContainer = styled.div `
  padding: 10px 0 0 16px;

  img {
    margin-bottom: 32px;
  }
`;
const CircleContainer = styled(BaseButton) `
  padding: 5px;
  display: flex;
  align-items: center;
  background: transparent;
`;
const DashboardSlider = (props) => {
    const { slides, subtitle } = props;
    const [getPosition, setPosition] = useState(0);
    return (React.createElement(DashboardCard, { col: 2 },
        React.createElement(Row, null,
            React.createElement(Col, { columns: [6] },
                React.createElement(ImgContainer, null,
                    React.createElement("div", { style: { width: 200, height: 186, overflow: 'hidden' } }, slides.map((slide, index) => (React.createElement(InOut, { key: index, enter: { keyframes: Keyframes.slideInFromLeft }, exit: { keyframes: Keyframes.slideOutToRight }, style: { position: 'absolute' }, show: getPosition === index },
                        React.createElement("img", { width: 200, height: 150, src: slide.imageUri, alt: slide.alt }))))),
                    React.createElement(Flex, { justifyContent: ['center'] }, slides.length > 1 &&
                        slides.map((slide, index) => (React.createElement(CircleContainer, { key: index, onClick: () => setPosition(index) },
                            React.createElement(Svg, { width: 24, height: 24 },
                                React.createElement("circle", { cx: "24", cy: "24", r: "21", fill: getPosition === index ? '#7a9899' : 'white', stroke: "#7A9899", strokeWidth: "3" })))))))),
            React.createElement(Col, { columns: [6] },
                React.createElement(DashboardCardSubtitle, null, subtitle),
                slides.map((slide, index) => (React.createElement(InOut, { key: index, style: { position: 'absolute' }, show: getPosition === index },
                    React.createElement(DashboardCardTitle, null, slide.title),
                    React.createElement(DashboardCardBody, null, slide.body))))))));
};

const InputDate = (_a) => {
    var { value, onChange, disabledDays, readOnly, right, locale } = _a, props = __rest(_a, ["value", "onChange", "disabledDays", "readOnly", "right", "locale"]);
    const [getType, setType] = useState(dateToString(value, locale));
    useEffect(() => {
        setType(dateToString(value, locale));
    }, [value, locale]);
    const onTypeDate = (value) => {
        const intlParams = localToDateParams(locale);
        if (!value) {
            setType('');
            if (value !== null) {
                onChange(null);
            }
            return;
        }
        const regDate = /^(\d{2}).?(\d{2}).?(\d{4})$/gm;
        const lastChar = value[value.length - 1];
        if (lastChar && lastChar !== intlParams.separator && isNaN(parseInt(lastChar, 10))) {
            return;
        }
        if (lastChar !== intlParams.separator && value.length === 3) {
            const withFirstSeparation = value.slice(0, 2) + intlParams.separator + value.slice(2);
            setType(withFirstSeparation);
            return;
        }
        if (lastChar !== intlParams.separator && value.length === 6) {
            const withSecondSeparation = value.slice(0, 5) + intlParams.separator + value.slice(5);
            setType(withSecondSeparation);
            return;
        }
        const result = regDate.exec(value);
        if (value.length > 10) {
            return;
        }
        if (result) {
            const day = Number.parseInt(result[intlParams.order.indexOf('d') + 1]);
            const month = Number.parseInt(result[intlParams.order.indexOf('m') + 1]);
            const year = Number.parseInt(result[intlParams.order.indexOf('y') + 1]);
            const date = new Date(year, month - 1, day);
            if (IsDisabledDate(date, disabledDays)) {
                return;
            }
            onChange(date);
        }
        setType(value);
    };
    return (React.createElement(Input, Object.assign({}, props, { readOnly, right }, { autoComplete: "off", value: getType, onChange: onTypeDate })));
};

const DatePicker = (props) => {
    const { value, onChange, right, disabledDays, locale, offsetWeekDay, readOnly } = props, rest = __rest(props, ["value", "onChange", "right", "disabledDays", "locale", "offsetWeekDay", "readOnly"]);
    const refDatePicker = useRef(null);
    const [getOpen, setOpen] = useState(false);
    const navigateCalendar = (keyCode, ctrl) => {
        let incrementDate = 0;
        let incrementMonth = 0;
        let incrementYear = 0;
        switch (keyCode) {
            case 37: // left
                if (ctrl) {
                    incrementMonth = -1;
                }
                else {
                    incrementDate = -1;
                }
                break;
            case 39: // right
                if (ctrl) {
                    incrementMonth = 1;
                }
                else {
                    incrementDate = 1;
                }
                break;
            case 40: // down
                if (ctrl) {
                    incrementYear = 1;
                }
                else {
                    incrementDate = 7;
                }
                break;
            case 38: // up
                if (ctrl) {
                    incrementYear = -1;
                }
                else {
                    incrementDate = -7;
                }
                break;
        }
        const previousDate = value || new Date();
        const date = new Date(previousDate.getFullYear() + incrementYear, previousDate.getMonth() + incrementMonth, previousDate.getDate() + incrementDate);
        if (IsDisabledDate(date, disabledDays)) {
            return;
        }
        onChange(date);
    };
    const keyDown = (event) => {
        if (readOnly) {
            return;
        }
        switch (event.keyCode || event.key) {
            case 'ArrowDown':
            case 'ArrowLeft':
            case 'ArrowUp':
            case 'ArrowRight':
            case 40: // down
            case 38: // up
            case 37: // left
            case 39: // right
                if (getOpen) {
                    event.stopPropagation();
                    event.preventDefault();
                    navigateCalendar(event.keyCode, event.ctrlKey);
                }
                else {
                    setOpen(true);
                }
                break;
            case 27: // escape
            case ' ': // escape
                if (getOpen) {
                    setOpen(false);
                }
                break;
            case 13: // enter
            case 'Enter': // enter
            case 9: // tab
            case 'Tab': // tab
                setOpen(false);
                break;
        }
    };
    return (React.createElement("div", { style: { position: 'relative', width: '100%' }, ref: refDatePicker },
        React.createElement(InputDate, Object.assign({}, rest, { readOnly, right }, { onKeyDown: keyDown, value: value, onChange: onChange, locale: locale, onFocus: () => !readOnly && setOpen(true), icon: readOnly ? undefined : 'down', afterChild: React.createElement(Dropdown, { wrapperRef: refDatePicker, onClose: () => setOpen(false), right: right, show: getOpen, offset: -16 },
                React.createElement(Calendar, { onChange: onChange, disabledDays: disabledDays, offsetWeekDay: offsetWeekDay, value: value, locale: locale })) }))));
};

const DeletableButton = styled(BaseButton) `
  background: transparent;

  svg {
    fill: ${({ theme }) => theme.colors.grey40};
    transition: ease-in-out fill 200ms;
  }

  &:hover {
    svg {
      fill: ${({ theme }) => theme.colors.grey20};
    }
  }
`;
const Deletable = (props) => {
    const { offsetTop, onDelete, hide, children } = props;
    return (React.createElement(InOut, { enter: { keyframes: Keyframes.slideInFrom('-50px') }, exit: { keyframes: Keyframes.slideOutFrom('50px') }, show: !hide },
        React.createElement(Row, { alignItems: ['center'] },
            React.createElement(Col, { style: { flex: '1' } }, children),
            React.createElement(Col, { style: { marginTop: offsetTop || '10px' } },
                React.createElement(DeletableButton, { onClick: () => onDelete && onDelete() },
                    React.createElement(Svg, { icon: 'delete', height: 24, width: 24 }))))));
};

const Portal = ({ children, attributeName, rootRef, }) => {
    const [getContainer, setContainer] = useState(null);
    useEffect(() => {
        const container = document.createElement('div');
        container.setAttribute(attributeName, 'true');
        if (rootRef) {
            rootRef.after(container);
        }
        else {
            document.body.appendChild(container);
        }
        setContainer(container);
        return () => {
            container.remove();
            setContainer(null);
        };
    }, [attributeName, rootRef]);
    return getContainer ? ReactDOM.createPortal(children, getContainer) : null;
};

const DialogContent = styled.div `
  padding: 48px;
  background-color: ${({ theme }) => theme.colors.light};
`;
const DialogFooterContainer = styled.footer `
  text-align: right;
  background-color: ${({ theme }) => theme.colors.grey10};
  color: ${({ theme }) => theme.colors.light};
  border-radius: 0 0 4px 4px;

  button {
    border-radius: 0;
  }
`;
const DialogContainer = styled.article `
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
  box-sizing: border-box;
  z-index: 100;
`;
const DialogHeader = styled.header `
  background-color: ${({ theme, backgroundColor }) => backgroundColor ? backgroundColor : theme.colors.primary};
  border-radius: 4px 4px 0 0;
`;
const DialogButtonClose = styled(BaseButton) `
  background-color: ${({ theme, backgroundColor }) => backgroundColor ? backgroundColor : theme.colors.primary};
  width: 40px;
  height: 40px;
  padding: 7px 0;
  border-radius: 0 4px 0 0;
`;
const DialogTitle = styled.h5 `
  ${getTypo('subtitle2')};
  padding: 12px 16px;
  margin: 0;
  color: ${({ theme }) => theme.colors.light};
`;
const Dialog = (props) => {
    const { show, children, onClose, size, noBackdrop, noEscape, title, rootRef, footer, backgroundColor, } = props;
    const theme = useContext(ThemeContext);
    const refDialog = useRef();
    const backDropClick = (event) => {
        const target = event.target;
        if (noBackdrop || !target) {
            return;
        }
        if (!target.hasAttribute('data-backdrop')) {
            return;
        }
        onClose();
    };
    const keyDownEscape = (event) => {
        if (event.keyCode === 27 || event.key === 'Escape') {
            if (!noEscape) {
                onClose();
            }
        }
    };
    useEffect(() => {
        if (rootRef) {
            rootRef.classList.add('root-ref');
        }
        const queryNotInModal = document.querySelectorAll(rootRef ? `.${rootRef.className}` : 'body > div:not([data-modal])');
        const reset = () => {
            for (let i = 0; i < queryNotInModal.length; i++) {
                queryNotInModal[i].style.filter = '';
                queryNotInModal[i].style.transition = 'blur ease-in-out 250ms';
            }
            setTimeout(() => {
                for (let i = 0; i < queryNotInModal.length; i++) {
                    queryNotInModal[i].style.transition = '';
                }
            }, 250); // after animation finish
        };
        if (show) {
            for (let i = 0; i < queryNotInModal.length; i++) {
                queryNotInModal[i].style.filter = 'blur(2px)';
                queryNotInModal[i].style.transition = 'blur ease-in-out 200ms';
            }
            setTimeout(() => {
                const el = refDialog.current;
                if (!el) {
                    return;
                }
                focusFirstFocusableElement(el);
            }, 20); // timeout for let time to dom appear
        }
        else {
            // reset style
            reset();
        }
        return () => {
            reset();
        };
    }, [show, refDialog, rootRef]);
    const focusTrap = (last) => {
        const el = refDialog.current;
        if (!show || !el) {
            return;
        }
        if (last) {
            focusLastFocusableElement(el);
            return;
        }
        focusFirstFocusableElement(el);
    };
    return (React.createElement(Portal, { attributeName: 'data-modal', rootRef: rootRef },
        React.createElement(InOut, { show: show },
            React.createElement(DialogContainer, { role: "dialog", tabIndex: -1, "aria-modal": "true", "data-backdrop": true, onClick: backDropClick, onKeyDown: keyDownEscape },
                React.createElement("div", { tabIndex: 0, onFocus: () => focusTrap(true) }),
                React.createElement("div", { style: { width: '100%' }, ref: refDialog },
                    React.createElement(Container$4, { size: size || 'lg' },
                        React.createElement(DialogHeader, { backgroundColor: backgroundColor },
                            React.createElement(Flex, { justifyContent: ['space-between'] },
                                React.createElement(DialogTitle, null, title),
                                React.createElement(DialogButtonClose, { onClick: onClose, backgroundColor: backgroundColor },
                                    React.createElement(Svg, { icon: theme.icons === 'back' ? 'cross' : 'close-fill', height: 24, width: 24, fill: theme.colors.light })))),
                        React.createElement(DialogContent, null, children),
                        React.createElement(DialogFooterContainer, null, footer))),
                React.createElement("div", { tabIndex: 0, onFocus: () => focusTrap() })))));
};

const SquareBtn = styled(Button) `
  padding: 0 0 0 ${({ small }) => (small ? '3px' : '5px')};
  width: ${({ small }) => (small ? '24px' : '40px')};
  height: ${({ small }) => (small ? '24px' : '40px')};
`;
const SquareButton = (props) => {
    return React.createElement(SquareBtn, Object.assign({}, props));
};

const SwitchContainer = styled.div `
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  padding: 2px 0;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: ${({ right }) => (right ? 'flex-end' : 'flex-start')};
`;
const SwitchLabelChoice = styled(SpanSubtitle2) `
  transition: ease-in-out color 200ms;
  padding-top: 6px;
  color: ${({ checked, theme }) => (checked ? theme.colors.grey10 : theme.colors.grey60)};
  cursor: ${({ checked, disabled }) => (checked || disabled ? 'normal' : 'pointer')};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  font-weight: ${({ small }) => small && 400};
`;
const SwitchButton = styled(BaseButton) `
  border-radius: 10px;
  border: none;
  width: 40px;
  height: 20px;
  position: relative;
  transition: background-color 200ms ease-in-out;
  background-color: ${({ active, theme, color }) => active ? (color ? color : theme.colors.primary) : theme.colors.grey80};

  &:after {
    content: '';
    position: absolute;
    top: 2px;
    transition: left ease-in-out 200ms;
    left: ${({ active }) => (active ? 22 : 2)}px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.light};
  }
`;
const IconButton$1 = styled(Svg) `
  position: absolute;
  z-index: 2;
  margin-top: 6px;
  pointer-events: none;
`;
const Switch = (props) => {
    const { onLabel, offLabel, caption, disabled, label, value, hasError, right, onChange, small, color, onIcon, offIcon, } = props;
    const theme = React.useContext(ThemeContext);
    const emitChange = (newVal) => {
        if (onChange) {
            onChange(newVal);
        }
    };
    const clickOn = (event) => {
        event.preventDefault();
        emitChange(true);
    };
    const clickOff = (event) => {
        event.preventDefault();
        emitChange(false);
    };
    const toggleSwitch = () => {
        emitChange(!value);
    };
    const keyDown = (event) => {
        if (event.keyCode === 37) {
            emitChange(false);
        }
        if (event.keyCode === 39) {
            emitChange(true);
        }
    };
    return (React.createElement(LabelWrapper, Object.assign({}, { caption, disabled, label, hasError, right, small }),
        React.createElement(SwitchContainer, Object.assign({}, { right, disabled }, { role: 'switch', "aria-checked": 'true', id: 'switch-select' }),
            !!offLabel && (React.createElement(SwitchLabelChoice, { style: { marginRight: small ? '6px' : '12px' }, onClick: clickOff, disabled: disabled, checked: !value, small: small }, offLabel)),
            React.createElement("div", null,
                offIcon && !value && (React.createElement(IconButton$1, { icon: offIcon, fill: color ? color : theme.colors.primary, width: 16, height: 16, style: { opacity: 0.5, left: 2 } })),
                React.createElement(SwitchButton, { disabled: disabled, active: value, onKeyDown: keyDown, onClick: toggleSwitch, color: color }),
                onIcon && value && (React.createElement(IconButton$1, { icon: onIcon, fill: color ? color : theme.colors.primary, width: 16, height: 16, style: { left: 22 } }))),
            !!onLabel && (React.createElement(SwitchLabelChoice, { style: { marginLeft: small ? '6px' : '12px' }, disabled: disabled, onClick: clickOn, checked: value, small: small }, onLabel)))));
};

const DRAGGABLE_PADDING_X = 24;
const DRAGGABLE_PADDING_Y$1 = 20;
const ParentDragCard = styled.div `
  margin: ${({ isCollapsed }) => (isCollapsed ? '0' : '16px')} 0;
  box-sizing: content-box;
  border-radius: 4px;
  border: 1px dashed ${({ isDragged, theme }) => (isDragged ? theme.colors.grey80 : 'transparent')};
  width: 100%;
`;
const DCard = styled.div `
  display: block;
  ${({ isDragged }) => (isDragged ? Shapes.shadow1 : '')};
  transform: ${({ isDragged }) => (isDragged ? 'scale(1.01)' : 'scale(1.0)')};
  transition: box-shadow ease-in-out 200ms, z-index ease-in-out 200ms,
    transform ease-in-out 100ms
      ${({ isDragged }) => (isDragged ? '' : ', top ease-in-out 100ms, left ease-in-out 100ms')};
  position: absolute;
  padding: ${DRAGGABLE_PADDING_Y$1}px ${DRAGGABLE_PADDING_X}px;
  width: 100%;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.grey95};
  border-radius: ${({ isCollapsed }) => (isCollapsed ? '0' : '4px')};
  ${({ isCollapsed, theme }) => (isCollapsed ? '0' : `border: 1px solid ${theme.colors.grey95}`)};
  ${({ isCollapsed, isDragged, theme }) => isDragged ? '' : isCollapsed ? `border-bottom: 1px solid ${theme.colors.grey80}` : ''};
  cursor: move;
  user-select: none;
  z-index: ${({ isDragged }) => (isDragged ? '2' : '1')};
`;
const Wrapper$8 = styled.div `
  ${getTypo('subtitle1')};
  > p {
    margin: 0;
  }
`;
const DraggableCard = (props) => {
    const theme = useContext(ThemeContext);
    const { subtitle, htmlSubtitle, title, onDragStart, isDragged, onMove, onDelete, index, onEdit, onToggle, toggleState, isCollapsed, custom, } = props;
    const [getHeight, setHeight] = useState(0);
    const emitDragStart = (event) => {
        const target = event.target;
        const img = new Image();
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
        event.dataTransfer.setDragImage(img, 0, 0);
        event.dataTransfer.setData('text', '');
        onDragStart({
            offSet: {
                x: event.pageX - target.offsetLeft - window.scrollX,
                y: event.pageY - target.offsetTop - window.scrollY,
            },
        });
    };
    const emitMove = (event) => {
        switch (event.keyCode) {
            case 40: // down
                event.preventDefault();
                onMove(1);
                break;
            case 38: // up
                event.preventDefault();
                onMove(-1);
                break;
        }
    };
    const cardRef = useRef();
    const parentRef = useRef();
    useEffect(() => {
        const cardCurrent = cardRef.current;
        if (cardCurrent) {
            setHeight(cardCurrent.clientHeight);
        }
    }, []);
    useEffect(() => {
        if (!getHeight) {
            return;
        }
        const parentCurrent = parentRef.current;
        if (!parentCurrent) {
            return;
        }
        const child = parentCurrent.firstChild;
        if (!child) {
            return;
        }
        child.style.left = parentCurrent.offsetLeft + 1 + 'px'; // + 1px for border
        child.style.top = parentCurrent.offsetTop + 1 + 'px';
    }, [getHeight, index, isDragged]);
    return (React.createElement(ParentDragCard, { ref: parentRef, isCollapsed: isCollapsed, isDragged: isDragged, style: { height: getHeight } },
        React.createElement(DCard, { ref: cardRef, isDragged: isDragged, onDragStart: emitDragStart, isCollapsed: isCollapsed, draggable: true, tabIndex: 0, onKeyDown: emitMove },
            React.createElement(Row, { alignItems: ['center'], style: {
                    transition: 'opacity 200ms ease-in-out',
                    opacity: onToggle ? (toggleState ? 1 : 0.5) : 1,
                } },
                React.createElement(Col, null,
                    React.createElement(Svg, { style: { marginTop: '6px' }, width: 24, height: 24, icon: 'handle', fill: theme.colors.grey40 })),
                !!onToggle && (React.createElement(Col, null,
                    React.createElement(Switch, { value: toggleState, onChange: onToggle }))),
                React.createElement(Col, { columns: [7] },
                    React.createElement("div", null,
                        React.createElement(SpanH4, null, title)),
                    htmlSubtitle && React.createElement(Wrapper$8, { dangerouslySetInnerHTML: { __html: htmlSubtitle } }),
                    subtitle && (React.createElement("div", null,
                        React.createElement(SpanSubtitle1, null, subtitle)))),
                React.createElement(Col, { style: { marginLeft: 'auto' } },
                    React.createElement(Row, { alignItems: ['center'] },
                        onEdit && (React.createElement(Col, null,
                            React.createElement(SquareButton, { icon: 'edit', onClick: onEdit }))),
                        onDelete && (React.createElement(Col, null,
                            React.createElement(SquareButton, { icon: 'delete', onClick: onDelete }))),
                        !!custom && React.createElement(Col, null, custom)))))));
};

const DRAGGABLE_PADDING_Y = 20;
const Container$3 = styled.div `
  position: relative;
`;
const switchItems = function (arr, from, to) {
    if (to < 0) {
        return arr;
    }
    const copy = arr.slice();
    copy.splice(to, 0, copy.splice(from, 1)[0]);
    return copy;
};
function DraggableList(props) {
    const { onChange, items, onEdit, isCollapsed, onToggle, onDelete } = props;
    const containerRef = useRef();
    const draggedElementRef = useRef();
    const lockRef = useRef(false);
    const buildItemsInfo = useCallback((arr) => {
        return arr.map((item, index) => ({
            item,
            isDragged: false,
            initialIndex: index,
        }));
    }, []);
    const [getItemsInfo, setItemsInfo] = useState(buildItemsInfo(items));
    useEffect(() => {
        setItemsInfo(buildItemsInfo(items));
    }, [items, buildItemsInfo]);
    const emitChange = (itemInfo) => {
        onChange(itemInfo.map((info) => info.item));
    };
    const onDragEnd = () => {
        if (lockRef.current) {
            return;
        }
        lockRef.current = true;
        setTimeout(() => {
            lockRef.current = false;
        }, 30);
        draggedElementRef.current = undefined;
        const unDragged = getItemsInfo.map((info) => {
            return Object.assign(Object.assign({}, info), { isDragged: false });
        });
        setItemsInfo(unDragged);
        emitChange(unDragged);
    };
    const onMoveItem = (index, direction) => {
        emitChange(switchItems(getItemsInfo, index, index + direction));
    };
    const onDragStart = (index, dragInfo) => {
        setItemsInfo(getItemsInfo.map((itemInfo, i) => {
            if (index !== i) {
                return itemInfo;
            }
            return Object.assign(Object.assign({}, itemInfo), { isDragged: true });
        }));
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const children = container.children;
        if (!children) {
            return;
        }
        const child = children[index].firstChild;
        if (!child) {
            return;
        }
        draggedElementRef.current = {
            element: child,
            offset: dragInfo.offSet,
        };
    };
    const moveDragged = (event, dragged) => {
        dragged.element.style.left = event.clientX - dragged.offset.x + 'px';
        dragged.element.style.top = event.clientY - dragged.offset.y + 'px';
    };
    const getDragPosition = (event) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const children = container.children;
        if (!children) {
            return;
        }
        const getMinMax = (element) => {
            const division = element.clientHeight / 4;
            const min = element.offsetTop + division - DRAGGABLE_PADDING_Y;
            const max = element.offsetTop + element.clientHeight - division;
            return {
                min,
                max,
            };
        };
        const rect = container.getBoundingClientRect();
        const mouseY = event.clientY - rect.top;
        const eligiblePos = [];
        for (let i = 0; i < children.length; i++) {
            const current = getMinMax(children[i]);
            if (i === 0) {
                if (mouseY < current.min) {
                    eligiblePos.push(0);
                }
            }
            else if (i === children.length - 1) {
                if (mouseY > current.max) {
                    eligiblePos.push(children.length - 1);
                }
            }
            else {
                const previous = getMinMax(children[i - 1]);
                const next = getMinMax(children[i + 1]);
                if (mouseY > previous.max && mouseY < current.min) {
                    eligiblePos.push(i - 1);
                }
                else if (mouseY > current.max && mouseY < next.min) {
                    eligiblePos.push(i);
                }
            }
        }
        return eligiblePos[0];
    };
    const onDragOver = (event) => {
        event.preventDefault();
        const dragged = draggedElementRef.current;
        if (!dragged) {
            return;
        }
        moveDragged(event, dragged);
        const pos = getDragPosition(event);
        if (typeof pos === 'undefined') {
            return;
        }
        const indexDragged = getItemsInfo.findIndex((info) => info.isDragged);
        if (pos === indexDragged) {
            return;
        }
        setItemsInfo(switchItems(getItemsInfo, indexDragged, pos));
    };
    return (React__default.createElement(Container$3, { ref: containerRef, onDragEnd: onDragEnd, onDragOver: onDragOver }, getItemsInfo.map((itemInfo, index) => (React__default.createElement(DraggableCard, { key: itemInfo.item.id, index: index, isDragged: itemInfo.isDragged, subtitle: itemInfo.item.subtitle, htmlSubtitle: itemInfo.item.htmlSubtitle, title: itemInfo.item.title, onDragStart: (event) => onDragStart(index, event), onMove: (event) => onMoveItem(index, event), onDelete: itemInfo.item.canRemove && onDelete ? () => onDelete(itemInfo.item) : undefined, onEdit: itemInfo.item.displayModify && onEdit ? () => onEdit(itemInfo.item) : undefined, onToggle: itemInfo.item.canToggle && onToggle ? () => onToggle(itemInfo.item) : undefined, toggleState: itemInfo.item.toggleState, isCollapsed: isCollapsed, custom: itemInfo.item.custom })))));
}

const Star = (props) => {
    const { half, color, onClick } = props;
    const theme = useContext(ThemeContext);
    return (React.createElement(Svg, { style: { cursor: onClick ? 'pointer' : 'initial' }, onClick: () => onClick && onClick(), width: 24, height: 24, icon: 'star', fill: color ? color : theme.colors.grey60 }, half && (React.createElement(React.Fragment, null,
        React.createElement("defs", null,
            React.createElement("pattern", { id: "Pattern", x: "0", y: "0", width: "48", height: "48" },
                React.createElement("rect", { x: "21", y: "0", width: "48", height: "48", fill: theme.colors.grey60 }))),
        React.createElement("path", { fill: "url(#Pattern)", d: "M24,37.79,13,43.85a1.18,1.18,0,0,1-1.74-1.23L13.36,29.7l-9-9.2a1.18,1.18,0,0,1,.67-2l12.41-1.89L22.93,4.93c.59-1.24,1.55-1.24,2.14,0l5.51,11.68L43,18.5a1.18,1.18,0,0,1,.67,2l-9,9.2,2.12,12.92A1.18,1.18,0,0,1,35,43.85Z" })))));
};

const Score = styled.span `
  ${getTypo('subtitle2')};
  padding-left: 10px;
  width: 50px;
  text-align: right;
  color: ${({ scoreColor, theme }) => (scoreColor ? scoreColor : theme.colors.grey60)};
`;
const Evaluation = (props) => {
    const { value, onChange, maxStars, color, showScore, scoreColor } = props;
    const max = maxStars || 5;
    const theme = useContext(ThemeContext);
    const readOnly = !onChange;
    const keyDown = (event) => {
        if (!onChange) {
            return;
        }
        switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
                onChange(Increment(value, 0.5, 0, max));
                break;
            case 'ArrowLeft':
            case 'ArrowDown':
                onChange(Increment(value, -0.5, 0, max));
                break;
            case '0':
            case '1':
            case '2':
            case '3':
            case '4':
            case '5':
            case '6':
            case '7':
            case '8':
            case '9':
                onChange(SetInRange(parseInt(event.key, 10), 0, max));
        }
    };
    const emitChange = (val) => {
        if (!onChange) {
            return;
        }
        if (val === value) {
            onChange(val - 0.5);
        }
        else {
            onChange(val);
        }
    };
    const stars = () => {
        const items = [];
        for (let i = 1; i <= max; i++) {
            const isHalf = Math.ceil(value) === i && value < i;
            const isActive = value >= i;
            items.push(React.createElement(Star, { key: i, color: isActive || isHalf ? color || theme.colors.primary : '', onClick: readOnly ? undefined : () => emitChange(i), half: isHalf }));
        }
        return items;
    };
    return (React.createElement(Flex, { onKeyDown: readOnly ? undefined : keyDown, alignItems: ['center'], tabIndex: readOnly ? undefined : 1 },
        stars(),
        showScore && (React.createElement(Score, { scoreColor: scoreColor },
            value,
            "/",
            max))));
};

const Menu = styled.ul `
  ${getTypo('subtitle1')};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  background-color: ${({ theme }) => theme.colors.grey90};
  padding: 0;
  margin: 0;
  list-style: none;
  border-radius: 0 0 4px 4px;
`;
const Item = styled.li `
  padding: 10px 15px 10px 30px;
  color: ${({ active, theme }) => (active ? theme.colors.grey20 : theme.colors.grey40)};
  background-color: ${({ active, theme }) => (active ? theme.colors.grey80 : theme.colors.grey90)};
  cursor: ${({ active }) => (active ? 'initial' : 'pointer')};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &:hover {
    background-color: ${({ active, theme }) => active ? theme.colors.grey80 : theme.colors.grey80};
  }
`;
function FilterMenu({ options, selected, onClick, }) {
    return (React.createElement(Menu, null, options.map((option) => (React.createElement(Item, { key: option.uid || option.label, title: option.label, active: selected &&
            (option.uid && selected.uid
                ? option.uid === selected.uid
                : option.label === selected.label), onClick: () => onClick(option) }, option.label)))));
}

const ButtonDropdown = styled(BaseButton) `
  ${getTypo('subtitle1')};
  padding: 15px 15px 15px 30px;
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.grey80};
  color: ${({ active, theme }) => (active ? theme.colors.grey20 : theme.colors.grey40)};
`;
const Wrapper$7 = styled.div `
  position: relative;
  width: fit-content;
`;
function FilterSelect(props) {
    const { value, options, onChange } = props;
    const [getOpen, setOpen] = useState(false);
    const refWrapper = useRef();
    const emitChange = (selected) => {
        onChange(selected);
        setOpen(false);
    };
    const getSelectedIndex = () => {
        return options.findIndex((item) => item.label === value.label);
    };
    const getSelectedLabel = () => {
        const index = getSelectedIndex();
        if (index !== -1) {
            return options[index].label;
        }
        return '';
    };
    const keyDown = (ev) => {
        const selectedIndex = getSelectedIndex();
        switch (ev.key) {
            case 'ArrowDown':
                onChange(options[Increment(selectedIndex, 1, 0, options.length - 1, true)]);
                break;
            case 'ArrowUp':
                onChange(options[Increment(selectedIndex, -1, 0, options.length - 1, true)]);
                break;
        }
    };
    const theme = useContext(ThemeContext);
    return (React.createElement(Wrapper$7, { ref: refWrapper },
        React.createElement(ButtonDropdown, { onClick: () => setOpen(true), onFocus: () => setOpen(true), onKeyDown: keyDown, active: getOpen },
            React.createElement("span", { style: { marginRight: '40px' } }, getSelectedLabel()),
            React.createElement(Svg, { width: 12, height: 12, icon: 'down', fill: getOpen ? theme.colors.grey20 : theme.colors.grey60 })),
        React.createElement(Dropdown, { wrapperRef: refWrapper, show: getOpen, onClose: () => setOpen(false), fulfilled: true },
            React.createElement(FilterMenu, { onClick: emitChange, selected: value, options: options }))));
}

const Hide = styled.div `
  ${({ devices }) => devices.map((d) => MakeCssMediaQueryStrict(`
  display:none;
  visibility:hidden;
  `, d))}
`;

const Wrapper$6 = styled.div `
  width: 100%;
  max-width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '300px'};
  position: relative;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.box.borderRadius};
  border: 1px solid
    ${({ redBorder, theme }) => (redBorder ? theme.colors.danger : theme.colors.grey90)};
  text-align: center;

  &:focus-within {
    ${Shapes.shadow2}
  }
`;
const Preview$1 = styled.div `
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: #ccc;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: absolute;
`;
const Container$2 = styled.div `
  background-color: ${({ theme }) => theme.colors.grey90};
  box-sizing: border-box;
  position: absolute;
  border-radius: inherit;
  width: 100%;
  height: 100%;
`;
const ImageBlockUploader = (props) => {
    const { value, onChange, buttonLabel, titleLabel, middleLabel, height, width, hasError, maxSize, onError, isLoading, small, accept, previewStyle, } = props;
    const [getLocal, setLocal] = useState('');
    const emitDelete = () => {
        onChange({ stringFormat: '' });
        setLocal('');
    };
    const onDropImage = (event) => {
        event.preventDefault();
        const data = event.dataTransfer.files;
        if (!(data && data.length)) {
            return;
        }
        const file = data[0];
        ReadFileToDataUrl(file, maxSize)
            .then((result) => {
            onChange({
                stringFormat: result,
                binary: file,
            });
        })
            .catch((error) => onError(error));
    };
    const theme = useContext(ThemeContext);
    return (React.createElement(Wrapper$6, { redBorder: hasError, onDragOver: (e) => e.preventDefault(), onDrop: onDropImage, width: width, height: height },
        React.createElement(Container$2, null,
            React.createElement(Flex, { style: { flexDirection: 'column', width: '100%', height: '100%' }, alignItems: ['center'], justifyContent: ['center'] },
                React.createElement(Flex, { alignItems: ['center'], justifyContent: ['center'] },
                    React.createElement("div", { style: { marginRight: '5px' } },
                        React.createElement(Svg, { icon: 'image', fill: theme.colors.grey40, width: small ? 32 : 48, height: small ? 32 : 48 })),
                    React.createElement(SpanH2, { style: {
                            color: theme.colors.grey40,
                            fontSize: small ? 24 : 36,
                        } }, titleLabel)),
                React.createElement("div", { style: { marginBottom: 10 } }, small ? (React.createElement(SpanSubtitle1, null, middleLabel)) : (React.createElement(SpanBody1, { style: { color: theme.colors.grey40 } }, middleLabel))),
                React.createElement("label", null,
                    React.createElement(BaseInputImage, { maxSize: maxSize, onChange: onChange, onError: onError, onLocalChange: setLocal, accept: accept, local: getLocal, style: { opacity: 0, position: 'absolute', width: 0, height: 0 } }),
                    small ? (React.createElement(SpanLink, null, buttonLabel)) : (React.createElement(Button, { as: 'div', colorType: 'secondary' }, buttonLabel))))),
        React.createElement(InOut, { show: !!value.stringFormat, startTriggering: true },
            React.createElement(Preview$1, { style: Object.assign(Object.assign({}, previewStyle), { backgroundImage: `url(${value.stringFormat})` }) },
                React.createElement(Flex, { justifyContent: ['flex-end'], style: { padding: small ? '5px' : '10px' } },
                    React.createElement(SquareButton, { icon: 'cross', small: small, onClick: emitDelete })))),
        React.createElement(InOut, { show: isLoading || false },
            React.createElement(Container$2, null,
                React.createElement(Flex, { style: { width: '100%', height: '100%' }, alignItems: ['center'], justifyContent: ['center'] },
                    React.createElement(Svg, { stroke: theme.colors.dark, animatedIcon: 'spinner' }))))));
};

const InfiniteScroll = (props) => {
    const { onNext, lock, children, containerElement, isLoading, moreResultLabel } = props;
    const theme = useContext(ThemeContext);
    useEffect(() => {
        let oneTime = false;
        const onScroll = () => {
            if (lock || oneTime) {
                return;
            }
            const pos = window.pageYOffset + window.innerHeight + 20;
            const max = document.body.scrollHeight;
            if (pos >= max) {
                onNext();
                oneTime = true;
            }
        };
        (containerElement || window).addEventListener('scroll', onScroll, { passive: true });
        return () => {
            (containerElement || window).removeEventListener('scroll', onScroll);
        };
    }, [onNext, lock, containerElement]);
    return (React__default.createElement(React__default.Fragment, null,
        children,
        React__default.createElement("div", { style: { textAlign: 'center' } },
            !lock && !isLoading && (React__default.createElement(BaseButton, { onClick: onNext },
                React__default.createElement(SpanLink, null, moreResultLabel || '+'))),
            React__default.createElement(InOut, { show: isLoading, keepContent: true },
                React__default.createElement(Svg, { animatedIcon: 'spinner', stroke: theme.colors.grey20 })))));
};

const ImagePreview = styled.div `
  width: 40px;
  height: 40px;
  margin-right: 10px;
  border: 1px solid #999999;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: #f2f2f2;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ButtonLink = styled.span `
  ${getTypo('link')};
  cursor: pointer;
`;
const InputImage = (props) => {
    const { value, onChange, onError, maxSize, addImageLabel, removeImageLabel, replaceImageLabel, label, caption, right, disabled, hasError, required, small, isLoading, accept, } = props;
    const [getLocal, setLocal] = useState('');
    const emitDelete = () => {
        onChange({});
        setLocal('');
    };
    const theme = useContext(ThemeContext);
    return (React.createElement(LabelWrapper, Object.assign({}, { label, caption, right, disabled, hasError, required, small }),
        React.createElement(Flex, { alignItems: ['center'] },
            React.createElement("label", null,
                React.createElement(Flex, { alignItems: ['center'] },
                    React.createElement(ImagePreview, null,
                        value.stringFormat && !isLoading && (React.createElement("img", { src: value.stringFormat, alt: 'preview', width: 40, height: 40 })),
                        !value.stringFormat && !isLoading && (React.createElement(Svg, { icon: 'image', width: 24, height: 24, fill: theme.colors.grey60 })),
                        isLoading && (React.createElement(Svg, { animatedIcon: 'spinner', width: 24, height: 24, stroke: theme.colors.dark }))),
                    React.createElement(BaseInputImage, { disabled: disabled, required: required, maxSize: maxSize, onChange: onChange, onError: onError, onLocalChange: setLocal, local: getLocal, accept: accept, style: { opacity: 0, position: 'absolute', width: 0, height: 0 } }),
                    value.stringFormat && (React.createElement(ButtonLink, { style: { marginRight: '10px' } }, replaceImageLabel || 'Remplacer l’image')),
                    !value.stringFormat && React.createElement(ButtonLink, null, addImageLabel || 'Ajouter une image'))),
            React.createElement(InOut, { show: !!value.stringFormat },
                React.createElement(ButtonLink, { onClick: emitDelete }, removeImageLabel || 'Supprimer l’image')))));
};

const InputNumber = (props) => {
    const { label, disabled, caption, right, hasError, required, small } = props, rest = __rest(props, ["label", "disabled", "caption", "right", "hasError", "required", "small"]);
    return (React.createElement(LabelWrapper, Object.assign({}, { label, disabled, caption, right, hasError, required, small }),
        React.createElement(BaseInputNumber, Object.assign({}, Object.assign({ disabled, hasError, required, small }, rest)))));
};

const UlStyled = styled.ul `
  ${getTypo('caption1')};
  color: ${({ theme }) => theme.colors.grey50};
  list-style: outside;
  margin: 0;
  padding: 0 0 0 20px;

  li {
    margin: 16px 0;
  }
`;

class List extends React.PureComponent {
    render() {
        return React.createElement(UlStyled, null, this.renderItems());
    }
    renderItems() {
        const items = [];
        if (this.props.items) {
            this.props.items.forEach((item, index) => {
                items.push(React.createElement("li", { key: index }, item));
            });
        }
        return items;
    }
}

const Container$1 = styled.div `
  position: relative;
`;
const ProgressBarContainer = styled.div `
  position: absolute;
  top: 0;
  width: 100%;
  background: transparent;
`;
const Bar$1 = styled.div `
  height: 4px;
  background: ${({ theme }) => theme.colors.primary};
`;
const LoadingContent = (props) => {
    const { children, isLoading } = props;
    return (React.createElement(Container$1, null,
        React.createElement(InOut, { show: !isLoading, startTriggering: true }, children),
        React.createElement(ProgressBarContainer, null,
            React.createElement(InOut, { startTriggering: true, show: isLoading, startStyle: { opacity: 1 }, enter: { keyframes: Keyframes.growInToRight, options: { duration: 5000 } } },
                React.createElement(Bar$1, null)))));
};

const Nav = styled.nav `
  background: ${({ theme }) => theme.colors.light};
  padding: 15px 40px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
`;
const RightTitle = styled.div `
  margin-left: auto;
`;
const Items = styled.div `
  display: flex;

  > *:not(:last-child) {
    margin-right: 30px;
  }
`;
const NavBar = (props) => {
    const { children, rightTitle } = props;
    return (React.createElement(Nav, null,
        React.createElement(Items, null, children),
        React.createElement(RightTitle, null,
            React.createElement(SpanSubtitle2, null, rightTitle))));
};

const Wrapper$5 = styled.div `
  ${getTypo('subtitle1')};
  color: ${({ active, color, theme }) => (active ? color : theme.colors.grey40)};
  cursor: pointer;

  &:hover {
    color: ${({ active, color, theme }) => (active ? color : lighten(0.1, theme.colors.grey40))};
  }
`;
const NavLink = (props) => {
    const { label, active, icon } = props;
    const theme = useContext(ThemeContext);
    return (React.createElement(Wrapper$5, { title: label, active: active, color: theme.colors.primary },
        React.createElement(Flex, { alignItems: ['center'] },
            React.createElement(Svg, { icon: icon, height: 24, width: 24, fill: active ? theme.colors.primary : theme.colors.grey40, style: { marginRight: '10px' } }),
            React.createElement("span", null, label))));
};

const Wrapper$4 = styled.div `
  background: ${({ theme }) => theme.colors.grey80};
  border-radius: 4px;
  height: 8px;
  width: 100%;
  position: relative;
`;
const Bar = styled.div `
  background-color: ${({ bgColor, theme }) => bgColor || theme.colors.primary};
  width: 0;
  transition: width 200ms ease-in-out;
  height: 100%;
  border-radius: 4px;
`;
const ProgressBar = (props) => {
    const { value, color } = props;
    return (React.createElement(Wrapper$4, null,
        React.createElement(Bar, { style: { width: value + '%' }, bgColor: color })));
};

const InvisibleInput = styled.input `
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;
const Label = styled.label `
  display: inline-block;
  white-space: nowrap;
`;
const Circle = styled.span `
  display: inline-block;
  vertical-align: top;

  > svg {
    border-radius: 50%;

    > :first-child {
      stroke: ${({ hasError, theme, checked }) => checked
    ? theme.colors.primary
    : hasError
        ? theme.colors.danger + '!important'
        : theme.colors.grey60};
    }

    > :last-child {
      fill: ${({ checked, theme }) => (checked ? theme.colors.primary : 'transparent')};
      transition: transform ease-in-out 150ms;
      transform: scale(${({ checked }) => (checked ? 1 : 0)});
      transform-origin: center;
    }
  }
`;
const Title$1 = styled(SpanBody1) `
  display: inline-block;
  white-space: normal;
  transition: color ease-in-out 200ms;
  color: ${({ checked, theme }) => (checked ? theme.colors.grey10 : theme.colors.grey60)};

  ${Label}:hover & {
    ${({ disabled, theme }) => (disabled ? '' : 'color: ' + theme.colors.grey40)};
  }

  ${Label}:focus-within & {
    text-decoration: underline;
  }
`;
const Radio = (props) => {
    const theme = useContext(ThemeContext);
    const { onChange, value, children, label, right, hasError, caption, disabled, required, name, small } = props, rest = __rest(props, ["onChange", "value", "children", "label", "right", "hasError", "caption", "disabled", "required", "name", "small"]);
    const changeCheckbox = (event) => {
        const target = event.target;
        onChange(target.checked, event);
    };
    const renderTitle = (React.createElement(Title$1, { checked: value, disabled: disabled, style: right
            ? {
                textAlign: 'right',
                marginRight: '5px',
            }
            : {
                textAlign: 'left',
                marginLeft: '5px',
            } }, children));
    return (React.createElement(LabelWrapper, Object.assign({}, { label, caption, required, hasError, right, disabled, small }),
        React.createElement(Label, null,
            React.createElement(InvisibleInput, Object.assign({}, rest, { disabled, required }, { onChange: changeCheckbox, checked: value, name: name, type: 'radio' })),
            right && renderTitle,
            React.createElement(Circle, { checked: value, hasError: hasError, disabled: disabled },
                React.createElement(Svg, { fill: theme.colors.light, width: 24, height: 24 },
                    React.createElement("circle", { cx: "24", cy: "24", r: "18", strokeWidth: "4" }),
                    React.createElement("circle", { cx: "24", cy: "24", r: "12" }))),
            !right && renderTitle)));
};

const RadioGroup = (props) => {
    const { value, onChange, label, caption, disabled, hasError, required, right, options, groupName, small, } = props;
    return (React.createElement(LabelWrapper, Object.assign({}, { label, caption, disabled, hasError, required, right, small }),
        React.createElement(Row, { wraps: ['wrap'] }, options.map((o) => (React.createElement(Col, { key: o.uid || o.name, columns: o.columns || [12, 6, 4, 3] },
            React.createElement(Radio, { label: o.label, caption: o.caption, required: required, disabled: o.disabled, onChange: () => onChange(o), value: o.uid && (value === null || value === undefined ? undefined : value.uid) ? o.uid === value.uid : o.name === (value === null || value === undefined ? undefined : value.name), name: groupName }, o.name)))))));
};

function Select(props) {
    const { typing, onTyping, onSelect, selected, caption, label, hasError, disabled, required, right, options, readOnly, small, debounceTimeout, onDebounceTyping, } = props;
    const [getTyping, setTyping] = useState(typing || '');
    const [getSelected, setSelected] = useState();
    useEffect(() => {
        if (typeof typing === 'undefined') {
            return;
        }
        setTyping(typing);
    }, [typing]);
    useEffect(() => {
        setSelected(selected);
        if (selected) {
            setTyping(selected.label);
        }
    }, [selected]);
    const [getFiltered, setFiltered] = useState(options || []);
    const [getSelIndex, setSelIndex] = useState();
    const [getNoFilter, setNoFilter] = useState(true);
    const [getOpen, setOpen] = useState(false);
    const refWrapper = useRef(null);
    useEffect(() => {
        if (!options) {
            return;
        }
        if (getNoFilter) {
            setFiltered(options);
            return;
        }
        const normalizeInput = NormalizeText(getTyping);
        setFiltered(options.filter((item) => {
            const normalizeItem = NormalizeText(item.label);
            return MatchLike(normalizeItem, normalizeInput);
        }));
    }, [getTyping, options, getNoFilter]);
    useEffect(() => {
        if (!getFiltered.length) {
            setSelIndex(undefined);
            return;
        }
        if (!getSelected) {
            setSelIndex(0);
            return;
        }
        const searchIndex = getFiltered.findIndex((f) => f.label === getSelected.label);
        if (searchIndex < 0) {
            setSelIndex(0);
            return;
        }
        setSelIndex(searchIndex);
    }, [getFiltered, getSelected]);
    const debounceRef = useRef();
    const emitTyping = (val) => {
        setNoFilter(false);
        setOpen(true);
        if (onTyping) {
            onTyping(val);
        }
        else {
            setTyping(val);
        }
        if (!onDebounceTyping) {
            return;
        }
        debounceRef.current = Debounce(() => {
            onDebounceTyping(val);
        }, debounceTimeout || 500, debounceRef.current || 0);
    };
    const getItemByIndex = (index) => {
        if (typeof index === 'undefined') {
            return;
        }
        return getFiltered[index];
    };
    const emitSelection = (item) => {
        if (!item) {
            return;
        }
        if (onSelect) {
            onSelect(item);
        }
        else {
            setSelected(item);
        }
        setOpen(false);
        setTimeout(() => {
            //for let time dropdown close
            setNoFilter(true);
        }, 300);
    };
    const selectDown = () => {
        if (typeof getSelIndex === 'undefined') {
            return;
        }
        let newIndex = getSelIndex + 1;
        if (newIndex > getFiltered.length - 1) {
            newIndex = 0;
        }
        setSelIndex(newIndex);
    };
    const selectUp = () => {
        if (typeof getSelIndex === 'undefined') {
            return;
        }
        let newIndex = getSelIndex - 1;
        if (newIndex < 0) {
            newIndex = setFiltered.length - 1;
        }
        setSelIndex(newIndex);
    };
    const onKeyDown = (event) => {
        if (readOnly) {
            return;
        }
        switch (event.keyCode) {
            case 40: // down
                selectDown();
                setOpen(true);
                break;
            case 38: // up
                selectUp();
                setOpen(true);
                break;
            case 27: // escape
                setTyping('');
                break;
            case 13: // enter
                emitSelection(getItemByIndex(getSelIndex));
                break;
        }
    };
    return (React.createElement("div", { style: { position: 'relative', width: '100%' }, ref: refWrapper },
        React.createElement(Input, Object.assign({}, { caption, label, hasError, disabled, required, right, small }, { onChange: emitTyping, value: getTyping, autoComplete: "off", onFocus: readOnly ? undefined : () => setOpen(true), onKeyDown: onKeyDown, readOnly: readOnly, icon: readOnly ? undefined : 'down' })),
        refWrapper && (React.createElement(Dropdown, { wrapperRef: refWrapper, onClose: () => setOpen(false), fulfilled: true, show: getOpen, offset: -12 },
            React.createElement(SelectMenu, { onSelect: emitSelection, highlight: getTyping, selected: typeof getSelIndex !== 'undefined' ? getFiltered[getSelIndex] : undefined, items: getFiltered })))));
}

function SimpleSelect(props) {
    const { onSelect, selected, caption, label, hasError, disabled, required, right, options, readOnly, small, } = props;
    const [getSelIndex, setSelIndex] = useState(null);
    useEffect(() => {
        if (!selected) {
            return;
        }
        const find = options.findIndex((f) => f.uid && selected.uid ? f.uid === selected.uid : f.label === selected.label);
        if (find < 0) {
            return;
        }
        setSelIndex(find);
    }, [selected, options]);
    const [getOpen, setOpen] = useState(false);
    const refWrapper = useRef(null);
    const emitSelection = (item) => {
        if (!item) {
            return;
        }
        if (onSelect) {
            onSelect(item);
        }
        setOpen(false);
    };
    const selectDown = () => {
        if (getSelIndex === null) {
            setSelIndex(0);
            return;
        }
        let newIndex = getSelIndex + 1;
        if (newIndex > options.length - 1) {
            newIndex = 0;
        }
        setSelIndex(newIndex);
    };
    const selectUp = () => {
        if (getSelIndex === null) {
            setSelIndex(options.length - 1);
            return;
        }
        let newIndex = getSelIndex - 1;
        if (newIndex < 0) {
            newIndex = options.length - 1;
        }
        setSelIndex(newIndex);
    };
    const onKeyDown = (event) => {
        if (readOnly) {
            return;
        }
        switch (event.keyCode) {
            case 40: // down
                selectDown();
                setOpen(true);
                break;
            case 38: // up
                selectUp();
                setOpen(true);
                break;
            case 27: // escape
                setOpen(false);
                break;
            case 13: // enter
                if (getSelIndex !== null) {
                    emitSelection(options[getSelIndex]);
                }
                break;
            case 9: // tab
                break;
            default: {
                const filterPropositions = options
                    .map((m, i) => (Object.assign(Object.assign({}, m), { originIndex: i })))
                    .filter((f) => f.label.toLowerCase().startsWith(String.fromCharCode(event.keyCode).toLowerCase()));
                if (!filterPropositions.length) {
                    return;
                }
                const filterIndex = filterPropositions.findIndex((fi) => fi.originIndex === getSelIndex);
                if (filterIndex > -1) {
                    let newIndex = filterIndex + 1;
                    if (newIndex > filterPropositions.length - 1) {
                        newIndex = 0;
                    }
                    setSelIndex(filterPropositions[newIndex].originIndex);
                }
                else {
                    setSelIndex(filterPropositions[0].originIndex);
                }
            }
        }
    };
    return (React.createElement("div", { style: { position: 'relative', width: '100%' }, ref: refWrapper },
        React.createElement(Input, Object.assign({}, { caption, label, hasError, disabled, required, right, small }, { onChange: () => { }, value: (selected === null || selected === undefined ? undefined : selected.label) || '', autoComplete: "off", onFocus: readOnly ? undefined : () => setOpen(true), onKeyDown: onKeyDown, readOnly: readOnly, icon: readOnly ? undefined : 'down' })),
        refWrapper && (React.createElement(Dropdown, { wrapperRef: refWrapper, onClose: () => setOpen(false), fulfilled: true, show: getOpen, offset: -12 },
            React.createElement(SelectMenu, { onSelect: emitSelection, selected: getSelIndex !== null ? options[getSelIndex] : undefined, items: options })))));
}

const TabNavBarContainer = styled.nav `
  border-color: #e6e6e6;
  border-width: 1px;
  border-top-style: solid;
  border-bottom-style: solid;
  padding: 0 20px;
  display: flex;
  overflow: auto;
  justify-content: ${({ withSteps }) => (withSteps ? 'center' : 'left')};
`;
const TabNavBarItem = styled(BaseButton) `
  padding: 13px 20px 9px;
  border-radius: 0;
  border-width: 2px;
  border-style: none none solid none;
  background: transparent;
  transition: all ease-in-out 200ms;
  border-color: ${({ active, withSteps, theme }) => withSteps ? 'transparent' : active ? theme.colors.primary : 'transparent'};
  color: ${({ active, previous, withSteps, theme }) => active || (previous && withSteps) ? theme.colors.primary : theme.colors.grey40};

  &:not(:disabled):hover {
    color: ${({ active, previous, withSteps, theme }) => active || (previous && withSteps) ? theme.colors.primary : theme.colors.grey20};
  }
`;
const TabBar = (props) => {
    const { tabTitles, withSteps, activeIndex, onChange } = props;
    const theme = useContext(ThemeContext);
    return (React.createElement(TabNavBarContainer, { withSteps: withSteps }, tabTitles.map((title, index) => (React.createElement(Flex, { key: index, alignItems: ['center'] },
        withSteps && index !== 0 && (React.createElement(Svg, { width: 24, height: 24, icon: 'right', fill: theme.colors.grey80 })),
        React.createElement(TabNavBarItem, { withSteps: withSteps, onClick: () => onChange(index), previous: index < activeIndex, active: index === activeIndex, disabled: withSteps && index > activeIndex },
            React.createElement(SpanSubtitle1, null, title)))))));
};

const Tabs = ({ disabled, tabs, margin }) => {
    const [activeTab, setActiveTab] = useState(0);
    const onTabChange = (tab) => {
        if (!disabled) {
            setActiveTab(tab);
        }
    };
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { style: { margin: margin && margin, opacity: !disabled ? 1 : 0.35 } },
            React__default.createElement(TabBar, { activeIndex: activeTab, tabTitles: tabs.map((el) => el.title), onChange: onTabChange })),
        tabs[activeTab].content));
};

const TagButton = styled(Button) `
  padding-left: 6px;
  padding-right: 6px;
`;
const Tag = (props) => {
    const { color, onClick, children, showCloseIcon, fontColor } = props;
    const fc = fontColor || SelectContrast(color, ['white', 'black']);
    return (React.createElement(TagButton, { small: true, fontColor: fc, backgroundColor: color, onClick: onClick, icon: showCloseIcon ? 'cross' : undefined, iconRight: true }, children));
};

const BaseTextareaStyled = styled(BaseInput) `
  height: initial;
  resize: none;
  overflow: hidden;
  white-space: pre-wrap;
  min-height: 90px;
  line-height: 1.25rem;
  padding: ${({ small }) => (small ? '5px' : '12px 8px')};
`;
const Textarea = (props) => {
    const { caption, disabled, hasError, required, right, label, onChange, value, noResize, small, readOnly } = props, rest = __rest(props, ["caption", "disabled", "hasError", "required", "right", "label", "onChange", "value", "noResize", "small", "readOnly"]);
    const refTextArea = React.useRef();
    const resize = () => {
        if (noResize) {
            return;
        }
        const textarea = refTextArea.current;
        if (!textarea)
            return;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    };
    const handler = (name, event) => {
        window.setTimeout(() => {
            resize();
        }, 0);
        if (name === 'onChange') {
            const target = event.target;
            onChange && onChange(target.value, event);
            return;
        }
        const eventFn = props[name];
        if (eventFn) {
            eventFn(event);
        }
    };
    React.useEffect(() => {
        resize();
    });
    return (React.createElement(LabelWrapper, Object.assign({}, { label, caption, disabled, hasError, required, right, small }),
        React.createElement(BaseTextareaStyled, Object.assign({}, rest, { disabled, hasError, required, right, small }, { value: value || '', ref: refTextArea, as: "textarea", readOnly: onChange ? readOnly : true, onChange: (e) => handler('onChange', e), onCut: (e) => handler('onCut', e), onPaste: (e) => handler('onPaste', e), onDrop: (e) => handler('onDrop', e), onKeyDown: (e) => handler('onKeyDown', e) }))));
};

const IconButton = styled(BaseButton) `
  background: transparent;
  padding: 0 8px;
`;
const NumberSelect = (props) => {
    const { value, onChange, max, min } = props;
    const change = (event) => {
        const element = event.target;
        let val = Number.parseInt(element.value, 10);
        if (val >= max) {
            val = max;
        }
        if (val <= min) {
            val = min;
        }
        onChange(val);
    };
    const showVal = Pad(value, 2);
    const upValue = () => {
        onChange(Increment(value, 1, min, max, true));
    };
    const downValue = () => {
        onChange(Increment(value, -1, min, max, true));
    };
    return (React.createElement("div", { style: { width: '45px', textAlign: 'center' } },
        React.createElement(IconButton, { onClick: upValue },
            React.createElement(Svg, { icon: 'up', width: 24, height: 24 })),
        React.createElement(BaseInput, { value: showVal, onChange: change, type: "text", style: { paddingLeft: '0', paddingRight: '0', textAlign: 'center' } }),
        React.createElement(IconButton, { onClick: downValue },
            React.createElement(Svg, { icon: 'down', width: 24, height: 24 }))));
};

const TimeSelectContainer = styled.div `
  ${Shapes.card};
  width: 140px;
  background: ${({ theme }) => theme.colors.light};
`;
const TimeSelect = (props) => {
    const { value, defaultTime, onChange } = props;
    const regex = /^(\w{2}):(\w{2})$/;
    const exec = regex.exec(value);
    let hours = 12;
    let minutes = 0;
    if (exec) {
        hours = Number.parseInt(exec[1]);
        minutes = Number.parseInt(exec[2]);
    }
    else {
        if (defaultTime) {
            const execDefault = regex.exec(defaultTime);
            if (execDefault) {
                hours = Number.parseInt(execDefault[1]);
                minutes = Number.parseInt(execDefault[2]);
            }
        }
    }
    const changeHour = (event) => {
        const val = Pad(event, 2) + ':' + Pad(minutes, 2);
        onChange(val);
    };
    const changeMinute = (event) => {
        const val = Pad(hours, 2) + ':' + Pad(event, 2);
        onChange(val);
    };
    return (React.createElement(TimeSelectContainer, null,
        React.createElement(Flex, { alignItems: ['center'], style: { padding: '0 16px' } },
            React.createElement(NumberSelect, { max: 23, min: 0, onChange: changeHour, value: hours }),
            React.createElement("span", { style: { margin: '0 5px' } }, ":"),
            React.createElement(NumberSelect, { max: 59, min: 0, onChange: changeMinute, value: minutes }))));
};

const InputContainer = styled.div `
  width: 140px;
`;
const TimePicker = (props) => {
    const { label, caption, disabled, hasError, required, right, onChange, value } = props, rest = __rest(props, ["label", "caption", "disabled", "hasError", "required", "right", "onChange", "value"]);
    const refTimePicker = React.useRef();
    const [open, setOpen] = React.useState(false);
    const emitChange = (val) => {
        onChange(val);
    };
    const changeInput = (event) => {
        const element = event.target;
        let val = element.value;
        const regexAddSeparation = /^\d{3}$/;
        if (val.match(regexAddSeparation)) {
            val = val.substr(0, 2) + ':' + val[2];
        }
        const regexTime = /^(\d{0,2}):?(\d{0,2})$/;
        const match = val.match(regexTime);
        if (!match) {
            return;
        }
        const exec = regexTime.exec(val);
        if (!exec) {
            return;
        }
        const hours = parseInt(exec[1], 10);
        const minutes = parseInt(exec[2], 10);
        if (hours > 23 || minutes > 59) {
            return;
        }
        emitChange(val);
    };
    const autoClose = () => {
        setOpen(false);
    };
    const openDropDown = () => {
        setOpen(true);
    };
    return (React.createElement("div", { style: { position: 'relative', width: '100%' }, ref: refTimePicker },
        React.createElement(LabelWrapper, Object.assign({}, { label, caption, disabled, hasError, required, right }),
            React.createElement(InputContainer, null,
                React.createElement(BaseInput, Object.assign({}, rest, { required: required, disabled: disabled, hasError: hasError, autoComplete: "off", value: value, onFocus: openDropDown, onChange: changeInput, type: "text" }))),
            React.createElement(Dropdown, { show: open, onClose: autoClose, wrapperRef: refTimePicker, offset: -8 },
                React.createElement(TimeSelect, { onChange: emitChange, value: value })))));
};

const Wrapper$3 = styled.div `
  ${Shapes.card};
  padding: 24px;
  border: transparent solid 1px;
  display: inline-block;
  margin: 10px auto;
`;
const Toast = ({ children, type, outlined, icon, closeButton }) => {
    const theme = useContext(ThemeContext);
    let color = theme.colors.light;
    let backgroundColor = theme.colors.grey10;
    let borderColor = theme.colors.grey10;
    switch (type) {
        case 'default':
            if (!outlined) {
                break;
            }
            color = theme.colors.grey40;
            borderColor = theme.colors.grey40;
            backgroundColor = theme.colors.grey95;
            break;
        case 'error':
            if (!outlined) {
                color = theme.colors.grey10;
                borderColor = theme.colors.danger;
                backgroundColor = theme.colors.danger;
                break;
            }
            color = '#BC3F40';
            borderColor = '#BC3F40';
            backgroundColor = '#FFE4E4';
            break;
        case 'success':
            if (!outlined) {
                color = theme.colors.grey10;
                borderColor = theme.colors.success;
                backgroundColor = theme.colors.success;
                break;
            }
            color = '#199752';
            borderColor = '#199752';
            backgroundColor = '#DCF8E7';
            break;
    }
    return (React__default.createElement(Wrapper$3, { style: { backgroundColor, borderColor } },
        React__default.createElement(Row, { justifyContent: ['center'], alignItems: ['start'] },
            icon ? (React__default.createElement(Col, null,
                React__default.createElement(Svg, { icon: icon, fill: color, width: 18, height: 18 }))) : null,
            React__default.createElement(Col, null,
                React__default.createElement(SpanSubtitle1, { style: { color } }, children)),
            closeButton ? (React__default.createElement(Col, null,
                React__default.createElement(Svg, { icon: 'cross-circle', fill: color, width: 18, height: 18 }))) : null)));
};
const ToasterContext = createContext(() => {
    console.info('toaster provider is not init add ToasterProvider in parent');
});
const useToaster = () => useContext(ToasterContext);
const ToastPositionContainer = styled.div `
  position: fixed;
  width: 100%;
  bottom: 10px;
  left: 0;
  pointer-events: none;
  text-align: center;
`;
const ToasterProvider = ({ children }) => {
    const [getToasts, setToast] = useReducer(ReducerArray, []);
    const count = useRef(0);
    const push = (message, config = {}, time) => {
        const newCount = count.current++;
        setToast({ push: { message, config, uid: newCount, show: true } });
        setTimeout(() => {
            setToast({
                findAnd: {
                    selection: { multiple: { key: 'uid', equal: { uid: newCount } } },
                    action: { merge: { show: false } },
                },
            });
            setTimeout(() => {
                setToast({
                    findAnd: {
                        selection: { multiple: { key: 'uid', equal: { uid: newCount } } },
                        action: { remove: true },
                    },
                });
            }, 250);
        }, time || 5000);
    };
    return (React__default.createElement(ToasterContext.Provider, { value: push },
        children,
        React__default.createElement(Portal, { attributeName: 'data-toaster' },
            React__default.createElement(ToastPositionContainer, null, getToasts.map((t) => (React__default.createElement(InOut, { show: t.show, startTriggering: true, key: t.uid, enter: { keyframes: Keyframes.slideInFrom(undefined, '50px') }, exit: { keyframes: Keyframes.slideOutFrom(undefined, '-50px') } },
                React__default.createElement(Toast, Object.assign({}, t.config), t.message))))))));
};

const Wrapper$2 = styled.div `
  padding: 25px;
  background-color: ${({ bgColor }) => bgColor};
`;
const Title = styled.h1 `
  margin: 0;
`;
const Subtitle = styled.h2 `
  margin: 0;
`;
const TopHeader = (props) => {
    const { title, subtitle } = props;
    const theme = React.useContext(ThemeContext);
    return (React.createElement(Wrapper$2, { bgColor: theme.colors.primary },
        React.createElement(Row, { alignItems: ['center'] },
            React.createElement(Col, null,
                React.createElement(Title, null,
                    React.createElement(SpanH1, { style: { color: theme.colors.secondary } }, title))),
            React.createElement(Col, null,
                React.createElement(Subtitle, null,
                    React.createElement(SpanBody1, { style: { color: theme.colors.invertPrimary } }, subtitle))))));
};

const Wrapper$1 = styled.div `
  z-index: 5;
  position: absolute;
  width: 100%;
  ${({ sticky }) => sticky
    ? `
    position: fixed;
    top:0;
    `
    : ''};
`;
const TopSticky = (props) => {
    const { children } = props;
    const [getSticky, setSticky] = useState(false);
    const navRef = useRef();
    const [getHeight, setHeight] = useState(0);
    const [getOffset, setOffset] = useState();
    useEffect(() => {
        const onScroll = () => {
            const query = navRef.current;
            if (!query) {
                return;
            }
            const contentRect = query.getBoundingClientRect();
            if (contentRect.top <= 0) {
                setSticky(true);
            }
            if (typeof getOffset === 'undefined') {
                return;
            }
            if (window.scrollY <= getOffset) {
                setSticky(false);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        if (typeof navRef !== 'undefined') {
            const current = navRef.current;
            if (current) {
                const rect = current.getBoundingClientRect();
                setHeight(rect.height);
                if (typeof getOffset === 'undefined') {
                    setOffset(rect.top + window.scrollY);
                }
            }
        }
        return () => window.removeEventListener('scroll', onScroll);
    }, [navRef, getOffset]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Wrapper$1, { sticky: getSticky, ref: navRef }, children),
        React.createElement("div", { style: { height: getHeight } })));
};

class UploaderApi {
    constructor(apiBaseUri) {
        this.apiBaseUri = apiBaseUri;
    }
    async createMedias(codeBouton, body, token, signal) {
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const options = {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal,
        };
        const response = await fetch(`${this.apiBaseUri}/${codeBouton}/medias`, options);
        if (!response.ok) {
            throw new Error(`Failed to create media: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }
    async addChunks(codeBouton, mediaId, file, index, token, signal) {
        const formData = new FormData();
        formData.append('file', file);
        const headers = {
            'Media-Chunk-Index': index.toString(),
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const options = {
            method: 'POST',
            headers,
            body: formData,
            signal,
        };
        const response = await fetch(`${this.apiBaseUri}/${codeBouton}/medias/${mediaId}/chunks`, options);
        if (!response.ok) {
            throw new Error(`Failed to add chunks: ${response.status} ${response.statusText}`);
        }
    }
    async completeMedias(codeBouton, mediaId, status, token, signal) {
        const body = JSON.stringify({ status });
        const headers = {
            'Content-Type': 'application/json',
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const options = {
            method: 'PATCH',
            headers,
            body,
            signal,
        };
        const response = await fetch(`${this.apiBaseUri}/${codeBouton}/medias/${mediaId}`, options);
        if (!response.ok) {
            throw new Error(`Failed to complete media: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }
    async getMedias(codeBouton, mediaId, token, signal) {
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const options = {
            method: 'GET',
            headers,
            signal,
        };
        const response = await fetch(`${this.apiBaseUri}/${codeBouton}/medias/${mediaId}`, options);
        if (!response.ok) {
            throw new Error(`Failed to get media: ${response.status} ${response.statusText}`);
        }
        return await response.json();
    }
}

const Wrapper = styled.div `
  width: ${({ width }) => width || '300px'};
  height: ${({ height }) => height || '200px'};
  position: relative;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.box.borderRadius};
  border: 1px solid ${({ theme, error }) => (error ? theme.colors.danger : theme.colors.grey90)};
  text-align: center;
`;
const Container = styled.div `
  background-color: ${({ theme }) => theme.colors.grey90};
  border-radius: inherit;
  box-sizing: border-box;
  position: absolute;
  width: 100%;
  height: 100%;
`;
const Preview = styled.div `
  width: 100%;
  height: 100%;
  border-radius: 3px;
  background-color: #ccc;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  position: absolute;
`;
const Uploader = ({ small, middleLabel, buttonLabel, height, width, value, previewStyle, apiBaseUri, onError, codeBouton, onChange, token, fileType, previewSize, }) => {
    var _a, _b, _c;
    const theme = useContext(ThemeContext);
    const [getLocal, setLocal] = useState('');
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(false);
    const api = useMemo(() => new UploaderApi(apiBaseUri), [apiBaseUri]);
    const controller = useRef();
    useEffect(() => {
        return () => { var _a; return (_a = controller.current) === null || _a === undefined ? undefined : _a.abort(); };
    }, []);
    const onUpload = async (val) => {
        var _a, _b, _c, _d, _e, _f;
        (_a = controller.current) === null || _a === undefined ? undefined : _a.abort();
        controller.current = new AbortController();
        try {
            setLoading(true);
            const resp = await api.createMedias(codeBouton, {
                fileSize: val.size,
                mimeType: val.type,
            }, token, (_b = controller.current) === null || _b === void 0 ? void 0 : _b.signal);
            if (!resp.chunkCount || !resp.id || !resp.chunkSize) {
                setHasError(true);
                return;
            }
            let index = 0;
            do {
                await api.addChunks(codeBouton, resp.id, val.slice(index * resp.chunkSize, resp.chunkSize + index * resp.chunkSize), index, token, (_c = controller.current) === null || _c === void 0 ? void 0 : _c.signal);
                index++;
            } while (index < resp.chunkCount);
            let response = await api.completeMedias(codeBouton, resp.id, completed, token, (_d = controller.current) === null || _d === void 0 ? void 0 : _d.signal);
            do {
                response = await api.getMedias(codeBouton, resp.id, token, (_e = controller.current) === null || _e === void 0 ? void 0 : _e.signal);
                switch (response.status) {
                    default:
                    case completed:
                        await new Promise((resolve) => setTimeout(resolve, 2000));
                        break;
                    case error:
                        setHasError(true);
                        onError && onError((_f = response.error) === null || _f === void 0 ? void 0 : _f.message);
                        break;
                    case finalized:
                        onChange(response);
                        setHasError(false);
                        break;
                }
            } while (response.status === completed);
            setLoading(false);
        }
        catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                setLoading(true);
            }
            else {
                setHasError(true);
                onError && onError(e);
                setLoading(false);
            }
        }
    };
    const onDrop = async (event) => {
        event.preventDefault();
        const data = event.dataTransfer.files;
        await onUpload(data[0]);
    };
    const onInputChange = async (event) => {
        event.preventDefault();
        const target = event.target;
        setLocal(target.value);
        const files = target.files || new FileList();
        await onUpload(files[0]);
    };
    const onDeleteClick = async () => {
        setLocal('');
        onChange(undefined);
    };
    return (React.createElement(Wrapper, { error: hasError, onDragOver: (e) => e.preventDefault(), onDrop: onDrop, width: width, height: height },
        React.createElement(Container, null,
            React.createElement(Flex, { style: {
                    flexDirection: 'column',
                    flexWrap: 'nowrap',
                    width: '100%',
                    height: '100%',
                    rowGap: 10,
                }, alignItems: ['center'], justifyContent: ['center'] },
                React.createElement(Svg, { icon: 'image', fill: theme.colors.grey40, width: small ? 32 : 48, height: small ? 32 : 48 }),
                React.createElement("div", { style: { width: small ? '100%' : '60%' } }, small ? (React.createElement(SpanSubtitle1, null, middleLabel)) : (React.createElement(SpanBody1, null, middleLabel))),
                React.createElement("label", null,
                    React.createElement("input", { value: getLocal, accept: fileType, type: 'file', onChange: onInputChange, style: { opacity: 0, position: 'absolute', width: 0, height: 0 } }),
                    small ? (React.createElement(SpanLink, { style: { fontSize: '15px' } }, buttonLabel)) : (React.createElement(Button, { as: 'div', colorType: 'secondary' }, buttonLabel))))),
        React.createElement(InOut, { show: !!(value === null || value === undefined ? undefined : value.thumbnails), startTriggering: true },
            React.createElement(Preview, { style: Object.assign(Object.assign({}, previewStyle), { backgroundImage: (value === null || value === undefined ? undefined : value.thumbnails)
                        ? `url(${previewSize && value.thumbnails[previewSize]
                            ? value.thumbnails[previewSize]
                            : (_c = (_b = (_a = value.thumbnails.large) !== null && _a !== undefined ? _a : value.thumbnails.medium) !== null && _b !== undefined ? _b : value.thumbnails.small) !== null && _c !== undefined ? _c : value.url})`
                        : 'none' }) },
                React.createElement(Flex, { justifyContent: ['flex-end'], style: { padding: small ? '5px' : '10px' } },
                    React.createElement(SquareButton, { icon: 'cross', small: small, onClick: onDeleteClick })))),
        React.createElement(InOut, { show: loading },
            React.createElement(Container, null,
                React.createElement(Flex, { style: { width: '100%', height: '100%' }, alignItems: ['center'], justifyContent: ['center'] },
                    React.createElement(Svg, { stroke: theme.colors.dark, animatedIcon: 'spinner' }))))));
};

export { AddDay, AnimatedIcons, BaseButton, BaseInput, BaseInputImage, BaseInputNumber, Box, BubbleComponent, Button, ButtonSelect, Calendar, Card, CardSubtitle, CardTitle, Checkbox, Col, ColorPicker, ColorSelect, ColorSelectGradientToCss, Compare, Container$4 as Container, ContainerSizeFluid, ContainerSizeLG, ContainerSizeMD, ContainerSizeSM, ContainerSizes, CoordinateToHsv, CssHexaColorToHsv, DEVICES, DashboardCard, DashboardCardBody, DashboardCardSubtitle, DashboardCardTitle, DashboardSlider, DatePicker, DayName, DaysName, Debounce, DecToHexaString, Deletable, Dialog, DraggableCard, DraggableList, Dropdown, DropdownMask, Evaluation, ExcludeChildrenByType, FilterMenu, FilterSelect, FindAndDeleteItemInArray, FindAndReplaceItemInArray, FirstDayOfMonth, FirstMonDayOfWeek, Flex, FlexColToCss, FlexCss, FlexWidth, Fonts, H1, H2, H3, H4, H5, H6, Hide, HslToCss, HslToHsv, HslToRgb, HsvToCoordinate, HsvToCssHexaColor, HsvToHsl, HueToRgb, IconsBackNames, IconsBackPaths, IconsFrontNames, IconsFrontPaths, ImageBlockUploader, InOut, Increment, InfiniteScroll, Input, InputDate, InputImage, InputNumber, IsDisabledDate, IsSameDay, Keyframes, LabelWrapper, LastDayOfMonth, List, LoadingContent, LoopInRange, MakeCssMediaQuery, MakeCssMediaQueryStrict, MatchLike, MergeItemInArray, MonthName, MonthsNames, NavBar, NavLink, NormalizeText, P, Pad, PageSubtitle, PageTitle, PickChoiceByDevice, Portal, ProgressBar, QueryChildrenByType, Radio, RadioGroup, RangeSign, ReadFileToDataUrl, ReducerArray, ReducerNumber, ReducerObject, RemoveItemInArray, RemoveItemInObject, ReplaceItemInArray, ResponsiveAlignItems, ResponsiveCol, ResponsiveDirection, ResponsiveJustifyContent, ResponsiveWrap, RgbToHsl, Ripple, Round, Row, Select, SelectContrast, SelectMenu, SetInRange, Shapes, SimpleSelect, SpanBody1, SpanBody2, SpanButton, SpanCaption1, SpanCaption2, SpanH1, SpanH2, SpanH3, SpanH4, SpanH5, SpanH6, SpanLink, SpanSmallButton, SpanSubtitle1, SpanSubtitle2, SquareButton, Star, Svg, Switch, TabBar, Tabs, Tag, Textarea, ThemeProvider, TimePicker, TimeSelect, Toast, ToasterProvider, TopHeader, TopSticky, TranslateItemInArray, UpdateItemInObject, Uploader, completed, created, dateToString, error, finalized, focusFirstFocusableElement, focusLastFocusableElement, getDayOfWeek, getFocusable, getTypo, localToDateParams, mergeStyled, setInRange, useRow, useToaster };
